/**
 * @fsp/contracts — Zod schemas and TypeScript types for API boundaries.
 *
 * This package is the single import path that API handlers (`rest-api`, `mcp-server`,
 * `wa-bot`) use for:
 *
 *   1. Request validation — the `*InputSchema` Zod schemas from `@fsp/pricing-engine`.
 *   2. Response typing — the `*Result` TypeScript types from `@fsp/pricing-engine`.
 *   3. Error handling — the pricing error classes for catching engine throws.
 *   4. Response envelopes — `ApiSuccessEnvelope` / `ApiErrorEnvelope` per CLAUDE.md § 3.4.
 *
 * Per CLAUDE.md § 4.2: "Zod schemas for every API boundary. No logic." Error-to-envelope
 * translation lives in `@fsp/utils` or handler code, not here.
 */
import { GangSheetSpecSchema } from '@fsp/gang-sheet-engine/schemas';
import { QuoteScreenPrintInputSchema } from '@fsp/pricing-engine';
import { z } from 'zod';
// ─────────────────────────────────────────────────────────────────
// Quote input schemas (for request validation)
// ─────────────────────────────────────────────────────────────────
export { PrintService, QuoteDtfPressAndPressInputSchema, QuoteDtfPressAndPressResultSchema, QuoteDtfTransferInputSchema, QuoteDtfTransferResultSchema, QuoteDtgInputSchema, QuoteDtgResultSchema, QuoteEmbroideryInputSchema, QuoteEmbroideryResultSchema, QuoteScreenPrintBreakdownSchema, QuoteScreenPrintInputSchema, QuoteScreenPrintResultSchema, RouteJobInputSchema, RouteJobResultSchema, SP_MIN_QTY, } from '@fsp/pricing-engine';
export { BelowFloorError, BelowMinimumError, isPricingError, OversizedBlankCostRequiredError, PricingError, RouteViolationError, ValidationError, } from '@fsp/pricing-engine';
export { applyMigrations, CURRENT_SCHEMA_VERSION, DEFAULT_PRICING_CONFIG_BODY, DtfPressAndPressConfigSchema, DtfTransferConfigSchema, DtgConfigSchema, EmbroideryConfigSchema, PricingConfigBodySchema, PricingConfigMetaSchema, PricingConfigSchema, RoutingConfigSchema, ScreenPrintConfigSchema, UniversalConfigSchema, } from '@fsp/pricing-engine';
// ─────────────────────────────────────────────────────────────────
// Pricing config viewer (Phase 3)
//
// Wire schema for `GET /pricing-config/current`. The dashboard renders
// this read-only. The body has every INTERNAL field marked optional so
// the same response shape works for two roles:
//   - admin (Cognito group `pricing-admin`) — receives the full body
//   - staff (any other signed-in user) — receives the body with
//     INTERNAL fields stripped server-side.
// `internalFieldsHidden` lists the dotted paths of fields that were
// stripped, so the UI can render an honest "internals hidden" disclaimer
// without guessing.
// ─────────────────────────────────────────────────────────────────
import { DtfPressAndPressConfigSchema as _DtfPp, DtfTransferConfigSchema as _DtfTr, DtgConfigSchema as _Dtg, EmbroideryConfigSchema as _Emb, RoutingConfigSchema as _Routing, ScreenPrintConfigSchema as _Sp, UniversalConfigSchema as _Univ, } from '@fsp/pricing-engine';
const _SpView = _Sp.extend({
    cpcTiers: _Sp.shape.cpcTiers.optional(),
    internalLocationCost: _Sp.shape.internalLocationCost.optional(),
    floorTiers: _Sp.shape.floorTiers.optional(),
});
const _DtfTrView = _DtfTr.extend({
    internalCostPerSqIn: _DtfTr.shape.internalCostPerSqIn.optional(),
    b2bMultiplier: _DtfTr.shape.b2bMultiplier.optional(),
    b2bMinRate: _DtfTr.shape.b2bMinRate.optional(),
    etsyDefaultRate: _DtfTr.shape.etsyDefaultRate.optional(),
    etsyMultiplier: _DtfTr.shape.etsyMultiplier.optional(),
    floor: _DtfTr.shape.floor.optional(),
});
const _DtfPpView = _DtfPp.extend({
    transferCostPerSqIn: _DtfPp.shape.transferCostPerSqIn.optional(),
    minSellBeforeLaborBlank: _DtfPp.shape.minSellBeforeLaborBlank.optional(),
    marginTiers: _DtfPp.shape.marginTiers.optional(),
    marginWarnFloorPct: _DtfPp.shape.marginWarnFloorPct.optional(),
    rateWarn: _DtfPp.shape.rateWarn.optional(),
});
const _DtgView = _Dtg.extend({
    internalCost: _Dtg.shape.internalCost.optional(),
    marginWarnFloorPct: _Dtg.shape.marginWarnFloorPct.optional(),
});
const _EmbView = _Emb.extend({
    costDefaults: _Emb.shape.costDefaults.optional(),
    digiCosts: _Emb.shape.digiCosts.optional(),
});
export const PricingConfigViewBodySchema = z.object({
    universal: _Univ,
    screenPrint: _SpView,
    dtfTransfer: _DtfTrView,
    dtfPressAndPress: _DtfPpView,
    dtg: _DtgView,
    embroidery: _EmbView,
    routing: _Routing,
});
/** Source of the active config — surfaces fallback states to the UI. */
export const PricingConfigSourceEnum = z.enum([
    'cache',
    'ddb',
    'fallback-unseeded',
    'fallback-error',
]);
/**
 * Meta block. All fields are nullable so the schema works equally for a
 * fully-seeded response and a fallback (where there is no DDB row to
 * report a version against).
 */
export const PricingConfigViewMetaSchema = z.object({
    source: PricingConfigSourceEnum,
    schemaVersion: z.number().int().positive().nullable(),
    version: z.number().int().positive().nullable(),
    parentVersion: z.number().int().nonnegative().nullable(),
    effectiveAt: z.string().datetime().nullable(),
    publishedAt: z.string().datetime().nullable(),
    publishedBy: z
        .object({
        sub: z.string(),
        email: z.string(),
    })
        .nullable(),
    notes: z.string().nullable(),
    fetchedAt: z.string().datetime(),
});
export const PricingConfigViewResponseSchema = z
    .object({
    meta: PricingConfigViewMetaSchema,
    body: PricingConfigViewBodySchema,
    isAdmin: z.boolean(),
    /** Dotted paths of fields stripped for non-admins (e.g. `screenPrint.cpcTiers`). Empty for admins. */
    internalFieldsHidden: z.array(z.string()),
})
    .meta({
    id: 'PricingConfigViewResponse',
    description: 'Read-only view of the active pricing config. INTERNAL fields are stripped server-side for non-admins; `internalFieldsHidden` lists the paths that were removed.',
});
// ─────────────────────────────────────────────────────────────────
// API response envelopes (per CLAUDE.md § 3.4)
// ─────────────────────────────────────────────────────────────────
/**
 * Error envelope body. All API errors include `type` (machine-readable code),
 * `title` (short summary), `detail` (actionable message), plus per-error context
 * fields (issues, quantity, floor, suggestedService, etc.) permitted via passthrough.
 */
export const ApiErrorBodySchema = z
    .object({
    type: z.string(),
    title: z.string(),
    detail: z.string(),
})
    .passthrough();
/** Error envelope. */
export const ApiErrorEnvelopeSchema = z.object({
    ok: z.literal(false),
    error: ApiErrorBodySchema,
});
/**
 * Builder for a typed success envelope. Use at route definitions:
 * `ApiSuccessEnvelope(QuoteScreenPrintResultSchema)` or `z.any()` where the
 * result is a plain TS type without a Zod schema.
 */
export function ApiSuccessEnvelope(dataSchema) {
    return z.object({
        ok: z.literal(true),
        data: dataSchema,
    });
}
// ─────────────────────────────────────────────────────────────────
// Resolved garment summary — echoed on every quote response whose
// garment was looked up against SS Activewear. Lets the UI display
// which specific SS variant (colorName, colorFamily, SKU) the cascade
// matcher picked, so "red" on the form can be traced to "Red" or
// "Cardinal Red" on the actual SS catalog.
// ─────────────────────────────────────────────────────────────────
export const ResolvedGarmentSummarySchema = z.object({
    source: z.enum(['ss_by_style', 'ss_by_sku']),
    brand: z.string(),
    styleName: z.string(),
    style: z.string(),
    colorName: z.string(),
    colorFamily: z.string().optional(),
    sku: z.string().optional(),
});
// ─────────────────────────────────────────────────────────────────
// Calculator natural-language parse (Phase C3)
// ─────────────────────────────────────────────────────────────────
/**
 * `POST /calc/parse` request. Single free-form string the user types
 * or pastes — "72 gildan black, 2c front 1c back, 10% off, B2B".
 * Length-capped so a runaway paste can't blow the LLM token budget.
 */
export const CalcParseRequestSchema = z.object({
    text: z.string().min(1).max(500),
});
/**
 * `POST /calc/parse` response. All fields optional — the LLM only
 * returns what it recognised in the input, and the dashboard merges
 * over current form state. Mirrors the dashboard's `UiValues` shape
 * so a single form.reset() can consume it.
 *
 * Shape is deliberately narrow: no nested garments, no SS lookups,
 * no pricing math. The engine stays oracle; the LLM only extracts.
 */
export const CalcParseResponseSchema = z.object({
    sxl: z.number().int().min(0).optional(),
    xxl: z.number().int().min(0).optional(),
    xxxl: z.number().int().min(0).optional(),
    xxxxl: z.number().int().min(0).optional(),
    xxxxxl: z.number().int().min(0).optional(),
    frontEnabled: z.boolean().optional(),
    frontColors: z.number().int().min(1).max(8).optional(),
    backEnabled: z.boolean().optional(),
    backColors: z.number().int().min(1).max(8).optional(),
    sleeveEnabled: z.boolean().optional(),
    sleeveColors: z.number().int().min(1).max(8).optional(),
    garmentSource: z.enum(['customer_provides', 'we_provide', 'we_provide_by_style']).optional(),
    blankCostEach: z.number().min(0).optional(),
    garmentColor: z.enum(['white', 'colored']).optional(),
    style: z.string().max(64).optional(),
    colorName: z.string().max(64).optional(),
    mode: z.enum(['b2c', 'b2b']).optional(),
    discountPct: z.number().min(0).max(100).optional(),
    /**
     * LLM's own note on what it did ("filled qty, front colors,
     * discount — style/color not mentioned"). Shown beneath the
     * textbox so the user knows what was/wasn't captured.
     */
    notes: z.string().max(500).optional(),
    /**
     * True when the user asked for a garment *recommendation* rather
     * than naming one ("cheapest tee", "lowest garment", "suggest a
     * blank"). Structured flag so the dashboard can surface a one-click
     * "Find cheapest garment" CTA instead of fuzzy-matching notes text.
     */
    wantsGarmentSuggestion: z.boolean().optional(),
    /**
     * High-level garment category when the user's phrasing implies one
     * ("t-shirts" / "tees" / "shirts" → `tee`; "hoodies" / "sweatshirts"
     * / "zip-ups" → `hoodie`). Passed to the suggestion endpoint so it
     * only ranks candidates from the matching category. Omit when the
     * user is ambiguous.
     */
    garmentCategory: z.enum(['tee', 'hoodie']).optional(),
});
// ─────────────────────────────────────────────────────────────────
// Calculator "Ask AI" assistant (Phase C4)
// ─────────────────────────────────────────────────────────────────
/**
 * Question type. Determines whether the backend calls the engine
 * (deterministic) or Haiku (prose grounded in engine output). All three
 * types use the same request shape and return the same response shape
 * so the dashboard drawer stays uniform.
 *
 *   explain_floor    → why your floor is what it is (prose, LLM)
 *   how_low          → max discount that still clears a margin target
 *                      (deterministic binary search, no LLM)
 *   why_margin       → which cost drivers are eating the margin (prose, LLM)
 */
export const CalcAssistQuestion = z.enum(['explain_floor', 'how_low', 'why_margin']);
/**
 * `POST /calc/assist`. The dashboard passes its already-validated wire
 * body (the same one it sent to `/quote/screen-print`) as `input` so
 * the backend can rerun the engine without re-deriving SS garment data.
 * Required optional `target` for the how_low variant (default 40 pct).
 */
export const CalcAssistRequestSchema = z.object({
    question: CalcAssistQuestion,
    /** Raw screen-print input body — matches QuoteScreenPrintInputSchema. */
    input: QuoteScreenPrintInputSchema,
    /**
     * Margin floor the user wants to protect during a how_low search.
     * Capped at 90 to keep the search space sane.
     */
    targetMarginPct: z.number().min(0).max(90).optional(),
});
/**
 * `POST /calc/assist` response. Always has a prose `answer` the
 * dashboard renders directly. May include an `actions` array — each
 * action is a one-click patch the UI can apply to the calculator form
 * (e.g., "apply 12% discount" sets discountPct=12).
 */
export const CalcAssistActionSchema = z.object({
    /** Human-readable button label ("Apply 12% discount"). */
    label: z.string().min(1).max(64),
    /**
     * Partial form patch — same shape as CalcParseResponse. Applied via
     * the same merge-over logic in the dashboard.
     */
    patch: CalcParseResponseSchema,
});
export const CalcAssistResponseSchema = z.object({
    /** The prose answer shown in the drawer. Plain text, up to ~300 chars. */
    answer: z.string().min(1).max(800),
    /**
     * Optional quick-apply chips. Rendered beneath the answer. Safe to
     * omit for purely informational answers ("your floor is $7.32").
     */
    actions: z.array(CalcAssistActionSchema).max(5).optional(),
    /**
     * Numeric summary the dashboard may surface separately from the prose
     * (e.g., the `how_low` max discount rendered as a headline number).
     * All fields optional.
     */
    metrics: z
        .object({
        maxDiscountPct: z.number().min(0).max(100).optional(),
        resultingMarginPct: z.number().optional(),
        resultingPerUnitSell: z.number().optional(),
        resultingOrderTotal: z.number().optional(),
    })
        .optional(),
});
// ─────────────────────────────────────────────────────────────────
// Cheaper-garment suggestions (Phase C5)
// ─────────────────────────────────────────────────────────────────
/**
 * `POST /calc/suggest-garment`. Pass the current calc input; the
 * backend prices a curated candidate list against SS Activewear,
 * filters to results that clear `targetMarginPct` (default 40),
 * and returns the 3 cheapest still-profitable alternatives with a
 * ready-to-apply patch.
 *
 * A preferred color name (e.g. "Black") narrows the candidate search
 * via `cascadeColorMatch`; omit to price against whatever the base
 * variant is.
 */
export const CalcSuggestGarmentRequestSchema = z.object({
    input: QuoteScreenPrintInputSchema,
    colorName: z.string().max(64).optional(),
    targetMarginPct: z.number().min(0).max(90).optional(),
    /** How many suggestions to return. Default 3, cap 5. */
    limit: z.number().int().min(1).max(5).optional(),
    /**
     * Restrict candidate search to a specific category when the user
     * asked for one ("hoodies" / "tees"). Omit to search every candidate.
     */
    category: z.enum(['tee', 'hoodie']).optional(),
});
export const CalcGarmentSuggestionSchema = z.object({
    /** `Brand StyleName` identifier the dashboard uses as its canonical style. */
    style: z.string(),
    colorName: z.string(),
    /** Short subtitle ("100% Cotton", "Hoodie"). */
    subtitle: z.string().optional(),
    /** Engine-priced per-unit sell for the user's current inputs. */
    perUnitSell: z.number(),
    /** Engine-priced order total. */
    orderTotal: z.number(),
    /** Engine margin percentage. */
    marginPct: z.number(),
    /** SS blank cost for the S-XL base variant. */
    blankCostEach: z.number(),
    /** Absolute per-unit savings vs the user's current quote. */
    savingsPerUnit: z.number(),
    /** Absolute order-total savings vs the user's current quote. */
    savingsOrderTotal: z.number(),
});
export const CalcSuggestGarmentResponseSchema = z.object({
    /** Baseline quote the user currently has (for the delta comparison). */
    baseline: z
        .object({
        perUnitSell: z.number(),
        orderTotal: z.number(),
        marginPct: z.number(),
    })
        .nullable(),
    suggestions: z.array(CalcGarmentSuggestionSchema).max(5),
    /** Human summary — "3 alternatives; G8000 saves $0.47/ea vs current". */
    summary: z.string(),
});
// ─────────────────────────────────────────────────────────────────
// Margin-recovery options (Phase C7)
// ─────────────────────────────────────────────────────────────────
/**
 * `POST /calc/recovery-options`. Pass the current calc; the backend
 * runs a bundle of deterministic engine patches in parallel — reduce
 * discount, add a location, double qty, swap to a cheaper SS blank —
 * and returns the ones that improve margin most. Purely engine-driven
 * — no LLM. Designed to surface automatically when live margin drops
 * below a target so the user never has to "remember to check".
 */
export const CalcRecoveryRequestSchema = z.object({
    input: QuoteScreenPrintInputSchema,
    colorName: z.string().max(64).optional(),
    category: z.enum(['tee', 'hoodie']).optional(),
    targetMarginPct: z.number().min(0).max(90).optional(),
    /** Max options to return. Default 4, cap 6. */
    limit: z.number().int().min(1).max(6).optional(),
});
/**
 * The patch shape the dashboard can merge directly into form values.
 * Narrower than CalcParseResponse — recovery only ever touches a few
 * fields (qty, discount, locations, garment). Explicit keys per
 * field keep the applied diff obvious.
 */
export const CalcRecoveryPatchSchema = z.object({
    sxl: z.number().int().min(0).optional(),
    xxl: z.number().int().min(0).optional(),
    xxxl: z.number().int().min(0).optional(),
    xxxxl: z.number().int().min(0).optional(),
    xxxxxl: z.number().int().min(0).optional(),
    discountPct: z.number().min(0).max(100).optional(),
    frontEnabled: z.boolean().optional(),
    frontColors: z.number().int().min(1).max(8).optional(),
    backEnabled: z.boolean().optional(),
    backColors: z.number().int().min(1).max(8).optional(),
    sleeveEnabled: z.boolean().optional(),
    sleeveColors: z.number().int().min(1).max(8).optional(),
    garmentSource: z.enum(['we_provide_by_style']).optional(),
    style: z.string().max(64).optional(),
    colorName: z.string().max(64).optional(),
});
export const CalcRecoveryOptionSchema = z.object({
    /** Short human label ("Add a back 1-color print", "Swap to Gildan 5000"). */
    label: z.string().min(1).max(96),
    /** Longer sentence explaining the tradeoff. One line max. */
    detail: z.string().min(1).max(200),
    /** The form patch to apply on click. */
    patch: CalcRecoveryPatchSchema,
    /** Category tag so the UI can group or colour-code actions. */
    kind: z.enum(['discount', 'location', 'qty', 'garment']),
    /** Engine-priced per-unit sell after the patch. */
    perUnitSell: z.number(),
    /** Engine-priced order total after the patch. */
    orderTotal: z.number(),
    /** Engine margin % after the patch. */
    marginPct: z.number(),
    /** marginPct(after) − marginPct(before). Positive = improvement. */
    marginDelta: z.number(),
});
export const CalcRecoveryResponseSchema = z.object({
    /** Engine-priced baseline (current calc) for the delta reference. */
    baseline: z
        .object({
        perUnitSell: z.number(),
        marginPct: z.number(),
        orderTotal: z.number(),
    })
        .nullable(),
    options: z.array(CalcRecoveryOptionSchema).max(6),
    /** One-line summary for the drawer header. */
    summary: z.string(),
});
// ═════════════════════════════════════════════════════════════════
// Shopify → DTF integration (docs/shopify-dtf-integration-plan-v1.md)
// Phases S0/S1: store registry, order ingest, unfulfilled-order queue.
// DDB record shapes carry PK/SK/GSI1 (single-table); API views drop them.
// ═════════════════════════════════════════════════════════════════
/**
 * Generic paginated-list body builder. Wrap in `ApiSuccessEnvelope(...)` at the
 * route. `nextCursor` is an opaque, base64-encoded DynamoDB LastEvaluatedKey the
 * client echoes back verbatim — never construct or parse it client-side.
 * (New convention — no list envelope existed before the Shopify work.)
 */
export function PaginatedList(item) {
    return z.object({ items: z.array(item), nextCursor: z.string().optional() });
}
// ── Shared state enums (record + view + worker all import these) ──
/** Shopify's fulfillment mirror — owned by the ingest worker + reconcile poll. */
export const FulfillmentStateEnum = z.enum(['UNFULFILLED', 'FULFILLED', 'CANCELLED']);
/**
 * Local work state — owned by the batch lifecycle (see plan § 4 two-writer split).
 * `ALREADY_PRINTED` is a terminal reached DIRECTLY from OPEN (never via a batch) when staff mark a
 * garment as already produced off-system — e.g. printed on a manual gang sheet. It behaves like a
 * done state everywhere: it drops out of the open-work queue + blank demand and can never be nested
 * (the batch builder gates on OPEN). It is intentionally NOT `PRINTED`, because a `PRINTED` line is
 * assumed to own a `batchId` (settle / reissue / mark-printed all key on it); an ALREADY_PRINTED line
 * never had a batch and must stay clear of those paths. On-hand for these garments is reconciled by a
 * physical count, so this transition writes NO blank movement (off the money path).
 */
/**
 * `PULLED_FROM_STOCK` is a terminal reached DIRECTLY from OPEN (never via a batch) when staff pull a
 * line's already-printed transfers off the shelf instead of reprinting them (printed-transfer-inventory
 * Phase 2). Like `ALREADY_PRINTED` it behaves as a done state — it drops out of the open-work queue +
 * blank demand and can never be nested — and writes NO blank movement (on-hand reconciled by count).
 * UNLIKE `ALREADY_PRINTED` it DOES move materials: it decrements the printed-transfer ledger (a reversible
 * `PULL` movement per (designId, band)). Kept distinct from `ALREADY_PRINTED` so a shelf-pull stays
 * auditable and its transfer decrement is reversible when the line is un-pulled back to OPEN.
 */
export const WorkflowStateEnum = z.enum([
    'OPEN',
    'BATCHED',
    'PRINTED',
    'SHIPPED',
    'ALREADY_PRINTED',
    'PULLED_FROM_STOCK',
]);
/** `POST /orders/.../pull-from-stock` body — pull a line's transfers off the shelf, or undo it. */
export const PullFromStockRequestSchema = z.object({
    pull: z.boolean(),
});
/**
 * Why a line was marked already-printed (off-system). Audit only in the current slice — the reason
 * does NOT drive any inventory movement (a physical count reconciles on-hand for every reason
 * uniformly). Kept so staff/owner can see WHY a garment was taken off the sheet.
 */
export const AlreadyPrintedReasonEnum = z.enum([
    'MANUAL_SHEET', // printed on a manually-generated gang sheet, outside the app
    'HAND_REPRINT', // printed by hand / one-off reprint
    'LEFTOVER', // left over from a past run
    'CUSTOMER_SUPPLIED', // customer brought their own shirt (no FSP blank involved)
]);
/**
 * Whether a line can enter a gang-sheet batch. `MAPPED` = ready; everything else
 * blocks batching. `PAYMENT_PENDING` (order not paid) is shown but non-batchable
 * by default; `BLOCKED` covers null-variant / unresolvable lines. (The S2
 * `LOW_CONFIDENCE` auto-suggest status was retired at the catalog-mapping cutover —
 * mappings are now operator-confirmed at the product level, never auto-suggested.)
 */
export const MappingStatusEnum = z.enum([
    'MAPPED',
    'UNMAPPED',
    'BLOCKED',
    'PAYMENT_PENDING',
    // Non-DTF product category (cap/sticker/etc) — set on a POSITIVE isApparel===false verdict from the
    // synced catalog. Excluded lines never enter the open-work queue or a gang-sheet batch (catalog-mapping v1).
    'EXCLUDED',
]);
// ── Catalog / product-mapping vocabulary (catalog-mapping v1 — see docs/catalog-mapping-plan-v1.md) ──
/** Which apparel type a synced product is. Only these categories feed the DTF gang-sheet pipeline. */
export const ApparelKindEnum = z.enum(['tshirt', 'hoodie']);
// ── Non-DTF production lane vocabulary (plan docs/non-dtf-production-lane-plan-v1.md) ──
// These are ORTHOGONAL to MappingStatus (the DTF-readiness axis). Introduced inert in Phase 2 — the
// classifier + the production rollup use them, but nothing stamps productionClass onto a line until
// Phase 4, so runtime behavior is unchanged until then.
/**
 * How FSP fulfils a synced product, one axis independent of DTF mapping:
 *   DTF_APPAREL      — t-shirt/hoodie → the gang-sheet DTF queue (today's only produced path).
 *   NON_DTF          — produced by FSP off the gang sheet (e.g. caps via heat-press/patch) → its own
 *                      worklist; never batched, never consumes a tee/hoodie blank.
 *   FULFILLMENT_ONLY — FSP does NOT produce it but DOES stock and ship it (decals, stickers,
 *                      pre-made goods) → the Ship-only worklist (pack, don't print). Assigned ONLY
 *                      by an operator `productionClassOverride` (no classifier heuristic emits it):
 *                      whether FSP ships a given item is business knowledge, not taxonomy.
 *   NOT_PRODUCED     — out of FSP scope entirely → excluded/invisible.
 *   UNCLASSIFIED     — nothing matched: the SAFE-VISIBLE default — never auto-dropped AND never
 *                      auto-batched (mirrors the resolver's "never silently drop pre-sync apparel" rule).
 */
export const ProductionClassEnum = z.enum([
    'DTF_APPAREL',
    'NON_DTF',
    'FULFILLMENT_ONLY',
    'NOT_PRODUCED',
    'UNCLASSIFIED',
]);
/** Where a product's production-class verdict came from — the Shopify field the classifier matched,
 *  `operator` for a manual override (set in a later phase), or `none` when nothing matched (⇒ UNCLASSIFIED). */
export const ClassificationSourceEnum = z.enum([
    'category',
    'categoryFullName',
    'productType',
    'operator',
    'none',
]);
/** Non-DTF production lifecycle for one NON_DTF line (cap/other) — a coarse whole-line OPEN → PRODUCED,
 *  distinct from the DTF {@link WorkflowStateEnum} (a cap never enters a gang-sheet batch). */
export const NonDtfWorkflowStateEnum = z.enum(['OPEN', 'PRODUCED']);
/**
 * Ship-only lifecycle for one FULFILLMENT_ONLY line — a coarse whole-line OPEN → PACKED, mirroring the
 * caps {@link NonDtfWorkflowStateEnum}. PACKED is the per-line STOCK event (P2b consume-at-fulfill keys
 * off it); the order-level "shipped" moment is deliberately NOT modeled here — it stays reserved for a
 * future ship-station label event (two-layer state, inventory-clarity P2/P5).
 */
export const FulfillWorkflowStateEnum = z.enum(['OPEN', 'PACKED']);
/** Print size band. `youth` is the ONLY smaller band; every non-youth size resolves to `adult`. */
export const SizeBandEnum = z.enum(['adult', 'youth']);
/**
 * How a line's placements are decorated, folded from its product mapping's per-placement
 * `ProductPlacement.method` (screen-print production lane plan v1 § 4.2).
 *   `dtf`          — every placement is DTF. Today's only shape; an ABSENT value reads as this.
 *   `screen-print` — every placement is screen print ⇒ the line resolves to ZERO DTF items and can
 *                    only go on a filmless batch (the LIMITED-edition front+back shape).
 *   `mixed`        — both techniques on one garment (e.g. screen-print front + DTF back). It HAS
 *                    film, so it rides a gang sheet like any DTF line.
 *
 * ⚠️ RE-DERIVED every ingest, exactly like `placementCount` and `productionClass` — so a mapping edit
 * self-heals every open line within one reconcile sweep and needs NO backfill. It is deliberately NOT
 * in `buildLineRecord`'s preserve list: preserving it would freeze a stale technique onto a line whose
 * mapping has since changed, which is the opposite of what a derived field is for.
 *
 * ⚠️ DISPLAY + SELECTION ONLY — never gate money on it, precisely because a mapping edit moves it. The
 * durable record of what was actually screen printed at build time is `OrderLineRecord`'s
 * `spLocationsAtBuild`, which is stamped at claim, preserved across reconcile, and only ever widens.
 */
export const LineDecorationEnum = z.enum(['dtf', 'screen-print', 'mixed']);
// ── Store registry ──
/**
 * Auth grant for a store's Admin API access (auth-model pivot 2026-07-07 — legacy
 * `shpat_` custom apps were deprecated 2026-01, so tokens now come from a Dev Dashboard app).
 * - `client_credentials` (default): org-owned store. The Admin token is MINTED at runtime from
 *   the app-level client_id/secret (24h TTL, re-minted on demand); see the rest-api token provider.
 * - `oauth`: third-party store installed via the authorization-code flow; the token is a
 *   long-lived offline token provisioned by the (future) `/callback`. Reserved — OAuth is not
 *   built yet; a store set to `oauth` must carry an `offlineAccessToken`.
 */
export const ShopifyAuthTypeEnum = z.enum(['client_credentials', 'oauth']);
/**
 * The ONE canonical `<shop>.myshopify.com` validator — anchored, ASCII, single-label, and
 * case/whitespace-normalized on parse. Reused by store registration AND the OAuth begin/callback so
 * nothing ever builds `https://{shop}/…` from an unvalidated host (SSRF + open-redirect guard).
 * Rejects `x.myshopify.com.evil.com`, `…@evil.com`, multi-label, trailing-dot, and non-ASCII
 * homographs; normalizes `MyShop.myshopify.com ` → `myshop.myshopify.com` (§ 5.5).
 */
export const ShopifyShopDomainSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(1)
    .max(128)
    .regex(/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/, 'must be a <shop>.myshopify.com domain (single label, lowercase)');
/**
 * One entry in the `fsp/shopify/stores` Secrets Manager JSON array (§ 14). Holds the
 * per-store SECRETS — never persisted to DynamoDB, never logged, never returned by the API.
 *
 * Auth-model pivot: `adminAccessToken` and `webhookSecret` are now OPTIONAL. Under the default
 * `client_credentials` grant the token is minted at runtime and the webhook HMAC key falls back
 * to the app-level client secret (`ShopifyAppConfig`), so an entry can be as small as
 * `{storeId, shopDomain, apiVersion}`. A static `adminAccessToken` (legacy custom-app token) is
 * still honored verbatim when present.
 */
export const StoreSecretEntrySchema = z.object({
    storeId: z.string().min(1),
    shopDomain: z.string().min(1),
    apiVersion: z.string().min(1),
    /** Grant type. Default `client_credentials` (org-owned brands). */
    authType: ShopifyAuthTypeEnum.default('client_credentials'),
    /**
     * Static Admin API token. Optional under client-credentials (minted at runtime); set it only
     * for a legacy long-lived custom-app token you want used verbatim.
     */
    adminAccessToken: z.string().min(1).optional(),
    /**
     * Webhook HMAC signing key. Optional — falls back to this entry's per-store `clientSecret` (below),
     * then the app-level client secret (`ShopifyAppConfig`), which is the Dev Dashboard app model.
     */
    webhookSecret: z.string().min(1).optional(),
    /**
     * PER-STORE app credentials for the client-credentials mint. Set these ONLY for a store in a
     * DIFFERENT Shopify organization than the shared `fsp/shopify/app` app — each org needs its own
     * Dev Dashboard app (a custom app can't install across orgs). When present, the token provider mints
     * this store's token with THESE (not the shared app config), and the webhook HMAC key falls back to
     * this `clientSecret`. Omit for stores that use the shared app (e.g. low-trendz). Never logged/returned.
     */
    clientId: z.string().min(1).optional(),
    clientSecret: z.string().min(1).optional(),
    // ── Reserved for the OAuth grant (provisioned by the future /callback; unused today) ──
    /** Long-lived offline access token from the authorization-code flow. */
    offlineAccessToken: z.string().min(1).optional(),
    /** OAuth refresh token, if the app uses expiring online tokens. */
    refreshToken: z.string().min(1).optional(),
    /** ISO-8601 expiry for a stored token, when applicable. */
    accessTokenExpiresAt: z.string().datetime().optional(),
    /** Granted access scopes (informational; scopes are configured in the Dev Dashboard). */
    scopes: z.array(z.string()).optional(),
});
export const StoreSecretSchema = z.array(StoreSecretEntrySchema);
/**
 * App-level Shopify credentials — ONE entry per app (not per store), in the
 * `fsp/shopify/app-<stage>` Secrets Manager secret. Holds the Dev Dashboard app's
 * client_id/client_secret (used to mint client-credentials Admin tokens) and, optionally, an
 * explicit webhook HMAC key. Never persisted to DynamoDB, never logged, never returned by the API.
 */
export const ShopifyAppConfigSchema = z.object({
    clientId: z.string().min(1),
    clientSecret: z.string().min(1),
    /**
     * Explicit webhook HMAC key, if it differs from `clientSecret`. Optional — Dev Dashboard apps
     * sign webhooks with the client secret, so this is normally omitted.
     */
    webhookSecret: z.string().min(1).optional(),
});
// ── Webhook registration (`POST /stores/:storeId/register-webhooks`) ──
/** `POST /stores/:storeId/register-webhooks` body. Trigger-agnostic — same shape the future OAuth `/callback` will use. */
export const RegisterWebhooksRequestSchema = z.object({
    /** Base URL of the Shopify webhook receiver (FspShopifyStack `WebhookApiEndpoint`). */
    callbackUrl: z.string().url().max(2048),
    /**
     * Shopify webhook topics as GraphQL enum values (e.g. `ORDERS_CREATE`). Omit to register the
     * default order/refund topic set.
     */
    topics: z.array(z.string().min(1)).min(1).max(50).optional(),
});
/** One topic's registration outcome. `exists` = Shopify reports the subscription already present (idempotent re-run). */
export const WebhookRegistrationResultSchema = z.object({
    topic: z.string(),
    status: z.enum(['created', 'exists', 'failed']),
    id: z.string().optional(),
    message: z.string().optional(),
});
export const RegisterWebhooksResponseSchema = z.object({
    storeId: z.string(),
    callbackUrl: z.string(),
    results: z.array(WebhookRegistrationResultSchema),
});
/** DDB record: `STORE#<storeId>` / `CONFIG`. Non-secret config only. GSI1 lists all stores. */
export const StoreConfigSchema = z.object({
    PK: z.string(),
    SK: z.literal('CONFIG'),
    GSI1PK: z.literal('STORES'),
    GSI1SK: z.string(),
    entity: z.literal('store'),
    storeId: z.string(),
    shopDomain: z.string(),
    apiVersion: z.string(),
    // Auth grant for this store. Defaulted so existing registry rows written before this field
    // (e.g. low-trendz) parse as `client_credentials` with no data migration.
    authType: ShopifyAuthTypeEnum.default('client_credentials'),
    brandLabel: z.string().optional(),
    active: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** `POST /stores` body. `storeId` is our internal slug (stable join key across the system). */
export const StoreRegisterRequestSchema = z.object({
    storeId: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-z0-9-]+$/, 'storeId must be a lowercase slug (a-z, 0-9, hyphen)'),
    // Anchored + normalized (was a loose `\.myshopify\.com$`): buildStoreConfig + register-webhooks
    // build `https://{shopDomain}/…` from this, so it must be the strict single-label form.
    shopDomain: ShopifyShopDomainSchema,
    apiVersion: z
        .string()
        .min(1)
        .max(16)
        .regex(/^\d{4}-\d{2}$/, 'apiVersion must be a Shopify date version like 2026-07')
        .optional(),
    brandLabel: z.string().max(80).optional(),
    active: z.boolean().optional(),
});
/**
 * `PATCH /stores/:storeId` body — the editable non-secret fields. `shopDomain` is immutable (it is
 * the join key to the store's secret + the webhook domain lookup), and `authType` changes ride the
 * OAuth callback, not this route. All fields optional; at least one is required.
 */
export const StoreUpdateRequestSchema = z
    .object({
    brandLabel: z.string().max(80).optional(),
    apiVersion: z
        .string()
        .min(1)
        .max(16)
        .regex(/^\d{4}-\d{2}$/, 'apiVersion must be a Shopify date version like 2026-07')
        .optional(),
    active: z.boolean().optional(),
})
    .refine((p) => p.brandLabel !== undefined || p.apiVersion !== undefined || p.active !== undefined, { message: 'Provide at least one field to update (brandLabel, apiVersion, or active).' });
/** API view of a store (non-secret). */
export const StoreSummarySchema = z.object({
    storeId: z.string(),
    shopDomain: z.string(),
    apiVersion: z.string(),
    authType: ShopifyAuthTypeEnum,
    brandLabel: z.string().optional(),
    active: z.boolean(),
    createdAt: z.string(),
});
// ── Shopify OAuth install flow (authorization-code grant) ──
/**
 * `POST /stores/oauth/begin` body. The server owns the requested scopes (read-only, § 9.3) and
 * derives the internal storeId from the shop subdomain (or reuses an existing store on the same
 * domain), so the admin supplies only the domain + an optional brand label.
 */
export const StoreOAuthBeginRequestSchema = z.object({
    shopDomain: ShopifyShopDomainSchema,
    brandLabel: z.string().max(80).optional(),
});
/** `POST /stores/oauth/begin` response — the Shopify consent URL the SPA redirects the admin to. */
export const StoreOAuthBeginResponseSchema = z.object({
    authorizeUrl: z.string().url(),
});
/**
 * `GET /shopify/oauth/callback` query params (Shopify's redirect). `code` is ABSENT when a merchant
 * declines consent. This parse is NEVER used to rebuild the signed string — the callback verifies
 * HMAC over the raw query independently (plan § 5.1), so a dropped/added param can't weaken the check.
 */
export const ShopifyOAuthCallbackQuerySchema = z.object({
    shop: z.string().min(1),
    state: z.string().min(1),
    hmac: z.string().min(1),
    timestamp: z.string().min(1),
    code: z.string().min(1).optional(),
    host: z.string().optional(),
});
/**
 * The per-store OAuth token secret `fsp/shopify/store/<storeId>/token-<stage>`. A DEDICATED secret
 * per store (blast-radius containment) — OAuth offline tokens live here, NEVER in the shared
 * `fsp/shopify/stores` array. Non-expiring offline token → no refresh_token slot by design.
 * Never logged, never returned by the API.
 */
export const StoreTokenSecretSchema = z.object({
    offlineAccessToken: z.string().min(1),
    scopes: z.array(z.string()),
    installedAt: z.string(),
});
/**
 * Shopify's `POST /admin/oauth/access_token` response for a NON-EXPIRING offline token. Deliberately
 * rejects `expires_in`/`refresh_token`: their presence means an EXPIRING token slipped in (which has
 * no refresh path here), so we fail loud instead of silently persisting it (plan § 3.5). Unknown
 * future fields are ignored — only the two dangerous fields are rejected.
 */
export const ExchangeTokenResponseSchema = z.object({
    access_token: z.string().min(1),
    scope: z.string(),
    // Must be ABSENT for a non-expiring offline token; a PRESENT value here ⇒ an expiring token slipped
    // in and is rejected. `.optional()` keeps the key optional in the inferred type (callers needn't
    // spell out `undefined`) while `z.undefined()` still rejects any present value at runtime.
    expires_in: z.undefined().optional(),
    refresh_token: z.undefined().optional(),
});
/**
 * DDB record `OAUTHSTATE#<nonce>` / `STATE` — the one-time CSRF state for an in-flight OAuth install.
 * Written by `POST /stores/oauth/begin`, consumed EXACTLY ONCE by the callback via an atomic
 * conditional delete (never read-then-delete — TOCTOU). GSI1 (`OAUTHSTATE` partition, keyed by
 * `expiresAt`) backs the lightweight expiry sweep (no DynamoDB TTL, § 9.4). `initiatedBy` is the
 * admin's Cognito sub — AUDIT ONLY (the callback is sessionless). Ephemeral (~10 min); never returned.
 */
export const OAuthStateRecordSchema = z.object({
    PK: z.string(),
    SK: z.literal('STATE'),
    GSI1PK: z.literal('OAUTHSTATE'),
    GSI1SK: z.string(),
    entity: z.literal('oauthState'),
    nonce: z.string().min(1),
    storeId: z.string().min(1),
    shopDomain: z.string().min(1),
    brandLabel: z.string().optional(),
    scopes: z.array(z.string()),
    initiatedBy: z.string(),
    expiresAt: z.string(),
});
// ── DTF film rolls ──
/**
 * A registered DTF film roll — the physical media a gang sheet is nested onto. `usableWidthIn` is
 * the PRINTABLE width the nester packs into (e.g. 34″ on a 35.43″ film — the printer can't reach the
 * edges), and `maxLengthIn` is the longest sheet the operator will cut from this roll (the per-build
 * length picker defaults to it and can't exceed it). One roll may be flagged `isDefault` so a batch
 * nests onto it without an explicit pick. Widths/lengths are capped to the gang-sheet-engine's spec
 * limits (120″ / 240″). DDB: `ROLL#<rollId>` / `CONFIG`; GSI1 (`ROLLS` / `NAME#<lowername>`) lists.
 */
export const RollRecordSchema = z.object({
    PK: z.string(),
    SK: z.literal('CONFIG'),
    GSI1PK: z.literal('ROLLS'),
    GSI1SK: z.string(),
    entity: z.literal('roll'),
    rollId: z.string(),
    name: z.string(),
    usableWidthIn: z.number().positive(),
    maxLengthIn: z.number().positive(),
    isDefault: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** API view of a roll. */
export const RollSummarySchema = z.object({
    rollId: z.string(),
    name: z.string(),
    usableWidthIn: z.number().positive(),
    maxLengthIn: z.number().positive(),
    isDefault: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * `POST /rolls` (create) + `PATCH /rolls/:rollId` (update) body. Width/length caps mirror the
 * gang-sheet-engine spec (`rollWidthIn` ≤ 120, `maxLengthIn` ≤ 240) so a roll can never encode a
 * spec the nester would reject.
 */
export const RollSaveRequestSchema = z.object({
    name: z.string().min(1).max(120),
    usableWidthIn: z.number().positive().max(120),
    maxLengthIn: z.number().positive().max(240),
    // When true, this roll becomes the default a batch nests onto without an explicit pick (any
    // previous default is cleared). Omitted on update → the default flag is left unchanged.
    isDefault: z.boolean().optional(),
});
// ── Presses — the shop's screen-printing machines (docs/screen-print-station-audit-and-plan-v1.md § 3.6) ──
/** The longest carousel the model allows for — stations, not colours. */
export const PRESS_MAX_STATIONS = 20;
/**
 * `automatic`: a carousel — print order IS head order and the flash units sit on fixed stations.
 * `manual`: stations but no fixed flashes; `ScreenSpec.flashAfter` is the flash there.
 */
export const PressKindEnum = z.enum(['automatic', 'manual']);
const pressShape = {
    name: z.string().min(1).max(60),
    kind: PressKindEnum,
    /** Stations around the carousel (12 on the shop's M&R). */
    stations: z.number().int().min(1).max(PRESS_MAX_STATIONS),
    /** Stations that hold a print head (10 on the M&R). */
    printHeads: z.number().int().min(1).max(PRESS_MAX_STATIONS),
    /** Stations that hold a flash unit — e.g. `[2, 8]`. Empty on a manual press. */
    flashStations: z.array(z.number().int().min(1).max(PRESS_MAX_STATIONS)).max(4),
};
/**
 * A head and a flash each occupy a station, so together they cannot exceed the stations; a flash
 * station is named at most once and lies within the carousel. A fact about the machine, so a record
 * that violates it is a typo, not a configuration.
 */
const pressIsPhysical = (p) => p.printHeads + p.flashStations.length <= p.stations &&
    new Set(p.flashStations).size === p.flashStations.length &&
    p.flashStations.every((s) => s <= p.stations);
const PRESS_PHYSICAL_MESSAGE = 'Heads plus flash units cannot exceed the stations, and each flash station must be a distinct station on the press.';
/**
 * DDB: `PRESS#<pressId>` / `CONFIG`; GSI1 (`PRESSES` / `NAME#<lowername>`) lists name-sorted. One
 * press may be the default a design spec's head numbers refer to when it names none. Mirrors the
 * film-roll registry.
 */
export const PressRecordSchema = z
    .object({
    PK: z.string(),
    SK: z.literal('CONFIG'),
    GSI1PK: z.literal('PRESSES'),
    GSI1SK: z.string(),
    entity: z.literal('press'),
    pressId: z.string(),
    ...pressShape,
    isDefault: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
})
    .refine(pressIsPhysical, { message: PRESS_PHYSICAL_MESSAGE });
/** API view of a press. */
export const PressSummarySchema = z.object({
    pressId: z.string(),
    ...pressShape,
    isDefault: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** `POST /presses` (create) + `PATCH /presses/:pressId` (update) body. */
export const PressSaveRequestSchema = z
    .object({
    ...pressShape,
    // When true, this press becomes the default (any previous default is cleared). Omitted on
    // update → the default flag is left unchanged.
    isDefault: z.boolean().optional(),
})
    .refine(pressIsPhysical, { message: PRESS_PHYSICAL_MESSAGE });
// ── S&S receiving — blank-inventory Capability 1 (read-only lookup) ──
/**
 * One S&S invoice line in a receiving audit: what we ordered vs what shipped. `sku` is
 * B-prefixed and identical to the inventory key `BLANK#<sku>` (and a mapped order line's
 * `resolvedBlankSku`), so this row keys directly to the eventual on-hand ledger (R2).
 */
export const ReceivingLineSchema = z.object({
    sku: z.string(),
    brandName: z.string(),
    styleName: z.string(),
    title: z.string().optional(),
    colorName: z.string(),
    sizeName: z.string(),
    gtin: z.string().optional(),
    lineNumber: z.number().int().optional(),
    qtyOrdered: z.number().int().nonnegative(),
    qtyShipped: z.number().int().nonnegative(),
    /** `max(0, ordered − shipped)` — pieces S&S still owes on this line. */
    shortShipQty: z.number().int().nonnegative(),
    shortShipped: z.boolean(),
    /** `shipped > ordered` — rare (substitution / overpack); surfaced so it isn't silently absorbed. */
    overShipped: z.boolean(),
    /**
     * Receipt state from OUR ledger (the lookup joins it; absent when the line was never received or the
     * ledger read was unavailable — see `ReceivingLookupResult.receipt`). `receivedQty` is the effective
     * arrived so far (receipt rows + corrections, box-reversed boxes excluded); `receivedBoxes` names the
     * boxes already in when the line was received box by box (empty for a whole-invoice receipt).
     */
    receivedQty: z.number().int().nonnegative().optional(),
    receivedBoxes: z.array(z.number().int().positive()).optional(),
    /** True when every receipt for this line was undone (whole-line reversal, or every box reversed). */
    reversed: z.boolean().optional(),
});
/** Roll-up of a receiving audit — headline numbers for the shipment. */
export const ReceivingTotalsSchema = z.object({
    lineCount: z.number().int().nonnegative(),
    totalOrdered: z.number().int().nonnegative(),
    totalShipped: z.number().int().nonnegative(),
    shortLineCount: z.number().int().nonnegative(),
    shortPieceCount: z.number().int().nonnegative(),
});
/**
 * How far an S&S shipment has got, derived server-side from S&S's delivery-status strings (order-level,
 * or per box when S&S lists every box's status). `partially_delivered` = some boxes are here and some
 * are not (S&S's own "Shipped - Partially Delivered", or a mix of box statuses). Anything S&S says that
 * is not a recognised FINAL delivered status — "Out for Delivery", "Exception", an unknown string —
 * is `in_transit`, so the app never claims a box arrived before the carrier says it did.
 */
export const ShipmentDeliveryStateSchema = z.enum([
    'in_transit',
    'partially_delivered',
    'delivered',
]);
/**
 * One invoice line's share of one box (bigger orders split a SKU across boxes). `lineNumber` is the
 * INVOICE line, resolved server-side by SKU — S&S's own per-box line number is the position within the
 * box and is deliberately not passed through. Absent when the SKU can't be tied to exactly one line.
 */
export const ReceivingBoxLineSchema = z.object({
    lineNumber: z.number().int().optional(),
    sku: z.string(),
    qty: z.number().int().nonnegative(),
});
/** How one box stands in OUR ledger — who received it and when, or that its receipt was undone. */
export const ReceivingBoxReceiptSchema = z.object({
    receivedAt: z.string(),
    receivedBy: z.string().optional(),
    /** Pieces recorded from this box (its manifest share; less when a line was received short). */
    pieces: z.number().int().nonnegative(),
    /** True when this box's receipt was reversed (`/receiving/reverse-box`) — it counts as NOT received. */
    reversed: z.boolean(),
});
/**
 * One physical carton of the shipment, as S&S packed it. Each box carries its OWN carrier tracking
 * number and delivery status, so a box that arrives a day late is visible as such. A box can be
 * received on its own (`/receiving/confirm-boxes`) — `receipt` says whether it has been.
 */
export const ReceivingBoxSchema = z.object({
    boxNumber: z.number().int(),
    /** The code printed on the box (`<invoice>.<carton>`); a scan of it matches exactly. */
    conveyorBarcode: z.string().optional(),
    trackingNumber: z.string().optional(),
    trackingUrl: z.string().optional(),
    /** S&S's raw status for this box, e.g. `Shipped - In Transit`. */
    deliveryStatus: z.string().optional(),
    deliveryState: ShipmentDeliveryStateSchema.optional(),
    weightLb: z.number().nonnegative().optional(),
    /** False when S&S sent no readable contents for this box — `lines` is then empty, not "an empty box". */
    contentsKnown: z.boolean(),
    pieces: z.number().int().nonnegative(),
    lines: z.array(ReceivingBoxLineSchema),
    /** This is the carton whose barcode was scanned. */
    scanned: z.boolean(),
    /**
     * Set when this box has been received (its own box receipt, or the whole invoice was received line by
     * line). Absent = not received, or receipt state unknown (see `ReceivingLookupResult.receipt`).
     */
    receipt: ReceivingBoxReceiptSchema.optional(),
});
/**
 * The invoice's receipt state, folded from the ledger at lookup time. `complete` = every box is in
 * (or every line, for a whole-invoice receipt); `partial` = some boxes/lines are in and the rest are
 * still to come; `none` = nothing recorded. A partially received invoice stays on the receiving to-do.
 */
export const ReceivingReceiptStateSchema = z.object({
    status: z.enum(['none', 'partial', 'complete']),
    /** Boxes with a live (non-reversed) box receipt. For a whole-invoice receipt = `boxesTotal`. */
    boxesReceived: z.number().int().nonnegative(),
    /** S&S's box count for the invoice, when known. */
    boxesTotal: z.number().int().nonnegative().optional(),
    /** Invoice lines with any live receipt (a box-split line counts once it has one box in). */
    linesReceived: z.number().int().nonnegative(),
    linesTotal: z.number().int().nonnegative(),
    /** True when the invoice was received line by line as a whole (`/receiving/confirm`), not box by box. */
    wholeInvoice: z.boolean(),
    /** Earliest / latest receipt time and the actor of the latest — for "received Mon 12:14 by …". */
    receivedAt: z.string().optional(),
    lastReceivedAt: z.string().optional(),
    receivedBy: z.string().optional(),
});
/**
 * `GET /receiving/lookup?code=` result — a full ordered-vs-shipped audit of one S&S shipment, from the
 * S&S order, joined with what our ledger already holds for it (`receipt`, per-line `receivedQty`,
 * per-box `receipt`).
 */
export const ReceivingLookupResultSchema = z.object({
    invoiceNumber: z.string(),
    orderNumber: z.string().optional(),
    poNumber: z.string().optional(),
    orderDate: z.string().optional(),
    shipDate: z.string().optional(),
    orderStatus: z.string().optional(),
    deliveryStatus: z.string().optional(),
    trackingNumber: z.string().optional(),
    shippingCarrier: z.string().optional(),
    warehouseAbbr: z.string().optional(),
    /** The raw code the operator scanned (may carry a `.carton` suffix). Echoed for confirmation. */
    scannedCode: z.string(),
    /** True when the scanned code exactly matched one of the shipment's carton barcodes. */
    cartonRecognized: z.boolean(),
    totalBoxes: z.number().int().nonnegative().optional(),
    totals: ReceivingTotalsSchema,
    lines: z.array(ReceivingLineSchema),
    /**
     * Whether every box is here, per S&S. Absent when S&S gave no status at all (unknown — the UI says
     * nothing rather than guessing). The confirm screen warns on anything but `delivered`.
     */
    deliveryState: ShipmentDeliveryStateSchema.optional(),
    /** Per-box contents + tracking. Absent/empty when S&S returned no box list. */
    boxes: z.array(ReceivingBoxSchema).optional(),
    /**
     * What OUR ledger already holds for this invoice. Absent ONLY when the ledger read failed (the UI then
     * says nothing about receipt state rather than guessing); `status: 'none'` is the positive "never
     * received" answer.
     */
    receipt: ReceivingReceiptStateSchema.optional(),
});
// ── Blank inventory — movement ledger (Capability 1 R2) ──
export const BlankMovementTypeEnum = z.enum(['RECEIVE', 'CONSUME', 'ADJUST', 'COUNT']);
/** Provenance of a CONSUME/RECEIVE tied to a specific order line. */
// ── Pulled blanks (pull station, `docs/pulled-blanks-pull-station-plan-v2.md`) — ledger side ─────
//
// A shelf count anchors the book to `shelfQty + pulled`, where `pulled` is what the pull station has
// recorded as off the shelf for sheets whose blanks the press has not drawn yet. The COUNT row keeps
// both halves so the movement audits itself and `aggregateBlank` can say which kind of count was last.
/** Which figure a COUNT anchored on: `book` = the operator typed the on-hand figure (every count made
 *  before the pull station, and the legacy body); `shelf` = they typed what is on the shelf and the
 *  server added the recorded pulled stacks. Absent on a legacy row ⇒ `book`. */
export const BlankCountBasisEnum = z.enum(['book', 'shelf']);
/** One sheet's contribution to a shelf count's `pulledQty`, frozen on the COUNT row: what the sheet had
 *  recorded for the blank, what the press had already drawn under it, and the record revision seen. */
export const BlankPulledRefSchema = z.object({
    batchId: z.string(),
    seq: z.number().int().positive().optional(),
    rev: z.number().int().nonnegative(),
    recorded: z.number().int().nonnegative(),
    drawn: z.number().int().nonnegative(),
});
/** The counter's per-sheet answers a shelf count was saved with (a `pulled_rose` resubmit): `exclude` =
 *  "yes, I counted that stack as shelf" (the sheet's record is left OUT of `pulledQty`); `confirm` = "no,
 *  it was at the press" (kept in). Persisted so the row explains its own arithmetic. */
export const BlankPulledAnswersSchema = z.object({
    exclude: z.array(z.string()).optional(),
    confirm: z
        .array(z.object({ batchId: z.string(), qty: z.number().int().nonnegative() }))
        .optional(),
});
export const BlankMovementOrderRefSchema = z.object({
    storeId: z.string(),
    orderId: z.string(),
    lineId: z.string(),
});
/**
 * DDB record: `BLANK#<ssSku>` / `<movement SK>`, GSI1 `BLANK_MOVES` / `<ssSku>#<createdAt>#<id>`.
 *
 * Append-only ledger. **On-hand is ALWAYS the running sum of `qtyDelta` over a blank's rows —
 * never a stored counter** (a bare counter can't be audited or rebuilt; the plan's make-or-break).
 * SK shapes encode idempotency:
 *  - RECEIVE  `RECV#<invoice>#<lineNo>`                        — idempotent per S&S invoice line
 *  - CONSUME  `PRINT#<storeId>#<orderId>#<lineId>#<batchId>`   — idempotent per printed line; deleted on unmark (R2b)
 *  - manual CONSUME/ADJUST/COUNT `MOVE#<createdAt>#<id>`       — unique, appended
 */
export const BlankMovementRecordSchema = z.object({
    PK: z.string(),
    SK: z.string(),
    GSI1PK: z.literal('BLANK_MOVES'),
    GSI1SK: z.string(),
    entity: z.literal('blankMovement'),
    ssSku: z.string(),
    type: BlankMovementTypeEnum,
    /** Signed change to on-hand: RECEIVE +, CONSUME −, ADJUST ±, COUNT = counted − prior on-hand. */
    qtyDelta: z.number().int(),
    /** For COUNT: the absolute physical count entered (qtyDelta was derived from it). */
    countedQty: z.number().int().nonnegative().optional(),
    /**
     * For a SHORT RECEIVE: what S&S's invoice CLAIMED shipped for this line, recorded when the operator
     * counted FEWER as actually arrived (a packing short — "S&S billed it, it wasn't in the box"). `qtyDelta`
     * is what actually arrived; `claimedQty − qtyDelta` is the shortfall S&S owes. Absent on a normal receipt
     * (arrived == claimed). A fully-missing line is a `qtyDelta: 0` RECEIVE row carrying `claimedQty > 0`.
     */
    claimedQty: z.number().int().nonnegative().optional(),
    reason: z.string().optional(),
    /** Who recorded it (Cognito email/sub or api-key consumer). Never a secret. */
    actor: z.string().optional(),
    sourceInvoice: z.string().optional(),
    sourceLineNumber: z.number().int().optional(),
    /**
     * On a per-box receipt row (`RECV#<invoice>#<line>#<box>`) and its reversal (`RECVREV#…#<box>`): the S&S
     * box number the units came from. Absent on a whole-line receipt (`RECV#<invoice>#<line>`, every
     * invoice received before box receiving existed, and manifest-less orders after it), which covers
     * every box of the line. A line has EITHER a whole-line row OR box rows, never both (the routes
     * refuse the mix), so on-hand is still the plain running sum.
     */
    sourceBox: z.number().int().positive().optional(),
    /**
     * On a RECEIVE row: the FSP PO# this receipt tied back to (`order.poNumber` echoed from S&S), when the
     * operator ordered under an FSP PO. Stamped so an open shortage can be reconciled against its PO — a
     * make-good shipped under the SAME PO# (a different invoice) tops the PO line up and clears the claim.
     * Absent for S&S-website orders (no FSP PO#).
     */
    poNumber: z.string().optional(),
    /**
     * On a RECEIVE row: the S&S order number this invoice belongs to. The identity that separates a
     * sibling invoice of the SAME order (multi-warehouse split — never a make-good) from a genuinely
     * NEW order (a reship): shortage reconciliation only counts a receipt as make-good when both its
     * and the claim's orderNumber are present and DIFFER. Absent on pre-stamp rows, which therefore
     * never count toward a make-good pool (the claim stays visible — fail-open).
     */
    orderNumber: z.string().optional(),
    orderRef: BlankMovementOrderRefSchema.optional(),
    batchId: z.string().optional(),
    // Garment identity denormalized on RECEIVE so the inventory list reads names without re-hitting S&S.
    brandName: z.string().optional(),
    styleName: z.string().optional(),
    colorName: z.string().optional(),
    sizeName: z.string().optional(),
    /** COUNT rows only (pulled-blanks v2): which figure the count anchored on. Absent ⇒ `book`. */
    basis: BlankCountBasisEnum.optional(),
    /** Shelf-basis COUNT: the figure the counter typed — what was physically on the shelf. */
    shelfQty: z.number().int().nonnegative().optional(),
    /** Shelf-basis COUNT: the recorded-pulled total the server added (`countedQty = shelfQty + pulledQty`). */
    pulledQty: z.number().int().nonnegative().optional(),
    /** Shelf-basis COUNT: the per-sheet split behind `pulledQty`. */
    pulledRef: z.array(BlankPulledRefSchema).optional(),
    /** Shelf-basis COUNT: the counter's per-sheet answers, when the save needed any. */
    pulledAnswers: BlankPulledAnswersSchema.optional(),
    createdAt: z.string(),
    id: z.string(),
});
/** API view: one blank's current (derived) on-hand + identity. */
export const BlankOnHandSchema = z.object({
    ssSku: z.string(),
    onHand: z.number().int(),
    brandName: z.string().optional(),
    styleName: z.string().optional(),
    colorName: z.string().optional(),
    sizeName: z.string().optional(),
    lastMovementAt: z.string().optional(),
    lastCountedAt: z.string().optional(),
    lastCountedQty: z.number().int().optional(),
    /** Which kind of count the newest COUNT row was (pulled-blanks v2) — once the pull station is live the
     *  count sheet treats only a `shelf` count as "done today". Absent when never counted. */
    lastCountedBasis: BlankCountBasisEnum.optional(),
    // Only populated by GET /inventory?includeArchived — true when this blank has been archived
    // (retired from the working views). Omitted (undefined) on the default, archived-hidden list.
    archived: z.boolean().optional(),
    // Display-only S&S colorway images (front photo + color swatch), resolved at READ time from S&S —
    // never persisted. Always absolute (CDN-prefixed) URLs; absent when the lookup misses its deadline,
    // S&S is down, or the colorway has no image.
    imageUrl: z.string().url().optional(),
    swatchUrl: z.string().url().optional(),
});
/** API view: one ledger movement (drops DDB keys). */
export const BlankMovementSchema = z.object({
    id: z.string(),
    ssSku: z.string(),
    type: BlankMovementTypeEnum,
    qtyDelta: z.number().int(),
    countedQty: z.number().int().nonnegative().optional(),
    reason: z.string().optional(),
    actor: z.string().optional(),
    sourceInvoice: z.string().optional(),
    orderRef: BlankMovementOrderRefSchema.optional(),
    batchId: z.string().optional(),
    basis: BlankCountBasisEnum.optional(),
    shelfQty: z.number().int().nonnegative().optional(),
    pulledQty: z.number().int().nonnegative().optional(),
    createdAt: z.string(),
});
/** `GET /inventory` — on-hand across every blank with a ledger history. */
export const BlankInventoryListSchema = z.object({
    items: z.array(BlankOnHandSchema),
});
// `BlankInventoryDetailSchema` is defined further down, after `BlankReorderRowSchema`: the detail
// response now embeds a `reorder` block, so it must follow the schema it composes (const before use).
/**
 * One blank's open-order demand vs derived on-hand — the "blanks short" hint (roadmap Phase 3).
 * `demand` is the garment count (Σ `unfulfilledQty`) over the MAPPED, still-UNFULFILLED order lines
 * in workflow state OPEN or BATCHED that resolve to this blank — i.e. blanks the open queue still
 * needs to pull. PRINTED/SHIPPED lines already drew their blank at mark-printed (reflected in on-hand),
 * so they're excluded; CANCELLED/FULFILLED owe nothing. `onHand` is the derived running sum of the
 * ledger (MAY be negative). `shortage = demand − onHand` (signed; > 0 means short).
 * Identity (brand/style/color/size) comes from the on-hand ledger when the blank has a movement
 * history; a blank demanded but never stocked has no ledger identity, so `sampleTitle` /
 * `sampleVariantTitle` carry a representative Shopify product/variant title to keep the row
 * recognizable on the floor.
 */
export const BlankDemandRowSchema = z.object({
    ssSku: z.string(),
    demand: z.number().int().nonnegative(),
    onHand: z.number().int(),
    shortage: z.number().int(),
    brandName: z.string().optional(),
    styleName: z.string().optional(),
    colorName: z.string().optional(),
    sizeName: z.string().optional(),
    sampleTitle: z.string().optional(),
    sampleVariantTitle: z.string().optional(),
});
/**
 * `GET /inventory/demand` — per-blank open demand vs on-hand across every blank the open queue needs,
 * sorted worst-shortage first. `totalShort` counts rows with `shortage > 0`. A read-time join (on-hand
 * is eventually consistent), NOT a reservation and NOT a full MRP model — a blanks-short hint only.
 */
export const BlankDemandListSchema = z.object({
    items: z.array(BlankDemandRowSchema),
    totalShort: z.number().int().nonnegative(),
});
// ── Blank inventory — reorder points (par + algorithmic suggestion, roadmap Phase 3 #2) ──
/**
 * Per-blank manual reorder point ("par"): the on-hand floor the owner wants to keep for a SKU.
 * DDB: PK `BLANKPAR#<ssSku>` / SK `SETTING`, mirrored on GSI1 (`BLANK_PARS` / `<ssSku>`) so all pars
 * list without a table scan. This is a SETTING, NOT a movement — deliberately kept OUT of the
 * `BLANK#<ssSku>` ledger partition so the on-hand running sum never sees it. The owner-set `par` is the
 * authority; the reorder endpoint also computes a *suggested* reorder point from consumption history
 * but never overwrites this stored value.
 */
export const BlankReorderSettingRecordSchema = z.object({
    PK: z.string(),
    SK: z.literal('SETTING'),
    GSI1PK: z.literal('BLANK_PARS'),
    GSI1SK: z.string(),
    entity: z.literal('blankReorderSetting'),
    ssSku: z.string(),
    par: z.number().int().nonnegative(),
    updatedAt: z.string(),
    updatedBy: z.string().optional(),
});
/**
 * Per-blank archive marker (roadmap Phase 2c): retires a dead/typo'd blank from the working inventory
 * views WITHOUT deleting its ledger — the audit trail is preserved. DDB: PK `BLANKARCH#<ssSku>` / SK
 * `SETTING`, mirrored on GSI1 (`BLANK_ARCH` / `<ssSku>`) so all archived blanks list without a table
 * scan. A SETTING, NOT a movement — deliberately OUT of the `BLANK#<ssSku>` ledger so on-hand's running
 * sum never sees it (same shape as par). Presence = archived; unarchive deletes the row.
 */
export const BlankArchiveSettingRecordSchema = z.object({
    PK: z.string(),
    SK: z.literal('SETTING'),
    GSI1PK: z.literal('BLANK_ARCH'),
    GSI1SK: z.string(),
    entity: z.literal('blankArchive'),
    ssSku: z.string(),
    archivedAt: z.string(),
    archivedBy: z.string().optional(),
});
/** `POST /inventory/:ssSku/archive` + `/unarchive` result — the blank's new archive state. */
export const BlankArchiveResultSchema = z.object({
    ssSku: z.string(),
    archived: z.boolean(),
});
/** Confidence in the computed reorder-point suggestion, from how much consumption history backs it. */
export const ReorderConfidenceEnum = z.enum(['none', 'low', 'ok']);
/**
 * `POST /inventory/:ssSku/par` body — set (or clear) the manual reorder point for a blank.
 * `par: null` CLEARS it (the below-threshold signal then falls back to the computed suggestion).
 */
export const BlankSetParRequestSchema = z.object({
    par: z.number().int().nonnegative().nullable(),
});
/**
 * One blank's reorder status (roadmap Phase 3 #2, gap A2). All numbers are DETERMINISTIC — computed by
 * the engine, never an LLM.
 *  - `onHand` — derived running sum of the ledger (MAY be negative).
 *  - `suggestedRop` — the ALGORITHMIC reorder point from trailing consumption:
 *      dailyRate × leadTimeDays + serviceZ · σ · √leadTimeDays, rounded up; `null` when there's no
 *      usage history (`confidence === 'none'`). `dailyRate` (garments/day) + `safetyStock` are the
 *      transparent inputs; `dataPoints` = CONSUME events in the window; `confidence` grades the data.
 *  - `manualPar` — the owner-set shelf BUFFER (authority). `effectiveThreshold` is that buffer ADDED to
 *      `committedDemand` (the open-order units the shelf still owes), because `onHand` is the WHOLE
 *      physical shelf INCLUDING blanks already committed to unpressed orders — a blank leaves the
 *      ledger at PRESS time (backflush), not at order time. `bufferTarget` is the buffer term actually
 *      used: `manualPar` when set, else only the SAFETY-STOCK half of `suggestedRop` (that ROP's other
 *      half is lead-time demand, which is already inside `committedDemand`). The result is floored at
 *      `manualPar ?? suggestedRop`, so a shallow queue never LOWERS the target.
 *      `belowThreshold` = (onHand + onOrder) < effectiveThreshold (false when threshold null).
 *      `suggestedOrderQty` = max(0, effectiveThreshold − (onHand + onOrder)).
 *      ⚠️ `committedDemand` is 0 on callers that pass no demand (`GET /inventory/reorder`), and demand
 *      0 is BIT-IDENTICAL to the pre-2026-08-12 `max(par, demand)` formula.
 * Identity (brand/style/color/size) mirrors BlankOnHand (absent for a par-only, never-stocked blank).
 */
export const BlankReorderRowSchema = z.object({
    ssSku: z.string(),
    brandName: z.string().optional(),
    styleName: z.string().optional(),
    colorName: z.string().optional(),
    sizeName: z.string().optional(),
    onHand: z.number().int(),
    manualPar: z.number().int().nonnegative().nullable(),
    suggestedRop: z.number().int().nonnegative().nullable(),
    dailyRate: z.number().nonnegative(),
    safetyStock: z.number().nonnegative(),
    dataPoints: z.number().int().nonnegative(),
    confidence: ReorderConfidenceEnum,
    effectiveThreshold: z.number().int().nonnegative().nullable(),
    belowThreshold: z.boolean(),
    suggestedOrderQty: z.number().int().nonnegative(),
    // Units already on an OPEN purchase order (QUEUED or ORDERED) for this blank (roadmap Phase 3b, gap
    // #4). The planning position is `onHand + onOrder`: `belowThreshold`/`suggestedOrderQty` are computed
    // against it so a blank already reordered stops re-suggesting ("did we order this?"). `onHand` stays
    // the true PHYSICAL running sum. 0 when no open PO covers this blank.
    onOrder: z.number().int().nonnegative(),
    // Open-order units this blank still owes (MAPPED + UNFULFILLED + OPEN|BATCHED — see
    // inventory-demand.ts), folded INTO effectiveThreshold. 0 for callers that pass no demand.
    committedDemand: z.number().int().nonnegative().optional(),
    // The shelf-buffer term used in effectiveThreshold: `manualPar` when set, else ceil(safetyStock).
    // Absent when there is neither a par nor a ROP (a pure buy-to-order blank has no buffer to hold).
    bufferTarget: z.number().int().nonnegative().optional(),
});
/**
 * `GET /inventory/:ssSku` — on-hand + recent movements (newest first) + the blank's reorder status.
 * `reorder` embeds the same BlankReorderRow the `/par` write returns (deterministic par + algorithmic
 * ROP — NO LLM), so the shared per-blank drawer shows + edits par and the effective threshold without a
 * second round-trip, from ANY tab (the On-hand list carries no par). Defined here, after
 * BlankReorderRowSchema, because it composes it.
 */
// ── Pulled blanks — the read-side view (drawer, count sheet, count response) ─────────────────────
/** One sheet with a pull record for this blank: what the puller recorded, what the press has drawn under
 *  that sheet, and the difference still off the shelf and in the book (`out = max(0, recorded − drawn)`). */
export const BlankPulledSheetSchema = z.object({
    batchId: z.string(),
    seq: z.number().int().positive().optional(),
    /** The sheet's status (a `BatchStatusEnum` value; a plain string here because the batch enums are
     *  declared later in this module — const before use). */
    status: z.string(),
    recorded: z.number().int().nonnegative(),
    drawn: z.number().int().nonnegative(),
    out: z.number().int().nonnegative(),
    /** This blank's quantity ON the sheet, when the sheet's items were read (the count sheet reads them for
     *  every started open sheet; the drawer skips a recorded sheet's). A short pull reads `recorded < onSheet`
     *  — the hub's "shelf short at the pull" item and the count row's short hint. Absent = unknown, never 0. */
    onSheet: z.number().int().nonnegative().optional(),
    rev: z.number().int().nonnegative(),
    at: z.string(),
    via: z.string(),
    who: z.string().optional(),
});
/** An open sheet whose pull STARTED (its pull list or labels printed, or the pull station opened) but which
 *  has no record for this blank — the one case the count still asks about. `qtyOnSheet` is this blank's
 *  quantity on the sheet; `pressed` is what the press has already drawn under it. */
export const BlankUnconfirmedSheetSchema = z.object({
    batchId: z.string(),
    seq: z.number().int().positive().optional(),
    qtyOnSheet: z.number().int().nonnegative(),
    printed: z.object({
        pullList: z.string().optional(),
        labels: z.string().optional(),
        station: z.string().optional(),
    }),
    pressed: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    orders: z.number().int().nonnegative(),
    ageH: z.number().nonnegative(),
});
/** A CLOSED (printed) sheet that still carries `out > 0` for this blank — a labelled blank pulled and never
 *  pressed (a refund after the pull, a short): physically off the shelf, still in the book. */
export const BlankStillOutSchema = z.object({
    batchId: z.string(),
    seq: z.number().int().positive().optional(),
    out: z.number().int().positive(),
    printedAt: z.string().optional(),
});
/** The pulled-blanks block one blank's readers receive (`GET /inventory/:ssSku`, the count sheet, the count
 *  response). `now` = Σ `out` over `sheets`. The planning readers (reorder, coverage, the overview) never
 *  receive it — the M5 firewall. */
export const BlankPulledBlockSchema = z.object({
    now: z.number().int().nonnegative(),
    sheets: z.array(BlankPulledSheetSchema),
    unconfirmed: z.array(BlankUnconfirmedSheetSchema),
    stillOut: z.array(BlankStillOutSchema),
    /** Server time the block was derived at — the shelf count echoes it back as `asOf`. */
    asOf: z.string(),
});
export const BlankInventoryDetailSchema = z.object({
    onHand: BlankOnHandSchema,
    movements: z.array(BlankMovementSchema),
    reorder: BlankReorderRowSchema,
    // Whether this blank is archived (retired from the working views). The drawer surfaces the
    // archive/unarchive action off this; an archived blank still renders its full detail here.
    /** Pulled-blanks block for the drawer (pulled-blanks v2). Absent when the derivation failed (a fail-open
     *  read: the drawer still renders and a WARN names the sheet) and on an older server. */
    pulled: BlankPulledBlockSchema.optional(),
    archived: z.boolean(),
});
/** One row of `GET /inventory/count-sheet` — the count station's universe: every blank with a ledger
 *  history, every blank with a pull record, and every demand-only blank. `book` is on-hand as the ledger
 *  reads it; `pulledQty` is what the pull station has recorded as off the shelf and not yet drawn. The
 *  pulled fields are absent when the derivation failed (`degraded` on the response) — the sheet still
 *  renders the book. */
export const CountSheetRowSchema = z.object({
    ssSku: z.string(),
    brandName: z.string().optional(),
    styleName: z.string().optional(),
    colorName: z.string().optional(),
    sizeName: z.string().optional(),
    book: z.number().int(),
    archived: z.boolean(),
    /** True for a blank with no ledger history (demanded by an open order, or only on a pull record). */
    demandOnly: z.boolean(),
    lastCountedAt: z.string().optional(),
    lastCountedBasis: BlankCountBasisEnum.optional(),
    pulledQty: z.number().int().nonnegative().optional(),
    pulledSheets: z.array(BlankPulledSheetSchema).optional(),
    unconfirmed: z.array(BlankUnconfirmedSheetSchema).optional(),
    stillOut: z.array(BlankStillOutSchema).optional(),
});
export const CountSheetResponseSchema = z.object({
    rows: z.array(CountSheetRowSchema),
    /** Server time the sheet was built — the shelf count posts it back as `asOf`. */
    asOf: z.string(),
    /** True when the pulled derivation failed and the rows carry no pulled fields (fail-open read). */
    degraded: z.boolean().optional(),
});
/** `POST /inventory/:ssSku/count` (and Restock · Set) response. An EXTENSION of `BlankOnHandSchema`, not a
 *  wrapper — the `/count` page that shipped before the pull station reads the on-hand fields straight off
 *  `data`, and additive optional fields are what an older bundle ignores. */
export const BlankCountResultSchema = BlankOnHandSchema.extend({
    /** True when this response replayed the stored COUNT for the same `clientToken` instead of writing one. */
    deduplicated: z.boolean().optional(),
    /** The COUNT row's split — what was typed, what was added, what moved. */
    movement: z
        .object({
        basis: BlankCountBasisEnum,
        countedQty: z.number().int().nonnegative(),
        qtyDelta: z.number().int(),
        shelfQty: z.number().int().nonnegative().optional(),
        pulledQty: z.number().int().nonnegative().optional(),
    })
        .optional(),
    /** The pulled block the count was derived against, plus any seen sheet whose `out` FELL during the walk
     *  because the press drew it (written and reported, never refused). Shelf basis only. */
    pulled: BlankPulledBlockSchema.extend({
        fell: z
            .array(z.object({
            batchId: z.string(),
            seq: z.number().int().positive().optional(),
            from: z.number().int().nonnegative(),
            to: z.number().int().nonnegative(),
            cause: z.literal('pressed'),
        }))
            .optional(),
    }).optional(),
});
/**
 * `GET /inventory/reorder` — every blank with a ledger history and/or a manual par, with its reorder
 * status, sorted below-threshold first then by shortfall. `belowCount` = rows with `belowThreshold`.
 * `params` echoes the (currently constant, owner-set) model inputs so the dashboard can explain the
 * math and stays honest about what drove each number.
 */
export const BlankReorderListSchema = z.object({
    items: z.array(BlankReorderRowSchema),
    belowCount: z.number().int().nonnegative(),
    params: z.object({
        leadTimeDays: z.number().int().positive(),
        windowDays: z.number().int().positive(),
        serviceZ: z.number().positive(),
        serviceLabel: z.string(),
    }),
});
// ── Blank inventory — editable reorder model config (roadmap Phase 3a, gap #10) ──
/**
 * The tunable reorder-model inputs — the four numbers that drive every suggested reorder point (and
 * therefore every draft-PO quantity). Previously the hardcoded `REORDER_MODEL` constant; now DDB config
 * an admin can edit. Bounds keep the echoed `params` (BlankReorderList/PurchaseDraft/InventoryOverview)
 * in range and stop absurd edits: lead time ≤ a quarter, window ≤ a year, service Z ≤ ~4σ (>99.99%).
 * `serviceLabel` is the human tag for `serviceZ` (e.g. "95%"); the editor sets the two together.
 */
export const ReorderConfigBodySchema = z.object({
    leadTimeDays: z.number().int().min(1).max(90),
    windowDays: z.number().int().min(1).max(365),
    serviceZ: z.number().positive().max(4),
    serviceLabel: z.string().min(1).max(16),
});
/**
 * DDB record for the reorder-model config: a SINGLE current row (PK `REORDER#CONFIG` / SK `CURRENT`),
 * upserted on edit (last-write-wins, like a par setting) — NOT versioned like pricing-config, because
 * reorder outputs are recomputed live and never persisted, so there's no "which version drove this"
 * reproducibility concern. `updatedBy` is provenance (an email/sub), never a secret.
 */
export const ReorderConfigRecordSchema = ReorderConfigBodySchema.extend({
    PK: z.literal('REORDER#CONFIG'),
    SK: z.literal('CURRENT'),
    entity: z.literal('reorderConfig'),
    updatedAt: z.string(),
    updatedBy: z.string().optional(),
});
/** Where the active reorder config came from — `fallback-*` means no DDB row (running on defaults). */
export const ReorderConfigSourceEnum = z.enum([
    'cache',
    'ddb',
    'fallback-unseeded',
    'fallback-error',
]);
/**
 * `GET /reorder-config/current` + `POST /reorder-config` response — the active model + provenance.
 * `updatedAt`/`updatedBy` are null on a `fallback-*` source (no DDB row backs the body). The dashboard
 * shows a "using defaults" hint when the source is `fallback-unseeded`.
 */
export const ReorderConfigViewSchema = z.object({
    body: ReorderConfigBodySchema,
    source: ReorderConfigSourceEnum,
    updatedAt: z.string().nullable(),
    updatedBy: z.string().nullable(),
});
// ── Ship-to config (S&S auto-send Phase A) — FSP's receiving address, sent as an S&S order's shippingAddress ──
/**
 * FSP's ship-to (receiving) address — the `shippingAddress` on every S&S order. Editable in the dashboard
 * (a single CURRENT row, like the reorder config). NOT a secret — a business address. `residential`
 * defaults false (a commercial receiving dock). `emailConfirmation` is an optional address S&S emails an
 * order confirmation to (kept '' during test sends unless set). Field lengths bound defensively.
 */
export const ShipToConfigBodySchema = z.object({
    customer: z.string().min(1).max(100),
    attn: z.string().max(100).optional(),
    address: z.string().min(1).max(200),
    city: z.string().min(1).max(100),
    state: z.string().min(1).max(50),
    zip: z.string().min(1).max(20),
    residential: z.boolean().default(false),
    emailConfirmation: z.string().max(200).optional(),
});
/**
 * S&S address rules, enforced on WRITE only (see `ShipToConfigRequestSchema`).
 *
 * S&S's Orders_Post rejects a spelled-out state with an opaque HTTP 400 — their example is `"IL"`, i.e.
 * the 2-letter code. A real send failed exactly this way (`state: "Texas"` → 400, 2026-07-18), so the
 * rule is enforced at the point of entry instead of at the vendor.
 *
 * Deliberately a 2-LETTER SHAPE check, not a whitelist of US state codes: a whitelist would hard-block a
 * jurisdiction S&S actually accepts (e.g. a Canadian province) with no override on the money path, which
 * is worse than letting a typo like `"XZ"` reach S&S — and since upstream error bodies are now logged and
 * surfaced, such a typo produces a readable S&S message rather than a silent mystery.
 */
export const SS_STATE_RE = /^[A-Za-z]{2}$/;
/** S&S documents `zip` as "Zip (5 digits)". ZIP+4 is accepted on write and normalized down to the 5. */
export const SS_ZIP_RE = /^(\d{5})(?:-\d{4})?$/;
/**
 * WRITE-side ship-to schema (`POST /shipto-config`). Strict + normalizing where the storage/read schema
 * (`ShipToConfigBodySchema`) stays lenient — the lenient read is load-bearing: a record predating these
 * rules (the live `state: "Texas"` row) must still PARSE so the owner can read it back and correct it. A
 * strict read schema would make the bad record unreadable and lock them out of the very field that needs
 * fixing. Strict on write, lenient on read.
 */
export const ShipToConfigRequestSchema = ShipToConfigBodySchema.extend({
    state: z
        .string()
        .trim()
        .regex(SS_STATE_RE, "State must be S&S's 2-letter code (e.g. TX), not the full name.")
        // S&S matches on the canonical uppercase code; accept 'tx' and store 'TX'.
        .transform((s) => s.toUpperCase()),
    zip: z
        .string()
        .trim()
        .regex(SS_ZIP_RE, 'ZIP must be 5 digits (ZIP+4 is accepted and trimmed to 5).')
        .transform((z5) => z5.slice(0, 5)),
});
/** DDB record for the ship-to config: a SINGLE current row (PK `SHIPTO#CONFIG` / SK `CURRENT`), upserted
 *  on edit (last-write-wins). `updatedBy` is provenance (email/sub), never a secret. */
export const ShipToConfigRecordSchema = ShipToConfigBodySchema.extend({
    PK: z.literal('SHIPTO#CONFIG'),
    SK: z.literal('CURRENT'),
    entity: z.literal('shipToConfig'),
    updatedAt: z.string(),
    updatedBy: z.string().optional(),
});
/** `GET /shipto-config/current` + `POST /shipto-config` response — the address (null until first set) +
 *  provenance. `body` null means no ship-to configured yet; the send-test action is blocked until it's set. */
export const ShipToConfigViewSchema = z.object({
    body: ShipToConfigBodySchema.nullable(),
    updatedAt: z.string().nullable(),
    updatedBy: z.string().nullable(),
});
// ── Payment profile (S&S auto-send Phase A) — the saved card/bank an S&S order bills against ──
/**
 * One saved payment method on the S&S account, from `GET /v2/paymentprofiles/?email=`. `profileID` is
 * what an order references; `name` is S&S's human label and embeds a partial instrument + holder (e.g.
 * "BMO Harris Bank 1234 (John Doe)") — financial PII, so every route carrying it is admin-gated and it
 * is NEVER logged. No PAN/CVV exists here: S&S stores the instrument, we only ever hold this reference.
 */
export const SsPaymentProfileSchema = z.object({
    profileID: z.number().int().positive(),
    profileType: z.string(),
    name: z.string(),
});
/** `GET /payment-profiles?email=` — the pickable saved methods for that S&S account email. */
export const PaymentProfileListSchema = z.object({
    email: z.string(),
    profiles: z.array(SsPaymentProfileSchema),
});
/**
 * The CHOSEN payment profile, stored so send-test can bill against it. S&S requires a PaymentProfile on
 * a prepay (card/bank) account even for a testOrder:true request — that request is still created-then-
 * cancelled and never billed, but it now carries a chargeable instrument, which is exactly why
 * `testOrder: true` stays hardcoded with no false path in this phase.
 *
 * `label`/`profileType` are cached display copy so the dashboard can show what's selected without
 * re-hitting S&S. Only ever returned to an inventory-write session.
 */
/**
 * Storage caps for the cached display copy. Exported because the writer must TRUNCATE to exactly these
 * before storing: `label`/`profileType` are now derived from S&S (not the client), and S&S bounds
 * neither. An over-long name would otherwise fail the record schema deep inside `putItem`, surfacing as
 * a 400 that blames the caller for `label` — a field the strict write schema forbids them from sending.
 * One definition so the cap and the truncation can never drift apart.
 */
export const PAYMENT_PROFILE_LABEL_MAX = 200;
export const PAYMENT_PROFILE_TYPE_MAX = 50;
export const PaymentProfileConfigBodySchema = z.object({
    email: z.string().email().max(200),
    profileID: z.number().int().positive(),
    label: z.string().max(PAYMENT_PROFILE_LABEL_MAX).optional(),
    profileType: z.string().max(PAYMENT_PROFILE_TYPE_MAX).optional(),
});
/**
 * WRITE-side schema (`POST /payment-profile-config`) — deliberately ONLY the pair that identifies the
 * instrument. `label`/`profileType` are NOT accepted from the client: the server re-fetches the profile
 * from S&S and derives them, because those strings become the authoritative dashboard display of which
 * card is being billed. A client-supplied label could read "Visa 4242" while `profileID` points at the
 * bank account — the UI would confidently misreport the instrument, and under a live send that is a
 * charge to something the operator never chose.
 *
 * The server also VERIFIES that `profileID` actually belongs to `email` on the S&S account. The browser's
 * "pick from the list you just saw" guard is a convenience, not a control: it can't survive an edited
 * email field, and it doesn't exist at all for a direct API call.
 */
export const PaymentProfileConfigRequestSchema = z.object({
    email: z.string().email().max(200),
    profileID: z.number().int().positive(),
});
/** DDB record: a SINGLE current row (PK `PAYMENTPROFILE#CONFIG` / SK `CURRENT`), upserted on edit. */
export const PaymentProfileConfigRecordSchema = PaymentProfileConfigBodySchema.extend({
    PK: z.literal('PAYMENTPROFILE#CONFIG'),
    SK: z.literal('CURRENT'),
    entity: z.literal('paymentProfileConfig'),
    updatedAt: z.string(),
    updatedBy: z.string().optional(),
});
/** `GET /payment-profile-config/current` + `POST /payment-profile-config` — null body until first set. */
export const PaymentProfileConfigViewSchema = z.object({
    body: PaymentProfileConfigBodySchema.nullable(),
    updatedAt: z.string().nullable(),
    updatedBy: z.string().nullable(),
});
// ── Blank inventory — draft purchase order (roadmap Phase 3 #2b) ──
/** Whether a draft line's unit cost was resolved. `unavailable` = S&S had no price / a lookup blip;
 *  `skipped` = beyond the per-request cost-enrichment cap (quantities are still shown). */
export const PurchaseDraftCostStatusEnum = z.enum(['priced', 'unavailable', 'skipped']);
/**
 * One proposed reorder line on the draft PO (roadmap Phase 3 #2b). `orderQty` is the reorder calc's
 * `suggestedOrderQty` (the operator adjusts it in the dashboard before placing the order manually).
 * `unitCost` is the DETERMINISTIC S&S effective cost (min of customerPrice and a valid, unexpired
 * salePrice — resolved server-side via the shared `effectivePrice`, never re-derived client-side); it
 * is an ESTIMATE (S&S may bill a higher single-piece price on a broken case, and shows the final price
 * at checkout). `null` when the cost couldn't be resolved. `lineTotal = unitCost × orderQty` or null.
 */
export const PurchaseDraftLineSchema = z.object({
    ssSku: z.string(),
    brandName: z.string().optional(),
    styleName: z.string().optional(),
    colorName: z.string().optional(),
    sizeName: z.string().optional(),
    onHand: z.number().int(),
    threshold: z.number().int().nonnegative(),
    // Why `threshold` is what it is: bufferTarget + committedDemand. Carried so the draft page can show
    // "40 level + 31 open orders" instead of an unexplained 71 on the page whose one click becomes a real
    // S&S order. Absent from an older API — render defensively and never print an equation that doesn't
    // add up (the outer max() in buildReorderRow can win, in which case the parts don't sum).
    committedDemand: z.number().int().nonnegative().optional(),
    bufferTarget: z.number().int().nonnegative().optional(),
    // Units already on an OPEN purchase order for this blank; the draft nets it out of `orderQty` (a fully
    // covered blank drops off the draft entirely) so the same shirts aren't ordered twice. 0 if none.
    onOrder: z.number().int().nonnegative(),
    orderQty: z.number().int().nonnegative(),
    unitCost: z.number().nonnegative().nullable(),
    lineTotal: z.number().nonnegative().nullable(),
    costStatus: PurchaseDraftCostStatusEnum,
    // The blank's S&S colorway photo, resolved at read time from the SAME getStyle the cost lookup
    // already made — never persisted. Absent for a line past the pricing cap, when S&S is down, or when
    // the colorway has no image.
    imageUrl: z.string().url().optional(),
});
/**
 * `GET /inventory/purchase-draft` — the below-threshold reorder lines assembled into a proposed S&S
 * order with an estimated cost. `estimatedTotal` = Σ lineTotal over PRICED lines only, so when some
 * lines are unavailable/skipped it's a floor ("at least"); `pricedCount`/`unavailableCount`/
 * `skippedCount` let the UI say how complete the estimate is. Cost is admin/inventory-admin-gated.
 * A PROPOSAL ONLY — it does NOT place any order, call S&S, or persist anything.
 */
export const PurchaseDraftSchema = z.object({
    lines: z.array(PurchaseDraftLineSchema),
    totalUnits: z.number().int().nonnegative(),
    estimatedTotal: z.number().nonnegative(),
    pricedCount: z.number().int().nonnegative(),
    unavailableCount: z.number().int().nonnegative(),
    skippedCount: z.number().int().nonnegative(),
    params: z.object({
        leadTimeDays: z.number().int().positive(),
        windowDays: z.number().int().positive(),
        serviceZ: z.number().positive(),
        serviceLabel: z.string(),
    }),
});
// ── Blank inventory — persisted purchase-order lifecycle (roadmap Phase 3b, gap #4) ──
/**
 * A persisted PO's lifecycle. Phase 3b uses QUEUED → ORDERED → CLOSED (+ CANCELED to void a mistake);
 * RECEIVING is reserved for Phase 3c (partial receive-against-PO). An OPEN PO (QUEUED, ORDERED, or
 * RECEIVING) nets its units out of the reorder signal so a reordered blank stops showing short;
 * CLOSED/CANCELED do not — so the operator marks a PO received (→ CLOSED) once its goods land, which
 * un-nets it and re-arms the reorder signal (3c will automate this via receive-against-PO).
 *  - QUEUED   — created from the draft, not yet placed with S&S (a reminder to place it).
 *  - ORDERED  — the operator placed it in S&S by hand (pasting the FSP PO#) and clicked Mark ordered.
 *  - RECEIVING — (3c) at least one receipt has landed but not the full quantity.
 *  - CLOSED   — goods received; the operator marked it received (3b manual) — stops netting.
 *  - CANCELED — voided before/after ordering; stops netting, keeps the record for audit.
 */
export const PurchaseOrderStatusEnum = z.enum([
    'QUEUED',
    'ORDERED',
    'RECEIVING',
    'CLOSED',
    'CANCELED',
]);
/** Whether an OPEN PO's units still count toward on-order netting. */
export const OPEN_PURCHASE_ORDER_STATUSES = [
    'QUEUED',
    'ORDERED',
    'RECEIVING',
];
/**
 * State of a LIVE (billable) S&S place-order attempt (auto-send Phase B). This is the outbound-idempotency
 * marker, NOT the PO status:
 *  - `in_flight` — a place-order was stamped BEFORE the S&S POST and its outcome is not yet resolved. A PO
 *    left here is unreconciled: the route REFUSES a second send (never blindly re-POST a maybe-placed order).
 *  - `placed` — S&S confirmed the order; the PO has moved QUEUED → ORDERED and `ssOrderNumbers` is set.
 *  - `unknown` — the POST completed with an UNCONFIRMED outcome (S&S 5xx, or a 2xx whose body we couldn't
 *    read/parse, or a network fault). The order MAY exist. A human must reconcile against the S&S portal
 *    before any resend; the route stays blocked until the attempt is cleared. Never auto-retried.
 * A definite rejection (S&S 4xx — no order created) does NOT land here: the marker is removed so a corrected
 * resend is allowed. Absent on a PO that was never sent live.
 */
export const PurchaseOrderLiveOrderAttemptStatusEnum = z.enum(['in_flight', 'placed', 'unknown']);
/**
 * How a PO originated. `reorder` — the auto-generated below-threshold draft (the only pre-3d source).
 * `manual` — an operator hand-composed the lines in the manual-PO builder (Phase 3d). Provenance only:
 * both net on-order identically; the flag distinguishes an ad-hoc buy from an algorithm-driven reorder in
 * the audit trail (and marks that a `manual` PO's `params` snapshot is not meaningful — no reorder math ran).
 */
export const PurchaseOrderSourceEnum = z.enum(['reorder', 'manual']);
/**
 * One line on a persisted PO (API view). `orderQty` is the operator-reviewed quantity (adjusted off the
 * draft's `suggestedOrderQty` before creating the PO). `unitCost` is the S&S effective cost RE-RESOLVED
 * server-side at creation (never trusted from the client) — an estimate snapshot; `null` if unresolved.
 */
export const PurchaseOrderLineSchema = z.object({
    ssSku: z.string(),
    brandName: z.string().optional(),
    styleName: z.string().optional(),
    colorName: z.string().optional(),
    sizeName: z.string().optional(),
    orderQty: z.number().int().positive(),
    // Units received against this line so far (Phase 3c receive-against-PO). Netting plans against the
    // remaining `orderQty − receivedQty`; `receivedQty` may exceed `orderQty` on an over-ship.
    receivedQty: z.number().int().nonnegative(),
    unitCost: z.number().nonnegative().nullable(),
    lineTotal: z.number().nonnegative().nullable(),
    costStatus: PurchaseDraftCostStatusEnum,
});
/** The reorder-model params snapshot recorded on a PO (provenance — what drove the proposed quantities). */
const PurchaseOrderParamsSchema = z.object({
    leadTimeDays: z.number().int().positive(),
    windowDays: z.number().int().positive(),
    serviceZ: z.number().positive(),
    serviceLabel: z.string(),
});
/**
 * A PO header without its lines — the list-view row (`GET /purchase-orders`). `poNumber` (e.g.
 * `FSP-1042`) is the human id the operator pastes into S&S's PO field so receipts tie back (3c);
 * `estimatedTotal` sums PRICED lines only. Cost fields are admin/inventory-admin-gated (the whole
 * endpoint 403s for others), so they're never stripped here.
 */
export const PurchaseOrderSummarySchema = z.object({
    poNumber: z.string(),
    seq: z.number().int().positive(),
    status: PurchaseOrderStatusEnum,
    totalUnits: z.number().int().nonnegative(),
    lineCount: z.number().int().nonnegative(),
    estimatedTotal: z.number().nonnegative(),
    note: z.string().optional(),
    createdAt: z.string(),
    createdBy: z.string().optional(),
    orderedAt: z.string().optional(),
    orderedBy: z.string().optional(),
    closedAt: z.string().optional(),
    closedBy: z.string().optional(),
    canceledAt: z.string().optional(),
    canceledBy: z.string().optional(),
    source: PurchaseOrderSourceEnum,
    // S&S auto-send Phase A (TEST orders only). Provenance of the last test send: the S&S order number(s)
    // returned (an array — S&S may split across warehouses) + who/when. A test order is created-then-cancelled
    // on S&S, so this is audit only; it does NOT mean the PO was really ordered (status stays QUEUED).
    ssTestOrderNumbers: z.array(z.string()).optional(),
    ssTestSubmittedAt: z.string().optional(),
    ssTestSubmittedBy: z.string().optional(),
    // S&S auto-send Phase B (LIVE billable orders). `ssOrderNumbers` are the real S&S order number(s) from a
    // placed order; `ssOrderAttemptStatus` is the outbound-idempotency marker (in_flight/placed/unknown — see
    // PurchaseOrderLiveOrderAttemptStatusEnum). A `placed` order also moves status QUEUED → ORDERED. An
    // in_flight/unknown marker means an unreconciled send — the dashboard must warn and block a re-send.
    ssOrderNumbers: z.array(z.string()).optional(),
    ssOrderAttemptStatus: PurchaseOrderLiveOrderAttemptStatusEnum.optional(),
    params: PurchaseOrderParamsSchema,
});
/** A PO header + its lines — the detail view (`GET /purchase-orders/:poNumber`, create/transition result). */
export const PurchaseOrderViewSchema = PurchaseOrderSummarySchema.extend({
    lines: z.array(PurchaseOrderLineSchema),
});
/** `GET /purchase-orders` — POs newest-first (optionally filtered by status) + a page cursor. */
export const PurchaseOrderListSchema = z.object({
    items: z.array(PurchaseOrderSummarySchema),
    nextCursor: z.string().optional(),
});
/**
 * `POST /purchase-orders` — persist a draft as a PO. The client posts the operator-reviewed lines
 * (identity is denormalized display, trusted from our own draft; `orderQty` is authoritative). The
 * server RE-RESOLVES unit costs from S&S at creation — the client never sends a dollar figure. Optional
 * `clientToken` (uuid) makes the create idempotent (a dropped-response retry replays the same PO rather
 * than minting a second one that would double the on-order netting).
 */
export const PurchaseOrderCreateRequestSchema = z.object({
    lines: z
        .array(z.object({
        ssSku: z.string().min(1).max(64),
        brandName: z.string().optional(),
        styleName: z.string().optional(),
        colorName: z.string().optional(),
        sizeName: z.string().optional(),
        orderQty: z.number().int().positive(),
    }))
        .min(1)
        .max(90),
    note: z.string().max(500).optional(),
    clientToken: z.string().uuid().optional(),
    // Provenance. Omitted by the auto-draft create (defaults `reorder`, so pre-3d clients are unaffected);
    // the manual-PO builder sends `manual`, which also opts the create into server-side SKU existence-reject
    // + canonicalization (see the POST /purchase-orders handler).
    source: PurchaseOrderSourceEnum.default('reorder'),
});
/** `GET /purchase-orders/open-on-order` — per-SKU units currently on order across all OPEN POs (netting
 * `Σ max(0, orderQty − receivedQty)`). Powers the manual-PO builder's duplicate-open-PO warning so an
 * operator never silently doubles the on-order for a SKU already covered. No cost data. */
export const PurchaseOrderOpenOnOrderSchema = z.object({
    items: z.array(z.object({ ssSku: z.string(), onOrder: z.number().int().nonnegative() })),
});
/** `POST /purchase-orders/:poNumber/cancel` — optional reason for the audit trail. */
export const PurchaseOrderCancelRequestSchema = z.object({
    reason: z.string().max(500).optional(),
    clientToken: z.string().uuid().optional(),
});
/**
 * One per-line problem S&S reported on an order (the `lineErrors[]` in a place-order result): the S&S line
 * `identifier` (SKU), when given, plus the message. Shared shape for the place-order response diagnostics.
 */
export const OrderLineErrorSchema = z.object({
    identifier: z.string().optional(),
    message: z.string(),
});
/**
 * S&S `POST /v2/orders/` `shippingMethod` codes — the full menu from S&S's Orders_Post docs
 * (https://api.ssactivewear.com/v2/Orders_Post.aspx). Staff pick one PER order; the app default is
 * `DEFAULT_SHIPPING_METHOD` = `'1'` (Ground, carrier chosen by S&S — S&S's own documented default). `54`
 * (Misc Cheapest) is the cheapest-ground pick; `6`/`8` are warehouse pickup ($0 freight, only usable if the
 * S&S warehouse is drivable). `SS_SHIPPING_METHODS` is the single source of truth for the labels the
 * dashboard dropdown renders; `ShippingMethodCodeEnum` validates the code an order request carries. A unit
 * test asserts the two never drift apart.
 */
export const SS_SHIPPING_METHODS = [
    { code: '1', label: 'Ground (carrier chosen by S&S)' },
    { code: '54', label: 'Misc Cheapest (S&S picks cheapest ground)' },
    { code: '40', label: 'UPS Ground' },
    { code: '14', label: 'FedEx Ground' },
    { code: '16', label: 'UPS 3 Day Select' },
    { code: '3', label: 'UPS 2nd Day Air' },
    { code: '22', label: 'UPS 2nd Day Air AM' },
    { code: '48', label: 'FedEx 2nd Day Air' },
    { code: '2', label: 'UPS Next Day Air' },
    { code: '21', label: 'UPS Next Day Air Saver' },
    { code: '17', label: 'UPS Next Day Air Early AM' },
    { code: '27', label: 'FedEx Next Day Standard' },
    { code: '26', label: 'FedEx Next Day Priority' },
    { code: '19', label: 'UPS Saturday' },
    { code: '20', label: 'UPS Saturday Early' },
    { code: '6', label: 'Will Call / Pickup' },
    { code: '8', label: 'Messenger Pickup' },
];
export const ShippingMethodCodeEnum = z.enum([
    '1',
    '54',
    '40',
    '14',
    '16',
    '3',
    '22',
    '48',
    '2',
    '21',
    '17',
    '27',
    '26',
    '19',
    '20',
    '6',
    '8',
]);
/** The app's shipping method when staff don't pick one: `1` = Ground (S&S's own documented default). */
export const DEFAULT_SHIPPING_METHOD = '1';
/**
 * `POST /purchase-orders/:poNumber/place-order` request (LIVE, BILLABLE order). `confirm: true` is an
 * explicit-intent flag the dashboard sends AFTER its own pieces-and-cost confirm dialog — a lightweight belt
 * so a body-less / accidental POST can't place a real order. It is NOT the access control: that is the
 * manager-only `admin` gate on the route, plus the server-side pre-POST `in_flight` idempotency marker
 * (a `clientToken` a caller forgets to rotate would not stop a double order, so it stays advisory).
 * `shippingMethod` picks the S&S carrier / service for THIS order; omitted → `DEFAULT_SHIPPING_METHOD`.
 */
export const PurchaseOrderPlaceRequestSchema = z.object({
    confirm: z.literal(true),
    clientToken: z.string().uuid().optional(),
    shippingMethod: ShippingMethodCodeEnum.optional(),
});
/**
 * `POST /purchase-orders/:poNumber/place-order` SUCCESS result (auto-send Phase B). Only returned when S&S
 * CONFIRMED a real order: `testOrder` is ALWAYS false here, `orderNumbers` are the real S&S order number(s)
 * (array — S&S may split across warehouses), and `status` is the PO's new status (ORDERED). A definite S&S
 * rejection returns a 502 error envelope (retry after fix); an UNCONFIRMED outcome returns a distinct
 * `order_outcome_unknown` 502 (do NOT resend — reconcile against the S&S portal). No cost/credential data.
 */
export const PlaceOrderResultSchema = z.object({
    poNumber: z.string(),
    testOrder: z.literal(false),
    orderNumbers: z.array(z.string()),
    lineErrors: z.array(OrderLineErrorSchema),
    submittedAt: z.string(),
    status: PurchaseOrderStatusEnum,
    /**
     * Set only when S&S ACCEPTED the order but recording it on the PO afterwards failed — the order EXISTS,
     * so this is a warning on a 200, never an error (reporting a placed order as failed invites a duplicate).
     */
    auditWarning: z.string().optional(),
});
// ── DDB records for the PO lifecycle (server-internal; not API responses) ──
/**
 * A PO header row: `PO#<poNumber>` / `META`, listed via GSI1 (`PURCHASE_ORDERS` /
 * `STATUS#<status>#<createdAt>`). `poNumber` IS the id (minted `FSP-<seq>` from an atomic counter), so a
 * 3c receipt ties back with a single point-read — no extra GSI. Cost totals are denormalized so the list
 * needs only the META rows.
 */
export const PurchaseOrderRecordSchema = z.object({
    PK: z.string(),
    SK: z.literal('META'),
    GSI1PK: z.literal('PURCHASE_ORDERS'),
    GSI1SK: z.string(),
    entity: z.literal('purchaseOrder'),
    poNumber: z.string(),
    seq: z.number().int().positive(),
    status: PurchaseOrderStatusEnum,
    totalUnits: z.number().int().nonnegative(),
    lineCount: z.number().int().nonnegative(),
    estimatedTotal: z.number().nonnegative(),
    note: z.string().optional(),
    createdAt: z.string(),
    createdBy: z.string().optional(),
    orderedAt: z.string().optional(),
    orderedBy: z.string().optional(),
    closedAt: z.string().optional(),
    closedBy: z.string().optional(),
    canceledAt: z.string().optional(),
    canceledBy: z.string().optional(),
    cancelReason: z.string().optional(),
    // Defaults `reorder` so a pre-3d PO row (no `source` attribute) parses cleanly as an auto-draft PO.
    source: PurchaseOrderSourceEnum.default('reorder'),
    // S&S auto-send Phase A (TEST): last test-send provenance (see PurchaseOrderSummarySchema). Audit only.
    ssTestOrderNumbers: z.array(z.string()).optional(),
    ssTestSubmittedAt: z.string().optional(),
    ssTestSubmittedBy: z.string().optional(),
    // S&S auto-send Phase B (LIVE): outbound-idempotency marker + real order provenance. `ssOrderAttemptStatus`
    // is stamped BEFORE the S&S POST (in_flight) and resolved after (placed → also stamps orderedAt/By + moves
    // to ORDERED; unknown → held for human reconciliation; a 4xx rejection REMOVES it). `ssOrderNumbers` holds
    // the confirmed real S&S order number(s). `ssOrderAttemptedAt/By` record who started the (maybe-billable) send.
    ssOrderNumbers: z.array(z.string()).optional(),
    ssOrderAttemptStatus: PurchaseOrderLiveOrderAttemptStatusEnum.optional(),
    ssOrderAttemptedAt: z.string().optional(),
    ssOrderAttemptedBy: z.string().optional(),
    params: PurchaseOrderParamsSchema,
});
/**
 * A PO line row: `PO#<poNumber>` / `LINE#<ssSku>`. One per distinct blank on the order. `receivedQty`
 * (Phase 3c) is the units received against this line so far via receive-against-PO; the on-order netting
 * uses `max(0, orderQty − receivedQty)` so a received PO stops muting reorder automatically. Defaults to
 * 0 so a pre-3c row parses cleanly.
 */
export const PurchaseOrderLineRecordSchema = z.object({
    PK: z.string(),
    SK: z.string(),
    entity: z.literal('purchaseOrderLine'),
    poNumber: z.string(),
    ssSku: z.string(),
    brandName: z.string().optional(),
    styleName: z.string().optional(),
    colorName: z.string().optional(),
    sizeName: z.string().optional(),
    orderQty: z.number().int().positive(),
    receivedQty: z.number().int().nonnegative().default(0),
    /**
     * Units retired from on-order via a shortage SHORT-CLOSE (P2 "made good elsewhere"): a claim on this
     * line was settled (substituted / credited / written-off) so those units will never arrive on THIS PO.
     * On-order nets `max(0, orderQty − receivedQty − writtenOffQty)` — the phantom remainder drops WITHOUT
     * faking `receivedQty` (audit stays truthful). Defaults 0 so a pre-P2 row parses cleanly.
     */
    writtenOffQty: z.number().int().nonnegative().default(0),
    unitCost: z.number().nonnegative().nullable(),
    lineTotal: z.number().nonnegative().nullable(),
    costStatus: PurchaseDraftCostStatusEnum,
});
/** Idempotency marker: `PO#TOKEN#<clientToken>` / `MARKER` → the poNumber a create minted. */
export const PurchaseOrderTokenRecordSchema = z.object({
    PK: z.string(),
    SK: z.literal('MARKER'),
    entity: z.literal('purchaseOrderToken'),
    poNumber: z.string(),
    createdAt: z.string(),
});
/**
 * Receive-against-PO idempotency marker (Phase 3c): `PO#<poNumber>` / `RECVAPPLIED#<invoice>#<lineNo>`.
 * One per receipt line already applied to this PO — makes applying a receipt idempotent + torn-write-safe
 * (independent of the blank-ledger RECEIVE dedup). Carries the exact `qty`/`ssSku` applied so a later
 * reverse subtracts precisely (even if S&S revised the shipped qty in between).
 */
export const PurchaseOrderReceiptRecordSchema = z.object({
    PK: z.string(),
    SK: z.string(),
    entity: z.literal('purchaseOrderReceipt'),
    poNumber: z.string(),
    invoice: z.string(),
    lineNumber: z.number().int(),
    ssSku: z.string(),
    /**
     * Units this marker applied to the PO line's `receivedQty`. SIGNED: the original apply is positive; a
     * receive CORRECTION marker (`RECVAPPLIED#<invoice>#<line>#<token>`) may be negative (correct-down).
     * An unapply subtracts the sum of a line's markers, so on-hand and the PO stay in lockstep.
     */
    qty: z.number().int(),
    appliedAt: z.string(),
    /** Set on a per-box apply marker (`RECVAPPLIED#<invoice>#<line>#box<N>`): the S&S box it came from. */
    box: z.number().int().positive().optional(),
});
/**
 * Shortage SHORT-CLOSE marker (P2): `PO#<poNumber>` / `SHORTWRITEOFF#<invoice>#<lineNo>`. One per settled
 * shortage line — makes the PO write-off idempotent + torn-write-safe, and carries `qty` so the un-settle
 * subtracts EXACTLY what was retired. `resolveToken` links it to the blank-side resolve marker: the
 * reverse deletes it conditional on that token so a stale un-settle can't erase a newer re-settle.
 */
export const PurchaseOrderShortCloseRecordSchema = z.object({
    PK: z.string(),
    SK: z.string(),
    entity: z.literal('purchaseOrderShortClose'),
    poNumber: z.string(),
    invoice: z.string(),
    lineNumber: z.number().int(),
    ssSku: z.string(),
    /** Units retired from on-order for this line (added to `writtenOffQty`). Always positive. */
    qty: z.number().int().positive(),
    /** The blank-side resolve marker's token — the reverse is conditional on it (scope-keyed undo). */
    resolveToken: z.string(),
    writtenOffAt: z.string(),
});
// ── Blank inventory — combined overview (roadmap Phase 1a: the merged "Short & reorder" view) ──
/**
 * One blank's combined "short & reorder" row — the UNION of the demand view and the reorder view on a
 * SINGLE row, so the two independent "short" numbers sit side by side instead of on two separate pages:
 *  - `demand` / `shortage` — open-queue pull-need vs on-hand (`shortage = demand − onHand`, signed).
 *  - `manualPar` / `suggestedRop` / `effectiveThreshold` / `belowThreshold` / `suggestedOrderQty` /
 *    `confidence` — the par/ROP replenishment view (see BlankReorderRow). The two frequently disagree.
 * `onHand` is the derived running sum (may be negative). Identity mirrors the ledger; a blank demanded
 * but never stocked has no ledger identity, so `sampleTitle`/`sampleVariantTitle` (Shopify) keep it
 * recognizable. The row universe is the UNION of {ledger}∪{par}∪{open-demand} SKUs, so a
 * demanded-but-never-stocked blank is never dropped (the old reorder view silently omitted it).
 */
export const InventoryOverviewRowSchema = z.object({
    ssSku: z.string(),
    brandName: z.string().optional(),
    styleName: z.string().optional(),
    colorName: z.string().optional(),
    sizeName: z.string().optional(),
    onHand: z.number().int(),
    lastCountedAt: z.string().optional(),
    demand: z.number().int().nonnegative(),
    shortage: z.number().int(),
    manualPar: z.number().int().nonnegative().nullable(),
    suggestedRop: z.number().int().nonnegative().nullable(),
    confidence: ReorderConfidenceEnum,
    effectiveThreshold: z.number().int().nonnegative().nullable(),
    belowThreshold: z.boolean(),
    suggestedOrderQty: z.number().int().nonnegative(),
    // Units already on an OPEN purchase order (QUEUED or ORDERED). The reorder signal
    // (`belowThreshold`/`suggestedOrderQty`) nets against `onHand + onOrder`; the demand `shortage` above
    // does NOT (on-order stock can't press today's queue). 0 when no open PO covers this blank.
    onOrder: z.number().int().nonnegative(),
    // The shelf-buffer half of `effectiveThreshold` (the other half is `demand` above): `manualPar` when
    // set, else ceil(safetyStock). See BlankReorderRowSchema — par is a FLOOR, not a ceiling.
    bufferTarget: z.number().int().nonnegative().optional(),
    sampleTitle: z.string().optional(),
    sampleVariantTitle: z.string().optional(),
    // Archived blanks are normally dropped from this view; one only appears here when it STILL has open
    // demand (so a fulfillment signal is never lost) — flagged so the row can be badged "archived".
    archived: z.boolean(),
    // Display-only S&S colorway images (front photo + color swatch), resolved at READ time from S&S —
    // never persisted. Always absolute (CDN-prefixed) URLs; absent when the lookup misses its deadline,
    // S&S is down, or the colorway has no image.
    imageUrl: z.string().url().optional(),
    swatchUrl: z.string().url().optional(),
});
/** At-a-glance counts for the inventory hub KPI strip — all derived from the same single walk. */
export const InventoryOverviewKpisSchema = z.object({
    tracked: z.number().int().nonnegative(), // blanks with a ledger history
    short: z.number().int().nonnegative(), // rows with shortage > 0 (open demand exceeds on-hand)
    belowPar: z.number().int().nonnegative(), // rows below their effective threshold (par/ROP)
    uncounted: z.number().int().nonnegative(), // ledger blanks never anchored to a physical count
    negative: z.number().int().nonnegative(), // ledger blanks with on-hand < 0
});
/**
 * `GET /inventory/overview` — the merged "Short & reorder" view: one reconciled row per blank across
 * the UNION of the ledger/par/open-demand universes, plus KPI counts + the (constant, owner-set)
 * reorder-model params. Pure DDB (no S&S cost calls) so it stays fast + open-read; the dollar reorder
 * ESTIMATE lives on the draft-PO view. Rows are sorted worst-first (needs-action, then shortfall, SKU).
 */
export const InventoryOverviewResponseSchema = z.object({
    rows: z.array(InventoryOverviewRowSchema),
    kpis: InventoryOverviewKpisSchema,
    params: z.object({
        leadTimeDays: z.number().int().positive(),
        windowDays: z.number().int().positive(),
        serviceZ: z.number().positive(),
        serviceLabel: z.string(),
    }),
});
// ── Inventory inbound (S&S in-transit shipments board — inbound-visibility Phase 1) ──
/**
 * One S&S order line that's on its way. `ssSku` is the S&S sku, which equals the `BLANK#<sku>` ledger
 * key, so an inbound line maps straight onto a blank. `shortShipQty`/`shortShipped` surface an S&S
 * backorder / short-ship (qtyShipped < qtyOrdered) — a coverage gap the PO-derived reorder view can't see.
 */
export const InventoryInboundLineSchema = z.object({
    ssSku: z.string(),
    brandName: z.string(),
    styleName: z.string(),
    colorName: z.string(),
    sizeName: z.string(),
    qtyOrdered: z.number().int().nonnegative(),
    qtyShipped: z.number().int().nonnegative(),
    shortShipQty: z.number().int().nonnegative(),
    shortShipped: z.boolean(),
});
/**
 * One inbound S&S shipment — a single invoice row (one order can split across warehouses into several).
 * A READ-ONLY reflection of the S&S account order feed, NOT tied to an FSP purchase order (the shop
 * orders on ssactivewear.com, so `poNumber` is usually absent). `expectedDeliveryDate` is S&S's OWN ETA;
 * `trackingUrl` is a carrier deep-link (null for an unknown carrier or before a tracking number exists).
 * Date strings are echoed verbatim from S&S (`YYYY-MM-DDTHH:mm:ss`, no timezone) — the client formats them.
 */
export const InventoryInboundShipmentSchema = z.object({
    orderNumber: z.string(),
    invoiceNumber: z.string().nullable(),
    poNumber: z.string().nullable(),
    orderType: z.string().nullable(),
    orderDate: z.string().nullable(),
    shipDate: z.string().nullable(),
    expectedDeliveryDate: z.string().nullable(),
    deliveryStatus: z.string().nullable(),
    orderStatus: z.string().nullable(),
    trackingNumber: z.string().nullable(),
    trackingUrl: z.string().nullable(),
    shippingCarrier: z.string().nullable(),
    warehouseAbbr: z.string().nullable(),
    totalPieces: z.number().int().nonnegative(),
    lineCount: z.number().int().nonnegative(),
    lines: z.array(InventoryInboundLineSchema),
    /**
     * True when ≥1 line's SKU has blank-ledger history — i.e. a blank FSP actually stocks, so this box
     * needs receiving. False = an S&S order for goods FSP doesn't track (owner's personal/other orders);
     * it renders in the neutral "Not tracked" bucket and never drives the receiving nag. When the ledger
     * read is unavailable the server defaults TRUE (fail-visible, never silently "not tracked").
     */
    forFspStock: z.boolean(),
    /**
     * Derived from `deliveryStatus` (see ShipmentDeliveryStateSchema). A `partially_delivered` shipment
     * sits in the receiving to-do (some boxes are here) but must be labelled as such — not "Delivered".
     */
    deliveryState: ShipmentDeliveryStateSchema.optional(),
    /** S&S's box count for the invoice, when the feed carries it. */
    totalBoxes: z.number().int().nonnegative().optional(),
    /**
     * Boxes already received box by box (live box receipts). Present only when > 0 and the invoice is not
     * yet complete — the shipment then stays in `awaitingReceipt` as "N of M boxes received".
     */
    boxesReceived: z.number().int().positive().optional(),
    /** Set only on entries in the `dismissed` list — when the operator dismissed this shipment. */
    dismissedAt: z.string().optional(),
});
/** At-a-glance counts for the Inbound board header. */
export const InventoryInboundKpisSchema = z.object({
    shipmentCount: z.number().int().nonnegative(), // in-transit shipments (dismissed excluded)
    totalPieces: z.number().int().nonnegative(), // pieces on the way
    blankCount: z.number().int().nonnegative(), // distinct blanks in transit
    shortShipCount: z.number().int().nonnegative(), // shipments with a short-shipped line
    /**
     * Delivered-but-unreceived shipments that are FOR FSP STOCK and not dismissed — THE receiving to-do
     * and the hub's red badge. Not-tracked and dismissed shipments never count here (P1: the nag is
     * truthful).
     */
    awaitingReceiptCount: z.number().int().nonnegative(),
    awaitingReceiptPieces: z.number().int().nonnegative(), // pieces on those FSP-stock unreceived boxes
    /** Delivered-but-unreceived shipments NOT tracked by FSP (neutral bucket; informational only). */
    awaitingNotTrackedCount: z.number().int().nonnegative(),
});
/**
 * `GET /inventory/inbound` — blanks on their way from S&S: every in-transit shipment on the account
 * (shipped, not yet delivered), soonest-ETA first, mapped to blanks. Sourced LIVE from the S&S order
 * feed, NOT from FSP purchase orders, so it reflects what's physically on the way regardless of how it
 * was ordered. `degraded:true` means the S&S feed (or the received-invoice lookup) was briefly
 * unreachable and the lists are empty/partial (a transient issue — NOT "nothing is coming"). Read-only:
 * no ledger write, no order placement.
 *
 * `awaitingReceipt` is the receiving to-do: shipments the carrier has DELIVERED whose S&S invoice has no
 * RECEIVE movement in the blank ledger yet — i.e. the box arrived but nobody has scanned it in, so its
 * blanks aren't in on-hand. Oldest-delivered first (the stalest box needs attention most). Delivery does
 * nothing automatically; a shipment only leaves this list once it's scanned at Receiving.
 */
export const InventoryInboundResponseSchema = z.object({
    shipments: z.array(InventoryInboundShipmentSchema),
    /** Delivered + unreceived + FOR FSP STOCK + not dismissed — the receiving to-do (amber, badge). */
    awaitingReceipt: z.array(InventoryInboundShipmentSchema),
    /** Delivered + unreceived but NOT tracked by FSP — neutral, collapsed, no obligation framing. */
    awaitingNotTracked: z.array(InventoryInboundShipmentSchema),
    /**
     * Shipments in the current window the operator DISMISSED (each carries `dismissedAt`) — filtered out
     * of every list and KPI above, restorable via POST /inventory/inbound/undismiss. Markers for invoices
     * that have left the window simply stop rendering (harmless; a re-scan still receives normally).
     */
    dismissed: z.array(InventoryInboundShipmentSchema),
    kpis: InventoryInboundKpisSchema,
    windowStart: z.string(), // the InvoiceStartDate used (YYYY-MM-DD)
    windowEnd: z.string(), // the InvoiceEndDate used (YYYY-MM-DD)
    degraded: z.boolean(),
});
/**
 * Operator dismissal of one inbound shipment (P1 inbound relevance): a DISPLAY-ONLY marker in its own
 * partition — never a ledger movement, never touches on-hand/netting. Dismiss = "this box isn't FSP's
 * problem" (someone else's order, already handled outside the system). Idempotent per invoice;
 * reversible via undismiss; the board and its KPIs filter dismissed invoices server-side so the hub
 * badge self-corrects.
 */
export const InboundDismissRecordSchema = z.object({
    PK: z.string(), // INBOUNDDISMISS#<invoiceNumber>
    SK: z.literal('DISMISS'),
    GSI1PK: z.literal('INBOUND_DISMISSES'),
    GSI1SK: z.string(), // <dismissedAt>#<invoiceNumber>
    entity: z.literal('inboundDismiss'),
    invoiceNumber: z.string(),
    dismissedAt: z.string(),
    /** Who dismissed it (Cognito email/sub or api-key consumer). Never a secret. */
    dismissedBy: z.string().optional(),
});
/** `POST /inventory/inbound/dismiss` and `/undismiss` body — the S&S invoice to (un)dismiss. */
export const InventoryInboundDismissRequestSchema = z.object({
    invoiceNumber: z.string().min(1).max(32),
});
/** `POST /inventory/inbound/dismiss` result. `newlyDismissed` false = idempotent replay. */
export const InventoryInboundDismissResultSchema = z.object({
    invoiceNumber: z.string(),
    dismissed: z.boolean(),
    newlyDismissed: z.boolean(),
});
/** `POST /inventory/inbound/undismiss` result. `restored` false = no marker existed (already visible). */
export const InventoryInboundUndismissResultSchema = z.object({
    invoiceNumber: z.string(),
    restored: z.boolean(),
});
// ── Blank inventory — per-blank cross-links (roadmap Phase 1b: the drawer "Jump to" section) ──
/** One open order demanding a blank — deep-links the drawer to the order job card. */
export const BlankUsageOrderSchema = z.object({
    storeId: z.string(),
    orderId: z.string(),
    orderName: z.string(),
    qty: z.number().int().nonnegative(),
});
/** One catalog product whose variants map to a blank (informational — no per-product route yet). */
export const BlankUsageProductSchema = z.object({
    storeId: z.string(),
    productId: z.string(),
    title: z.string(),
});
/**
 * `GET /inventory/:ssSku/usage` — read-time cross-links for the per-blank drawer: which OPEN orders
 * demand this blank (SAME window as the demand view — MAPPED, UNFULFILLED, OPEN|BATCHED — so the count
 * agrees with Short & reorder) and which catalog products map to it. A hint (scans order lines +
 * catalog on demand), NOT a reservation. `demandPcs` = Σ the orders' `qty`.
 */
export const BlankUsageSchema = z.object({
    demandPcs: z.number().int().nonnegative(),
    orders: z.array(BlankUsageOrderSchema),
    products: z.array(BlankUsageProductSchema),
});
/**
 * Optional client-generated idempotency key (a UUID) for a manual inventory write. When present, the
 * server keys the movement row on it (deterministic SK) so a dropped-response retry or double-submit is
 * applied AT MOST ONCE — a duplicate replays the same on-hand instead of double-counting the running sum.
 * Omitted by internal (api-key) callers, who keep the append-always behavior. See blank-inventory-store.
 */
const clientTokenField = { clientToken: z.string().uuid().optional() };
/** `POST /inventory/:ssSku/adjust` body — a manual correction (non-zero, reason required). */
export const BlankAdjustRequestSchema = z.object({
    qtyDelta: z.number().int(),
    reason: z.string().min(1).max(200),
    ...clientTokenField,
});
/** `POST /inventory/:ssSku/count` legacy body — anchor on-hand to the figure typed (`basis: 'book'`). Kept
 *  byte for byte: the `/count` page that shipped before the pull station posts exactly this. */
export const BlankCountBookRequestSchema = z.object({
    countedQty: z.number().int().nonnegative(),
    reason: z.string().max(200).optional(),
    ...clientTokenField,
});
/** A sheet the count row DISPLAYED as pulled when it loaded: its id, the record revision and the `out`
 *  shown. The server compares each against the live record (moved → refused, pressed → reported). */
export const BlankPulledSeenSchema = z.object({
    batchId: z.string().min(1).max(80),
    rev: z.number().int().nonnegative(),
    qty: z.number().int().nonnegative(),
});
/**
 * `POST /inventory/:ssSku/count` shelf body (pulled-blanks v2) — the counter typed what is ON THE SHELF; the
 * server adds the stacks the pull station recorded as pulled and not yet drawn, and anchors the book to the
 * sum. `asOf` is the server time the sheet loaded with; `pulledSeen` is what the row displayed. Neither
 * enters the idempotency key — `clientToken` alone does, as on the legacy body. `pulledExclude` /
 * `confirmPulled` are the per-sheet answers to a `pulled_rose` refusal.
 */
export const BlankCountShelfRequestSchema = z.object({
    basis: z.literal('shelf'),
    shelfQty: z.number().int().nonnegative(),
    asOf: z.string().datetime(),
    pulledSeen: z.array(BlankPulledSeenSchema).max(50).optional(),
    pulledExclude: z.array(z.string().min(1).max(80)).max(50).optional(),
    confirmPulled: z
        .array(z.object({ batchId: z.string().min(1).max(80), qty: z.number().int().nonnegative() }))
        .max(50)
        .optional(),
    reason: z.string().max(200).optional(),
    ...clientTokenField,
});
/** `POST /inventory/:ssSku/count` body — the shelf body or the legacy book body. The route picks the branch
 *  on the presence of `basis` BEFORE parsing, so a malformed shelf body can never fall through to a book
 *  count (a union parse would). */
export const BlankCountRequestSchema = z.union([
    BlankCountShelfRequestSchema,
    BlankCountBookRequestSchema,
]);
/**
 * `POST /inventory/:ssSku/consume` body — a manual draw-down for a walk-in / phone / sample pull.
 * `quantity` is the positive number of garments pulled (the route writes a CONSUME with qtyDelta = −quantity);
 * `reason` is optional because a routine pull is self-explanatory, unlike an ADJUST correction.
 */
export const BlankConsumeRequestSchema = z.object({
    quantity: z.number().int().positive(),
    reason: z.string().max(200).optional(),
    ...clientTokenField,
});
/**
 * `POST /inventory/:ssSku/add-stock` body — introduce/restock a blank without an S&S receiving. The
 * server resolves the SKU's identity (brand/style/color/size) from S&S, then applies `quantity` per
 * `mode`:
 *  - `add` — ADD `quantity` to current on-hand (a relative, race-safe increment; records an ADJUST).
 *    Use for a restock or a blank bought LOCALLY (source doesn't matter to on-hand). `note` defaults
 *    to "added stock".
 *  - `set` — SET on-hand to `quantity` (records an opening COUNT anchored to it; corrects/re-anchors
 *    without doubling). Use for a physical count. `note` defaults to "opening stock".
 * `mode` is optional and defaults to `set` (backward-compatible with the original anchor behavior).
 */
export const BlankAddStockRequestSchema = z.object({
    /** Positive on every path but one: a shelf-basis `set` may be 0 — nothing left on the shelf, the book
     *  becomes 0 + recorded pulled. The route refuses 0 everywhere else (schema-level `.positive()` would
     *  have refused the one legitimate zero). */
    quantity: z.number().int().nonnegative(),
    mode: z.enum(['add', 'set']).optional(),
    /** `mode: 'set'` only (pulled-blanks v2): Restock · Set typed what is ON THE SHELF — anchor to
     *  `quantity + recorded pulled` through the shelf-count branch, with the same refusals. Ignored on `add`. */
    basis: z.literal('shelf').optional(),
    asOf: z.string().datetime().optional(),
    pulledSeen: z.array(BlankPulledSeenSchema).max(50).optional(),
    pulledExclude: z.array(z.string().min(1).max(80)).max(50).optional(),
    /** The other `pulled_rose` answer ("no, it was at the press") — both answers the refusal offers must
     *  be sendable from every door that can meet it. */
    confirmPulled: z
        .array(z.object({ batchId: z.string().min(1).max(80), qty: z.number().int().nonnegative() }))
        .max(50)
        .optional(),
    note: z.string().max(200).optional(),
    ...clientTokenField,
});
// ── Printed-transfer inventory — movement ledger (film stock; printed-transfer-inventory plan Phase 0) ──
//
// A physical, already-printed DTF transfer (film) sitting ready to press. Keyed by (designId, band):
// that pair fully determines the film — both the art PNG and the print geometry (widthIn/heightIn =
// file.px / 300). DTF-only (screen-print has no stable design identity). This is a MATERIALS ledger, NOT
// pricing — no floors, no pricing-engine; the "money-path" caution here is about double-counting physical
// film, not dollars. Mirrors the blank-inventory ledger: append-only, on-hand = Σ qtyDelta (never a
// stored counter), physical COUNT owns on-hand, manual ADJUST for scrap/damage/found.
// COUNT/ADJUST are manual (Phase 0). PULL is a keyed, reversible decrement written when a line's
// transfers are pulled off the shelf instead of reprinted (Phase 2) — mirrors the blank ledger's CONSUME.
export const TransferMovementTypeEnum = z.enum(['COUNT', 'ADJUST', 'PULL']);
/**
 * DDB record: `TRANSFER#<designId>#<band>` / `<movement SK>`, GSI1 `TRANSFER_MOVES` /
 * `<designId>#<band>#<createdAt>#<id>` (lists the whole ledger without a scan).
 *
 * Append-only. **On-hand is ALWAYS the running sum of `qtyDelta` over a (designId, band)'s rows —
 * never a stored counter** (a bare counter can't be audited or rebuilt). Phase 0 has only manual
 * movements, so the SK is `MOVE#<createdAt>#<id>` (unique, always appended) or `MOVE#<clientToken>`
 * (deterministic → a retry dedupes under a conditional put). `designName`/`widthIn`/`heightIn` are
 * denormalized from the resolved design at write time so the list renders without the design store.
 */
export const TransferMovementRecordSchema = z.object({
    PK: z.string(),
    SK: z.string(),
    GSI1PK: z.literal('TRANSFER_MOVES'),
    GSI1SK: z.string(),
    entity: z.literal('transferMovement'),
    designId: z.string(),
    sizeBand: SizeBandEnum,
    type: TransferMovementTypeEnum,
    /** Signed change to on-hand: ADJUST ±, COUNT = counted − prior on-hand. */
    qtyDelta: z.number().int(),
    /** For COUNT: the absolute physical count entered (qtyDelta was derived from it). */
    countedQty: z.number().int().nonnegative().optional(),
    reason: z.string().optional(),
    /** Who recorded it (Cognito email/sub or api-key consumer). Never a secret. */
    actor: z.string().optional(),
    // Design identity + print geometry, denormalized from the design record at write time.
    designName: z.string().optional(),
    widthIn: z.number().positive().optional(),
    heightIn: z.number().positive().optional(),
    /** For a PULL: the order line the transfer was pulled for (so the pull can be reversed on un-pull). */
    orderRef: BlankMovementOrderRefSchema.optional(),
    /** For a placement-grain build PULL: the print location pulled (part of the SK, so a line can pull
     *  one placement while another reprints). Absent on the legacy whole-line PULL + COUNT/ADJUST. */
    location: z.string().optional(),
    /** For a build-time PULL: the batch it was pulled into — AUDIT ONLY, deliberately NOT in the SK, so a
     *  rebuild after a leaked reversal self-heals via the idempotent conditional put (one outstanding pull
     *  per line+location) rather than minting a second decrement. */
    batchId: z.string().optional(),
    createdAt: z.string(),
    id: z.string(),
});
/** API view: one printed transfer's current (derived) on-hand + identity. */
export const TransferOnHandSchema = z.object({
    designId: z.string(),
    sizeBand: SizeBandEnum,
    onHand: z.number().int(),
    designName: z.string().optional(),
    widthIn: z.number().positive().optional(),
    heightIn: z.number().positive().optional(),
    lastMovementAt: z.string().optional(),
    lastCountedAt: z.string().optional(),
    lastCountedQty: z.number().int().optional(),
});
/** API view: one ledger movement (drops DDB keys). */
export const TransferMovementSchema = z.object({
    id: z.string(),
    designId: z.string(),
    sizeBand: SizeBandEnum,
    type: TransferMovementTypeEnum,
    qtyDelta: z.number().int(),
    countedQty: z.number().int().nonnegative().optional(),
    reason: z.string().optional(),
    actor: z.string().optional(),
    /** The order line a PULL served — the investigation backlink (production-records R0). Absent on
     *  COUNT/ADJUST. The ledger rows always carried this; the view used to drop it. */
    orderRef: BlankMovementOrderRefSchema.optional(),
    /** Placement grain (build-time pulls): the sheet + location the shelf transfer replaced. */
    batchId: z.string().optional(),
    location: z.string().optional(),
    createdAt: z.string(),
});
/** `GET /print-inventory` — on-hand across every printed transfer with a ledger history. */
export const TransferInventoryListSchema = z.object({
    items: z.array(TransferOnHandSchema),
});
/** `GET /print-inventory/:designId/:band` — on-hand + the full movement history, newest first. */
export const TransferInventoryDetailSchema = z.object({
    onHand: TransferOnHandSchema,
    movements: z.array(TransferMovementSchema),
});
/** `POST /print-inventory/:designId/:band/count` body — anchor on-hand to a physical count. */
export const TransferCountRequestSchema = z.object({
    countedQty: z.number().int().nonnegative(),
    reason: z.string().max(200).optional(),
    ...clientTokenField,
});
/** `POST /print-inventory/:designId/:band/adjust` body — a signed, non-zero correction (scrap/damage/found). */
export const TransferAdjustRequestSchema = z.object({
    qtyDelta: z.number().int(),
    reason: z.string().max(200).optional(),
    ...clientTokenField,
});
// ── Printed-transfer match (Phase 1 advisory: which of a build selection is already on the shelf) ──
/** One selected order line to project onto the printed transfers it would need. */
export const TransferMatchRefSchema = z.object({
    storeId: z.string(),
    orderId: z.string(),
    lineId: z.string(),
});
/** `POST /print-inventory/match` body — the order lines currently selected to build. */
export const TransferMatchRequestSchema = z.object({
    lineRefs: z.array(TransferMatchRefSchema).min(1).max(500),
    /**
     * Locations the operator has chosen to SCREEN-PRINT for this build (the same per-build choice as
     * `BatchCreateRequest.screenPrintLocations`). A screen-printed placement is produced off the DTF
     * sheet and needs no printed transfer, so the advisory must NOT count it against shelf stock.
     * Defaults to `[]` (count every placement). Typed as a plain slug array — not `PrintLocationSchema`
     * — only because that schema is declared later in this file; values are compared verbatim to a
     * placement's `location` and this route is advisory/read-only, so a stray value simply matches nothing.
     */
    screenPrintLocations: z.array(z.string().min(1).max(40)).max(12).optional().default([]),
});
/** One design+band the selection needs that ALSO has printed transfers on the shelf. */
export const TransferMatchRowSchema = z.object({
    designId: z.string(),
    sizeBand: SizeBandEnum,
    designName: z.string().optional(),
    widthIn: z.number().positive().optional(),
    heightIn: z.number().positive().optional(),
    /** Films this selection would print for this (designId, band) — Σ unfulfilled qty over matching placements. */
    needed: z.number().int().nonnegative(),
    /** On-hand printed transfers for it (derived running sum). */
    onHand: z.number().int(),
    /** How many of `needed` could be pulled from the shelf instead of printed = min(needed, max(0, onHand)). */
    coverable: z.number().int().nonnegative(),
});
/**
 * `POST /print-inventory/match` — ADVISORY ONLY (Phase 1). For a build selection, the designs already
 * on the shelf. It changes NOTHING — no subtraction from the gang sheet, no ledger write, no line state.
 * Staff read it and decide (a wrong auto-exclude would ship an order short — that risk is why Phase 1
 * only informs). Fallback-safe: an inventory read failure returns no rows rather than blocking the build.
 */
export const TransferMatchResponseSchema = z.object({
    rows: z.array(TransferMatchRowSchema),
    /** Σ coverable — total films that could be pulled from the shelf instead of printed. */
    totalCoverable: z.number().int().nonnegative(),
    /** Distinct (designId, band) in the selection that have shelf stock. */
    designsWithStock: z.number().int().nonnegative(),
});
// ── Screens preview (/orders build panel) — what a selection will BURN, before it is built ─────────
/**
 * `POST /batches/screen-preview` body — the order lines currently selected to build.
 *
 * Sibling of {@link TransferMatchRequestSchema} and deliberately the same shape, because it answers
 * the mirror-image question over the same selection: that one asks which placements need FILM, this
 * one asks which need a SCREEN.
 */
export const ScreenPreviewRequestSchema = z.object({
    lineRefs: z.array(TransferMatchRefSchema).min(1).max(500),
    /**
     * Locations the operator has chosen to SCREEN-PRINT for this build — the same per-build choice as
     * `BatchCreateRequest.screenPrintLocations`, and the reason this cannot be a value stamped on the
     * order line at ingest: the effective screen set is the mapping's durable `method` UNION this, and
     * this half is a checkbox the operator ticks in the Build dialog. Defaults to `[]`.
     */
    screenPrintLocations: z.array(z.string().min(1).max(40)).max(12).optional().default([]),
});
/**
 * ONE SCREEN the selection would burn: one design, at one location, at one art size.
 *
 * ⚠️ The key is `(designId, location, sizeBand)` — the same key `runSetups` folds a BUILT run on, so
 * the number promised before the build equals the number `/screen-print` reports after it. Owner
 * decision 2026-09-11: youth art is a separate burn (the printed-transfer shelf already keys
 * `(designId, band)` for the same reason), and the error is asymmetric — an extra row costs a glance,
 * a missing one stalls the run mid-press with a screen nobody burned.
 */
export const ScreenPreviewRowSchema = z.object({
    designId: z.string(),
    designName: z.string().optional(),
    location: z.string(),
    sizeBand: SizeBandEnum,
    /** Garments through THIS screen — Σ unfulfilled qty over the selected lines that share it. */
    garments: z.number().int().nonnegative(),
});
/**
 * `POST /batches/screen-preview` — ADVISORY ONLY. What the current selection would burn, so the
 * `/orders` build panel can size a filmless run in the units it actually has.
 *
 * It changes NOTHING: no batch, no ledger write, no line-state change. Fallback-safe in the same
 * direction as the transfer advisory — a catalog or order read failure yields FEWER rows, never a
 * thrown build blocker.
 *
 * ⚠️ `screens` is the COUNT OF DISTINCT SCREENS, which is NOT a per-line number that can be summed:
 * two sizes of one tee share one physical screen. `garments` is the per-LINE fold (one shirt however
 * many placements it carries), so the two answer different questions and neither derives the other.
 */
export const ScreenPreviewResponseSchema = z.object({
    rows: z.array(ScreenPreviewRowSchema),
    /** Distinct screens to burn = `rows.length`. */
    screens: z.number().int().nonnegative(),
    /** Garments the run will press — Σ unfulfilled qty folded per LINE, never per placement. */
    garments: z.number().int().nonnegative(),
    /** Selected lines that resolved no screen at all (unmapped, no product, nothing screen-printed). */
    linesWithoutScreens: z.number().int().nonnegative(),
});
// ── Stock items (inventory-clarity P2b: tracked shelf stock for Ship-only / FULFILLMENT_ONLY goods) ──
// An INTERNAL stock-item catalog the owner creates in the app (e.g. "Bumper decal 4in"), keyed
// `ITEM#<slug>`. Deliberately OUTSIDE the S&S blank namespace (`BLANK#…`): these items must never
// leak into S&S receiving, purchase drafts, or S&S cost resolution. Identity is the internal item,
// not the per-store Shopify listing — many listings across stores converge (P2b-B mapping) onto ONE
// shelf count. Restock is manual (Add stock / Count), never an S&S invoice. Same ledger doctrine as
// blanks/transfers: append-only movements, on-hand = Σ qtyDelta (never a stored counter).
/** Slugs are the immutable item key (derived from the name at create): kebab, 1–64 chars. */
export const STOCK_ITEM_SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;
export const StockItemSlugSchema = z.string().regex(STOCK_ITEM_SLUG_RE, {
    message: 'slug must be 1–64 chars of a-z, 0-9, and non-leading/trailing hyphens',
});
// COUNT anchors on-hand to a physical count; ADJUST is a signed correction (scrap/damage/found);
// ADD records a manual restock delivery. PACK is the reserved consume-at-pack decrement (P2b-C) —
// keyed on the order line and reversible, mirroring the transfer ledger's PULL precedent.
export const StockItemMovementTypeEnum = z.enum(['COUNT', 'ADJUST', 'ADD', 'PACK']);
/**
 * DDB record: the item's META row — `ITEM#<slug>` / `META`, mirrored onto GSI1 (`ITEMS` / `<slug>`)
 * so the catalog lists without a scan. Owner-created; the slug is immutable (it is the ledger key and
 * the P2b-B mapping target). `reorderLevel` lives HERE (items have a real META row, unlike blanks,
 * whose pars needed their own record). `archived` retires an item from pickers; its ledger survives.
 */
export const StockItemRecordSchema = z.object({
    PK: z.string(),
    SK: z.literal('META'),
    GSI1PK: z.literal('ITEMS'),
    GSI1SK: z.string(),
    entity: z.literal('stockItem'),
    slug: StockItemSlugSchema,
    name: z.string().min(1).max(120),
    /** Per-item reorder threshold (canon: "Reorder level"). Absent = no threshold set. */
    reorderLevel: z.number().int().nonnegative().optional(),
    archived: z.boolean().optional(),
    /**
     * Package weight/dims for the future ship station (P5) — schema-optional placeholders, no v1 UI.
     * One item, one weight: another reason identity is the internal item, not the per-store listing.
     */
    weightOz: z.number().positive().optional(),
    createdBy: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * DDB record: one append-only ledger movement — `ITEM#<slug>` / `MOVE#<clientToken|createdAt#id>`
 * (P2b-C's PACK rows use `PACK#<store>#<order>#<line>` so the consume is idempotent per line and
 * reversible by key). GSI1 `ITEM_MOVES` / `<slug>#<createdAt>#<id>` lists the whole ledger.
 */
export const StockItemMovementRecordSchema = z.object({
    PK: z.string(),
    SK: z.string(),
    GSI1PK: z.literal('ITEM_MOVES'),
    GSI1SK: z.string(),
    entity: z.literal('stockItemMovement'),
    slug: StockItemSlugSchema,
    type: StockItemMovementTypeEnum,
    /** Signed change to on-hand: ADJUST ±, ADD +, PACK −, COUNT = counted − prior on-hand. */
    qtyDelta: z.number().int(),
    /** For COUNT: the absolute physical count entered (qtyDelta was derived from it). */
    countedQty: z.number().int().nonnegative().optional(),
    reason: z.string().optional(),
    /** Who recorded it (Cognito email/sub or api-key consumer). Never a secret. */
    actor: z.string().optional(),
    /** For a PACK: the order line packed (the reversal key). P2b-C. */
    orderRef: BlankMovementOrderRefSchema.optional(),
    createdAt: z.string(),
    id: z.string(),
});
/** API view: one stock item + its current (derived) on-hand. */
export const StockItemOnHandSchema = z.object({
    slug: StockItemSlugSchema,
    name: z.string(),
    onHand: z.number().int(),
    reorderLevel: z.number().int().nonnegative().optional(),
    archived: z.boolean().optional(),
    /**
     * Open Ship-only demand mapped to this item (P2b-B): Σ unfulfilledQty over the open FULFILLONLY
     * lines whose variant maps here, resolved LIVE through `variantStockItems` at read time. Absent on
     * responses that don't compute it (the create/patch/movement echoes) — never a stored counter.
     */
    openDemand: z.number().int().nonnegative().optional(),
    lastMovementAt: z.string().optional(),
    lastCountedAt: z.string().optional(),
    lastCountedQty: z.number().int().optional(),
});
/** API view: one ledger movement (drops DDB keys). */
export const StockItemMovementSchema = z.object({
    id: z.string(),
    slug: StockItemSlugSchema,
    type: StockItemMovementTypeEnum,
    qtyDelta: z.number().int(),
    countedQty: z.number().int().nonnegative().optional(),
    reason: z.string().optional(),
    actor: z.string().optional(),
    /** The order line a PACK drew for — the investigation backlink (production-records R0). Absent on
     *  COUNT/ADJUST/ADD. The ledger rows always carried this; the view used to drop it. */
    orderRef: BlankMovementOrderRefSchema.optional(),
    createdAt: z.string(),
});
/** `GET /stock-items` — every stock item (incl. archived; the UI filters) + derived on-hand. */
export const StockItemsListSchema = z.object({
    items: z.array(StockItemOnHandSchema),
});
/** `GET /stock-items/:slug` — one item + its full movement history, newest first. */
export const StockItemDetailSchema = z.object({
    item: StockItemOnHandSchema,
    movements: z.array(StockItemMovementSchema),
});
/** `POST /stock-items` body — create an item. The server derives the immutable slug from the name. */
export const CreateStockItemRequestSchema = z.object({
    name: z.string().min(1).max(120),
    reorderLevel: z.number().int().nonnegative().optional(),
});
/** `PATCH /stock-items/:slug` body — merge semantics: `undefined` keeps, `null` clears, value sets. */
export const UpdateStockItemRequestSchema = z.object({
    reorderLevel: z.number().int().nonnegative().nullable().optional(),
    archived: z.boolean().optional(),
});
/** `POST /stock-items/:slug/count` body — anchor on-hand to a physical count. */
export const StockItemCountRequestSchema = z.object({
    countedQty: z.number().int().nonnegative(),
    reason: z.string().max(200).optional(),
    ...clientTokenField,
});
/** `POST /stock-items/:slug/adjust` body — a signed, non-zero correction (scrap/damage/found). */
export const StockItemAdjustRequestSchema = z.object({
    qtyDelta: z.number().int(),
    reason: z.string().max(200).optional(),
    ...clientTokenField,
});
/** `POST /stock-items/:slug/add` body — a manual restock (positive units put on the shelf). */
export const StockItemAddRequestSchema = z.object({
    qty: z.number().int().positive(),
    reason: z.string().max(200).optional(),
    ...clientTokenField,
});
// ── Garment stock (finished-garment-stock plan v1: pressed, ready-to-ship garments on the shelf) ──
// The FOURTH shelf ledger. A finished garment is its ART SET + its EXACT BLANK: identity is the
// canonical `stockKey` = placements sorted by location as `<location>=<designId>` joined by `+`, then
// `#<bareSsSku>` (the S&S sku is per-size, so the key pins style·colour·size exactly). Keyed by the
// PHYSICAL tuple — never by the Shopify variant — so a mapping edit makes old stock stop matching
// (fail-safe) instead of matching wrong art. Same ledger doctrine as blanks/transfers/stock-items:
// append-only movements, on-hand = Σ qtyDelta (never a stored counter), COUNT owns on-hand.
// ⚠️ This ledger NEVER touches the blank ledger: the shelf shirt's blank was drawn at its ORIGINAL
// press (or predates go-live). Press is the only blank→garment conversion.
// INTAKE puts a physical shirt on the shelf (scan a hem tag, or pick a catalog variant by hand);
// PICK is the keyed, reversible decrement written when an order line ships from the shelf (PR-4 —
// mirrors the blank ledger's CAPPRODUCE consume); ADJUST/COUNT are the manual corrections.
export const GarmentStockMovementTypeEnum = z.enum(['INTAKE', 'PICK', 'ADJUST', 'COUNT']);
/** Where an INTAKE's identity came from: a hem-tag scan or a manual catalog-variant pick. */
export const GarmentStockIntakeSourceEnum = z.enum(['scan', 'manual']);
/**
 * One print placement of the stocked garment. `location` is a plain slug (compared verbatim to a
 * mapping placement's `location`; `PrintLocationSchema` is declared later in this file, same
 * ordering constraint the transfer-match schema documents). `designUpdatedAt` freezes the design's
 * `artUpdatedAt` at intake — the art-drift sentinel: a design whose PNG was replaced since intake no
 * longer looks like this shelf stock, and the matcher demotes it to an advisory. ⚠️ Rows intaken
 * before 2026-09-20 froze the record's general `updatedAt` instead (the field name predates the
 * art-only timestamp); the matcher's compare is still correct for them — see `hasArtDrift`.
 */
export const GarmentStockPlacementSchema = z.object({
    location: z.string().min(1).max(40),
    designId: z.string().min(1).max(80),
    designName: z.string().optional(),
    designUpdatedAt: z.string().optional(),
});
/**
 * DDB record: `GARMENTSTOCK#<stockKey>` / `<movement SK>`, GSI1 `GARMENTSTOCK_MOVES` /
 * `<stockKey>#<createdAt>#<id>` (lists the whole ledger without a scan). Append-only; on-hand is
 * ALWAYS the running sum of `qtyDelta`. SKs: `MOVE#<clientToken>` (deterministic → a retry dedupes
 * under a conditional put) or `MOVE#<createdAt>#<id>` for INTAKE/ADJUST/COUNT; PR-4's PICK uses
 * `PICK#<store>#<order>#<line>` so one line picks at most once and the pick is reversible by key.
 * Identity (`placements`, `ssSku`) + the human blank quad + `apparelKind` are denormalized onto
 * EVERY row so the list renders without joining four other stores.
 */
export const GarmentStockMovementRecordSchema = z.object({
    PK: z.string(),
    SK: z.string(),
    GSI1PK: z.literal('GARMENTSTOCK_MOVES'),
    GSI1SK: z.string(),
    entity: z.literal('garmentStockMovement'),
    /** Canonical identity key (see the section comment). Grouping folds on this STORED value, never a
     *  re-derivation — a later canonicalization change must not re-shard history. */
    stockKey: z.string(),
    /** Bare S&S sku of the pressed blank (style·colour·size exact). No `BLANK#` prefix. */
    ssSku: z.string(),
    placements: z.array(GarmentStockPlacementSchema).min(1),
    type: GarmentStockMovementTypeEnum,
    /** Signed change to on-hand: INTAKE +, PICK −, ADJUST ±, COUNT = counted − prior on-hand. */
    qtyDelta: z.number().int(),
    /** For COUNT: the absolute physical count entered (qtyDelta was derived from it). */
    countedQty: z.number().int().nonnegative().optional(),
    reason: z.string().optional(),
    /** Who recorded it (Cognito email/sub or api-key consumer). Never a secret. */
    actor: z.string().optional(),
    /** For an INTAKE: which door supplied the identity. */
    source: GarmentStockIntakeSourceEnum.optional(),
    /** For a scan INTAKE: the raw scanned hem-tag code, verbatim — audit + the advisory duplicate
     *  check. ⚠️ NOT a dedup key: a re-batched line re-mints owedRanks, so the same code can name two
     *  different physical shirts across production runs. */
    code: z.string().optional(),
    /** For a PICK: the order line shipped from the shelf (the reversal key). PR-4. */
    orderRef: BlankMovementOrderRefSchema.optional(),
    // The blank's human identity, denormalized at write time (from the catalog variant or the blank
    // ledger's newest named row); absent when neither source knew it — the UI falls back to the sku.
    brandName: z.string().optional(),
    styleName: z.string().optional(),
    colorName: z.string().optional(),
    sizeName: z.string().optional(),
    /** tee vs hoodie, from the catalog product when known — the browse page's filter. */
    apparelKind: ApparelKindEnum.optional(),
    createdAt: z.string(),
    id: z.string(),
});
/**
 * Reverse index for the bin-label QR (`s1|<binId>` → stockKey): one row per stocked identity at
 * `GARMENTSTOCKBINID#<binId>`, written idempotently at intake and read-repaired on a scan miss
 * (bin-labels plan § 3.2). A pure index — losing one is harmless (the repair re-mints it from the
 * movement ledger), so writes are best-effort and NEVER gate an intake.
 */
export const GarmentStockBinPointerRecordSchema = z.object({
    PK: z.string(),
    SK: z.literal('CONFIG'),
    entity: z.literal('garmentStockBinPointer'),
    /** 12 lowercase hex — sha256(stockKey) sliced; the QR payload's id half. */
    binId: z.string(),
    stockKey: z.string(),
    createdAt: z.string(),
});
/** API view: one stocked garment identity's current (derived) on-hand. */
export const GarmentStockOnHandSchema = z.object({
    stockKey: z.string(),
    /** Deterministic short id for the bin label QR (sha256(stockKey) first 12 hex). Derived at read
     *  time server-side — never stored, never computed by a client. */
    binId: z.string().optional(),
    ssSku: z.string(),
    placements: z.array(GarmentStockPlacementSchema),
    onHand: z.number().int(),
    brandName: z.string().optional(),
    styleName: z.string().optional(),
    colorName: z.string().optional(),
    sizeName: z.string().optional(),
    apparelKind: ApparelKindEnum.optional(),
    lastMovementAt: z.string().optional(),
    lastCountedAt: z.string().optional(),
    lastCountedQty: z.number().int().optional(),
});
/** API view: one ledger movement (drops DDB keys). */
export const GarmentStockMovementSchema = z.object({
    id: z.string(),
    stockKey: z.string(),
    type: GarmentStockMovementTypeEnum,
    qtyDelta: z.number().int(),
    countedQty: z.number().int().nonnegative().optional(),
    reason: z.string().optional(),
    actor: z.string().optional(),
    source: GarmentStockIntakeSourceEnum.optional(),
    code: z.string().optional(),
    /** The order line a PICK shipped for — the investigation backlink. Absent on INTAKE/ADJUST/COUNT. */
    orderRef: BlankMovementOrderRefSchema.optional(),
    createdAt: z.string(),
});
/** `GET /garment-stock` — on-hand across every stocked garment identity with a ledger history. */
export const GarmentStockListSchema = z.object({
    items: z.array(GarmentStockOnHandSchema),
});
/** `GET /garment-stock/movements?key=` — one identity's on-hand + full history, newest first. */
export const GarmentStockDetailSchema = z.object({
    onHand: GarmentStockOnHandSchema,
    movements: z.array(GarmentStockMovementSchema),
});
/**
 * `POST /garment-stock/intake` body — put physical shirts on the shelf. Exactly ONE door:
 * `code` (a scanned hem-tag QR, resolved to the FROZEN batch-item identity server-side), or the
 * catalog-variant triple (resolved through the SAME mapping resolution order matching uses, so a
 * manual intake can never mint a key an order would never match). The route enforces the XOR with
 * legible copy — both doors present or neither is a 400.
 */
export const GarmentStockIntakeRequestSchema = z.object({
    code: z.string().min(1).max(256).optional(),
    storeId: z.string().min(1).optional(),
    productId: z.string().min(1).optional(),
    variantId: z.string().min(1).optional(),
    quantity: z.number().int().min(1).max(500),
    ...clientTokenField,
});
/** `POST /garment-stock/intake` result — the identity's on-hand after the write. */
export const GarmentStockIntakeResultSchema = z.object({
    onHand: GarmentStockOnHandSchema,
    /** True when this exact scanned code already appears on an earlier movement of this identity —
     *  ADVISORY (a re-batched line legitimately reuses codes): the UI says "this tag was already
     *  added — remove one via Adjust if it's the same shirt", it never refuses. */
    duplicateCode: z.boolean().optional(),
    /** True when the clientToken deduped a retry (the shelf moved once, not twice). */
    deduplicated: z.boolean().optional(),
});
/** `POST /garment-stock/count` body — anchor one identity's on-hand to a physical count. */
export const GarmentStockCountRequestSchema = z.object({
    stockKey: z.string().min(1).max(512),
    countedQty: z.number().int().nonnegative(),
    reason: z.string().max(200).optional(),
    ...clientTokenField,
});
/** `POST /garment-stock/adjust` body — a signed, non-zero correction (damage/found/shrink). */
export const GarmentStockAdjustRequestSchema = z.object({
    stockKey: z.string().min(1).max(512),
    qtyDelta: z.number().int(),
    reason: z.string().max(200).optional(),
    ...clientTokenField,
});
// ── Garment-stock match (plan PR-3: which open lines the shelf could ship, advisory) ──
/** One order line to answer for. */
export const GarmentStockMatchRefSchema = z.object({
    storeId: z.string(),
    orderId: z.string(),
    lineId: z.string(),
});
/** `POST /garment-stock/match` body — the lines a page is showing (orders queue / one job card). */
export const GarmentStockMatchRequestSchema = z.object({
    lineRefs: z.array(GarmentStockMatchRefSchema).min(1).max(500),
});
/**
 * One requested line's verdict. `coverable` is an ALLOCATION, not a lookup: on-hand is dealt out
 * FIFO (oldest Shopify-created first) across EVERY open candidate line — not just the requested ones
 * — all-or-nothing per line (whole-line rule, owner decision D4), so two orders never both read the
 * last shirt as theirs. BATCHED lines are excluded outright (already in production; film exists).
 */
export const GarmentStockMatchLineSchema = z.object({
    storeId: z.string(),
    orderId: z.string(),
    lineId: z.string(),
    /** The physical identity this line resolves to today. Absent when it can't resolve one
     *  (unmapped, uncovered colorway, unresolved blank) — such a line can never ship from the shelf. */
    stockKey: z.string().optional(),
    /** Shelf on-hand for that identity, BEFORE allocation (the card copy needs the raw number). */
    onHand: z.number().int().optional(),
    /** The line's whole-line need: max(0, currentQty ?? unfulfilledQty). */
    coverQty: z.number().int().nonnegative(),
    coverable: z.boolean(),
    /** True when the shelf stock was intaken before this design's art last changed — "eyeball the
     *  shirt before trusting the match". Advisory; only claimed when BOTH timestamps are known. */
    artDrift: z.boolean().optional(),
    /** The bin-label short id for `stockKey` (present whenever `stockKey` is) — what the pick dialog
     *  tells the puller to look for on the printed label. */
    binId: z.string().optional(),
    /**
     * The SHELF row's human identity (present only when the shelf actually holds this key) — the
     * "grab the bin labeled …" copy. Sourced from the stock row, never re-composed from the live
     * catalog, so it always reads exactly like the printed label.
     */
    identity: z
        .object({
        /** Brand + style + color, ` · `-joined; falls back to the bare ssSku. */
        blankLabel: z.string(),
        sizeName: z.string().optional(),
        apparelKind: ApparelKindEnum.optional(),
        placements: z.array(z.object({ location: z.string(), designName: z.string().optional() })),
    })
        .optional(),
});
/**
 * `POST /garment-stock/match` — ADVISORY ONLY. Changes nothing: no reservation, no ledger write, no
 * line state. Fallback-safe: any read failure returns an EMPTY `lines` array rather than blocking
 * the page (a missing chip is a shrug; a wrong one sends someone hunting a shirt that isn't there).
 */
export const GarmentStockMatchResponseSchema = z.object({
    lines: z.array(GarmentStockMatchLineSchema),
});
// ── Ship from stock (plan PR-4: the money action — the pick and its undo) ──
/** `POST /orders/:storeId/:orderId/lines/:lineId/ship-from-stock` body. `pick: true` draws the
 *  whole line from the garment shelf and marks it ALREADY_PRINTED (production-complete, ships like
 *  any printed line); `pick: false` undoes that — re-queues the line and credits the shelf back. */
export const ShipFromStockRequestSchema = z.object({
    pick: z.boolean(),
});
// ── Blank coverage (Phase 1 advisory: can a build selection be delivered from blank stock?) ──
/** One selected order line to check for blank availability. */
export const BlankCoverageRefSchema = z.object({
    storeId: z.string(),
    orderId: z.string(),
    lineId: z.string(),
});
/** `POST /inventory/coverage` body — the order lines currently selected to build. */
export const BlankCoverageRequestSchema = z.object({
    lineRefs: z.array(BlankCoverageRefSchema).min(1).max(500),
});
/** One selected line that can't be fully delivered from blank stock. */
export const BlankCoverageLineSchema = z.object({
    storeId: z.string(),
    orderId: z.string(),
    lineId: z.string(),
    orderName: z.string().optional(),
    title: z.string().optional(),
    variantTitle: z.string().optional(),
    /** The line's unfulfilled garment count (= blanks it needs). */
    needed: z.number().int().nonnegative(),
    /**
     * Garments of this line NOT covered by available blanks (the true shortfall = blanks you'd need to
     * cover it). Batching is all-or-nothing, so any `shortBy > 0` means the line can't be batched until
     * you get `shortBy` more blanks (or deselect it) — but the number itself is the honest units short.
     */
    shortBy: z.number().int().positive(),
});
/**
 * One blank the selection is short on. `availableToSelection` is `onHand` minus what HIGHER-priority
 * demand NOT in this selection claims first (already-committed BATCHED work + older open orders) —
 * so `short` can exceed `neededBySelection − onHand` when older orders have first call on the pool.
 */
export const BlankShortSummarySchema = z.object({
    ssSku: z.string(),
    brandName: z.string().optional(),
    styleName: z.string().optional(),
    colorName: z.string().optional(),
    sizeName: z.string().optional(),
    sampleTitle: z.string().optional(),
    sampleVariantTitle: z.string().optional(),
    /** Blanks this selection needs for this SKU (Σ the selected lines' unfulfilled qty). */
    neededBySelection: z.number().int().nonnegative(),
    /** Total physical on-hand (derived running sum; MAY be negative). */
    onHand: z.number().int(),
    /** On-hand left for this selection after higher-priority demand claims its share. */
    availableToSelection: z.number().int(),
    /** Units on open purchase orders (context — arriving, not on hand today). */
    onOrder: z.number().int().nonnegative(),
    /** Σ the short lines' shortBy for this SKU. */
    short: z.number().int().positive(),
    lines: z.array(BlankCoverageLineSchema),
});
/**
 * `POST /inventory/coverage` — ADVISORY ONLY. For a build selection, which lines can't be delivered
 * from blank stock, given the shared pool (blanks aren't reserved) allocated oldest/committed-first
 * across the WHOLE open queue. Changes nothing — no reservation, no ledger write, no line-state change.
 * Fallback-safe: an inventory/order read failure returns no shortfalls rather than blocking the build.
 */
export const BlankCoverageResponseSchema = z.object({
    blanks: z.array(BlankShortSummarySchema),
    /** Selected lines fully deliverable from stock. */
    coveredLineCount: z.number().int().nonnegative(),
    /** Selected lines that can't be fully delivered. */
    shortLineCount: z.number().int().nonnegative(),
    /** Σ shortBy across the short lines. */
    shortUnits: z.number().int().nonnegative(),
});
/**
 * One operator override on a receiving line: the quantity that ACTUALLY arrived in the box, when it
 * differs from what S&S's invoice claims shipped (a packing short — "S&S billed it, it wasn't in the
 * box"). `receivedQty` is clamped server-side to `[0, that line's qtyShipped]`; a fully-missing line is
 * `0`. `note` is a short free-text reason stored on the shortage record for the S&S claim. Over-receipt
 * (arrived > claimed) is deliberately NOT expressible here — it's a separate case handled by S&S's own
 * shipped numbers.
 */
export const ReceivingConfirmLineOverrideSchema = z.object({
    lineNumber: z.number().int().positive(),
    receivedQty: z.number().int().nonnegative(),
    note: z.string().max(280).optional(),
});
/**
 * `POST /receiving/confirm` body — the scanned code, plus OPTIONAL per-line short-receive overrides.
 * A line WITHOUT an override records S&S's shipped qty verbatim (unchanged pre-existing behavior). At
 * most one entry per lineNumber (last wins); entries for lines not in the fetched shipment are ignored.
 */
export const ReceivingConfirmRequestSchema = z.object({
    code: z.string().min(1).max(64),
    lineOverrides: z.array(ReceivingConfirmLineOverrideSchema).max(200).optional(),
});
/**
 * One line recorded SHORT this receipt — S&S claimed `claimedQty`, only `receivedQty` arrived, so S&S
 * still owes `shortQty`. Echoed in the confirm result so the dashboard can narrate the shortfall and
 * remind the operator to open an S&S claim.
 */
export const ReceivingShortageLineSchema = z.object({
    lineNumber: z.number().int(),
    ssSku: z.string(),
    brandName: z.string().optional(),
    styleName: z.string().optional(),
    colorName: z.string().optional(),
    sizeName: z.string().optional(),
    claimedQty: z.number().int().nonnegative(),
    receivedQty: z.number().int().nonnegative(),
    shortQty: z.number().int().positive(),
    note: z.string().optional(),
    /** The box the short was counted in, when the line was received box by box. */
    boxNumber: z.number().int().positive().optional(),
});
/**
 * Receive-against-PO tie-back (Phase 3c): when a receipt's S&S `poNumber` matched an OPEN FSP PO, the
 * confirm/reverse applied it to that PO. Surfaced so the dashboard can say "applied to PO FSP-N (now
 * Received)". Absent when no matching open PO was found (a plain receipt — unchanged pre-3c behavior).
 */
export const ReceivingPurchaseOrderTieBackSchema = z.object({
    poNumber: z.string(),
    status: PurchaseOrderStatusEnum,
    fullyReceived: z.boolean(),
});
/** `POST /receiving/confirm` result — what this receipt added to inventory (idempotent per line). */
export const ReceivingConfirmResultSchema = z.object({
    invoiceNumber: z.string(),
    receivedLineCount: z.number().int().nonnegative(),
    alreadyReceivedLineCount: z.number().int().nonnegative(),
    receivedPieces: z.number().int().nonnegative(),
    /** On-hand (post-receipt) for each blank this shipment touched. */
    onHand: z.array(BlankOnHandSchema),
    /**
     * Lines recorded SHORT this receipt (arrived < S&S-claimed, via a per-line override). Empty when
     * everything arrived in full. The shortfall stays owed on the matching PO (partial → RECEIVING).
     */
    shortages: z.array(ReceivingShortageLineSchema),
    /** Set when this receipt matched an open FSP PO and was applied to it (Phase 3c). */
    purchaseOrder: ReceivingPurchaseOrderTieBackSchema.optional(),
});
/**
 * One box to receive: its S&S box number (from the scanned carton `<invoice>.<box>` or a tapped box
 * tile) plus optional per-line overrides for what ACTUALLY came out of THIS box. An override's
 * `lineNumber` is the INVOICE line (as `ReceivingBoxLine.lineNumber` reports it — never S&S's box-local
 * line number, which is the position inside the box); its `receivedQty` is clamped to `[0, that
 * line's share of this box]`.
 */
export const ReceivingConfirmBoxSchema = z.object({
    boxNumber: z.number().int().positive(),
    lineOverrides: z.array(ReceivingConfirmLineOverrideSchema).max(200).optional(),
});
/**
 * `POST /receiving/confirm-boxes` body — receive one or more boxes of an S&S shipment, as S&S packed
 * them. Each box's lines and quantities come from the fresh S&S box manifest, never the client. Writes
 * one receipt per (invoice line, box), so a box that arrives a day later is received on its own, the
 * invoice stays "1 of 2 boxes received" until then, and nothing is claimed from S&S for a box that is
 * still with the carrier. Idempotent per box: re-receiving a box adds nothing.
 */
export const ReceivingConfirmBoxesRequestSchema = z.object({
    code: z.string().min(1).max(64),
    boxes: z.array(ReceivingConfirmBoxSchema).min(1).max(100),
});
/**
 * Outcome for one requested box. `received` = at least one line newly recorded; `alreadyReceived` =
 * every line of the box was already on file (a re-scan, or the invoice was received as a whole);
 * `undone` = this box was received and then undone — terminal for the box (correct the line's count
 * instead of re-scanning); `notInShipment` = S&S lists no such box; `noContents` = S&S sent no readable
 * contents for it (receive the whole invoice instead).
 */
export const ReceivingBoxOutcomeSchema = z.object({
    boxNumber: z.number().int().positive(),
    status: z.enum(['received', 'alreadyReceived', 'undone', 'notInShipment', 'noContents']),
    receivedLineCount: z.number().int().nonnegative(),
    alreadyReceivedLineCount: z.number().int().nonnegative(),
    receivedPieces: z.number().int().nonnegative(),
    /** Lines recorded SHORT from this box (arrived < the box's share) — each becomes an S&S claim. */
    shortages: z.array(ReceivingShortageLineSchema),
});
/** `POST /receiving/confirm-boxes` result. */
export const ReceivingConfirmBoxesResultSchema = z.object({
    invoiceNumber: z.string(),
    boxes: z.array(ReceivingBoxOutcomeSchema),
    receivedPieces: z.number().int().nonnegative(),
    /** On-hand (post-receipt) for each blank these boxes touched. */
    onHand: z.array(BlankOnHandSchema),
    /** Set when the receipt matched an open FSP PO and was applied to it. */
    purchaseOrder: ReceivingPurchaseOrderTieBackSchema.optional(),
    /** The invoice's receipt state AFTER this call — "1 of 2 boxes received", or complete. Absent only
     *  when the post-write ledger read failed (the write itself succeeded). */
    receipt: ReceivingReceiptStateSchema.optional(),
});
/** `POST /receiving/reverse-box` body — undo ONE box's receipt (the other boxes stay received). */
export const ReceivingReverseBoxRequestSchema = z.object({
    code: z.string().min(1).max(64),
    boxNumber: z.number().int().positive(),
});
/**
 * `POST /receiving/reverse-box` result — a compensating undo of one box: each of its lines gets a
 * negative movement netting that box's receipt out (the receipt rows are PRESERVED). `reversed` = at
 * least one line newly undone; `alreadyReversed` = every line of the box was already undone;
 * `notReceived` = no line of this box was ever received (or the invoice was received as a whole —
 * use `/receiving/reverse`). Refused (422) when a line of the box was corrected after receipt —
 * correct the count instead.
 */
export const ReceivingReverseBoxResultSchema = z.object({
    invoiceNumber: z.string(),
    boxNumber: z.number().int().positive(),
    status: z.enum(['reversed', 'alreadyReversed', 'notReceived']),
    reversedLineCount: z.number().int().nonnegative(),
    reversedPieces: z.number().int().nonnegative(),
    onHand: z.array(BlankOnHandSchema),
    purchaseOrder: ReceivingPurchaseOrderTieBackSchema.optional(),
    /** The invoice's receipt state AFTER the undo. Absent only when the post-write ledger read failed. */
    receipt: ReceivingReceiptStateSchema.optional(),
});
/** `POST /receiving/reverse` body — the scanned code (same shape lookup/confirm use). */
export const ReceivingReverseRequestSchema = z.object({
    code: z.string().min(1).max(64),
});
/**
 * `POST /receiving/reverse` result — a compensating UNDO of a confirmed receipt. Each received line
 * gets a negative movement that nets its RECEIVE back out; the original RECEIVE row is PRESERVED for
 * audit (never deleted), and re-reversing is a no-op. The reversal uses the ACTUAL received quantity
 * from the ledger, so a later S&S quantity revision can't leave a residue.
 */
export const ReceivingReverseResultSchema = z.object({
    invoiceNumber: z.string(),
    /** Lines whose RECEIVE was compensated out this call. */
    reversedLineCount: z.number().int().nonnegative(),
    /** Lines already reversed by a prior call (idempotent no-op). */
    alreadyReversedLineCount: z.number().int().nonnegative(),
    /** Lines with nothing to reverse (never received under this invoice). */
    notReceivedLineCount: z.number().int().nonnegative(),
    /** Pieces removed from on-hand this call (Σ of the reversed lines' received quantities). */
    reversedPieces: z.number().int().nonnegative(),
    /** On-hand (post-reversal) for each blank this receipt touched. */
    onHand: z.array(BlankOnHandSchema),
    /** Set when this receipt had been applied to an FSP PO and was un-applied here (Phase 3c). */
    purchaseOrder: ReceivingPurchaseOrderTieBackSchema.optional(),
});
/**
 * One OPEN receiving shortage — a line recorded short (via a confirm override) whose RECEIVE has NOT
 * been reversed. The operator's S&S-claim worklist. `shortQty` is the raw invoice shortfall (what S&S
 * billed but didn't deliver on THIS invoice); `residualShort` is what's STILL owed after reconciling
 * against the PO — a make-good shipped under the SAME PO# (a different invoice) tops the PO line up by
 * SKU, so `residualShort` shrinks and the claim drops off the worklist once the PO line is whole again
 * (residualShort would hit 0). When the claim has no `poNumber`, or the PO hasn't been topped up,
 * `residualShort === shortQty`. Substitution / credit / no-PO make-goods are cleared by the manual
 * `POST /receiving/resolve` settle (P2).
 */
export const ReceivingOpenShortageSchema = z.object({
    ssSku: z.string(),
    invoice: z.string(),
    lineNumber: z.number().int(),
    claimedQty: z.number().int().nonnegative(),
    receivedQty: z.number().int().nonnegative(),
    /** Raw invoice shortfall: `claimedQty − receivedQty` on this invoice. */
    shortQty: z.number().int().positive(),
    /** Still owed after PO reconciliation (≤ shortQty; equals shortQty when there's no PO or no top-up). */
    residualShort: z.number().int().positive(),
    /** The FSP PO# this shortage's receipt tied back to, if any (drives the PO reconciliation). */
    poNumber: z.string().optional(),
    /**
     * The S&S order number of the claim's own invoice. Auto-clear requires it: a make-good receipt only
     * counts when both orderNumbers are present and differ (same-order sibling invoices never count).
     * Absent on claims recorded before order-number stamping — those never auto-clear (settle manually).
     */
    orderNumber: z.string().optional(),
    /**
     * Set when a make-good-shaped receipt (same PO+SKU, different order) exists but was CONFIRMED before
     * this short was recorded — the time guard blocks it forever, so auto-clear will never come. The row
     * points the operator at "Made good…" with this invoice instead of silently looking auto-clearable.
     */
    timeBlockedInvoice: z.string().optional(),
    note: z.string().optional(),
    recordedAt: z.string(),
    brandName: z.string().optional(),
    styleName: z.string().optional(),
    colorName: z.string().optional(),
    sizeName: z.string().optional(),
    /** The box the short was counted in, when exactly one box of the line is short (box receiving). */
    boxNumber: z.number().int().positive().optional(),
});
/** `GET /receiving/shortages` result — open (un-reversed, un-resolved) receiving shortages, newest first. */
export const ReceivingShortagesResultSchema = z.object({
    shortages: z.array(ReceivingOpenShortageSchema),
    /** Σ of the still-owed `residualShort` across open shortages (post-PO-reconciliation). */
    totalShortUnits: z.number().int().nonnegative(),
});
/**
 * Why an open "S&S owes us" claim was manually SETTLED (P2) — cases Phase-1 auto-reconcile can't close
 * because no same-PO/same-SKU make-good receipt will ever arrive:
 *  - `made-good` — a replacement arrived as a DIFFERENT sku/order (substitution, or an off-PO reship);
 *    its stock rode its OWN receive, so the claim is closed with no on-hand change here.
 *  - `credited`  — S&S issued a money credit/refund instead of reshipping; no goods arrive.
 *  - `written-off` — unrecoverable; give up chasing it.
 * All three drop the claim off the worklist and (if the claim was PO-tied) short-close the phantom
 * on-order remainder for its SKU line. None writes on-hand.
 */
export const ReceivingResolveReasonEnum = z.enum(['made-good', 'credited', 'written-off']);
/**
 * Shortage RESOLVE marker (P2): `BLANKSHORTRESOLVE#<ssSku>` / `RESOLVE#<invoice>#<lineNo>`. A SEPARATE
 * partition (like par/archive) so on-hand's Σ qtyDelta is blind to it BY CONSTRUCTION — a resolve never
 * moves stock. Mirrored onto GSI1 (`BLANK_SHORT_RESOLVES`) so `foldShortages` reads all resolves in one
 * walk and skips resolved groups. `resolveToken` scope-keys the reverse (a stale un-settle can't erase a
 * newer re-settle). `poNumber`/`writtenOffQty` mirror the PO short-close for display + reverse.
 */
export const BlankShortageResolveRecordSchema = z.object({
    PK: z.string(),
    SK: z.string(),
    GSI1PK: z.literal('BLANK_SHORT_RESOLVES'),
    GSI1SK: z.string(),
    entity: z.literal('blankShortageResolve'),
    ssSku: z.string(),
    invoice: z.string(),
    lineNumber: z.number().int(),
    reason: ReceivingResolveReasonEnum,
    /** Server-minted per settle; the reverse is conditional on it (scope-keyed undo). */
    resolveToken: z.string(),
    /**
     * The make-good's invoice #, free text — LOAD-BEARING since P0 reconcile hardening: reconciliation
     * excludes this invoice's receipts (exact-string match on `${ssSku}#${invoice}`) from sibling claims'
     * make-good pools, and a `made-good` marker whose value is absent or matches NO receipt on the claim's
     * (PO, SKU) instead QUARANTINES its shortQty from that pool. A typo therefore fails open (siblings
     * stay visible), never falsely closes one.
     */
    replacementInvoice: z.string().optional(),
    note: z.string().optional(),
    /** The PO this claim was tied to (if any) — the reverse un-short-closes it. */
    poNumber: z.string().optional(),
    /** Units retired from the PO's on-order by this settle (mirror of the PO SHORTWRITEOFF marker). */
    writtenOffQty: z.number().int().nonnegative().optional(),
    // Claim snapshot at settle time (so the "recently settled" list renders without re-folding).
    claimedQty: z.number().int().nonnegative(),
    shortQty: z.number().int().nonnegative(),
    actor: z.string().optional(),
    resolvedAt: z.string(),
    brandName: z.string().optional(),
    styleName: z.string().optional(),
    colorName: z.string().optional(),
    sizeName: z.string().optional(),
});
/**
 * `POST /receiving/resolve` body — manually SETTLE an open "S&S owes us" claim that Phase-1 can't
 * auto-close. Identifies the claim by `(ssSku, invoice, lineNumber)` — `ssSku` disambiguates the rare
 * SKU-reassignment case where two blanks share an (invoice, line). Writes a zero-inventory resolve marker
 * (drops the claim off the worklist) and, if the claim was PO-tied, short-closes the phantom on-order.
 * Idempotent per `(ssSku, invoice, lineNumber)`.
 */
export const ReceivingResolveRequestSchema = z.object({
    invoice: z.string().min(1).max(32),
    lineNumber: z.number().int().positive(),
    ssSku: z.string().min(1),
    reason: ReceivingResolveReasonEnum,
    /** Optional make-good invoice #. Enter the EXACT S&S invoice number the replacement arrived on (not
     *  the order number): reconciliation matches it verbatim to mark those units consumed; an absent or
     *  unmatchable value makes the settle quarantine its shortfall from sibling pools instead (fail-open). */
    replacementInvoice: z.string().max(32).optional(),
    note: z.string().max(280).optional(),
});
/** `POST /receiving/resolve` result — the settled claim + how much on-order was short-closed. */
export const ReceivingResolveResultSchema = z.object({
    invoice: z.string(),
    lineNumber: z.number().int(),
    ssSku: z.string(),
    reason: ReceivingResolveReasonEnum,
    resolved: z.boolean(),
    /** True when this call wrote the marker; false = idempotent replay of an already-settled claim. */
    newlyResolved: z.boolean(),
    poNumber: z.string().optional(),
    /** Units retired from on-order (0 when the claim wasn't PO-tied or nothing remained). */
    writtenOffQty: z.number().int().nonnegative(),
    purchaseOrder: ReceivingPurchaseOrderTieBackSchema.optional(),
});
/** `POST /receiving/resolve/reverse` body — UN-settle a claim (it re-appears on the worklist). */
export const ReceivingResolveReverseRequestSchema = z.object({
    invoice: z.string().min(1).max(32),
    lineNumber: z.number().int().positive(),
    ssSku: z.string().min(1),
});
/** `POST /receiving/resolve/reverse` result. */
export const ReceivingResolveReverseResultSchema = z.object({
    invoice: z.string(),
    lineNumber: z.number().int(),
    ssSku: z.string(),
    /** True when a resolve marker was removed; false = nothing was settled (idempotent no-op). */
    reversed: z.boolean(),
    /** Units restored to on-order by un-short-closing the PO (0 when there was no short-close). */
    restoredOnOrder: z.number().int().nonnegative(),
    purchaseOrder: ReceivingPurchaseOrderTieBackSchema.optional(),
});
/** One recently-SETTLED claim (the undo worklist). */
export const ReceivingResolvedShortageSchema = z.object({
    ssSku: z.string(),
    invoice: z.string(),
    lineNumber: z.number().int(),
    reason: ReceivingResolveReasonEnum,
    claimedQty: z.number().int().nonnegative(),
    shortQty: z.number().int().nonnegative(),
    replacementInvoice: z.string().optional(),
    note: z.string().optional(),
    poNumber: z.string().optional(),
    writtenOffQty: z.number().int().nonnegative().optional(),
    resolvedAt: z.string(),
    brandName: z.string().optional(),
    styleName: z.string().optional(),
    colorName: z.string().optional(),
    sizeName: z.string().optional(),
});
/** `GET /receiving/resolved` result — recently settled claims (newest first) for review + undo. */
export const ReceivingResolvedListResultSchema = z.object({
    resolved: z.array(ReceivingResolvedShortageSchema),
});
/**
 * One recently-CORRECTED receipt line — the undo worklist for a mis-click on "All arrived (miscount)" or
 * a wrong `Save`. `receivedQty` is the current effective received; `previousReceivedQty` is what it was
 * BEFORE the most recent correction (the value an Undo restores it to, via `POST /receiving/correct`).
 * A correction that raised received to `claimedQty` dropped the claim off the shortages worklist, so this
 * is the only place to catch + undo it.
 */
export const ReceivingRecentCorrectionSchema = z.object({
    ssSku: z.string(),
    invoice: z.string(),
    lineNumber: z.number().int(),
    claimedQty: z.number().int().nonnegative(),
    receivedQty: z.number().int().nonnegative(),
    previousReceivedQty: z.number().int().nonnegative(),
    correctedAt: z.string(),
    note: z.string().optional(),
    brandName: z.string().optional(),
    styleName: z.string().optional(),
    colorName: z.string().optional(),
    sizeName: z.string().optional(),
});
/** `GET /receiving/recent-corrections` result — recently corrected receipt lines (newest first), for undo. */
export const ReceivingRecentCorrectionsResultSchema = z.object({
    corrections: z.array(ReceivingRecentCorrectionSchema),
});
/**
 * `POST /receiving/correct` body — fix a MISCOUNT on an already-received line by SETTING its received
 * quantity to the true value (e.g. recorded 30, then all 50 arrived → set to 50; or the missing units
 * finally turned up → set to `claimed`). Reconciles on-hand AND the matching PO together (unlike a bare
 * on-hand Adjust). `receivedQty` is the NEW absolute value, clamped server-side to `[0, claimed]`.
 * `clientToken` is a required idempotency key (a fresh UUID per correction) — a retry/double-submit with
 * the same token applies at most once. Refused if the line was never received or was reversed.
 */
export const ReceivingCorrectRequestSchema = z.object({
    /** S&S invoice number of the receipt line (as shown on the shortage record). */
    invoice: z.string().min(1).max(32),
    lineNumber: z.number().int().positive(),
    /** The corrected ACTUAL-arrived quantity (absolute, not a delta). Clamped to [0, claimed]. */
    receivedQty: z.number().int().nonnegative(),
    note: z.string().max(280).optional(),
    clientToken: z.string().min(1).max(64),
});
/** `POST /receiving/correct` result — the reconciled receipt line after setting received to the new value. */
export const ReceivingCorrectResultSchema = z.object({
    invoiceNumber: z.string(),
    ssSku: z.string(),
    lineNumber: z.number().int(),
    /** Effective received BEFORE this correction. */
    previousReceivedQty: z.number().int().nonnegative(),
    /** Effective received AFTER this correction (the clamped new value). */
    receivedQty: z.number().int().nonnegative(),
    claimedQty: z.number().int().nonnegative(),
    /** `max(0, claimed − received)` — remaining owed by S&S (0 once fully resolved). */
    shortQty: z.number().int().nonnegative(),
    /** True when this call changed nothing (already at the requested value / idempotent replay). */
    unchanged: z.boolean(),
    /** On-hand (post-correction) for the affected blank. */
    onHand: z.array(BlankOnHandSchema),
    /** Set when the correction was applied to a matching open FSP PO. */
    purchaseOrder: ReceivingPurchaseOrderTieBackSchema.optional(),
});
// ── Orders + line items ──
/** DDB record: `ORDER#<storeId>#<orderId>` / `ORDER`. */
export const OrderRecordSchema = z.object({
    PK: z.string(),
    SK: z.literal('ORDER'),
    entity: z.literal('order'),
    storeId: z.string(),
    orderId: z.string(),
    orderName: z.string(),
    financialStatus: z.string().nullable(),
    fulfillmentState: FulfillmentStateEnum,
    cancelledAt: z.string().nullable().optional(),
    // The observed ship moment (production-records R2): stamped ONCE by ingest when the header
    // transitions to FULFILLED with at least one actually-FULFILLED line, then PRESERVED by every
    // subsequent ingest overwrite (the header build reads the prior record — its preserve list; see
    // shopify-ingest `resolveFulfilledAt`). NOT a Shopify field, so an unpreserved stamp would be
    // silently wiped within ≤15 min. Going-forward only: an order first seen already-FULFILLED (or
    // fulfilled before R2 deployed) has no recoverable ship date and stays unstamped forever.
    fulfilledAt: z.string().optional(),
    // Queue prioritization, order-scoped (see OrderLineRecord). Kept on the order so the recheck
    // reconstruct path can rehydrate them. Optional: orders ingested before this field existed omit it.
    dueDate: z.string().nullable().optional(),
    rush: z.boolean().optional(),
    shopifyCreatedAt: z.string(),
    shopifyUpdatedAt: z.string(),
    ingestedAt: z.string(),
});
/**
 * DDB record: `ORDER#<storeId>#<orderId>` / `LINE#<lineId>`. GSI1 attrs are present
 * ONLY while `fulfillmentState=UNFULFILLED` AND `workflowState=OPEN` (the sparse
 * open-work queue). `unfulfilledQty` is the ONE normalized demand field — populated
 * from webhook `fulfillable_quantity` or GraphQL `unfulfilledQuantity`, NEVER raw
 * `quantity` (which counts refunded units). See plan § 5 / § 7.
 */
export const OrderLineRecordSchema = z.object({
    PK: z.string(),
    SK: z.string(),
    GSI1PK: z.string().optional(),
    GSI1SK: z.string().optional(),
    // GSI2 (`GSI2PK='ORDERLINE'`, `GSI2SK=<shopifyCreatedAt>#<storeId>#<orderId>#<lineId>`) — the
    // NON-SPARSE all-order-lines index that replaces the full-table Scan behind demand/overview/usage.
    // Optional here because a row written before the backfill (or in a test that doesn't stamp) has none;
    // the write path (`putLine`) stamps every new/overwritten line, so post-backfill every row carries them.
    GSI2PK: z.string().optional(),
    GSI2SK: z.string().optional(),
    entity: z.literal('orderLine'),
    storeId: z.string(),
    orderId: z.string(),
    orderName: z.string(),
    lineId: z.string(),
    variantId: z.string().nullable(),
    productId: z.string().nullable().optional(),
    sku: z.string().nullable().optional(),
    title: z.string(),
    variantTitle: z.string().optional(),
    unfulfilledQty: z.number().int().nonnegative(),
    // Units still ordered NET OF REFUNDS/REMOVALS but NOT of fulfillment (webhook `current_quantity`
    // / GraphQL `currentQuantity`). Distinct from `unfulfilledQty`, which also nets out fulfilled units
    // — so a refund and a shipment both drop `unfulfilledQty`, but only a refund drops `currentQty`.
    // This is the ONLY signal that separates "customer refunded a batched garment" (do not press) from
    // "batched garment shipped" (normal completion). Optional: a line ingested before this field
    // existed omits it, and the qty-drop guard treats absent as "no refund" (safe — never a false flag).
    currentQty: z.number().int().nonnegative().optional(),
    fulfillmentState: FulfillmentStateEnum,
    workflowState: WorkflowStateEnum,
    mappingStatus: MappingStatusEnum,
    financialStatus: z.string().nullable(),
    // Mapping-derived, RE-DERIVED from the synced catalog each ingest (a re-map / re-sync propagates
    // to open lines). The design + placement geometry now live on the product MAPPING, not the line —
    // the batch builder reads them from the catalog at build time, keyed by (productId, sizeBand).
    resolvedBlankSku: z.string().optional(),
    // Print size band echoed from the catalog variant at ingest (catalog-mapping v1). Optional
    // (a pre-catalog line omits it); the batch builder falls back to 'adult' when absent.
    sizeBand: SizeBandEnum.optional(),
    // Number of print placements a MAPPED line expands to at build (= its product mapping's placement
    // count), stamped at ingest. A line → one batch ITEM per placement, and a batch caps at 99 items
    // (one transactWrite), so the queue sums this over the selection to warn BEFORE Build. Optional:
    // absent on non-mapped / pre-catalog lines (which aren't batchable anyway).
    placementCount: z.number().int().nonnegative().optional(),
    // Which technique(s) this line's placements use — folded from the product mapping at ingest and
    // RE-DERIVED every ingest, exactly like `placementCount` above. Absent ⇒ treated as 'dtf' (every
    // pre-feature line). Powers the /orders technique badge + filter; see LineDecorationEnum for why it
    // must NEVER gate money and must NEVER enter buildLineRecord's preserve list.
    decoration: LineDecorationEnum.optional(),
    batchId: z.string().optional(),
    // Every print location an operator has EVER declared screen-print for this line at build time
    // (P7-0). Stored as a DDB LIST and extended with `list_append` on each claim, so it only ever
    // WIDENS. De-duplicated and sorted HERE, on read, because `list_append` appends unconditionally: a
    // line built three times with the same screen-printed front holds `['front','front','front']` at
    // rest and must read as `['front']`.
    //
    // ⚠️ It is a List rather than a DDB String Set ON PURPOSE — see the long note at `markLineBatched`.
    // A Set would be the natural union type (and is what `ReprintQueueEntryRecord.ruinedLocations`
    // uses), but this row is `putItem`-ed whole by every ingest, and that round-trip rewrites a Set as a
    // List and makes the next `ADD` a hard ValidationException. Do not "fix" this to `ADD`.
    //
    // ⚠️ ABSENT MEANS "NO RECORD", **NOT** "there was no screen print". This field can only ever DENY:
    // its presence proves the garment had screen-print work, its absence proves nothing at all. The
    // build-time tick is per-BUILD and `batches.ts` says it is "never persisted on the product", so a
    // garment printed before this field existed — or one printed 100% by screen print, which never
    // reaches a gang sheet and so never claims a line — carries nothing here. Any future gate reading
    // this MUST treat absence as UNKNOWN and require a human attestation, never as permission
    // (`docs/reprint-queue-and-merge-plan-v1.md` § 14.4, P0-3).
    //
    // ⚠️ Union, never a last-write SET. A front-screen-print + back-DTF line stamped `['front']` on
    // sheet #40 gets reopened when that sheet is deleted at close-out, and re-batched next morning as
    // DTF-only; a SET would erase the one fact this field exists to keep. Locally-owned with NO Shopify
    // source, so — like the alreadyPrinted*/producedBlankSku/packedItemSlug fields — it MUST survive
    // reconcile/recheck via `buildLineRecord`'s preserve list or it is wiped within ≤15 min.
    //
    // It also OUTLIVES the claim it was written by: both `resetLineOpen` branches `REMOVE batchId` only,
    // so a rolled-back build or a deleted sheet leaves this stamped. Deliberate — the record is about the
    // garment's history, not its current sheet — and it errs toward OVER-recording, which is the safe
    // direction here: over-refusing a re-send costs a phone call, while silently reproducing a
    // screen-printed front as blank DTF film ships a garment the customer did not order.
    spLocationsAtBuild: z
        .preprocess((v) => {
        // Arrays are the normal case (a List, or an already-parsed record round-tripping through
        // `buildLineRecord`). The `Set` branch is not dead: a legacy row, or any future writer reaching
        // for `ADD`, unmarshals as one, and reading it as garbage would silently drop the evidence.
        if (Array.isArray(v))
            return [...new Set(v.map(String))].sort();
        if (v instanceof Set)
            return [...v].map(String).sort();
        return v;
    }, z.array(z.string().min(1)))
        .optional(),
    // Set only while `workflowState === 'ALREADY_PRINTED'` — the audit trail for an off-system
    // mark (why / when / who). Cleared on undo. These are NOT re-derivable from Shopify, so the ingest
    // MUST carry them across a reconcile/recheck (see shopify-ingest `buildLineRecord`) or they'd be
    // silently wiped within ≤15 min. Absent on every other line.
    alreadyPrintedReason: AlreadyPrintedReasonEnum.optional(),
    alreadyPrintedAt: z.string().optional(),
    alreadyPrintedBy: z.string().optional(),
    // Pulled-from-stock audit (set only when `workflowState === 'PULLED_FROM_STOCK'`). Like the
    // alreadyPrinted fields, these MUST survive a reconcile/recheck (shopify-ingest `buildLineRecord`).
    pulledFromStockAt: z.string().optional(),
    pulledFromStockBy: z.string().optional(),
    // Non-DTF lane (plan v1, Phase 4). `productionClass` is RE-DERIVED from the catalog each ingest
    // (absent ⇒ treated as DTF_APPAREL by the rollup). `nonDtfWorkflowState` (default OPEN) + producedAt/
    // producedBy are locally-owned (set by the Phase 5 caps mark-produced) with NO Shopify source, so —
    // like alreadyPrinted*/pulledFromStock* — they MUST survive reconcile/recheck.
    productionClass: ProductionClassEnum.optional(),
    nonDtfWorkflowState: NonDtfWorkflowStateEnum.optional(),
    producedAt: z.string().optional(),
    producedBy: z.string().optional(),
    // The BARE S&S ssSku actually DRAWN when this cap was marked PRODUCED (caps-materials Phase 3). The
    // undo reverses the blank consume against THIS frozen value — never the live `resolvedBlankSku`, which
    // a catalog re-map + reconcile can change under a produced cap and would otherwise orphan the original
    // draw. Locally-owned + no Shopify source → MUST survive reconcile like producedAt/producedBy. Absent
    // for an unmapped cap (nothing was drawn) and for DTF lines.
    producedBlankSku: z.string().optional(),
    // Ship-only lane (inventory-clarity P2). `fulfillWorkflowState` (default OPEN) + packedAt/packedBy
    // are locally-owned (set by the Ship-only mark-packed) with NO Shopify source, so — exactly like the
    // caps trio above — they MUST survive reconcile/recheck or they'd be wiped within ≤15 min.
    fulfillWorkflowState: FulfillWorkflowStateEnum.optional(),
    packedAt: z.string().optional(),
    packedBy: z.string().optional(),
    // The stock-item slug actually DRAWN when this line was marked PACKED (P2b-C consume-at-pack). The
    // undo restores the item consume against THIS frozen value — never the live `variantStockItems`
    // mapping, which an operator re-map between pack and undo would otherwise point at the wrong shelf
    // and orphan the original draw (the caps `producedBlankSku` pattern, verbatim). Locally-owned + no
    // Shopify source → MUST survive reconcile like packedAt/packedBy. Absent when the line was packed
    // unmapped (nothing was drawn) and for non-Ship-only lines.
    packedItemSlug: z.string().optional(),
    // Ship-from-stock (finished-garment-stock v1 PR-4): set by the pick alongside
    // `workflowState = ALREADY_PRINTED` + alreadyPrintedAt/By, cleared by its undo. `stockPickId` is
    // the garment-stock ledger PICK row's id (the scope key for the reverse); `stockPickKey` is the
    // FROZEN identity actually drawn — the undo credits THIS key, never a re-derived one (a mapping
    // edit between pick and undo must credit the shelf the shirts came from); `stockPickQty` is the
    // whole-line quantity drawn. Locally-owned, no Shopify source → MUST survive reconcile/recheck
    // (shopify-ingest `buildLineRecord`) like the caps/pack trios above, or the undo goes blind within
    // ≤15 min. Their PRESENCE is also the discriminator the generic already-printed undo refuses on —
    // that door unflips without crediting the shelf, so it must send staff to the ship-from-stock undo.
    stockPickId: z.string().optional(),
    stockPickKey: z.string().optional(),
    stockPickQty: z.number().int().positive().optional(),
    // Queue prioritization — order-scoped, denormalized onto every line (like shopifyCreatedAt) so the
    // dashboard can sort the open-work queue by rush ↓, dueDate ↑, then FIFO. Parsed at ingest from the
    // allow-listed `rush` tag + `due_date` order attribute only (never the PII note). Optional: lines
    // ingested before this field existed omit it (treated as no due date / not rush).
    dueDate: z.string().nullable().optional(),
    rush: z.boolean().optional(),
    shopifyCreatedAt: z.string(),
    shopifyUpdatedAt: z.string(),
});
/** API view of a single order line (the unfulfilled-queue row + order-detail row). */
export const OrderLineViewSchema = z.object({
    storeId: z.string(),
    orderId: z.string(),
    orderName: z.string(),
    lineId: z.string(),
    sku: z.string().nullable(),
    variantId: z.string().nullable(),
    title: z.string(),
    variantTitle: z.string().optional(),
    /** The product's Shopify featured image (a CDN mockup — reference only, never printed), JOINED AT
     *  READ TIME by the order-detail route and by nothing else. Never persisted on the line, so no
     *  worker has to preserve it and no backfill exists.
     *
     *  ⚠️ It is the PRODUCT's image, not the variant's: Shopify selects `featuredImage` on the product
     *  node and the sync stores one URL per product, so a Black 3XL and a Red S share a picture. The
     *  row stamps the variant's own size on the thumbnail for exactly that reason — see the size chip
     *  in `order-detail-page.tsx`. Do not let anything downstream treat this as identifying a variant.
     *
     *  Absent when: the line has no `productId` (custom/product-less), the product has no META row,
     *  the product has no image, or the catalog read failed — the join fails OPEN, so absence means
     *  "no picture to show" and NEVER "this garment doesn't exist".
     *
     *  ⚠️ Deliberately a plain `z.string()`, not `.url()` (the plan proposed `.url()`). The source
     *  field `CatalogProductRecord.featuredImageUrl` is an unvalidated nullable string straight from
     *  Shopify; validating harder at the view than at the source can only invent a failure mode the
     *  source cannot prevent — and it would be a decorative image taking out a whole order page. */
    imageUrl: z.string().optional(),
    /** The variant's OWN size, as the catalog sync derived it from Shopify's structured
     *  `selectedOptions` — "X-Large", "4X-Large", "Youth Small". Joined at read time beside
     *  {@link imageUrl}, from the same product partition, by the order-detail route only.
     *
     *  ⚠️ It exists BECAUSE `imageUrl` does. The photo is product-level, so the order card stamps this
     *  size on the thumbnail to make a picture that belongs to the wrong variant visibly disagree with
     *  itself. That is the entire mitigation, and it has to be right: `variantTitle` cannot supply it.
     *  This shop's titles are SIZE-FIRST (`"X-Large / Blue"`, `"Youth Small / Blue"` — measured across
     *  286 live variants in both stores), so the obvious "take the part after the slash" rule yields
     *  the COLOUR, and a chip reading "Blue" beside a blank-consuming checkbox is worse than no chip.
     *  `deriveVariantAttrs` already answered this at sync time from Shopify's own option names.
     *
     *  Absent when the product has no variant row for this line's `variantId`, when the variant has no
     *  size option at all (a one-variant cap or decal — `"Default Title"`, measured `size: null`), or
     *  when the catalog read failed. Raw, NOT abbreviated: the dashboard owns the size canon. */
    sizeName: z.string().optional(),
    unfulfilledQty: z.number().int().nonnegative(),
    /** Echo of `OrderLineRecord.currentQty` — units still ordered net of refunds but NOT of fulfilment.
     *  Carried on the view so `deriveOrderProduction`'s piece counts are the SAME number whichever shape
     *  a caller hands it: `ProductionLine` claims to be "satisfied by both OrderLineRecord and
     *  OrderLineView", and without this that claim was false in a way that fails silently — every one of
     *  the nine call sites happens to pass records today, so a view-based caller would have been the
     *  first to under-count, computing pieces off `unfulfilledQty` (0 on a fulfilled line). Optional for
     *  the same reason as on the record: a row last written before 2026-07-15 omits it. */
    currentQty: z.number().int().nonnegative().optional(),
    mappingStatus: MappingStatusEnum,
    fulfillmentState: FulfillmentStateEnum,
    workflowState: WorkflowStateEnum,
    financialStatus: z.string().nullable(),
    /** Requested ship-by date (ISO `YYYY-MM-DD`) from the allow-listed `due_date` order attribute; the
     *  queue sorts ascending on it. Absent when not set / malformed. */
    dueDate: z.string().nullable().optional(),
    /** True when the order carries the allow-listed `rush` tag — the queue floats it to the top. */
    rush: z.boolean().optional(),
    /** Placements a MAPPED line expands to at build — powers the queue's 99-item batch-cap warning. */
    placementCount: z.number().int().nonnegative().optional(),
    /** Technique badge + filter on the /orders queue. Absent ⇒ 'dtf'. Display/selection only — see
     *  {@link LineDecorationEnum}. */
    decoration: LineDecorationEnum.optional(),
    /** The batch this line was claimed into (set at BATCHED, kept through PRINTED); absent while OPEN. */
    batchId: z.string().optional(),
    /** Off-system-print audit (set only when `workflowState === 'ALREADY_PRINTED'`) — why/when/who it
     *  was taken off the gang sheet, so the queue/detail can show the badge + stamp (`By` added in
     *  production-records R0 — the record always carried it, the view used to drop it). */
    alreadyPrintedReason: AlreadyPrintedReasonEnum.optional(),
    alreadyPrintedAt: z.string().optional(),
    alreadyPrintedBy: z.string().optional(),
    /** Pulled-from-stock audit (set only when `workflowState === 'PULLED_FROM_STOCK'`) — when/who
     *  pulled the line's transfers off the shelf instead of reprinting (`By` added in R0, as above). */
    pulledFromStockAt: z.string().optional(),
    pulledFromStockBy: z.string().optional(),
    /** Non-DTF lane (plan v1). `productionClass` routes the line (NON_DTF → caps worklist); the caps
     *  lifecycle is `nonDtfWorkflowState` (OPEN → PRODUCED). Absent productionClass ⇒ DTF_APPAREL.
     *  `producedAt`/`producedBy` are the caps produce-audit (when / who), set at mark-produced. */
    productionClass: ProductionClassEnum.optional(),
    nonDtfWorkflowState: NonDtfWorkflowStateEnum.optional(),
    producedAt: z.string().optional(),
    producedBy: z.string().optional(),
    /** The BARE ssSku actually DRAWN when this cap was marked PRODUCED — the frozen draw identity the
     *  undo reverses against. The caps twin of {@link packedItemSlug}, and on the view for the same
     *  reason: **absent means nothing was drawn**, so a UI that tells an operator what an undo will put
     *  back cannot be honest without it. Absent for an unmapped cap, for a cap whose consume quantity
     *  was 0, and for every cap produced before consume-at-produce shipped (2026-07-30) — all three are
     *  designed states (`markLineProduced` explicitly `REMOVE`s the field when nothing was drawn), not
     *  data gaps. Added after a money review found the order card claiming a blank goes back on caps
     *  where none had been drawn, because the client had no way to know. */
    producedBlankSku: z.string().optional(),
    /** Ship-only lane (inventory-clarity P2): the pack lifecycle (OPEN → PACKED) for a
     *  FULFILLMENT_ONLY line, with its pack audit (when / who), set at mark-packed. `packedItemSlug`
     *  (P2b-C) is the stock item actually drawn at pack — frozen for the undo's restore. */
    fulfillWorkflowState: FulfillWorkflowStateEnum.optional(),
    packedAt: z.string().optional(),
    packedBy: z.string().optional(),
    packedItemSlug: z.string().optional(),
    /** Ship-from-stock (finished-garment-stock v1 PR-4): present iff the line was shipped from the
     *  garment shelf. `stockPickQty` renders the stamp ("shipped from the shelf · N taken");
     *  `stockPickId` presence is what routes the undo to the shelf-crediting door. */
    stockPickId: z.string().optional(),
    stockPickKey: z.string().optional(),
    stockPickQty: z.number().int().positive().optional(),
    /** The shelf item this line's variant maps to RIGHT NOW, joined live from the product's
     *  `classification.variantStockItems` by the order-detail route only. Distinct from
     *  {@link packedItemSlug}, which is the FROZEN item a past pack actually drew: this one answers
     *  "what would a pack draw if I pressed the button", that one answers "what did it draw".
     *
     *  ⚠️ **Absent means the product is not mapped, and therefore that a pack draws NOTHING** —
     *  `markLinePacked` only draws when a slug resolves. Measured 2026-08-10: 20 of the 21 recorded
     *  packs in prod drew nothing, because exactly one ship-only product has ever been mapped. A UI
     *  stating what a pack will cost has to read this rather than assume a draw.
     *
     *  `FulfillLineView` has carried its own copy of this field since P2b-B; it is on the base view now
     *  so the order card can answer the same question without the worklist's payload — which it cannot
     *  use anyway, since the worklist only lists UNFULFILLED lines. */
    stockItemSlug: StockItemSlugSchema.optional(),
    shopifyCreatedAt: z.string(),
});
/** API view of an order header. */
export const OrderSummarySchema = z.object({
    storeId: z.string(),
    orderId: z.string(),
    orderName: z.string(),
    financialStatus: z.string().nullable(),
    fulfillmentState: FulfillmentStateEnum,
    /** The observed ship moment (R2) — see OrderRecordSchema.fulfilledAt. Absent on orders fulfilled
     *  before R2 deployed (unrecoverable) and on orders not yet fulfilled. */
    fulfilledAt: z.string().optional(),
    shopifyCreatedAt: z.string(),
    shopifyUpdatedAt: z.string(),
});
/**
 * DTF-production rollup for one order, derived from its lines' workflow/mapping/fulfillment states
 * (plan § status). Headline `status` (worst-first precedence):
 *   NEEDS_ACTION  — a line is UNMAPPED / BLOCKED / PAYMENT_PENDING (can't be produced until resolved)
 *   IN_PRODUCTION — some DTF line is BATCHED, or a mix of done + still-open (incl. unproduced non-DTF) lines
 *   NOT_STARTED   — producible lines exist but all are still open (DTF OPEN and/or non-DTF unproduced)
 *   PRINTED       — every producible DTF line is PRINTED (production complete)
 *   PRODUCED      — every producible line was NON_DTF (e.g. caps) and is produced — done, no DTF work
 *   SHIPPED       — every producible line is SHIPPED or already fulfilled in Shopify
 *   CANCELLED     — the order has no producible line and at least one is cancelled
 *   NO_DTF        — the order has no producible line at all (all excluded / not-produced non-apparel)
 * A NON_DTF line (produced off the gang sheet) counts as outstanding work (`nonDtfOpen`) until it is
 * produced (`nonDtfProduced`, done), so an unproduced cap never lets an order read PRINTED/PRODUCED.
 * Fulfillment outranks workflow: a CANCELLED or FULFILLED line owes no production regardless of its
 * workflow state (a FULFILLED line shipped in Shopify — counted `fulfilled`, never as open work). The
 * counts are line tallies; a MAPPED, still-unfulfilled line is counted by its workflowState, else by
 * its category.
 */
export const OrderProductionStatusEnum = z.enum([
    'NOT_STARTED',
    'IN_PRODUCTION',
    'PRINTED',
    // Every producible line was NON_DTF (e.g. caps) and is produced — a done headline distinct from
    // PRINTED (which is the DTF terminal) and from NO_DTF (nothing to make). Non-DTF lane, plan v1.
    'PRODUCED',
    // Every producible line was FULFILLMENT_ONLY (ship-only stocked goods) and is packed — done, ready
    // to ship, nothing was produced. Ship-only lane, inventory-clarity P2.
    'PACKED',
    'SHIPPED',
    'NEEDS_ACTION',
    'CANCELLED',
    'NO_DTF',
]);
export const OrderProductionSchema = z.object({
    status: OrderProductionStatusEnum,
    totalLines: z.number().int().nonnegative(),
    /** MAPPED, still-unfulfilled lines by workflow state. */
    open: z.number().int().nonnegative(),
    batched: z.number().int().nonnegative(),
    printed: z.number().int().nonnegative(),
    shipped: z.number().int().nonnegative(),
    /** Marked already-printed off-system (e.g. a manual gang sheet) — production complete, no sheet owed. */
    alreadyPrinted: z.number().int().nonnegative(),
    /** Fulfilled in Shopify (any mapping/workflow) — shipped, no production owed. */
    fulfilled: z.number().int().nonnegative(),
    /** Cancelled (any mapping) — no production needed. */
    cancelled: z.number().int().nonnegative(),
    /** EXCLUDED (non-apparel, not produced — decals/stickers) — out of DTF scope. */
    nonDtf: z.number().int().nonnegative(),
    /** UNMAPPED / BLOCKED / PAYMENT_PENDING — can't be produced until resolved. */
    needsAction: z.number().int().nonnegative(),
    /** The three reasons `needsAction` fires, split out (production-UI PR-8). INVARIANT:
     *  `needsMapping + blocked + unpaid === needsAction` — they partition the same lines, they do not
     *  add to them. The board's "Needs action" lane held a bare count, so the one thing an operator
     *  needs to know before opening the card — whether this is a mapping job, a blocked product or an
     *  unpaid order, three different people's work — was only discoverable by opening it. */
    needsMapping: z.number().int().nonnegative(),
    blocked: z.number().int().nonnegative(),
    unpaid: z.number().int().nonnegative(),
    /**
     * GARMENTS, not lines, that this shop owes production on — done and not-yet-done together.
     *
     * Every other number on this rollup counts LINES, so "3 batched" could be 3 shirts or 300 and the
     * board could not tell an operator how big a job was. Σ per-line quantity over every line EXCEPT
     * CANCELLED (owes nothing) and EXCLUDED/`nonDtf` (decals and the like — real goods, but nothing this
     * shop produces, so counting them would inflate a *production* board). ⚠️ Caps and ship-only goods
     * therefore count only while their rollup flag is on — with `CAPS_LANE_ROLLUP` off, a cap falls to
     * the EXCLUDED branch and contributes nothing, exactly as its line counters do. Both flags are
     * 'true' in prod; this is the same dormant-flag behaviour as `nonDtfOpen` / `fulfillOpen`, not a
     * separate rule.
     *
     * ⚠️ Per-line quantity is `currentQty ?? unfulfilledQty` — see `OrderLineRecord.currentQty`. It has
     * to be `currentQty`: `unfulfilledQty` also nets out FULFILLED units, and measured against the live
     * ledger 859 of 987 lines read `unfulfilledQty: 0`, so a piece count built on it would print "0
     * pieces" on the shipped-but-unproduced cards that exist to be chased. The `??` fallback is a known
     * under-count on a row last written before `currentQty` shipped 2026-07-15 (measured: 2 of 987
     * lines, both on one fully-shipped order that no lane carries) — acceptable here because this
     * number is DISPLAYED, never spent. Do not copy the fallback into anything that authorises work:
     * `ResendLine.resendQty` deliberately refuses instead.
     */
    pieces: z.number().int().nonnegative(),
    /** Of `pieces`, the garments still to make: the open / batched / needs-action DTF work plus
     *  unproduced caps and unpacked ship-only goods. Always `<= pieces`. */
    outstandingPieces: z.number().int().nonnegative(),
    /** NON_DTF lines (e.g. caps) not yet produced — real outstanding work off the gang sheet. Non-DTF
     *  lane, plan v1; zero until Phase 4 stamps productionClass, so today's rollups are unchanged. */
    nonDtfOpen: z.number().int().nonnegative(),
    /** NON_DTF lines produced (done off the gang sheet). Counts toward "done" like printed/shipped. */
    nonDtfProduced: z.number().int().nonnegative(),
    /** FULFILLMENT_ONLY lines not yet packed — real outstanding ship-only work (inventory-clarity P2).
     *  Zero until the rollup flag is flipped AND a product is classified, so today's rollups hold. */
    fulfillOpen: z.number().int().nonnegative(),
    /** FULFILLMENT_ONLY lines packed (done — ready to ship). Counts toward "done" like produced. */
    fulfillPacked: z.number().int().nonnegative(),
});
/** `GET /orders/:storeId/:orderId` body: header + all its lines + the DTF-production rollup. */
export const OrderDetailSchema = z.object({
    order: OrderSummarySchema,
    lines: z.array(OrderLineViewSchema),
    production: OrderProductionSchema,
});
/**
 * `GET /orders/completed` — one row per TERMINAL order (no line still UNFULFILLED): the "review what
 * we did" list every worklist deliberately drops (production-records R0). Recently-shipped first:
 * sorted by `fulfilledAt` where stamped (R2, going-forward only), falling back to order creation for
 * the pre-R2 book. `production` is the same derived rollup the job card shows, so "shipped but never
 * marked printed" is visible right in the list. `titles`/`skus` exist for client-side search — the
 * whole book is small, so the server returns the full set with no cursor (the production board
 * already does this exact read).
 */
export const CompletedOrderViewSchema = z.object({
    storeId: z.string(),
    orderId: z.string(),
    orderName: z.string(),
    shopifyCreatedAt: z.string(),
    /** FULFILLED when anything shipped; CANCELLED when the whole order was cancelled. */
    fulfillmentState: FulfillmentStateEnum,
    lineCount: z.number().int().nonnegative(),
    /** Distinct garment titles (row preview + client-side search). */
    titles: z.array(z.string()),
    /** Distinct non-null SKUs (client-side search). */
    skus: z.array(z.string()),
    production: OrderProductionSchema,
    /**
     * Lines that SHIPPED before their production was ever recorded: FULFILLED but the DTF work never
     * reached a done state (still OPEN/BATCHED), the cap was never marked produced, or the ship-only
     * line was never marked packed. The rollup alone can't show this (fulfillment outranks workflow in
     * `production.status`), and it is exactly the silent gap the review lane exists to surface.
     */
    unrecordedLines: z.number().int().nonnegative(),
    /** The observed ship moment (R2), joined from the order header — see OrderRecordSchema.fulfilledAt.
     *  Absent for the pre-R2 book (no recoverable ship date) and for CANCELLED orders. */
    fulfilledAt: z.string().optional(),
});
export const CompletedOrdersResponseSchema = z.object({
    items: z.array(CompletedOrderViewSchema),
});
/**
 * Production event log (production-records R1) — the append-only who/when/what for every production
 * action, INCLUDING undos. This is the durable memory the state rows deliberately don't keep: an
 * undo erases its stamp and deletes its ledger draw (correct for money), and the event row is what
 * remains. Written fire-and-forget beside the primary write (an event failure never fails the money
 * write) and NEVER deleted — owner decision 2026-07-30: kept forever, no TTL.
 */
export const ProductionEventTypeEnum = z.enum([
    // Per-copy DTF press check-off (press station / scan kiosk) and its uncheck.
    'PRESS_COPY',
    'UNPRESS_COPY',
    // Whole-placement DTF press toggle (the order card's checkbox, no copyIndex).
    'PRESS_PLACEMENT',
    'UNPRESS_PLACEMENT',
    // Screen-print placement press / unpress.
    'SP_PRESS',
    'SP_UNPRESS',
    // Garment completed (settleLine: line → PRINTED, blank drawn) / reopened (draw reversed).
    'LINE_PRINTED',
    'LINE_REOPENED',
    // Whole-batch close-out / undo (R1.1: one event per affected ORDER, with the batchId; qty = the
    // lines this close bulk-settled / this undo reopened for that order — 0 means a pure status flip
    // over already-settled garments).
    'BATCH_PRINTED',
    'BATCH_UNPRINTED',
    // Whole-batch delete/unbuild (R1.1) — the sheet's rows and their press state are erased and its
    // lines return to the open queue (one event per affected ORDER; qty = lines reopened). This is
    // the close-out staff actually perform daily, and this event is what remains of it.
    'BATCH_DELETED',
    // Caps lane.
    'PRODUCED',
    'UNDO_PRODUCED',
    // Ship-only lane.
    'PACKED',
    'UNDO_PACKED',
    // Off-system print mark and undo.
    'ALREADY_PRINTED',
    'UNDO_ALREADY_PRINTED',
    // Printed-transfer shelf pull and undo.
    'PULLED_FROM_STOCK',
    'UNDO_PULLED_FROM_STOCK',
    // Finished-garment shelf: an order line shipped whole from the shelf, and its undo.
    'SHIPPED_FROM_STOCK',
    'UNDO_SHIPPED_FROM_STOCK',
    // A per-garment reprint (reissue batch) was created off this order's garment(s).
    'REISSUE_CREATED',
    // A garment was added to the shared reprint queue (one event per add, including a MERGE by a second
    // station — two stations reporting the same shirt is two real defect reports and both belong in the
    // history). This and the reissue event together are what finally COUNT reprints: before them the
    // reissue batch was the only trace, and the shop's daily close-out deletes batches.
    'REPRINT_QUEUED',
    // A queued garment was dismissed WITHOUT being reprinted. Requires a reason: removing it un-blocks
    // the order for shipping, so it is a money decision and has to be attributable.
    'REPRINT_QUEUE_REMOVED',
    // The queued garment's reprint sheet was marked printed, so its row left the queue and stopped
    // blocking the order (P5). The happy end of a reprint, and deliberately NOT the same event as a
    // dismissal — /activity's reprint lens would otherwise read every completed reprint as someone
    // deciding not to do it.
    'REPRINT_RETIRED',
    // A flushed garment came BACK to the queue because its reprint sheet was deleted/unbuilt (P5). The
    // row is outstanding again and the order is blocked again — a silent resurrection is as bad as a
    // silent drop, so it gets a row of its own.
    'REPRINT_RETURNED',
    // The order's Shopify fulfillment flipped to FULFILLED — the observed ship moment (R2), stamped
    // beside the header's once-only `fulfilledAt`. Order-scoped (no lineId); actor is 'shopify' (the
    // transition arrives via webhook/reconcile ingest, not a signed-in user).
    'ORDER_FULFILLED',
    // ── Shipping station (P5 S2) ──
    // A garment verified into the box: one event per QR scan (qty 1) or per eye-check entry (qty =
    // the count entered; reason carries why the unit had no scannable tag — reissue / ship-only).
    'SHIP_VERIFIED',
    // The order's pack verification was cleared (re-pack / mis-scan recovery). qty = rows removed.
    'SHIP_VERIFY_RESET',
    // A manager bought the label past an incomplete verification or an outstanding reprint. Requires
    // a reason: it is the loud trace of the gate being bypassed, next to the LABEL_BOUGHT it enabled.
    'SHIP_GATE_OVERRIDDEN',
    // Postage purchased (the money moment). reason carries carrier/service/cost for the timeline.
    'LABEL_BOUGHT',
    // Postage refund requested for a bought label (USPS refunds settle asynchronously, days–weeks).
    'LABEL_VOIDED',
    // Shopify fulfillment created by the ship station (tracking pushed; Shopify emails the customer).
    'FULFILLMENT_CREATED',
]);
/** DDB row: `PK=ORDER#<storeId>#<orderId>`, `SK=EVT#<at>#<id>` — co-located with the order's lines
 *  so the job-card timeline is one query. `GSI1PK='PROD_EVENTS'` is the shop-wide feed (R2). */
export const ProductionEventRecordSchema = z.object({
    PK: z.string(),
    SK: z.string(),
    GSI1PK: z.literal('PROD_EVENTS'),
    GSI1SK: z.string(),
    entity: z.literal('productionEvent'),
    id: z.string(),
    type: ProductionEventTypeEnum,
    at: z.string(),
    /** Who did it — the signed-in user's email (or sub). The press seams never recorded this before. */
    actor: z.string(),
    storeId: z.string(),
    orderId: z.string(),
    lineId: z.string().optional(),
    batchId: z.string().optional(),
    location: z.string().optional(),
    /** 0-based copy index for per-copy press events. */
    copyIndex: z.number().int().nonnegative().optional(),
    /** Units moved for money-adjacent events (LINE_PRINTED / PRODUCED / PACKED draws). */
    qty: z.number().int().optional(),
    /** The blank the event drew/restored (settle, caps produce). */
    blankSku: z.string().optional(),
    /** The stock item the event drew/restored (ship-only pack). */
    itemSlug: z.string().optional(),
    /** Freeform context (e.g. the off-system reason, a reissue's source batch). */
    reason: z.string().optional(),
});
/** API view: the record minus DDB keys. */
export const ProductionEventViewSchema = ProductionEventRecordSchema.omit({
    PK: true,
    SK: true,
    GSI1PK: true,
    GSI1SK: true,
    entity: true,
});
/** `GET /orders/:storeId/:orderId/events` — the order's production history, newest first. */
export const OrderEventsResponseSchema = z.object({
    events: z.array(ProductionEventViewSchema),
});
/** One row of the shop-wide activity feed (R2): a production event plus `orderName`, joined from the
 *  order header at read time (events deliberately don't denormalize it — a rename propagates free).
 *  Absent only if the header vanished (never observed in practice; the row still renders by id). */
export const ActivityEventViewSchema = ProductionEventViewSchema.extend({
    orderName: z.string().optional(),
});
/** `GET /orders/activity` — one page of the shop-wide production event feed (the `PROD_EVENTS` GSI1
 *  partition), newest first. `nextCursor` is opaque (base64url of the DDB key) — echo it back
 *  verbatim to page older; absent on the last page. */
export const ActivityFeedResponseSchema = z.object({
    events: z.array(ActivityEventViewSchema),
    nextCursor: z.string().optional(),
});
/**
 * `POST /orders/:storeId/:orderId/lines/:lineId/already-printed` body. `alreadyPrinted:true` moves the
 * line OPEN → ALREADY_PRINTED (off the gang sheet); `false` undoes it back to OPEN. `reason` is audit
 * only (optional) — it drives no inventory movement. Rejected server-side unless the line is currently
 * OPEN (to mark) / ALREADY_PRINTED (to undo).
 */
export const MarkPrintedOffSystemRequestSchema = z.object({
    alreadyPrinted: z.boolean(),
    reason: AlreadyPrintedReasonEnum.optional(),
});
/**
 * Caps (non-DTF) worklist mark-produced toggle. `produced: true` marks a NON_DTF line
 * OPEN → PRODUCED (dropping it off the caps worklist); `produced: false` is the undo
 * (PRODUCED → OPEN, re-queued while the line is still unfulfilled). OFF the money path — a cap
 * consumes NO tee/hoodie blank — so the one boolean serves both directions (the
 * MarkPrintedOffSystem / PullFromStock idiom). Rejected server-side unless the line is
 * `productionClass === 'NON_DTF'` and in the matching state (OPEN to mark / PRODUCED to undo).
 */
export const MarkProducedRequestSchema = z.object({
    produced: z.boolean(),
});
/**
 * `POST /fulfill/:storeId/:orderId/lines/:lineId/packed` body — the Ship-only pack toggle
 * (inventory-clarity P2). `packed:true` moves the line OPEN → PACKED (off the Ship-only worklist);
 * `false` undoes it back to OPEN. Whole-line, idempotent, reversible — mirrors the caps produce
 * toggle. v1 writes NO stock movement; P2b's consume-at-fulfill will key off this same event.
 */
export const MarkPackedRequestSchema = z.object({
    packed: z.boolean(),
});
/**
 * One Ship-only worklist line (P2b-B): the order-line view PLUS its shelf-stock resolution, joined
 * LIVE at read time from the product's classification (`variantStockItems`) — order lines carry no
 * stamp, so a mapping edit shows here immediately. `needsItemMapping` is the honest "this line has
 * no shelf item to draw from" flag (the UNMAPPED-blanks pattern — never silently wrong).
 */
export const FulfillLineViewSchema = OrderLineViewSchema.extend({
    stockItemSlug: StockItemSlugSchema.optional(),
    /** The mapped item's display name (joined from the item META at read). */
    stockItemName: z.string().optional(),
    needsItemMapping: z.boolean(),
});
/**
 * One order on the production / WIP board: its DTF-production rollup plus the identity + priority
 * fields a card needs to render and sort. Derived from the order's lines (see `deriveOrderProduction`).
 * `batchIds` are the distinct gang-sheet batches this order's BATCHED/PRINTED lines belong to, so the
 * operator can jump to the sheet. `rush`/`dueDate` are order-scoped (see OrderLineRecord), aggregated
 * across the order's lines (any line rush → rush; the earliest non-null due date).
 */
// Batch lifecycle. Declared HERE rather than beside the batch schemas below because
// `ProductionBoardSheetSchema` (PR-9) needs it and `const` declarations are not hoisted — a `z.enum`
// is a leaf with no dependencies of its own, so the earlier position costs nothing.
export const BatchStatusEnum = z.enum([
    'DRAFT',
    'COMPOSITING',
    'READY',
    'PRINTED',
    'FAILED',
    'SHIPPED',
]);
/**
 * A gang sheet this order's work sits on, named well enough for the board card to LINK to it (PR-9).
 *
 * `batchIds` alone are raw UUIDs, so the card could only ever say "2 sheets" — a dead string. The
 * operator's actual question at that moment is "which sheet, and has it printed yet?", and answering it
 * meant opening the order, reading a batch id, and searching `/batches`.
 *
 * ⚠️ A THIN PROJECTION, deliberately — batchId + seq + status + createdAt and nothing else. The board
 * renders one line per sheet on a card in a narrow lane; anything more (piece counts, density, the
 * screen-print worklist) is a `/batches` question, and widening this would put a second, drifting copy
 * of BatchSummary on a surface that only needs to name a link.
 */
export const ProductionBoardSheetSchema = z.object({
    batchId: z.string(),
    /** The human "Sheet #N". Optional because BatchSummary's is — a batch predating the seq counter has
     *  none, and the card falls back to a short id. (Measured 2026-08-09: all 22 live batches carry one.) */
    seq: z.number().int().positive().optional(),
    status: BatchStatusEnum,
    createdAt: z.string(),
});
export const ProductionBoardOrderSchema = z.object({
    storeId: z.string(),
    /** The store's human name ("Low Trendz"), joined from the store record. `/batches`, `/press`, the
     *  pick-list run sheet and `/catalog` all show this; the board was the one surface still printing the
     *  raw slug `low-trendz` at an operator. OPTIONAL and fail-open: the join is display garnish, so an
     *  unknown store (or a store-read blip) falls back to `storeId` rather than failing the board. */
    brandLabel: z.string().optional(),
    orderId: z.string(),
    orderName: z.string(),
    shopifyCreatedAt: z.string(),
    dueDate: z.string().nullable().optional(),
    rush: z.boolean().optional(),
    production: OrderProductionSchema,
    batchIds: z.array(z.string()),
    /**
     * The same sheets as `batchIds`, named (PR-9). OPTIONAL and fail-open: the join is one bounded batch
     * read, and if it fails the card falls back to the "N sheets" count it shows today rather than the
     * board failing to load.
     *
     * ⚠️ NOT a replacement for `batchIds`, which stays the source of truth for WHICH sheets an order
     * touches. A sheet the lookup could not resolve (deleted mid-read, or a read that partially failed) is
     * simply absent here, so `sheets.length <= batchIds.length` and the card must never derive its sheet
     * COUNT from this array — that would silently under-report a sheet the operator still has to find.
     */
    sheets: z.array(ProductionBoardSheetSchema).optional(),
    /** How many of this order's lines were IN PRODUCTION (BATCHED — claimed onto a gang sheet) when Shopify
     *  marked them FULFILLED (shipped), before they were marked printed. >0 ⇒ an in-production job that
     *  shipped unfinished; the order is listed in the board's `shippedUnproduced` lane. Normally 0. */
    shippedUnproducedLines: z.number().int().nonnegative(),
    /** How many OUTSTANDING reprints (reissue/reprint batches not yet marked printed) touch this order. A
     *  reissue never repoints `line.batchId`, so a reprinted garment is otherwise invisible here — an order
     *  could read "printed · to ship" while a reprint is still on the press. >0 ⇒ don't ship yet: the order
     *  is DEMOTED out of the ship lanes (see the response docblock) AND badged. Normally 0. (Rollo Phase 4;
     *  the demotion is decision D4 / §8.25.) */
    reprintOutstanding: z.number().int().nonnegative(),
});
/**
 * `GET /production/board` data — every order with outstanding or in-flight DTF production, laned by
 * its headline production status (see {@link OrderProductionStatusEnum}):
 *   needsAction  ← NEEDS_ACTION   queued ← NOT_STARTED
 *   inProduction ← IN_PRODUCTION  printed ← PRINTED  readyToShip ← PRODUCED *and* PACKED
 * `readyToShip` holds BOTH all-caps orders produced off the gang sheet (status PRODUCED) and ship-only
 * orders whose stocked goods are all packed (status PACKED) — the two analogs of `printed`, staying on
 * the board until Shopify ships them. ⚠️ This docblock used to name only the PRODUCED arm, which is why
 * the dashboard lane was titled "Caps · to ship" while `SHIP_ONLY_ROLLUP='true'` was quietly routing
 * packed decal orders into it. The lane is now "Ready to ship"; keep both arms named here.
 * Terminal orders (SHIPPED / CANCELLED / NO_DTF) are omitted from the lanes, so an order drops off
 * the moment its lines fulfil in Shopify (the derivation reads SHIPPED and it's excluded). EXCEPTION —
 * `shippedUnproduced`: orders Shopify fulfilled while a line was still IN PRODUCTION (BATCHED, not yet
 * marked printed) would otherwise vanish entirely, so they're surfaced here as a warning. It is ADDITIVE —
 * a partially-shipped order also appears in its normal lane. Each lane is sorted rush ↓, dueDate ↑, then
 * FIFO — the same ordering as the open-work queue. `totalOrders` is the pipeline sum of the five active
 * lanes (needsAction + queued + inProduction + printed + readyToShip); it does NOT include
 * `shippedUnproduced`.
 *
 * ⚠️ ONE EXCEPTION to "lane = status" (decision D4 / §8.25): an order with `reprintOutstanding > 0` is
 * DEMOTED out of the two ship lanes (`printed`, `readyToShip`) into `inProduction`, because a reprint on
 * the press means the order is not shippable no matter what its lines roll up to. `totalOrders` is
 * unaffected — a demotion moves an order between two of the five summed lanes.
 */
export const ProductionBoardResponseSchema = z.object({
    needsAction: z.array(ProductionBoardOrderSchema),
    queued: z.array(ProductionBoardOrderSchema),
    inProduction: z.array(ProductionBoardOrderSchema),
    printed: z.array(ProductionBoardOrderSchema),
    readyToShip: z.array(ProductionBoardOrderSchema),
    shippedUnproduced: z.array(ProductionBoardOrderSchema),
    totalOrders: z.number().int().nonnegative(),
    /** The outstanding-reprint lookup did NOT run (a batch-store failure the board deliberately survives —
     *  see the route). ⚠️ Then every `reprintOutstanding` is 0 because it is UNKNOWN, not because it is
     *  zero, so no order could be demoted and the ship lanes are NOT trustworthy. The board must say so
     *  rather than present a green lane it cannot stand behind — "unknown" is not "safe". Normally false. */
    reprintCheckFailed: z.boolean(),
});
// ── Shopify webhook payloads (parsed at the receiver/worker boundary; NOT in OpenAPI) ──
/**
 * REST-shaped webhook line item. Shopify uses numeric ids and has NO
 * `unfulfilledQuantity` — the demand analog is `fulfillable_quantity` (falls back to
 * `current_quantity`/`quantity` only when absent). `variant_id` is null for deleted
 * products / custom one-off lines → those lines are auto-flagged BLOCKED, never crash.
 * `.passthrough()` so Shopify adding fields never breaks parsing.
 */
export const ShopifyLineItemWebhookSchema = z
    .object({
    id: z.number(),
    variant_id: z.number().nullable(),
    product_id: z.number().nullable().optional(),
    sku: z.string().nullable().optional(),
    quantity: z.number().int(),
    fulfillable_quantity: z.number().int().nonnegative().optional(),
    current_quantity: z.number().int().nonnegative().optional(),
    title: z.string(),
    name: z.string().optional(),
    variant_title: z.string().nullable().optional(),
})
    .passthrough();
/** REST-shaped order webhook payload (orders/create|updated|cancelled|fulfilled). */
export const ShopifyOrderWebhookSchema = z
    .object({
    id: z.number(),
    admin_graphql_api_id: z.string().optional(),
    name: z.string(),
    financial_status: z.string().nullable().optional(),
    fulfillment_status: z.string().nullable().optional(),
    cancelled_at: z.string().nullable().optional(),
    created_at: z.string(),
    updated_at: z.string(),
    // Queue prioritization (allow-listed only): REST webhook `tags` is a COMMA-SEPARATED STRING;
    // `note_attributes` is [{name,value}]. The free-text `note` is deliberately NOT typed/read — PII.
    tags: z.string().optional(),
    note_attributes: z
        .array(z.object({ name: z.string(), value: z.string().nullable().optional() }))
        .optional(),
    line_items: z.array(ShopifyLineItemWebhookSchema),
})
    .passthrough();
/** The Shopify webhook headers the receiver reads (API Gateway lowercases header names). */
export const ShopifyWebhookHeadersSchema = z
    .object({
    'x-shopify-topic': z.string(),
    'x-shopify-shop-domain': z.string(),
    'x-shopify-hmac-sha256': z.string(),
    'x-shopify-webhook-id': z.string(),
})
    .passthrough();
/** SQS message body the receiver enqueues for the order-ingest worker. */
export const ShopifyIngestMessageSchema = z.object({
    storeId: z.string(),
    topic: z.string(),
    webhookId: z.string(),
    shopDomain: z.string(),
    receivedAt: z.string(),
    payload: ShopifyOrderWebhookSchema,
});
// ═════════════════════════════════════════════════════════════════
// Shopify → DTF · Phase S2: design library + SKU→blank mapping.
// A Design = a print-ready PNG in S3 + per-placement print geometry. A
// Mapping binds (storeId, variantId) → a Design placement + a resolved S&S
// blank. A line is batchable (mappingStatus=MAPPED) only when its variant has
// a CONFIRMED mapping whose blank identity RESOLVED. See plan § 10 / § 10.1.
// NOTE (scope guard § 10): no `printMethod` field — S2 is DTF-only.
// ═════════════════════════════════════════════════════════════════
// ── Design library ──
/**
 * Per-file validation lifecycle. `PENDING` until the uploaded PNG is validated;
 * the design-validate Lambda sets `READY` (transparent RGBA PNG) or `INVALID`
 * (flattened / non-PNG). The validator owns this; `POST /designs` never writes it.
 */
export const DesignStatusEnum = z.enum(['PENDING', 'READY', 'INVALID']);
/**
 * One uploaded design file (one size band). Print size is derived from the PIXEL dimensions at a
 * fixed 300 DPI — 1 print inch = 300 px — because halftones cannot be resampled: the compositor
 * places the file 1:1 and NEVER scales it (catalog-mapping v1.1). The artist authors each file at
 * its exact print size (e.g. a 12″ back print = a 3600 px file). `densityDpi` is captured for info
 * only; it is NOT used for layout. The validate worker owns every field here.
 */
export const DesignFileSchema = z.object({
    status: DesignStatusEnum,
    transparentBgValidated: z.boolean(),
    widthPx: z.number().int().positive().nullable().optional(),
    heightPx: z.number().int().positive().nullable().optional(),
    densityDpi: z.number().positive().nullable().optional(),
    channels: z.number().int().nullable().optional(),
    format: z.string().nullable().optional(),
    validationError: z.string().nullable().optional(),
});
/**
 * A design's short SKU-style code, mirroring the code the shop uses in the Shopify SKU: fronts are
 * `FR` / `FW` / `LC` (red / white front, left-chest), backs the full `XX-NNN` form (e.g. `LT-001`,
 * `XX` = the design's home store). Deliberately LOOSE — up to 12 uppercase letters, digits, or dashes —
 * so both shapes pass and the convention can evolve; NOT enforced-unique (every store has its own `FR`)
 * and never auto-numbered (owner-managed). Display/lookup metadata only — off the money path.
 * (Named `styleCode`, NOT `style`, which is the S&S blank garment style number.)
 */
export const DESIGN_STYLE_CODE_RE = /^[A-Z0-9-]{1,12}$/;
export const DesignStyleCodeSchema = z
    .string()
    .regex(DESIGN_STYLE_CODE_RE, 'Up to 12 uppercase letters, digits, or dashes (e.g. FR, LT-001).');
/**
 * A staff-raised flag that a design's film prints badly (a new / untested design, bad registration,
 * low-res art, …). Any signed-in staff sets it (POST /designs/:id/problem); the designer sees it in the
 * library, replaces the file, and resolves it (DELETE .../problem). PRODUCTION GATE: while set, the design
 * is BLOCKED from batch build, gang-sheet generate, and reissue (422 batch_blocked) — so a known-bad film
 * can't be printed until the flag is resolved.
 */
export const DesignProblemSchema = z.object({
    note: z.string().min(1).max(500),
    flaggedAt: z.string(),
    /** Who flagged it (Cognito email, else sub) — so the designer knows who to ask. */
    flaggedBy: z.string().optional(),
});
/**
 * How far a design's alignment box may sit outside its file before the box is nonsense. Fractions come
 * from a UI that snaps to ⅛ in, so an exact 1.0 is normal and float error is the only thing to absorb.
 */
const ALIGNMENT_EPSILON = 1e-9;
const alignmentGeometryShape = {
    /** Left edge of the box, as a fraction of the file's WIDTH. 0 = the file's left edge. */
    x: z.number().min(0).max(1),
    /** Top edge, as a fraction of the file's HEIGHT. */
    y: z.number().min(0).max(1),
    /** Box width as a fraction of the file's width. 1 = the whole file (the unset default). */
    w: z.number().gt(0).max(1),
    /** Box height as a fraction of the file's height. */
    h: z.number().gt(0).max(1),
};
const alignmentBoxShape = {
    ...alignmentGeometryShape,
    /** The raster the box was marked against — the staleness signal after a band is re-uploaded. */
    forWidthPx: z.number().int().positive().optional(),
    forHeightPx: z.number().int().positive().optional(),
};
const boxIsInsideFile = (a) => a.x + a.w <= 1 + ALIGNMENT_EPSILON && a.y + a.h <= 1 + ALIGNMENT_EPSILON;
const INSIDE_FILE_MESSAGE = 'The alignment box must sit inside the file.';
/**
 * Just the four fractions — what a CONSUMER of an alignment box needs to place a design. The scan
 * station gets this rather than the stored record: it has no use for who marked the box or when, and
 * `setBy` is an operator's email, which has no business on a kiosk that anyone on the floor can see.
 */
export const AlignmentGeometrySchema = z
    .object(alignmentGeometryShape)
    .refine(boxIsInsideFile, { message: INSIDE_FILE_MESSAGE });
/**
 * The wire shape a client sends: the box only. `setAt`/`setBy` are stamped by the server, never
 * accepted from the caller.
 */
export const DesignAlignmentBoxSchema = z
    .object(alignmentBoxShape)
    .refine(boxIsInsideFile, { message: INSIDE_FILE_MESSAGE });
/**
 * **The part of a design that the press guides line up with** (docs/design-alignment-plan-v1.md § 3.1).
 *
 * Design files are trimmed to ink, so a satellite element — a logo in a corner, a vertical wordmark down
 * one edge — stretches the file's bounding box sideways and drags the artwork off the shirt's centre
 * line when the press centres that box. This rectangle names the part that counts instead; the laser
 * anchors resolve against it rather than the whole file.
 *
 * Stored as FRACTIONS of the file box so one set of numbers works against the 300-DPI print PNG, the
 * downscaled preview WebP, and the frozen print inches on a batch item alike. **Absent is the identity
 * case** — `{x: 0, y: 0, w: 1, h: 1}` — which reproduces today's behaviour exactly, so a design without
 * one presses the way it always has.
 */
export const DesignAlignmentSchema = z
    .object({
    ...alignmentBoxShape,
    setAt: z.string(),
    /** Who marked it (Cognito email, else sub) — so a questionable box has someone to ask. */
    setBy: z.string().optional(),
})
    .refine(boxIsInsideFile, { message: INSIDE_FILE_MESSAGE });
/** Per-band alignment, as the API returns it. Bands are separate rasters, so each carries its own box. */
export const DesignAlignmentMapSchema = z.object({
    adult: DesignAlignmentSchema.optional(),
    youth: DesignAlignmentSchema.optional(),
});
// ── Screen-print spec — the technical sheet a design carries for the screen room ──
// (docs/screen-print-station-audit-and-plan-v1.md § 3.1, § 3.6). A property of the ART: typed once per
// design, reused by every run that prints it.
/** A CSS-style hex colour, `#rrggbb` — the ink's RENDER colour behind the Pantone name. */
export const HexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'A hex colour like #1E4FD8.');
/**
 * One SCREEN = one colour separation of a design, in print order.
 *
 * `head` is the STATION the screen sits on when the run goes on an automatic (print order IS head
 * order there); `flashAfter` is the manual-press notion (flash-cure before the next colour). Both are
 * optional so a spec typed for one press still reads on the other. `hex` exists because a Pantone
 * NAME cannot paint a proof or tint a carousel tile. `separationKey` (P4b) is this colour's FILM from
 * the RIP — an S3 key, absent until uploaded.
 */
export const ScreenSpecSchema = z.object({
    /** Print order, 1-based. The underbase is 1. */
    seq: z.number().int().positive(),
    /** What the shop calls it — "Underbase", "Glow green", "Black". */
    name: z.string().min(1).max(60),
    pantone: z.string().max(40).optional(),
    /** Ink system + mix recipe, free text: "Wilflex Epic Glow, 3:1 with Glow Base, 2 strokes". */
    ink: z.string().max(200).optional(),
    hex: HexColorSchema.optional(),
    /** Mesh count (threads / in). 110 · 156 · 200 · 230 · 305 are the common ones. */
    meshCount: z.number().int().min(20).max(500).optional(),
    /** Frame, free text — "23 × 31 al". */
    frame: z.string().max(40).optional(),
    flashAfter: z.boolean().optional(),
    head: z.number().int().min(1).max(PRESS_MAX_STATIONS).optional(),
    separationKey: z.string().optional(),
});
const screenPrintSpecShape = {
    /**
     * The screens, in print order. Capped at the longest press the model allows — a fact about a
     * machine, never a pricing limit (pricing is being reworked; production does not inherit its shape).
     */
    screens: z.array(ScreenSpecSchema).min(1).max(PRESS_MAX_STATIONS),
    cureTempF: z.number().int().min(200).max(450).optional(),
    cureSeconds: z.number().int().min(5).max(600).optional(),
    /** Off-contact, squeegee, strokes — anything the operator needs at the press that is not a number above. */
    pressNotes: z.string().max(1000).optional(),
    /** Which press the head numbers were assigned for (a `PressRecord.pressId`); absent = the shop default. */
    pressId: z.string().max(80).optional(),
    /** Tier-1 proof — Separation Studio's Proof Positive export, uploaded (P2). An S3 key. */
    proofImageKey: z.string().optional(),
};
/** No two screens may share a print-order number — a duplicate `seq` is a typo that would hide a burn. */
const screensHaveDistinctSeq = (s) => new Set(s.screens.map((x) => x.seq)).size === s.screens.length;
const DISTINCT_SEQ_MESSAGE = 'Each screen needs its own print-order number.';
/** The wire shape a client sends: the spec only. `setAt` / `setBy` are stamped by the server. */
export const DesignScreenPrintSpecBoxSchema = z
    .object(screenPrintSpecShape)
    .refine(screensHaveDistinctSeq, { message: DISTINCT_SEQ_MESSAGE });
/**
 * The stored spec. Lives as a TOP-LEVEL attribute `screenPrintSpec` on `DESIGN#<id>` / `META` — never
 * nested inside `adult` / `youth`, which `applyValidation` rewrites wholesale on every re-upload (the
 * rule that put `alignmentAdult` at the top level). Absent = no spec: the packet warns, never refuses.
 */
export const DesignScreenPrintSpecSchema = z
    .object({
    ...screenPrintSpecShape,
    setAt: z.string(),
    /** Who typed it (Cognito email, else sub) — so a questionable recipe has someone to ask. */
    setBy: z.string().optional(),
})
    .refine(screensHaveDistinctSeq, { message: DISTINCT_SEQ_MESSAGE });
/**
 * DDB record: `DESIGN#<designId>` / `META`. GSI1 (`DESIGNS` / `NAME#<lowername>`) lists + name-sorts
 * the library. A design holds an `adult` file (required) and an optional `youth` file — each is a
 * SEPARATE pre-rasterized PNG (youth is never the adult art scaled down). `POST /designs` seeds the
 * PENDING slots (name + hasYouth); the design-validate Lambda writes each band's file fields
 * independently after its S3 upload (`designs/<id>-<band>.png`).
 */
export const DesignRecordSchema = z.object({
    PK: z.string(),
    SK: z.literal('META'),
    GSI1PK: z.literal('DESIGNS'),
    GSI1SK: z.string(),
    entity: z.literal('design'),
    designId: z.string(),
    name: z.string(),
    // Optional short SKU-style code (see DesignStyleCodeSchema). Absent on designs created before the field.
    styleCode: DesignStyleCodeSchema.optional(),
    // Optional staff-raised "this film prints badly" flag (blocks batching until resolved); absent when none.
    problem: DesignProblemSchema.optional(),
    adult: DesignFileSchema,
    youth: DesignFileSchema.optional(),
    // Alignment lives at the TOP LEVEL, one attribute per band — never inside `adult`/`youth`, which
    // `applyValidation` overwrites wholesale on every re-upload and would silently wipe a nested box.
    // Two attributes rather than one nested map so each band is an independent attribute-scoped SET,
    // which also works on rows created before the field existed.
    alignmentAdult: DesignAlignmentSchema.optional(),
    alignmentYouth: DesignAlignmentSchema.optional(),
    // The screen room's technical sheet (§ 3.1). Top-level for the same reason as the alignment boxes:
    // an attribute-scoped SET/REMOVE that a band re-upload can never wipe. Absent on every design that
    // predates the field and on every DTF-only design.
    screenPrintSpec: DesignScreenPrintSpecSchema.optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    /**
     * When a band FILE last landed — written ONLY by `applyValidation` (the design-validate worker, on
     * every `designs/*` Object Created), never by `update` (name / styleCode / problem / alignment /
     * spec edits) or `markBandPending`. `updatedAt` moves on all of those, which made it useless as an
     * "art changed" signal (a 2026-09 alignment backfill flipped the shelf's art-drift badge on every
     * bin). This is the art-only sibling the garment-stock sentinel freezes and compares. Absent on
     * every design whose art has not been re-uploaded since the field shipped (2026-09-20).
     */
    artUpdatedAt: z.string().optional(),
});
/** `POST /designs` body — create the record + PENDING slots. Files are uploaded separately per band. */
export const DesignCreateRequestSchema = z.object({
    name: z.string().min(1).max(120),
    // Optional short SKU-style code (fronts FR/FW/LC, backs LT-001). Absent → set later via PATCH.
    styleCode: DesignStyleCodeSchema.optional(),
    /**
     * Per-band alignment box, marked in the browser against the File it is about to upload (the preview
     * WebP does not exist yet at this point — a worker renders it after the S3 upload). Carried on the
     * create so a design never exists in an unaligned state. Absent = the whole file = today's behaviour.
     */
    alignment: z
        .object({
        adult: DesignAlignmentBoxSchema.optional(),
        youth: DesignAlignmentBoxSchema.optional(),
    })
        .optional(),
    // Declares that a separate youth-size file will also be uploaded (its own pre-rasterized art).
    // When true a PENDING youth slot is seeded; a youth order line can only batch once that file
    // validates READY. Adult-only designs (false) block any youth line that references them.
    hasYouth: z.boolean().optional().default(false),
});
/**
 * `PATCH /designs/:designId` body — edit a design's name and/or style code (at least one required).
 * Setting `name` also rewrites the name-sorted GSI key (`GSI1SK = NAME#<lowername>`) so the library
 * stays A→Z. A `styleCode` of `""` CLEARS the code; a non-empty value must match the style-code shape.
 */
export const DesignUpdateRequestSchema = z
    .object({
    name: z.string().min(1).max(120).optional(),
    styleCode: z.union([DesignStyleCodeSchema, z.literal('')]).optional(),
    /**
     * Per-band alignment. A box SETS that band's alignment; `null` CLEARS it (back to the whole file,
     * i.e. today's behaviour); an absent band is left alone. Used by the library's re-align action and
     * by the backfill, which works down the ranked worklist.
     */
    alignment: z
        .object({
        adult: DesignAlignmentBoxSchema.nullable().optional(),
        youth: DesignAlignmentBoxSchema.nullable().optional(),
    })
        .optional(),
    /**
     * The screen-print spec. A box SETS it (the server stamps `setAt` / `setBy`); `null` CLEARS it;
     * absent leaves it alone. Same three-way shape as `alignment`.
     */
    screenPrintSpec: DesignScreenPrintSpecBoxSchema.nullable().optional(),
})
    .refine((v) => v.name !== undefined ||
    v.styleCode !== undefined ||
    v.alignment !== undefined ||
    v.screenPrintSpec !== undefined, {
    message: 'Provide a name, a style code, an alignment and/or a screen-print spec to update.',
});
/**
 * `POST /designs/:designId/problem` body — flag (or re-note) a problem with a design's film. Setting a
 * problem BLOCKS the design from being batched until it's resolved (DELETE .../problem).
 */
export const DesignFlagProblemRequestSchema = z.object({
    note: z.string().min(1).max(500),
});
/** `POST /designs/upload-url` body — mint a presigned S3 POST for one band's PNG of an existing design. */
export const DesignUploadUrlRequestSchema = z.object({
    designId: z.string().min(1).max(80),
    band: SizeBandEnum.default('adult'),
    contentType: z.literal('image/png').optional(),
});
/**
 * `POST /designs/upload-url` response — the presigned POST envelope. The browser
 * POSTs a multipart form with these `fields` + the file to `url`. The S3 policy
 * constrains `Content-Type=image/png` and a max byte size, so oversized / non-PNG
 * objects are rejected at S3 before the validate Lambda ever runs (§ 10).
 */
export const DesignUploadUrlResponseSchema = z.object({
    designId: z.string(),
    band: SizeBandEnum,
    key: z.string(),
    url: z.string().url(),
    fields: z.record(z.string(), z.string()),
    maxBytes: z.number().int().positive(),
    expiresInSeconds: z.number().int().positive(),
});
/**
 * API view of one band's file — adds the derived print size (px ÷ 300) + two presigned GETs:
 * `previewUrl` (a lightweight downscaled WebP the thumbnail worker renders — the browser loads
 * this, not the heavy print PNG) and `originalUrl` (the full-res print PNG, for a "view full-res"
 * link). `previewUrl` may 404 briefly right after upload / for a design uploaded before the
 * thumbnail worker existed; the UI falls back to `originalUrl` on error.
 */
export const DesignFileViewSchema = DesignFileSchema.extend({
    previewUrl: z.string().url().optional(),
    originalUrl: z.string().url().optional(),
    /**
     * The same full-res PNG as `originalUrl`, presigned as an ATTACHMENT with a meaningful filename
     * (`<styleCode ?? name>-<band>-<W>x<H>in.png`) so "Download original" saves a file the screen room
     * can find, not `<uuid>-adult.png` opened inline in a tab. READY bands only, like the other two.
     */
    downloadUrl: z.string().url().optional(),
    printWidthIn: z.number().positive().nullable().optional(),
    printHeightIn: z.number().positive().nullable().optional(),
});
/** API view of a design (drops DDB keys; per-band file views carry the presigned GET + print size). */
export const DesignSummarySchema = z.object({
    designId: z.string(),
    name: z.string(),
    styleCode: DesignStyleCodeSchema.optional(),
    problem: DesignProblemSchema.optional(),
    adult: DesignFileViewSchema,
    youth: DesignFileViewSchema.optional(),
    /** Per-band alignment box, nested for the client even though the record stores one attribute each. */
    alignment: DesignAlignmentMapSchema.optional(),
    /** The screen room's technical sheet; absent when none has been typed. */
    screenPrintSpec: DesignScreenPrintSpecSchema.optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// ── Blank resolution ──
/**
 * Whether a variant's free-text blank (style/color/size) resolved to a canonical S&S
 * identity. `RESOLVED` carries a `resolvedBlankSku` (`BLANK#<ssSku>`); `UNRESOLVED` /
 * `AMBIGUOUS` leave the variant unbatchable. Used by the catalog variant record + the
 * product-mapping blank pre-resolution (catalog-mapping v1).
 */
export const BlankResolveStatusEnum = z.enum(['UNRESOLVED', 'RESOLVED', 'AMBIGUOUS']);
// ── Catalog-driven product mapping (catalog-mapping v1 — replaces the S2 per-variant model at PR5) ──
/** Seed list for the editor's location Select. `location` is a slug (editable without a redeploy). */
export const PLACEMENT_LOCATIONS = [
    'front',
    'back',
    'left-chest',
    'right-chest',
    'sleeve',
];
export const PrintLocationSchema = z
    .string()
    .min(1)
    .max(40)
    .regex(/^[a-z0-9-]+$/, 'location must be a lowercase slug (a-z, 0-9, hyphen)');
/** Denormalized mapping badge on the product META row (drives the browse filter without a mapping read). */
export const CatalogMappingStateEnum = z.enum(['UNMAPPED', 'PARTIAL', 'MAPPED']);
/**
 * Shopify product lifecycle status, normalized to lowercase (Shopify's GraphQL returns ACTIVE /
 * ARCHIVED / DRAFT). The sync pulls all three (an archived/draft product referenced by a real order
 * still needs a META row so its lines classify + resolve), so listing views filter on it to hide
 * discontinued products. Order-line resolution + demand DO NOT filter on it — a real order for a
 * since-archived product must still consume/demand its blank.
 */
export const ProductStatusEnum = z.enum(['active', 'archived', 'draft']);
/**
 * One print in a product's recipe: a design placed at a location. There is NO size here — the
 * print size comes from the design FILE's own pixels (1 in = 300 px; halftones can't be scaled),
 * and the band (adult/youth) selects which of the design's two files to use at batch time
 * (catalog-mapping v1.1). One placement per location (unique-location refinement on the mapping).
 *
 * `designId` is the default design printed at this location for every variant. `designByColor`
 * (catalog-mapping v1.2) is an OPTIONAL per-colorway override: for shops where one product carries
 * several art colorways on the same blank (e.g. low-trendz — one black tee, the "Blue/White/Heat
 * Check" option picks a different back design), it maps the Shopify variant colorway
 * (`CatalogVariantRecord.color`, matched VERBATIM — never trimmed/lowercased) to the designId to
 * print for that colorway. Absent/empty ⇒ every variant uses `designId` (the 26 legacy rows).
 * A variant colorway with no entry BLOCKS the line at batch build — it never silently falls back
 * to `designId` (that would print the wrong art on a colorway configured for its own design, § 1.1).
 */
export const ProductPlacementSchema = z.object({
    location: PrintLocationSchema,
    designId: z.string().min(1).max(80),
    designByColor: z.record(z.string().min(1), z.string().min(1).max(80)).optional(),
    /**
     * How this placement is DECORATED. Absent ⇒ `'dtf'`, and `'dtf'` is never written explicitly — the
     * field stays sparse so every pre-existing mapping round-trips byte-identical and back-compat is
     * exact. Spelled to match {@link PressMethodEnum} so "screen print" has ONE spelling across the
     * technique vocabulary.
     *
     * ⚠️ `'screen-print'` is STRUCTURALLY binding, not advisory. The batch builder selects its
     * screen-print branch on this BEFORE any design / geometry / nest logic runs, so such a placement
     * can never construct an `ExpandedItem` — the type that becomes a batch item, then a nested piece,
     * then film. "Cannot be printed in DTF" is therefore a property of the type graph, not a rule an
     * operator has to remember.
     *
     * ⚠️ The per-build `BatchCreateRequest.screenPrintLocations` toggle is strictly ADDITIVE on top of
     * this: effective screen-print set = this field ∪ the per-build locations. It can move a placement
     * dtf → screen-print, NEVER the reverse. There is no tick-box state that puts a screen-print
     * placement onto a gang sheet.
     *
     * Inert until the resolver reads it (plan v1 PR-2) — PR-1 only accepts and persists it.
     */
    method: z.enum(['dtf', 'screen-print']).optional(),
});
/** DDB: `PRODUCT#<storeId>#<productId>` / `META`. Sync-owned mirror of a Shopify product. GSI1 sparse on apparel. */
export const CatalogProductRecordSchema = z.object({
    PK: z.string(),
    SK: z.literal('META'),
    GSI1PK: z.string().optional(),
    GSI1SK: z.string().optional(),
    entity: z.literal('catalogProduct'),
    storeId: z.string(),
    productId: z.string(),
    title: z.string(),
    handle: z.string().nullable().optional(),
    category: z.string().nullable().optional(),
    categoryFullName: z.string().nullable().optional(),
    productType: z.string().nullable().optional(),
    isApparel: z.boolean(),
    apparelKind: ApparelKindEnum.nullable().optional(),
    // Shopify lifecycle status (SYNC-OWNED, recomputed every sync). Optional for back-compat: rows synced
    // before this field lack it; a re-sync stamps it. Listing views (e.g. the caps view) hide `archived`;
    // resolution + demand never filter on it. Absent/unknown is treated as visible.
    status: ProductStatusEnum.nullable().optional(),
    // Non-DTF lane (plan v1, Phase 3): the classifier's production-class verdict + which Shopify field
    // matched. SYNC-OWNED — recomputed from classifyProductionClass and rewritten on every sync. Optional
    // for back-compat (rows synced before Phase 3 lack it; a re-sync stamps it). Coexists with isApparel
    // (which stays classifyApparel-derived) until Phase 4 makes productionClass the authoritative router;
    // the two only disagree on the pathological "<garment> Decal" naming, which Phase 4 reconciles.
    suggestedProductionClass: ProductionClassEnum.optional(),
    classificationSource: ClassificationSourceEnum.optional(),
    featuredImageUrl: z.string().nullable().optional(),
    variantCount: z.number().int().nonnegative(),
    mappingState: CatalogMappingStateEnum,
    syncedAt: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** DDB: `PRODUCT#<storeId>#<productId>` / `VARIANT#<variantId>`. Sync owns color/size; mapping owns blank. */
export const CatalogVariantRecordSchema = z.object({
    PK: z.string(),
    SK: z.string(),
    entity: z.literal('catalogVariant'),
    storeId: z.string(),
    productId: z.string(),
    variantId: z.string(),
    sku: z.string().nullable().optional(),
    variantTitle: z.string().nullable().optional(),
    color: z.string().nullable().optional(),
    size: z.string().nullable().optional(),
    sizeBand: SizeBandEnum,
    /**
     * The VARIANT's own Shopify photo, when the merchant assigned one (per-colorway blank v1, P3b).
     * SYNC-OWNED, like colour/size — never written by the mapping editor.
     *
     * A per-colourway mapping decision cannot be confirmed against the product's featured image, because
     * that image is ONE colour: Low Trendz Logo Tee's featured photo is the red tee while the operator is
     * also deciding what Grey and White pull. Nullable because Shopify only sets it when media is attached
     * to the variant, so most single-colour products have none and the UI falls back to the product's.
     */
    imageUrl: z.string().nullable().optional(),
    blankResolveStatus: BlankResolveStatusEnum,
    resolvedBlankSku: z.string().optional(),
    ssSku: z.string().optional(),
    // The matched S&S blank's human identity, DENORMALIZED at resolve time (catalog-mapping) so the
    // variant table reads a real name instead of the opaque `BLANK#<ssSku>`. Present only while RESOLVED
    // (dropped on a reset/re-resolve). Absent on rows resolved before this field existed — a re-sync
    // stamps them.
    blankBrandName: z.string().optional(),
    blankStyleName: z.string().optional(),
    blankColorName: z.string().optional(),
    blankSizeName: z.string().optional(),
    /**
     * WHY this variant is not RESOLVED, in the operator's words (per-colorway blank v1, P3). Written by
     * the same stamp that sets the status, and REMOVED the moment it resolves, so it can never go stale.
     *
     * It exists because the reason was previously computed and thrown away everywhere except the
     * mapping-save response: on the catalog-sync path — the one that re-resolves after a colour rename or
     * a new variant — the operator saw a bare `UNRESOLVED` and had to guess between four different causes
     * (no youth style, no colour match, AMBIGUOUS, no per-colour blank entry). The per-colour axis makes
     * that worse, because its refusal is the one an operator can actually FIX, and only the text says how.
     *
     * ⚠️ **Deliberately UNBOUNDED here, and truncated at the writer instead** (`MAX_BLANK_REASON_LEN` in
     * `catalog-store.ts`). A `.max()` on a persisted field is enforced on every READ, and reads go through
     * `queryItems`, which parses a whole page — so one over-long row would throw for the entire product
     * partition, not just itself, and surface as a 400 nobody is alarmed on. The reason interpolates the
     * Shopify colourway, which has no length bound of its own, so that is reachable rather than theoretical.
     * Constrain at the write, stay tolerant at the read.
     */
    blankResolveReason: z.string().optional(),
    syncedAt: z.string(),
    updatedAt: z.string(),
});
/**
 * Per-colorway blank COLOUR (per-colorway-blank plan v1) — the second, independent per-colorway axis.
 *
 * `blankColor` says "one S&S colour for the whole product". That is right for a store that models colour
 * at the product level, and right for the "one black blank, several ART colorways" shape that
 * `ProductPlacementSchema.designByColor` exists to serve. It cannot express the ORTHOGONAL shape: one
 * product whose Shopify option is a real GARMENT colour, so each colorway needs its own blank
 * (`teamobs` Team OBS Tee, `low-trendz` Low Trendz Logo Tee — 5 colours each).
 *
 * KEY   = the Shopify colorway (`CatalogVariantRecord.color`), matched VERBATIM via `Object.hasOwn` —
 *         never trimmed or lowercased, exactly like `designByColor`.
 * VALUE = an S&S colour NAME, which the operator picks from the style's real variant list rather than
 *         typing. That is the whole point of the field: the resolver's colour cascade is 7 tiers deep and
 *         its later tiers match on family, so a hand-typed near-miss can resolve to a real-but-WRONG
 *         shade with no warning. Naming the shade removes the guess.
 *
 * OPT-IN and non-destructive: absent or empty ⇒ resolution is byte-for-byte today's `blankColor` path, so
 * all 219 existing mappings are untouched. When present, a colorway with no entry resolves UNRESOLVED
 * (visible, blocks batch build) and NEVER falls back to `blankColor` — a wrong-but-plausible blank is the
 * expensive failure and a block is the cheap one.
 *
 * Declared once and reused by the record, the API view and the save request. `blankColor` is declared
 * independently in all three, which is exactly the drift this avoids.
 */
export const BlankColorByColorSchema = z.record(z.string().min(1), z.string().min(1).max(64));
/** DDB: `PRODUCT#<storeId>#<productId>` / `MAPPING`. Operator-owned recipe. Never written by sync. */
export const ProductMappingRecordSchema = z.object({
    PK: z.string(),
    SK: z.literal('MAPPING'),
    entity: z.literal('productMapping'),
    storeId: z.string(),
    productId: z.string(),
    blankStyle: z.string().min(1).max(64),
    // Seam: set only when youth is a SEPARATE S&S style number; otherwise youth reuses `blankStyle`.
    youthBlankStyle: z.string().min(1).max(64).optional(),
    // Operator-picked S&S blank COLOUR for the whole product (one colour per product, used for both
    // bands). Overrides the per-variant colour when set — needed for stores that model colour at the
    // product level (one product per colour), where the Shopify variant carries only a size. Left
    // unset, resolution falls back to the variant's own colour (stores that model colour per variant).
    blankColor: z.string().min(1).max(64).optional(),
    // Per-colorway blank colour — overrides `blankColor` when present. See BlankColorByColorSchema.
    blankColorByColor: BlankColorByColorSchema.optional(),
    placements: z
        .array(ProductPlacementSchema)
        .min(1)
        .max(12)
        .refine((a) => new Set(a.map((p) => p.location)).size === a.length, 'duplicate location'),
    createdBy: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * DDB: `PRODUCT#<storeId>#<productId>` / `CLASSIFICATION`. OPERATOR-OWNED overrides, NEVER written by
 * sync (co-located with META/VARIANT/MAPPING so `queryProduct` returns it in one Query). Two independent
 * optional operator inputs (caps-materials plan v1, Phase 1):
 *  - `productionClassOverride` — forces a product's production class when the Shopify-taxonomy classifier
 *    misses (e.g. a cap that lands UNCLASSIFIED). `effectiveProductionClass` applies it over the sync's
 *    suggestion. It can only refine WITHIN the non-apparel (`isApparel === false`) branch — it never
 *    pulls an `isApparel === true` garment off the DTF path (the load-bearing silent-drop guard).
 *  - `capBlankSsSku` — the caps-materials 1:1 cap→S&S blank mapping (colour + size live in S&S). Stored
 *    BARE; the order-line resolver returns it as `BLANK#<capBlankSsSku>` so it keys the on-hand ledger.
 */
export const ProductClassificationRecordSchema = z.object({
    PK: z.string(),
    SK: z.literal('CLASSIFICATION'),
    entity: z.literal('productClassification'),
    storeId: z.string(),
    productId: z.string(),
    productionClassOverride: ProductionClassEnum.optional(),
    capBlankSsSku: z.string().min(1).max(64).optional(),
    // Human identity of the mapped cap blank, DENORMALIZED from the operator's S&S selection at map time
    // (brand/style from the style pick, colour + size from the pickers) — the bare `capBlankSsSku` is an
    // opaque number no one can read. Same denormalize-at-write pattern as the RECEIVE ledger's
    // brandName/styleName/colourName/sizeName. Display-only; the SKU stays the source of truth (a re-map
    // refreshes these if S&S ever renames). Absent for a production-class-override-only classification.
    capBlankBrandName: z.string().max(128).optional(),
    capBlankStyleName: z.string().max(128).optional(),
    capBlankColorName: z.string().max(128).optional(),
    capBlankSizeName: z.string().max(64).optional(),
    /**
     * Ship-only shelf mapping (P2b-B): variantId → internal stock-item slug — which physical shelf item
     * each Shopify variant of this listing draws from (many listings across stores may converge on ONE
     * item). Read LIVE at view time by the fulfill worklist + per-item demand — order lines carry no
     * stamp, so a mapping edit is visible immediately; the future consume-at-pack (P2b-C) also resolves
     * it fresh at pack time. Keys are Shopify variantIds; values are `ITEM#` slugs.
     */
    variantStockItems: z.record(z.string(), StockItemSlugSchema).optional(),
    createdBy: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// ── Catalog API views + requests ──
export const CatalogVariantSummarySchema = z.object({
    variantId: z.string(),
    sku: z.string().nullable().optional(),
    color: z.string().nullable().optional(),
    size: z.string().nullable().optional(),
    sizeBand: SizeBandEnum,
    variantTitle: z.string().nullable().optional(),
    /** The variant's own Shopify photo (P3b) — see `CatalogVariantRecordSchema.imageUrl`. */
    imageUrl: z.string().nullable().optional(),
    blankResolveStatus: BlankResolveStatusEnum,
    resolvedBlankSku: z.string().optional(),
    ssSku: z.string().optional(),
    // The matched S&S blank's human identity (denormalized at resolve) so the variant table shows a real
    // name, not the opaque `BLANK#<ssSku>`.
    blankBrandName: z.string().optional(),
    blankStyleName: z.string().optional(),
    blankColorName: z.string().optional(),
    blankSizeName: z.string().optional(),
    /** Why this variant is not RESOLVED (P3) — see `CatalogVariantRecordSchema.blankResolveReason`. */
    blankResolveReason: z.string().optional(),
});
export const CatalogProductSummarySchema = z.object({
    storeId: z.string(),
    productId: z.string(),
    title: z.string(),
    handle: z.string().nullable().optional(),
    category: z.string().nullable().optional(),
    productType: z.string().nullable().optional(),
    apparelKind: ApparelKindEnum.nullable().optional(),
    featuredImageUrl: z.string().nullable().optional(),
    variantCount: z.number().int().nonnegative(),
    mappingState: CatalogMappingStateEnum,
    // Non-DTF lane (plan v1): the EFFECTIVE production class (Phase 3 = the suggestion; Phase 6 adds the
    // operator override) plus the raw suggestion + matched source, so the classify UI can show the
    // verdict and flag a low-confidence (productType-only) guess. `productionClass` is always computed.
    productionClass: ProductionClassEnum,
    suggestedProductionClass: ProductionClassEnum.optional(),
    classificationSource: ClassificationSourceEnum.optional(),
    updatedAt: z.string(),
});
/** A NON_DTF cap product for the catalog "Caps" view (caps-materials Phase 2b): the product summary
 *  plus its current cap→S&S blank mapping, so the grid shows which caps still need a blank. */
export const CapCatalogProductSchema = CatalogProductSummarySchema.extend({
    capBlankSsSku: z.string().optional(),
    // Human identity of the mapped cap blank (denormalized at map time) so the grid shows a real name.
    capBlankBrandName: z.string().optional(),
    capBlankStyleName: z.string().optional(),
    capBlankColorName: z.string().optional(),
    capBlankSizeName: z.string().optional(),
});
export const ProductMappingViewSchema = z.object({
    blankStyle: z.string(),
    youthBlankStyle: z.string().optional(),
    blankColor: z.string().optional(),
    blankColorByColor: BlankColorByColorSchema.optional(),
    placements: z.array(ProductPlacementSchema),
    updatedAt: z.string(),
});
/** API view of a product's operator classification overrides (caps-materials plan v1). Defined here
 *  (before CatalogProductDetail) so the product detail can surface the current cap→S&S blank mapping. */
export const ProductClassificationViewSchema = z.object({
    productionClassOverride: ProductionClassEnum.optional(),
    capBlankSsSku: z.string().optional(),
    // Human identity of the mapped cap blank (denormalized at map time) so the UI reads a real name, not
    // the bare SKU.
    capBlankBrandName: z.string().optional(),
    capBlankStyleName: z.string().optional(),
    capBlankColorName: z.string().optional(),
    capBlankSizeName: z.string().optional(),
    /** Ship-only shelf mapping (P2b-B): variantId → stock-item slug, as stored. */
    variantStockItems: z.record(z.string(), StockItemSlugSchema).optional(),
    updatedAt: z.string(),
});
export const CatalogProductDetailSchema = z.object({
    product: CatalogProductSummarySchema,
    variants: z.array(CatalogVariantSummarySchema),
    mapping: ProductMappingViewSchema.optional(),
    // Operator classification overrides (caps-materials Phase 2b): the cap→S&S blank mapping the caps
    // editor reads to show/prefill the current SKU. Absent for a product with no CLASSIFICATION row.
    classification: ProductClassificationViewSchema.optional(),
});
/** `PUT /catalog/:storeId/products/:productId/mapping` body. */
export const ProductMappingSaveRequestSchema = z.object({
    blankStyle: z.string().min(1).max(64),
    youthBlankStyle: z.string().min(1).max(64).optional(),
    blankColor: z.string().min(1).max(64).optional(),
    /**
     * MERGE semantics, the same three-state shape the classification route uses: `undefined` KEEPS the
     * stored map, `null` (or `{}`) CLEARS it, a non-empty map REPLACES it in full. Nullable because the
     * rest of this body is an unconditional full-record replace, so a browser tab still running the
     * pre-feature bundle would otherwise erase the map on any save — it simply omits the key, which must
     * mean "leave it alone", which then needs a separate spelling for "turn it off".
     */
    blankColorByColor: BlankColorByColorSchema.nullish(),
    placements: z
        .array(ProductPlacementSchema)
        .min(1)
        .max(12)
        .refine((a) => new Set(a.map((p) => p.location)).size === a.length, 'duplicate location'),
});
/** One variant's blank-resolution outcome, returned after a mapping save. */
export const VariantResolveResultSchema = z.object({
    variantId: z.string(),
    blankResolveStatus: BlankResolveStatusEnum,
    resolvedBlankSku: z.string().optional(),
    reason: z.string().optional(),
    /**
     * The matched blank's human identity, as just resolved (per-colorway blank v1 § 6). Already computed
     * by the resolver and stamped onto the variant rows; returning it here is what lets the just-saved
     * table read `Grey / L → Tultex 602 · Black · L` instead of a bare SKU. That matters because the SKU
     * is the one thing an operator cannot check by eye, so a wholly wrong mapping used to report as a
     * full green — `Saved — 5/5 variants resolved` — with the wrong colour invisible.
     */
    blankBrandName: z.string().optional(),
    blankStyleName: z.string().optional(),
    blankColorName: z.string().optional(),
    blankSizeName: z.string().optional(),
});
export const ProductMappingSaveResponseSchema = z.object({
    mapping: ProductMappingViewSchema,
    mappingState: CatalogMappingStateEnum,
    variantResolve: z.array(VariantResolveResultSchema),
    resolvedCount: z.number().int().nonnegative(),
    totalVariants: z.number().int().nonnegative(),
});
/**
 * `PUT /catalog/:storeId/products/:productId/classification` body. MERGE semantics: an OMITTED field is
 * left unchanged; an explicit `null` CLEARS it; a value SETS it — so a caller can set the cap blank
 * without disturbing a production-class override, and vice-versa.
 */
export const ProductClassificationSaveRequestSchema = z.object({
    productionClassOverride: ProductionClassEnum.nullable().optional(),
    capBlankSsSku: z.string().min(1).max(64).nullable().optional(),
    // Denormalized identity sent alongside capBlankSsSku (the editor has it from the S&S pick). Merge
    // semantics match capBlankSsSku: undefined keeps, null clears — the editor sends all five together on
    // save and all null on remove, so the label can never drift from the SKU.
    capBlankBrandName: z.string().max(128).nullable().optional(),
    capBlankStyleName: z.string().max(128).nullable().optional(),
    capBlankColorName: z.string().max(128).nullable().optional(),
    capBlankSizeName: z.string().max(64).nullable().optional(),
    /**
     * Ship-only shelf mapping (P2b-B). WHOLE-MAP semantics (unlike the per-field merges above): an
     * omitted field keeps the stored map, `null` clears it, and a map REPLACES the stored map in full —
     * the editor always renders + submits every variant row, so partial-map merges would only invite
     * stale-key drift. Keys are variantIds; values are stock-item slugs (validated to exist on save).
     */
    variantStockItems: z.record(z.string(), StockItemSlugSchema).nullable().optional(),
});
export const ProductClassificationSaveResponseSchema = z.object({
    classification: ProductClassificationViewSchema,
});
/**
 * `GET /catalog/blank-style-pairs` — the (adult, youth) blank-style pairs THIS SHOP ACTUALLY USES, read
 * out of its own mapping history (per-colorway blank v1 § 6.1).
 *
 * ⚠️ The point of the endpoint is that the list is **derived, never maintained.** Every mapping row
 * already stores `blankStyle` and `youthBlankStyle` together, so their co-occurrence IS the adult↔youth
 * link — there is no table of pairs to curate and no deploy needed to add a third garment. Map one
 * product to a new style pair and it appears for everyone; change tee supplier and the old pair fades
 * while the new one rises. A hardcoded pair list would need a release for each of those.
 *
 * Deliberately product-agnostic and shop-wide (not per-store): the shop maps products from several
 * Shopify stores onto the same blanks, so more history means better ranking, and a global response
 * caches once for everyone. `apparelKinds` is returned so the CLIENT can rank a hoodie product's
 * hoodie pair first — ranking server-side would make the response per-product and uncacheable.
 */
export const GarmentStylePairSchema = z.object({
    blankStyle: z.string(),
    /** Absent when mappings using this adult style have never set a youth style. */
    youthBlankStyle: z.string().optional(),
    /** How many product mappings use exactly this pair — the "most used" signal. */
    productCount: z.number().int().nonnegative(),
    /** Which apparel kinds use it (`tshirt`, `hoodie`, …), for client-side ranking. */
    apparelKinds: z.array(z.string()),
});
export const GarmentStylePairsResponseSchema = z.object({
    pairs: z.array(GarmentStylePairSchema),
    /** Total mappings scanned — lets the UI say "of N mapped products" without a second call. */
    totalMappings: z.number().int().nonnegative(),
    /** When this was computed (it is cached for minutes, not seconds — never freshness-critical). */
    computedAt: z.string(),
});
/** `POST /catalog/:storeId/sync` response — counts from the paged catalog pull. */
export const CatalogSyncResponseSchema = z.object({
    storeId: z.string(),
    scanned: z.number().int().nonnegative(),
    apparelKept: z.number().int().nonnegative(),
    nonApparel: z.number().int().nonnegative(),
    variantsUpserted: z.number().int().nonnegative(),
    pages: z.number().int().nonnegative(),
    /** productIds with >100 variants (v1 does not page inner variants — a coverage flag). */
    truncatedVariantProducts: z.array(z.string()),
});
/** Lifecycle of an async catalog-sync job: QUEUED → RUNNING → SUCCEEDED | FAILED. */
export const CatalogSyncStatusEnum = z.enum(['QUEUED', 'RUNNING', 'SUCCEEDED', 'FAILED']);
/** SQS message body the sync route enqueues for the catalog-sync worker (mirrors ShopifyIngestMessage). */
export const CatalogSyncMessageSchema = z.object({
    storeId: z.string(),
    jobId: z.string(),
});
/**
 * DDB record: `CATALOGSYNC#<storeId>` / `JOB#<jobId>`. Tracks one async catalog-sync run so the
 * dashboard can poll for completion — the sync moved to a background worker because a big catalog
 * blows the 29s REST timeout. `summary` carries the {@link CatalogSyncResponse} counts on success;
 * `error` a short message on failure. The SK is point-addressable by `jobId` (the worker's hot path);
 * `createdAt` is a plain attribute, and "the store's latest job" is derived by querying the PK and
 * sorting by `createdAt` in memory (see catalog-store `listSyncJobs`).
 */
export const CatalogSyncJobRecordSchema = z.object({
    PK: z.string(),
    SK: z.string(),
    entity: z.literal('catalogSyncJob'),
    jobId: z.string(),
    storeId: z.string(),
    status: CatalogSyncStatusEnum,
    createdAt: z.string(),
    updatedAt: z.string(),
    startedAt: z.string().optional(),
    finishedAt: z.string().optional(),
    summary: CatalogSyncResponseSchema.optional(),
    error: z.string().optional(),
});
/** API view of a catalog-sync job (DDB keys stripped). */
export const CatalogSyncJobViewSchema = z.object({
    jobId: z.string(),
    storeId: z.string(),
    status: CatalogSyncStatusEnum,
    createdAt: z.string(),
    updatedAt: z.string(),
    startedAt: z.string().optional(),
    finishedAt: z.string().optional(),
    summary: CatalogSyncResponseSchema.optional(),
    error: z.string().optional(),
});
/** `POST /catalog/:storeId/sync` (202): the QUEUED (or already in-flight) job to poll. */
export const CatalogSyncEnqueueResponseSchema = z.object({ job: CatalogSyncJobViewSchema });
/** `GET /catalog/:storeId/sync`: the store's latest sync job, or null if it has never synced. */
export const CatalogSyncStatusResponseSchema = z.object({
    job: CatalogSyncJobViewSchema.nullable(),
});
// ── Gang-sheet batches (S3) ──
// Re-export the sheet spec so @fsp/contracts stays the single boundary import path.
// (`/schemas` subpath is pure zod — it pulls no packer into consumers' bundles.)
export { GangSheetSpecSchema };
/** One order line a batch would include. `quantity` overrides the line's unfulfilledQty. */
export const BatchLineRefSchema = z.object({
    storeId: z.string().min(1).max(64),
    orderId: z.string().min(1).max(64),
    lineId: z.string().min(1).max(64),
    quantity: z.number().int().positive().optional(),
});
/**
 * `POST /batches` body. Every referenced line must be `MAPPED` (else 422 `BatchBlockedError`).
 *
 * Roll selection: pass `rollId` to nest onto a registered film roll (its `usableWidthIn` sets the
 * roll width; `lengthIn` sets the sheet length, defaulting to and capped at the roll's `maxLengthIn`).
 * Omit `rollId` and the route uses the DEFAULT roll if one is registered, else the engine defaults.
 * `spec` is a raw escape hatch for the roll/gutter/length defaults; when both are present the
 * resolved roll width + length win over `spec`.
 *
 * `screenPrintLocations` are placement location slugs produced by SCREEN PRINT for THIS batch:
 * every selected line's placement at one of these locations is EXCLUDED from the gang sheet
 * (printed separately, for a faster turnaround). Applied by location across all lines; unknown
 * locations are harmless no-ops. It is a per-build choice — NOT persisted on the product mapping.
 */
export const BatchCreateRequestSchema = z.object({
    lineRefs: z.array(BatchLineRefSchema).min(1).max(500),
    rollId: z.string().min(1).max(80).optional(),
    lengthIn: z.number().positive().max(240).optional(),
    spec: GangSheetSpecSchema.optional(),
    screenPrintLocations: z.array(PrintLocationSchema).max(12).optional().default([]),
    /** When true, each DTF placement whose printed transfer is already on the shelf is PULLED from stock
     *  (flagged off the sheet + the shelf decremented) instead of reprinted. Default false — the feature
     *  biases to reprinting, so staff opt in per build (advisory → human-confirmed). */
    pullFromStock: z.boolean().optional().default(false),
});
/**
 * One garment to REPRINT (roadmap Phase 2 reissue): a batched line and how many of its copies
 * (garments) to reissue. The reprint unit is a GARMENT, not an individual placement — a ruined
 * garment gets ALL its placements (front + back …) re-pressed onto ONE fresh blank, so the count is
 * per LINE, shared across placements (this keeps one garment = one blank, and matches the
 * "consume the replacement" rule). `copies` must not exceed the copies the source batch printed.
 */
/**
 * Why a garment nobody owes is being reprinted anyway. A FIXED SET, deliberately — never free text:
 * the scan station captures keystrokes at page level and skips them while an input is focused, so an
 * open text box on the kiosk would swallow the next scan.
 */
export const ReissueNotOwedReasonEnum = z.enum([
    'goodwill',
    'customer-confirmed',
    'staff-error',
    'other',
]);
/**
 * Per-garment, explicit override of the owed gate (a goodwill reprint of a refunded/cancelled
 * garment). `acknowledgedOwed` MUST equal the server's live owed count for the line: it proves the
 * operator was shown the real number, and stops a client that hardcodes an override from silently
 * defeating the gate forever. A mismatch is a 400, not a pass.
 */
export const ReissueNotOwedOverrideSchema = z.object({
    acknowledgedOwed: z.number().int().nonnegative(),
    reason: ReissueNotOwedReasonEnum,
});
export const BatchReissueGarmentSchema = z.object({
    storeId: z.string().min(1).max(64),
    orderId: z.string().min(1).max(64),
    lineId: z.string().min(1).max(64),
    copies: z.number().int().positive(),
    /** TRANSFER-ONLY for THIS garment: only its printed transfer was ruined (e.g. torn while cutting the
     *  sheet) and the garment + blank are intact, so the reprint presses onto the EXISTING blank and NO
     *  replacement blank is drawn for it at mark-printed. Per-garment so one reprint sheet can mix
     *  transfer-only garments with normal (ruined-garment → fresh-blank) ones. Default/absent = draw a
     *  blank (normal reprint). */
    transferOnly: z.boolean().optional(),
    /**
     * WHICH garments of the line — 1-based ranks, the same ordinal as the QR payload's `owedRank` and
     * the manifest's `copyIndex + 1`. Naming the ranks is the ONLY form that reproduces the client-side
     * gate, which is a RANK predicate (`owedRank <= copyOf`), not a count: on a line batched 3 and
     * refunded to 1, reprinting "1 copy" passes a count check while the scanned garment (rank 3) is not
     * owed at all. When ranks are absent the server cannot prove which copies are meant, so it fails
     * CLOSED on any partially-refunded line. `copies` must equal the number of distinct ranks.
     */
    owedRanks: z.array(z.number().int().positive()).min(1).max(99).optional(),
    /** Explicit per-garment override when the owed gate would refuse. See ReissueNotOwedOverrideSchema. */
    notOwedOverride: ReissueNotOwedOverrideSchema.optional(),
    /**
     * WHICH sheet this garment was printed on — the batch its frozen items are cloned from. Optional so
     * the path-scoped `POST /batches/:batchId/reissue` keeps working unchanged (it defaults every garment
     * to its path batch); REQUIRED by the pathless `POST /batches/reissue`, which is what lets ONE reprint
     * sheet carry garments ruined off SEVERAL different sheets.
     *
     * That is the whole point of the pathless route: a defect found at the cutting bench, another at the
     * press, and a remake for a lost package are three different source sheets, and reprinting them
     * separately is three sheets of film and three print/powder/cure cycles for what fits on one.
     */
    sourceBatchId: z.string().min(1).max(128).optional(),
});
/**
 * `POST /batches/:batchId/reissue` body — the garments to reprint from that (SOURCE) batch. The
 * reissue creates a NEW, standalone batch cloned from the source's frozen batch items (every
 * placement of each selected line, `copies` times); it NEVER touches the source order lines'
 * workflow state (their production is unchanged — only ruined garments are re-run). Duplicate line
 * entries are summed; the total per line must not exceed the copies the source printed.
 */
export const BatchReissueRequestSchema = z.object({
    garments: z.array(BatchReissueGarmentSchema).min(1).max(99),
    /**
     * Idempotency token, minted client-side per USER INTENT (not per click or render) and replayed on a
     * retry. Without it, one retry after a 502 mints a SECOND billable reprint — a real film and a real
     * blank. Deliberately keyed on the token rather than on (source, store, order, line): a tuple key
     * would fail-close a legitimate second reprint of a line with two separately-ruined copies, i.e.
     * ship short, which is the worse direction.
     *
     * OPTIONAL PERMANENTLY, not "for one release": the scan station is a wake-locked tablet on a
     * CloudFront SPA, so a stale bundle is normal, and 400-ing a reprint because the bundle predates
     * this field would itself ship short. Absent ⇒ today's behaviour, plus a warn so the gap is visible.
     */
    clientToken: z.string().uuid().optional(),
});
/** Batch lifecycle: DRAFT → (enqueue) COMPOSITING → READY | FAILED; SHIPPED after fulfilment (S6). */
/**
 * One OUTSTANDING reprint touching an order — a reissue (per-garment reprint) batch that has NOT yet
 * been marked printed (its replacement transfer is still on the press). A reissue keeps the same
 * (store, order, line) on its items but NEVER repoints `line.batchId`, so without this reverse lookup a
 * reprinted garment is invisible on the order/board and can strand. Surfaced by Rollo Phase 4 as the
 * ship-short safety net (there is no in-app "ship" action to hard-block; this is the loud warning).
 */
/** Where a reprint need was discovered. Drives the copy on the queue card, never a money decision. */
export const ReprintReasonEnum = z.enum([
    /** Ruined at the heat press — usually the blank too. */
    'PRESS_DEFECT',
    /** Transfer ruined while cutting the film; the blank is normally untouched. */
    'CUT_DAMAGE',
    /** Carrier lost the parcel; the whole garment is remade, days later. */
    'LOST_IN_TRANSIT',
    'OTHER',
]);
/**
 * A reprint that has already been COMMITTED TO FILM — a reissue batch not yet marked printed.
 */
export const BatchedReprintRefSchema = z.object({
    kind: z.literal('batched'),
    batchId: z.string(),
    /** The reissue batch's "Sheet #N" (its own seq); absent if it predates the seq counter. */
    seq: z.number().int().positive().optional(),
    /** Current status of the reissue batch — always one of the OUTSTANDING (non-PRINTED) statuses. */
    status: BatchStatusEnum,
    /** The SOURCE sheet this reprints ("Reprint of #N"); absent if the source predates the seq counter. */
    reissueOfSeq: z.number().int().positive().optional(),
    /** The distinct lineIds OF THIS ORDER that the reissue batch reprints. */
    lineIds: z.array(z.string()),
});
/**
 * A reprint still WAITING IN THE QUEUE — reported at a station, no film spent yet. One ref aggregates
 * every queued garment of the order, because the unit the gate cares about is the order.
 *
 * It deliberately carries no `batchId`/`seq`/`status`: there is no sheet yet, and the discriminated
 * union means a caller cannot read one off it by accident and render "Sheet #undefined".
 */
export const QueuedReprintRefSchema = z.object({
    kind: z.literal('queued'),
    /** One per queued GARMENT of this order — so `entryIds.length` is the garment count. */
    entryIds: z.array(z.string()).min(1),
    /** The distinct lineIds OF THIS ORDER with a garment in the queue. */
    lineIds: z.array(z.string()),
    /** Why they were queued, deduped — drives the copy ("lost in transit" reads very differently from
     *  "press defect" to whoever is deciding whether to ship). */
    reasons: z.array(ReprintReasonEnum),
    /** The oldest add, so an ageing garment can be shown amber at 24h and red at 72h (D5). */
    oldestAddedAt: z.string(),
});
/**
 * One OUTSTANDING reprint touching an order. TWO arms since the reprint queue (P4):
 *
 *  - `batched` — a reissue batch not yet marked printed; its replacement transfer is on the press.
 *  - `queued`  — garments reported ruined at a station, waiting for the next flush. No film yet.
 *
 * Both block. A reissue keeps the same (store, order, line) on its items but NEVER repoints
 * `line.batchId`, and a queue entry has no batch at all, so without this reverse lookup a garment that
 * still owes work is invisible on the order/board and the box ships short.
 *
 * ⚠️ Discriminated on `kind` ON PURPOSE. The two arms answer different questions and share almost no
 * fields; a plain optional-field object would let a reader take `ref.batchId` off a queued entry, get
 * `undefined`, and quietly render or link to nothing.
 */
export const ReprintRefSchema = z.discriminatedUnion('kind', [
    BatchedReprintRefSchema,
    QueuedReprintRefSchema,
]);
/** `GET /orders/:storeId/:orderId/reprints` data — outstanding reprints for one order (empty when none). */
export const OrderReprintsResponseSchema = z.object({
    reprints: z.array(ReprintRefSchema),
    /**
     * The literal `true`, never a boolean.
     *
     * This is a SHIP GATE response, and its dangerous failure is an empty array that came from a lookup
     * which never really ran — indistinguishable from "this order is clear", which is how a box goes out
     * a shirt short. Typing it as a literal means the only way to produce a well-formed response is to
     * have completed BOTH legs of the lookup: a handler cannot half-succeed, catch, and hand back
     * `{reprints: []}` without the type screaming. The route deliberately has no try/catch — a failure
     * 500s and the dashboard renders "unknown" rather than "clear".
     */
    checked: z.literal(true),
});
// ── P7-3: the read-only re-send preview ───────────────────────────────────────────────────────────
//
// A parcel is lost weeks after the order shipped. Shopify says FULFILLED, `resetLineOpen` already
// flipped the line back to OPEN, and `isShippedUnproducedLine` needs BATCHED — so the garment is
// invisible to the ENTIRE production board (plan § 14.1) and the office has to reach it BY ORDER
// NUMBER from the order card. This is what that card asks for BEFORE anything is spent: what would be
// rebuilt, how many, from which art, on which blank, and what refuses.
//
// ZERO WRITES, structurally: the preview omits `takeFromReserve` from `resolveDtfLine`'s deps, and
// omission is the guarantee (a caller that never injects the claim provably cannot spend film).
//
// ⚠️ It is a PREVIEW, not a PLAN. P7-1 (the CATALOG sentinel + the flush blockers) is NOT built, so
// there is no write path to predict and this refusal list is NECESSARILY INCOMPLETE with respect to
// one. Do not present it to an operator as a guarantee that a re-send will flush.
/**
 * Per-line outcome. `status` and `garments` move together, and the pairing is the contract P7-4 is
 * built on:
 *   `eligible`       → garments NON-empty, `refusal` absent.
 *   `blocked`        → garments EMPTY. There is nothing for a caller to spend, structurally — the same
 *                      shape `DtfLineResolution`'s `refused` branch uses, for the same reason.
 *   `needs_override` → garments NON-empty AND `refusal` + `overrideReasons` set. The rebuild resolved
 *                      and the operator can see every garment; a named human must accept a specific
 *                      risk before P7-4 may spend. Precedent: `notOwedOverride` / `acknowledgedOwed`
 *                      (`reissue-idempotency.ts:118-134`), which is logged and stamped on every item.
 *
 * The three-way split is what a zero-write route buys you: it can afford to refuse and still SHOW the
 * work, so the human decides with the garments in front of them instead of a dead end.
 */
export const ResendLineStatusEnum = z.enum(['eligible', 'blocked', 'needs_override']);
/**
 * Which risk a `needs_override` line is asking a human to accept. A CLOSED enum, not free text — the
 * same reason `ReissueNotOwedReasonEnum` is closed: P7-4 has to gate on the VALUE, and an open box on
 * a shop surface swallows the next barcode scan.
 *   SCREEN_PRINT_AT_BUILD — `spLocationsAtBuild` is non-empty, so this garment provably HAD
 *                           screen-print work. A DTF-only rebuild reproduces those placements as blank
 *                           film and the customer gets a shirt missing its front. (Absence proves
 *                           nothing — see `OrderLineRecord.spLocationsAtBuild` — which is why UNKNOWN
 *                           is deliberately NOT this.)
 *   NOT_PAID              — Shopify's financial status is outside {paid, partially_paid}. The mapping
 *                           resolved fine; the money did not. `refunded` / `partially_refunded` /
 *                           `pending` / `authorized` / `voided` all collapse into ONE `mappingStatus`
 *                           (`shopify-ingest.ts` `computeMappingStatus`), so the line returns
 *                           `financialStatus` verbatim and lets the human read the real one.
 */
export const ResendOverrideReasonEnum = z.enum(['SCREEN_PRINT_AT_BUILD', 'NOT_PAID']);
/**
 * What the server can honestly say about HOW this garment was produced (plan § 15.3, D7).
 *
 * ⚠️ There is NO `PROVEN` member, and that is measured rather than lazy. The only positive permit that
 * exists is `workflowState === 'PULLED_FROM_STOCK'` plus a printed-transfer PULL row for every
 * placement, and prod holds ZERO lines in that state (851/851 order lines drained, 2026-08-05). So
 * every line is UNKNOWN today, omitting the permit changes behaviour for exactly zero lines, and
 * shipping a money-relevant PERMIT value that no production data can exercise is the worse trade —
 * especially one that is unsound under mapping drift (it certifies the placement set AS IT WAS AT PULL
 * TIME, `[[gate-keyed-on-a-transition-misses-a-fresh-entity]]`) and is erased without residue by an
 * un-pull (`[[derived-gate-evaporates-needs-durable-marker]]`). `pulledFromStockAt` rides along on the
 * technique block so P7-5 can still show it; P7-4 may add the permit once the lane has population.
 *   SCREEN_PRINT_DENIED — durable DENY evidence → `needs_override`.
 *   UNKNOWN             — no record either way. NOT permission (plan § 14.4, P0-3).
 */
export const ResendTechniqueVerdictEnum = z.enum(['SCREEN_PRINT_DENIED', 'UNKNOWN']);
/**
 * One placement of one garment a re-send would print, fully resolved SERVER-SIDE.
 *
 * Mirrors `ExpandedItem` (`dtf-line-resolver.ts:42-55`) minus `pulledFromStock` — always `false` here,
 * because the preview omits `takeFromReserve`, so returning it would invite a reader to think the
 * preview consulted the shelf.
 *
 * ⚠️ NOTHING here is client-injectable. The request carries `(storeId, orderId)` in the path and
 * nothing else: no quantity, no designId, no geometry, no blank SKU, no roll. Geometry is the band
 * FILE's own pixels at 300 DPI (`file.widthPx / PRINT_DPI`) — placed 1:1, never scaled, because
 * halftones cannot be resampled.
 */
export const ResendPreviewGarmentSchema = z.object({
    location: z.string(),
    designId: z.string(),
    designName: z.string().optional(),
    widthIn: z.number().positive(),
    heightIn: z.number().positive(),
    sizeBand: SizeBandEnum,
    /**
     * TODAY'S blank (plan § 16.7, DECIDED 2026-08-05). There was never a "frozen" option to choose:
     * `line.resolvedBlankSku` is RE-DERIVED from the synced catalog by `buildLineRecord` on every webhook
     * and every ≤15-min reconcile, so the blank the customer actually received was never recorded on the
     * line. The preview resolves it exactly the way the build does.
     */
    resolvedBlankSku: z.string().optional(),
    /** Copies of THIS placement = the line's `resendQty`. Echoed so P7-4's confirm can be checked. */
    quantity: z.number().int().positive(),
    /**
     * `DesignRecord.updatedAt` — the art-version signal, at ZERO extra IO (the whole record is already in
     * the request's `designCache`, which `resolveDtfLine` just read through).
     *
     * It is the strongest art signal available without an S3 call, and strictly stronger than the
     * `status|WxH|densityDpi` descriptor plan § 14.2 warns about: `applyValidation` writes
     * `SET #band = :file, updatedAt = :now` on EVERY `designs/*` Object Created
     * (`design-store.ts:198-213`), so it moves on a same-size re-export — the exact edit § 14.2 says a
     * dimensions fingerprint is blind to, and which a prod version census found twice in 233 real
     * transitions.
     *
     * ⚠️ IT IS NOT "the art changed since this order shipped". `design-store.ts`'s `update` bumps it for a
     * name / styleCode / problem-flag edit too, and measured against live data 132 of 132 designs carry
     * an `updatedAt` LATER than their newest PNG write (a 2026-07-26/27 styleCode backfill moved
     * essentially the whole library; one design by +19 days). It is 100% false-positive for that
     * question. Its ONLY job is the short-window confirm token below, where over-sensitivity is
     * fail-closed ("refresh the preview") and therefore the safe direction on a money path.
     *
     * ⚠️ Nor is it an art CONTENT identity. Answering "is today's file the file that shipped" needs
     * `ListObjectVersions` per key filtered by `order.fulfilledAt` plus an ETag compare, and it is
     * unanswerable for a third of the target corpus (the design library's earliest S3 version is
     * 2026-07-08 while 145 FULFILLED order lines predate it). That belongs in P7-5, lazily, for the one
     * design an operator clicks — never precomputed for the order.
     *
     * ⚠️ Same name, DIFFERENT source from `DesignRecordSchema.artUpdatedAt` (added 2026-09-20, moved
     * only by a band-file write). This token deliberately stays on the record's `updatedAt`: for a
     * confirm token, over-sensitivity is fail-closed and therefore the safe direction, whereas the
     * garment-stock art-drift badge needed the art-only field precisely because over-sensitivity there
     * is noise staff learn to ignore.
     */
    artUpdatedAt: z.string(),
});
/** What the server knows about how this garment was produced, plus the raw evidence (plan § 15.3). */
export const ResendTechniqueSchema = z.object({
    verdict: ResendTechniqueVerdictEnum,
    /** Operator-facing sentence, server-owned, rendered verbatim. */
    because: z.string(),
    /** P7-0's widening union of location slugs. PRESENT ⇒ DENY; ABSENT ⇒ NO RECORD, not "no screen print". */
    spLocationsAtBuild: z.array(z.string()).optional(),
    workflowState: WorkflowStateEnum,
    pulledFromStockAt: z.string().optional(),
});
/**
 * Reprint work already in flight for THIS line — the only pre-spend evidence that exists (§ 14.6-5:
 * `retire` DELETES the queue row, so nothing durable survives a completed reprint and a second
 * re-send is invisible to every derived gate).
 *
 * ⚠️ THE TWO ARMS ARE DIFFERENT UNITS AND MUST NOT BE SUMMED. `BatchedReprintRef` is one ref per
 * SHEET; `QueuedReprintRef` is ONE ref per ORDER carrying `entryIds[]` (garments) and `lineIds[]`
 * (lines) with NO mapping between them (`reprint-visibility.ts:110-121` says so, and refuses to emit
 * per-garment refs for exactly this reason). So how many queued garments belong to THIS line is NOT
 * derivable — `inQueue` is a BOOLEAN on purpose. Reporting `entryIds.length` per line would attribute
 * the whole order's queued garments to every matching line: a 2-line order with one queued garment
 * each would read "2 outstanding" on both.
 */
export const ResendOutstandingSchema = z.object({
    /** Outstanding reissue SHEETS whose item rows name this line. */
    reissueSheetIds: z.array(z.string()),
    /** Whether the shared reprint queue holds at least one garment for this line. Never a count. */
    inQueue: z.boolean(),
});
/** One order line, previewed. */
export const ResendPreviewLineSchema = z.object({
    lineId: z.string(),
    title: z.string(),
    variantTitle: z.string().optional(),
    sku: z.string().nullable().optional(),
    status: ResendLineStatusEnum,
    /**
     * SHIPPED-AND-STILL-OWED — `max(0, currentQty − unfulfilledQty)` (plan § 14.3 Q2) — or `null` for
     * UNKNOWABLE. Never `undefined`, never silently `0`, never `unfulfilledQty`.
     *
     * WHY THE SUBTRACTION, and why bare `currentQty` is wrong. Per `OrderLineRecord.currentQty`: a refund
     * and a shipment BOTH drop `unfulfilledQty`, but only a refund drops `currentQty`. So `currentQty`
     * alone authorises re-sending garments still sitting on the shop floor. Driven through the real
     * ingest, `{quantity:5, fulfillable:2, current:5}` writes `currentQty 5 / unfulfilledQty 2` with the
     * correct answer `3` — three distinct numbers.
     *
     * ⚠️ FIXTURE TRAP. On the dominant P7 shape (fully shipped) `unfulfilledQty` is 0, so `currentQty`
     * and `currentQty − unfulfilledQty` COINCIDE — measured equal on 247 of 247 real
     * FULFILLED+OPEN+MAPPED prod lines. A fixture copied from any real target line passes with the wrong
     * derivation. Discriminate on a PARTIALLY shipped line
     * (`[[fixture-coincidence-hides-wrong-derivation]]`).
     *
     * ⚠️ DOCUMENTED LIMITATION, in the direction § 15.6 rules out. Assuming Shopify's
     * `fulfillable = quantity − max(refunded, fulfilled)` and `current = quantity − refunded`, this
     * reduces to `max(0, fulfilled − refunded)` — shipped copies minus EVERY refunded unit, whichever
     * copies the refund removed. On a line that is BOTH partly shipped AND partly refunded it therefore
     * UNDER-sends by `min(refunded, shipped)`, i.e. it ships short, which § 15.6 forbids. It is
     * nonetheless the MAXIMUM DERIVABLE: the keep-pressing bound `min(currentQty, orderedQty −
     * unfulfilledQty)` needs the original ordered `quantity`, and that is DROPPED at normalize
     * (`shopify-normalize.ts` uses `li.quantity` only as a fallback into these two fields and never
     * stores it) — two equations, three unknowns. Hence `basis` below: the operator gets the arithmetic,
     * not just its answer, and P7-4 owns any explicit count override.
     *
     * ⚠️ `null` ONLY when `currentQty` is absent, which means exactly one thing: a row last written
     * before the field shipped 2026-07-15. The ingest always fills it (`li.current_quantity ??
     * li.quantity`), and such rows are unreachable by any refresh — the reconcile poll queries
     * `fulfillment_status:unfulfilled AND status:open` then a 3-day lookback, `POST .../recheck` replays
     * STORED data (`reconstructNormalizedOrder` passes `currentQty: l.currentQty`, i.e. `undefined`
     * straight back in), and `ShopifyAdminClient` has no single-order fetch at all. There is also NO safe
     * proxy: the doctrine fallback `currentQty ?? unfulfilledQty` (`caps.ts`) yields **0** on a shipped
     * line — a silent under-send wearing a permissive fallback's costume. So unknown DOMINATES, the line
     * is `blocked`, and the refusal names the datum (`[[unknown-must-dominate-not-contribute-empty]]`).
     */
    resendQty: z.number().int().nonnegative().nullable(),
    /** The two numbers `resendQty` came from, verbatim, so P7-5 can show the arithmetic. */
    basis: z.object({
        currentQty: z.number().int().nonnegative().optional(),
        unfulfilledQty: z.number().int().nonnegative(),
    }),
    /**
     * Why this line is not plain-eligible. Server-owned FREE TEXT, rendered verbatim — nothing
     * machine-reads it, exactly like the eleven refusals `resolveDtfLine` owns (§ 16.4) and
     * `FlushBlocker.reason`. ⚠️ Do NOT loosen a test on one of these to a regex to make a change pass:
     * three of the resolver's strings have no other assertion anywhere in the repo.
     */
    refusal: z.string().optional(),
    /**
     * Non-empty iff `status === 'needs_override'`. An ARRAY, not one value: a line can be BOTH
     * screen-printed at build AND unpaid, and a single-valued field would let the operator attest to one
     * risk while the other silently passes. P7-4 must require an attestation naming EVERY entry
     * (`[[gate-a-multistep-flow-at-every-entry]]`).
     */
    overrideReasons: z.array(ResendOverrideReasonEnum),
    garments: z.array(ResendPreviewGarmentSchema),
    technique: ResendTechniqueSchema,
    outstanding: ResendOutstandingSchema,
    /** Verbatim Shopify / mapping state, so P7-5 can explain a NOT_PAID override without a second call. */
    financialStatus: z.string().nullable(),
    fulfillmentState: FulfillmentStateEnum,
    mappingStatus: MappingStatusEnum,
});
/**
 * What a re-send of this order would cost, over ELIGIBLE LINES ONLY.
 *
 * Eligible-only on purpose: a `needs_override` line's cost is not yet authorised, and folding it into
 * one headline is how an operator reads a number that includes work they have not accepted. P7-5 can
 * sum the pending cost itself — every line carries `resendQty` and `garments`.
 */
export const ResendPreviewTotalsSchema = z.object({
    /** Physical shirts. Each needs a fresh blank, so this is also the replacement-blank count. */
    garments: z.number().int().nonnegative(),
    /** Individual films = Σ over eligible lines of `resendQty × garments.length`. */
    transfers: z.number().int().nonnegative(),
    eligibleLines: z.number().int().nonnegative(),
    blockedLines: z.number().int().nonnegative(),
    needsOverrideLines: z.number().int().nonnegative(),
});
/** The film roll the oversize verdict was computed against. */
export const ResendPreviewRollSchema = z.object({
    rollId: z.string().optional(),
    name: z.string().optional(),
    rollWidthIn: z.number().positive(),
    maxLengthIn: z.number().positive(),
    /**
     * `default-roll` — the registered default roll. `engine-default` — NO roll is registered and
     * `GangSheetSpecSchema`'s own 22"×120" defaults were used, which silently changes the oversize
     * verdict, so it is surfaced rather than assumed.
     */
    source: z.enum(['default-roll', 'engine-default']),
});
/**
 * `GET /orders/:storeId/:orderId/resend-preview` data — the whole ORDER, never one garment.
 *
 * Whole-order because a lost box is not garment 3 of 5, and because § 14.5's argument is money: 20
 * sequential per-garment POSTs from an office iPad with one partial failure is a silently SHORT
 * re-send that the ship gate then under-reports.
 */
export const ResendPreviewResponseSchema = z.object({
    order: OrderSummarySchema,
    lines: z.array(ResendPreviewLineSchema),
    totals: ResendPreviewTotalsSchema,
    roll: ResendPreviewRollSchema,
    /**
     * The literal `true`, never a boolean — the `OrderReprintsResponse.checked` pattern, for the same
     * reason. The outstanding-reprint lookup is the ONE field here that can be wrong in the
     * unrecoverable direction about the PRESENT (everything else is a claim about the past), and its
     * dangerous failure is an empty list from a lookup that never ran. A literal means a handler cannot
     * half-succeed, catch, and hand back a well-formed "nothing outstanding".
     */
    outstandingChecked: z.literal(true),
    /**
     * SERVER-COMPUTED, OPAQUE to the client. P7-4 recomputes it at write time and refuses a mismatch
     * with 400 *"the rebuild changed — refresh and confirm again"*, never a silent pass. Exactly the
     * `notOwedOverride.acknowledgedOwed === v.owed` shape (`reissue-idempotency.ts:118-134`): the confirm
     * must echo the SERVER's live value, so a client hardcoding it cannot defeat the gate.
     *
     * COVERS every spendable quantity the operator was shown — the per-line count, per-garment
     * location / designId / sizeBand / geometry / blank SKU / art version, the technique verdict, the
     * line's `status` and `overrideReasons`, and the roll.
     *
     * DELIBERATELY EXCLUDES the `refusal` STRING. `status` + `overrideReasons` already carry every
     * machine-meaningful part of a refusal, and hashing the prose would invalidate every operator's open
     * preview on a copy edit — coupling a money gate to wording.
     *
     * ⚠️ The technique verdict is an INPUT, and must stay one: without it a build landing between preview
     * and confirm stamps `spLocationsAtBuild`, flips the line to SCREEN_PRINT_DENIED, and the operator's
     * stale confirm still validates.
     *
     * ⚠️ IT IS NOT `reissueFingerprint` AND MUST NEVER CALL IT. That function hashes `sourceBatchId` +
     * `copies`/`ranks`/`transferOnly`; a re-send has no source sheet (which is the entire reason the
     * CATALOG sentinel exists), its variables are per-garment geometry and art, and
     * `reissue-idempotency.test.ts` pins its output to an ABSOLUTE hash guarding LIVE DynamoDB reissue
     * markers — sharing the helper would turn a P7 tweak into a reissue-replay 409.
     */
    rebuildFingerprint: z.string(),
});
// ── P7-4: the writer ────────────────────────────────────────────────────────────────────────────────
/**
 * Why the office is re-sending. A deliberate SUBSET of `ReprintReasonEnum`: `PRESS_DEFECT` and
 * `CUT_DAMAGE` describe a garment ruined *in production* and are reported at a station by the person
 * who ruined it. Accepting them here would let an office confirm mis-attribute a carrier loss as a
 * press defect, and `/activity`'s reprint lens reads `reason` to tell the shop's own mistakes from the
 * carrier's — the one number in this feature that informs a business decision rather than a print.
 */
export const ResendReasonEnum = z.enum(['LOST_IN_TRANSIT', 'OTHER']);
/**
 * The bound on one confirm. NOT a silent truncation — over this the whole request is refused and the
 * number is named, because a re-send that quietly dropped garments is the exact silent-ship-short
 * § 14.5 built the bulk route to prevent.
 *
 * 200 is ~8× the largest real order observed (24 placements / 9 designs) and each garment is ONE
 * `UpdateItem`, so the ceiling is Lambda wall-clock, not a transact budget. A refusal here is
 * recoverable by re-sending fewer lines; a timeout mid-loop is recoverable too (the token makes the
 * retry a merge), but slowly and with an operator staring at a spinner.
 */
export const RESEND_MAX_GARMENTS = 200;
/**
 * `POST /orders/:storeId/:orderId/resend` body.
 *
 * ⚠️ NOTHING SPENDABLE IS IN HERE. No quantity, no geometry, no designId, no blank SKU, no roll — the
 * server recomputes the whole preview and takes every number from its own resolution. The client
 * chooses only WHICH LINES and echoes two opaque server values. § 14.3 rejected an operator-entered
 * count outright and § 17.1 closed it again: there is no field to enter one into.
 *
 * ⚠️ TWO RULES HERE ARE INVISIBLE IN `openapi.json`, so the SPEC IS NOT THE WHOLE CONTRACT. Both live
 * in the `superRefine` below, and a `superRefine` has no JSON Schema representation — the generated
 * spec lists `note` as plainly optional and says nothing about `lineIds` uniqueness. A client generated
 * from the spec will therefore compile a request the server rejects with 400. Verified against the
 * emitted spec, not assumed. If either rule ever needs to be machine-visible to clients it has to
 * become structural (a discriminated union on `reason`, say), not just documented.
 */
export const ResendConfirmBodySchema = z
    .object({
    /**
     * The `rebuildFingerprint` the operator was shown. Recomputed server-side and refused with 400 on
     * any mismatch — never a silent pass. See `ResendPreviewResponseSchema.rebuildFingerprint` for
     * what it covers and why the technique verdict is an input.
     */
    rebuildFingerprint: z.string().min(1),
    /**
     * ONE token per confirm-dialog SESSION, minted by the client. It becomes the CATALOG cohort key
     * (`C.<resendToken>`), i.e. an SK segment, so it is the identity of this re-send INTENT.
     *
     * ⚠️ THE CLIENT OWES FRESHNESS, and the contract cannot enforce it — so the SERVER makes both
     * failure modes loud instead of silent:
     *  · REUSING a token for a genuinely second re-send of the same line, while the first is still
     *    outstanding, merges onto the existing rows rather than adding new ones. That would be a
     *    ship-short — so such a line comes back `already_queued` with `queuedGarments: 0`, never
     *    `queued`. A caller that renders the outcome cannot mistake it for success.
     *  · A SECOND token while another cohort's rows are still outstanding for that line is REFUSED
     *    (`ResendLineOutcome`'s `already-outstanding` refusal), because two live cohorts for one
     *    garment is the rank-ordering hazard P7-1's disjoint keys were built to make unrepresentable.
     *
     * ⚠️ Retry-stability is also what gives the bulk write its ATOMICITY. Each garment is a separate
     * `UpdateItem`; a failure part-way through 500s with some rows written. Re-confirming with the SAME
     * token merges the written rows as no-ops and lands the rest. A per-attempt uuid or a timestamp
     * would double-spend on that retry; a constant or an `orderId`-derived token collapses two
     * independent re-sends into one film. Charset excludes `#` STRUCTURALLY — a `#` would collapse the
     * SK tuple (`reprintEntrySk`'s `assertCohortKey` throws on one, and this stops it ever reaching
     * there).
     */
    resendToken: z
        .string()
        .regex(/^[A-Za-z0-9._-]{8,64}$/, 'resendToken must be 8-64 chars of [A-Za-z0-9._-] — a uuid is ideal. It becomes an SK segment, so "#" is structurally excluded.'),
    /**
     * The lines to re-send. Explicit rather than "every eligible line": the preview is whole-order
     * because a lost box is not garment 3 of 5, but a partial delivery is real and the operator is the
     * only one who knows which box went missing. Every id must appear in the order — an unknown id is
     * a 400, not a silently skipped line.
     */
    lineIds: z.array(z.string().min(1)).min(1).max(100),
    reason: ResendReasonEnum,
    /** Required when `reason` is `OTHER` — an unattributable spend is not acceptable on this path. */
    note: z.string().min(1).max(500).optional(),
})
    .superRefine((v, ctx) => {
    if (v.reason === 'OTHER' && (v.note == null || v.note.trim().length === 0)) {
        ctx.addIssue({
            code: 'custom',
            path: ['note'],
            message: 'A note is required when reason is OTHER — say what happened.',
        });
    }
    const dupes = v.lineIds.filter((id, i) => v.lineIds.indexOf(id) !== i);
    if (dupes.length > 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['lineIds'],
            // Not de-duplicated silently: a repeated id means the caller's selection state is confused,
            // and guessing which of the two it meant is how a double-spend gets rationalised.
            message: `lineIds must be unique — repeated ${JSON.stringify([...new Set(dupes)])}.`,
        });
    }
});
/** One garment actually written (or found already present), so a caller can inspect the spend. */
export const ResendQueuedGarmentSchema = z.object({
    /** The garment's ordinal within the line, 1-based. */
    owedRank: z.number().int().positive(),
    entryId: z.string(),
    /**
     * `true` ⇒ a row for this garment ALREADY existed in this cohort and this confirm changed nothing.
     * On a retry that is the correct, expected answer. On a reused token it is the ship-short signal —
     * see `ResendConfirmBodySchema.resendToken`.
     */
    merged: z.boolean(),
});
/**
 * What happened to one requested line.
 *
 * ⚠️ THE DECISION GRAIN IS THE LINE, NOT THE GARMENT, and that is deliberate. § 14.5 asks for
 * per-garment outcomes and they are HERE — nested in `garments` — but the *status* is per line, because
 * every garment of a line shares one status by construction (same qty, same placements, same
 * technique). A FLAT per-garment outcome array would invite the § 16.2 mistake at a new grain: the
 * natural `outcomes.filter(ok)` would re-send 2 of 3 owed shirts and call it success.
 */
export const ResendLineOutcomeSchema = z.object({
    lineId: z.string(),
    status: z.enum([
        /** Rows written. `garments` names each one. */
        'queued',
        /**
         * Every garment merged onto a row that already existed in THIS cohort — `queuedGarments` is 0 and
         * nothing was spent. A retry's expected answer, and a reused token's loud one. NEVER folded into
         * `queued`: a caller must not be able to render "4 shirts queued" when the count is 0.
         */
        'already_queued',
        /** Nothing written. `refusal` says why, verbatim. */
        'refused',
    ]),
    /**
     * Server-owned FREE TEXT, rendered verbatim, present iff `status === 'refused'`. Same contract as
     * `ResendPreviewLineSchema.refusal` and `FlushBlocker.reason` — nothing machine-reads it.
     */
    refusal: z.string().optional(),
    /** Newly created rows only — the actual spend this confirm authorised. */
    queuedGarments: z.number().int().nonnegative(),
    garments: z.array(ResendQueuedGarmentSchema),
    /**
     * ADVISORY — outstanding rows for this line in OTHER cohorts (a bench report, or an earlier
     * re-send), counted from the queue partition directly.
     *
     * ⚠️ IT MUST NEVER BECOME A GATE, for the reason the bench route's `alreadyOutstanding` says: ranks
     * are PER RUN, so no cross-cohort comparison identifies a physical garment. And the coexistence is
     * CORRECT, not a hazard — PR-3b deliberately lets two production runs of one line flush side by side
     * onto separate sheets, which is exactly what two lost parcels (or a press defect plus a lost parcel)
     * owe. Refusing here would refuse real work; P7-1's prefix-disjoint cohorts are what make the
     * coexistence safe.
     *
     * A COUNT is derivable here, unlike `ResendOutstandingSchema.inQueue` — that one is a boolean because
     * `QueuedReprintRef` aggregates a whole order with no garment→line mapping, whereas this reads the
     * rows themselves and every row names its `lineId`.
     */
    otherCohortsOutstanding: z.number().int().nonnegative(),
});
/**
 * `POST /orders/:storeId/:orderId/resend` response.
 *
 * ⚠️ A 200 here does NOT mean everything was re-sent. Per-line refusal is a normal outcome (a
 * screen-printed line sits beside a DTF one in the same order), so the caller MUST render `lines` and
 * must lead with `queuedGarments` rather than treating a 2xx as success. `refusedLines > 0` with
 * `queuedGarments: 0` is a completely refused confirm that still answers 200.
 */
export const ResendConfirmResponseSchema = z.object({
    /** Echoed so a log line or a screenshot carries the cohort this spend belongs to. */
    resendToken: z.string(),
    /** The cohort key the rows were written under — `C.<resendToken>`. */
    cohortKey: z.string(),
    lines: z.array(ResendLineOutcomeSchema),
    /** NEWLY created rows across every line. The spend. `0` means nothing was queued. */
    queuedGarments: z.number().int().nonnegative(),
    /** Garments that were already in this cohort — a retry's count, and not a spend. */
    mergedGarments: z.number().int().nonnegative(),
    queuedLines: z.number().int().nonnegative(),
    alreadyQueuedLines: z.number().int().nonnegative(),
    refusedLines: z.number().int().nonnegative(),
});
/** Per-sheet summary the dashboard renders (dims + placed-copy count). */
export const GangSheetSummarySchema = z.object({
    sheetIndex: z.number().int().nonnegative(),
    widthPx: z.number().int().positive(),
    heightPx: z.number().int().positive(),
    placementCount: z.number().int().nonnegative(),
    /** When this physical sheet was marked printed (run through the DTF printer); absent until then.
     *  Per-sheet PRINT tracking is a pure floor-tracking aid, INDEPENDENT of the whole-batch mark-printed
     *  money event (no blank consume, no line flip) — it lets staff see which of a multi-sheet batch's
     *  sheets have been run (e.g. "printed 1 of 3") so a job spanning days is resumable. */
    printedAt: z.string().optional(),
    /** Presigned GET for THIS sheet's downscaled preview (added at read time by the batch-detail route
     *  from the batch's `sheetPreviewKeys`). Lets the dashboard preview every sheet, not just the first.
     *  Absent on batches composited before per-sheet previews shipped (fall back to the top-level
     *  `GangSheetResult.previewUrl`, which is the first sheet). Never stored on the record. */
    previewUrl: z.string().optional(),
});
/** Composited gang-sheet output, present on a READY batch. URLs are presigned S3 GETs. */
export const GangSheetResultSchema = z.object({
    sheetCount: z.number().int().nonnegative(),
    densityPct: z.number().min(0).max(1),
    sheets: z.array(GangSheetSummarySchema),
    previewUrl: z.string().optional(),
    printReadyUrls: z.array(z.string()),
});
/**
 * One garment+location the operator produces by SCREEN PRINT (not on the DTF sheet) for a batch
 * (money-waster #1 / plan § screen-print worklist). Recorded at build for each placement the operator
 * excluded from the sheet, so the shop has a worklist of what to screen-print and the batch isn't
 * "done" until it's produced. `designId`/`designName` are best-effort (screen-print art is separate —
 * the excluded placement's DTF file need not exist or be READY, preserving the #69 exclusion rule).
 */
export const ScreenPrintWorkItemSchema = z.object({
    storeId: z.string(),
    orderId: z.string(),
    orderName: z.string(),
    lineId: z.string(),
    title: z.string(),
    variantTitle: z.string().optional(),
    location: z.string().min(1),
    designId: z.string().optional(),
    designName: z.string().optional(),
    sizeBand: SizeBandEnum,
    quantity: z.number().int().positive(),
    resolvedBlankSku: z.string().optional(),
    /**
     * The Shopify product this line was ordered from, frozen at build so the run packet can fetch the
     * product's image (the customer-facing mockup) without a per-order read. DISPLAY ONLY — never a key,
     * never a settle input. Absent on work items built before the field.
     */
    productId: z.string().optional(),
    /** When this individual placement was pressed (screen-printed). Absent = still needs pressing.
     *  Per-placement check-off powers the cross-batch worklist; the batch-level `screenPrintedAt` is
     *  derived on only once EVERY item here is pressed, so the mark-printed gate can't clear while any
     *  garment is still unpressed. Absent on batches built before per-item check-off shipped. */
    pressedAt: z.string().optional(),
});
/** DDB record: `BATCH#<batchId>` / `META`, GSI1 `BATCHES` / `STATUS#<status>#<createdAt>`. */
/** One source sheet a reprint draws from — `batchId` plus its human "Sheet #N" when it had one. */
export const BatchReissueSourceSchema = z.object({
    batchId: z.string(),
    seq: z.number().int().positive().optional(),
});
// ── Pulled blanks — the pull record on the sheet header (`docs/pulled-blanks-pull-station-plan-v2.md`) ──
//
// The puller ticks each pull-list row as the stack comes off the shelf; the record lives on
// `BATCH#<id>/META` keyed by the row's bare S&S sku, so the count can add it back (shelf + recorded −
// drawn). Nothing here moves a ledger: the press's CONSUME row stays the only draw.
/** Which door recorded a pull row. */
export const BatchPulledViaEnum = z.enum([
    'pull-list',
    'labels',
    'card',
    'run',
    'press',
    'count',
    'close',
    'step0',
]);
/** One pull-list row's record: how many came off the shelf (≤ the sheet's quantity for that blank; a short
 *  pull records what was taken), when, by which login, through which door, and optionally who (initials —
 *  the station login is shared). */
export const BatchPulledRowSchema = z.object({
    qty: z.number().int().nonnegative(),
    at: z.string(),
    by: z.string(),
    via: BatchPulledViaEnum,
    who: z.string().max(40).optional(),
});
/** The whole record: bare `ssSku` → row. Written as ONE map under a compare-and-set on `pulledRev`
 *  (DynamoDB rejects a nested SET on an absent map, and a tap has negligible contention). */
export const BatchPulledMapSchema = z.record(z.string(), BatchPulledRowSchema);
/** The advisory stamps' kinds: the pull list printed, the labels printed, the pull station opened. */
export const BatchPullStampKindEnum = z.enum(['pull-list', 'labels', 'station']);
/**
 * The pull-station fields, shared by the header record and every projection of it (`BatchSummary`, the
 * press worklist's batch, the manifest). OPTIONAL TO READ, so a legacy header parses; the compositor and
 * sweeper lambdas parse `BatchRecord` too and strip nothing (no `.strict()`).
 *  - `pulled` / `pulledAt` (the first tick) / `pulledRev` (bumped by every set, edit and clear — the
 *    compare-and-set token and the count's fingerprint; it SURVIVES a clear so a later set must present it).
 *  - `pullListPrintedAt` / `labelsPrintedAt` / `pullStationOpenedAt` — advisory, first time only, never a
 *    gate: they decide which sheets the count may still ask about, never the arithmetic.
 */
export const batchPulledFields = {
    pulled: BatchPulledMapSchema.optional(),
    pulledAt: z.string().optional(),
    pulledRev: z.number().int().nonnegative().optional(),
    pullListPrintedAt: z.string().optional(),
    labelsPrintedAt: z.string().optional(),
    pullStationOpenedAt: z.string().optional(),
};
/** `POST /batches/:batchId/pulled` body — the COMPLETE record after this write: every row still recorded
 *  (`qty` is what came off the shelf; a row omitted is un-ticked), with the `pulledRev` the caller read
 *  (`null` when the sheet carried none). `rows: []` clears the record. */
export const BatchPulledRequestSchema = z.object({
    rev: z.number().int().nonnegative().nullable(),
    rows: z
        .array(z.object({ ssSku: z.string().min(1).max(64), qty: z.number().int().nonnegative() }))
        .max(200),
    via: BatchPulledViaEnum,
    who: z.string().trim().max(40).optional(),
    note: z.string().max(200).optional(),
});
/** `POST /batches/:batchId/pull-stamp` body — which advisory stamp to set (first time only). */
export const BatchPullStampRequestSchema = z.object({ kind: BatchPullStampKindEnum });
/**
 * The RESERVED `BatchRecord.reissueOf` value for a reprint sheet whose SOURCE SHEET NO LONGER EXISTS —
 * an office re-send of a lost parcel, rebuilt from the live product mapping (P7, `CATALOG` origin).
 *
 * ⚠️ `reissueOf` is a TRUTHINESS discriminator in thirteen places (eight server, five client) and its
 * VALUE is a comparison target in NONE of them — verified by census, `grep "reissueOf\s*[!=]="` returns
 * zero. A sentinel keeps every one of those gates behaving exactly as it does for an ordinary reprint,
 * which is what a re-send genuinely wants: replacement blanks drawn additively under its own batchId, no
 * source-line flips, always visible to the ship gate.
 *
 * Why a sentinel and not the alternatives, all of which were considered and rejected:
 *  · ABSENT — the worst option on the table, and `reprint-flush.ts`'s `NO_SOURCE` already documents why:
 *    an unmarked reprint sheet is not a milder reprint, it is a NORMAL batch holding reprinted art. Its
 *    mark-printed flips the SOURCE order lines to PRINTED and re-consumes their full quantity on top of
 *    the draw the original batch already made, and `settleLine`'s early return disappears so ONE press
 *    check-off does the same.
 *  · a synthetic UUID — renders as "Reprint of 4f3a9c2b", a sheet number staff will hunt for and never
 *    find.
 *  · the ORDER ID — writes a false sentence into the forever-kept `REISSUE_CREATED` event.
 *  · a new `isReissue` flag demoting `reissueOf` to provenance — the right model, wrong PR: because every
 *    legacy row HAS `reissueOf`, no existing record would exercise the new branch, so eight money gates
 *    would be rewritten with the first real exercise being a production re-send.
 *
 * ⚠️ It is NOT a batchId and must never be passed to `getBatch`. No call site does (25 enumerated; the
 * keys are `batchId`, `marker.batchId`, `input.sourceBatchId`, `row.flushedIntoBatchId`, `line.batchId`).
 * The `:` is deliberate — a uuid cannot contain one, so a stray lookup fails loudly rather than matching.
 * Read `reissueRebuilt` rather than comparing to this constant: it is true on a MIXED sheet too, where
 * this value is absent because a real source sheet is the primary.
 */
export const CATALOG_REISSUE_SOURCE = 'catalog:rebuilt';
/**
 * What KIND of production container a batch is — set ONCE at build, immutable thereafter.
 *   `gang-sheet`   — has ≥1 DTF item, so it owns a film. Screen-print work may ride along on it.
 *   `screen-print` — FILMLESS: zero DTF items, ≥1 screen-print placement. No sheet, no compositing,
 *                    straight to READY. Lives on the /screen-print page, not /batches.
 *
 * ⚠️ EXPLICIT — never derive this from `itemCount === 0`. That would branch on a mergeable attribute
 * ([[derive-decisions-from-the-key-not-a-mergeable-attribute]]): a gang sheet whose items were all
 * removed would silently re-read as a screen-print run and jump pages mid-life. Both production pages
 * filter on this, so it has to be stable for the life of the batch.
 *
 * ⚠️ ABSENT means `'gang-sheet'`, never "unknown" — every batch built before this field existed owns a
 * film. Readers must DEFAULT it, never branch on its presence.
 *
 * Inert until PR-4/PR-5 read it; PR-1 only defines it.
 */
export const BatchKindEnum = z.enum(['gang-sheet', 'screen-print']);
export const BatchRecordSchema = z.object({
    PK: z.string(),
    SK: z.literal('META'),
    GSI1PK: z.literal('BATCHES'),
    GSI1SK: z.string(),
    entity: z.literal('batch'),
    batchId: z.string(),
    /** Monotonic human-facing batch number from an atomic counter — rendered "Sheet #<seq>".
     *  Optional: batches created before the counter existed have none and fall back to the short id. */
    seq: z.number().int().positive().optional(),
    status: BatchStatusEnum,
    /** Which production container this is — see {@link BatchKindEnum}. Absent ⇒ `'gang-sheet'`. */
    kind: BatchKindEnum.optional(),
    failureReason: z.string().optional(),
    /** Atomic delete claim — set true before an unbuild reopens the lines. Blocks a concurrent
     *  compositing start (setCompositing guards on its absence) so a delete can't race a Generate. */
    deleting: z.boolean().optional(),
    spec: GangSheetSpecSchema,
    storeIds: z.array(z.string()),
    /** Denormalized human brand label of the batch's store (single-store per the no-mix guard), for
     *  floor artifacts (sheet caption + pick list) so an operator never sees a raw storeId or an
     *  order # that collides across brands. Resolved from the store registry at build; absent on
     *  batches built before this shipped (callers fall back to `storeIds`). */
    brandLabel: z.string().optional(),
    lineCount: z.number().int().nonnegative(),
    pieceCount: z.number().int().nonnegative(),
    /** The film roll this batch was nested onto — the resolved DEFAULT when the build omitted a rollId,
     *  else the explicit pick. Denormalized build-time provenance for film accounting (Phase 2 roll
     *  consumption). Absent when no roll resolved (no default registered → engine-default width) and on
     *  batches built before this field existed. May dangle if the roll is later deleted; the real dims
     *  already live in `spec`, so nesting never depends on it. */
    rollId: z.string().optional(),
    /** Placement location slugs produced by SCREEN PRINT for this batch — those placements were
     *  excluded from the sheet. Recorded so the detail view can explain the missing placements;
     *  absent (not empty) on an all-DTF batch. */
    screenPrintLocations: z.array(z.string()).optional(),
    /** The screen-print worklist (one entry per excluded placement of a batched line); absent on an
     *  all-DTF batch and on batches built before this feature. Powers the worklist + the mark-printed
     *  gate (a batch with outstanding screen-print work can't be marked printed). */
    screenPrintWork: z.array(ScreenPrintWorkItemSchema).optional(),
    /** When the operator marked the screen-print work done; absent until then (and always absent when
     *  there is no screen-print work). Gates mark-printed for a mixed batch. */
    screenPrintedAt: z.string().optional(),
    // Filled by the compositor once the sheet(s) render.
    sheetCount: z.number().int().nonnegative().optional(),
    densityPct: z.number().min(0).max(1).optional(),
    sheets: z.array(GangSheetSummarySchema).optional(),
    previewKey: z.string().optional(),
    printReadyKeys: z.array(z.string()).optional(),
    /** S3 keys for each sheet's downscaled preview, parallel to `sheets`/`printReadyKeys` by index.
     *  Presigned into `sheets[].previewUrl` at read time. Absent on pre-per-sheet-preview batches. */
    sheetPreviewKeys: z.array(z.string()).optional(),
    // Pull station (pulled-blanks v2) — see `batchPulledFields`.
    ...batchPulledFields,
    createdBy: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    /** When the operator marked the batch PRINTED (production done). Set on READY→PRINTED, cleared on
     *  the PRINTED→READY undo. Absent until first printed. */
    printedAt: z.string().optional(),
    /** Set on a REISSUE batch (per-transfer reprint) → the SOURCE batchId it reprints. Presence marks
     *  this batch as a reissue: it carries its OWN items + lifecycle + replacement-blank consume, but
     *  NEVER claims or flips the source order lines (mark-printed/delete/unmark skip line state for it).
     *  Absent on a normal batch. */
    reissueOf: z.string().optional(),
    /** The source batch's human "Sheet #N", denormalized for the reprint label; absent if the source
     *  predates the seq counter. */
    reissueOfSeq: z.number().int().positive().optional(),
    /**
     * EVERY source sheet this reprint draws from, when it is a cross-source merge. `reissueOf` /
     * `reissueOfSeq` stay populated with the PRIMARY (lowest-seq) source and remain the single-source
     * answer, because every correctness reader of `reissueOf` is a truthiness check — the ship gate
     * (`reprint-visibility`), `settleLine`'s early return, the mark-printed branch. Only the batch card
     * reads its VALUE, and only to render a label. So a merged sheet stays a reissue everywhere that
     * matters and this array exists purely so the card can say "reprint of #12, #15, #18" instead of
     * naming one of three. Absent on a single-source reissue (and on every non-reissue batch).
     */
    reissueSources: BatchReissueSourceSchema.array().optional(),
    /**
     * Set on a reprint sheet built by the QUEUE FLUSH (P5), as opposed to the direct reissue route.
     *
     * Not cosmetic. It tells the delete/unbuild path that queue rows are expected to point at this
     * batch, so finding NONE is an anomaly worth shouting about rather than a normal empty result —
     * which is exactly the "an empty result is not evidence" shape that has bitten this repo before.
     * The one way to reach that state is mark-printed (which retires the rows) followed by
     * unmark-printed and then a delete; see the residual documented on the delete handler.
     */
    fromReprintQueue: z.literal(true).optional(),
    /**
     * TRUE when this reprint sheet carries at least one garment REBUILT FROM THE CATALOG rather than cloned
     * off a surviving source sheet (P7 `CATALOG` origin) — a sheet-level rollup of
     * `BatchItemRecord.rebuiltFromCatalog`.
     *
     * ⚠️ NOT redundant with `reissueOf === CATALOG_REISSUE_SOURCE`, which is the narrower fact "EVERY garment
     * here was rebuilt". Merging an office re-send onto the day's bench tears is the feature, so the MIXED
     * sheet is the normal case — and on a mixed sheet `reissueOf` names a real source, so the sentinel is
     * absent while rebuilt garments are present. This is also why the rollup lives on the header: the batch
     * LIST renders `BatchSummary` and items are a separate read, so "this sheet has art nobody verified"
     * cannot be derived on a list page without N extra reads.
     *
     * `z.literal(true).optional()` — presence-means-true, the same shape as `fromReprintQueue` above and
     * `alreadyReprintedOnce` on the queue row. OPTIONAL TO READ, REQUIRED TO WRITE: a required field here
     * would fail every legacy batch's parse during a rolling deploy, and `getBatch`/`listBatches`/`setReady`
     * /`setFailed` all parse through this schema in the compositor and sweeper lambdas as well as the API.
     */
    reissueRebuilt: z.literal(true).optional(),
});
/**
 * DDB record: `BATCH#<batchId>` / `ITEM#<storeId>#<orderId>#<lineId>#<location>`. One row per
 * PLACEMENT of a batched line — the builder expands a MAPPED line into one item per placement in
 * its product mapping (catalog-mapping v1), so `location` is part of the SK (a line with a front +
 * a back print is two rows). Carries the band-resolved print geometry (`widthIn`/`heightIn` at the
 * line's `sizeBand`) so the compositor re-nests deterministically without a re-lookup.
 */
export const BatchItemRecordSchema = z.object({
    PK: z.string(),
    SK: z.string(),
    entity: z.literal('batchItem'),
    batchId: z.string(),
    storeId: z.string(),
    orderId: z.string(),
    lineId: z.string(),
    variantId: z.string().nullable().optional(),
    sku: z.string().nullable().optional(),
    designId: z.string(),
    /** The resolved design's human name, denormalized at build so the manifest reads names, not
     *  UUIDs (the builder already loads the design record to validate it). Optional: items batched
     *  before this field existed fall back to the short id in the UI. */
    designName: z.string().optional(),
    /** Print location slug (front / back / left-chest / …) — the placement identity within the line. */
    location: z.string().min(1),
    /** The line's size band; the geometry below is this band's dims from the product placement. */
    sizeBand: SizeBandEnum,
    widthIn: z.number().positive(),
    heightIn: z.number().positive(),
    quantity: z.number().int().positive(),
    resolvedBlankSku: z.string().optional(),
    /** When this DTF placement's owed copies are ALL pressed onto blanks — the per-PLACEMENT completion
     *  flag that `settleLine`, the press worklist, and the pulled-stock ship-short guards read
     *  (per-garment completion, B2: when every placement of a line is pressed, the line flips PRINTED
     *  and consumes its blank). Absent until the last owed copy is pressed. Kept in sync with
     *  `pressedCopies` by the mark handler: set when every owed copy is present, cleared when one drops. */
    pressedAt: z.string().optional(),
    /** Per-copy DTF press check-off: the sorted, deduped set of 0-based copy indexes (0..quantity-1) of
     *  this placement that have been individually pressed. Absent = none marked individually — and a set
     *  `pressedAt` with NO `pressedCopies` is a legacy row that predates per-copy tracking, read as fully
     *  pressed. Source of truth for the per-copy checkbox UI; `pressedAt` above is derived from it. DTF
     *  only — screen-print stays coarse (run-level) and never sets this.
     *  Stored in DynamoDB as a Number Set (NS) so the mark handler can `ADD`/`DELETE` one copy
     *  atomically without a version lock; the DocumentClient unmarshals NS → a JS `Set`, so we normalize
     *  it to a sorted array here — every reader and the JSON API then sees a stable ordered list. */
    pressedCopies: z
        .preprocess((v) => (v instanceof Set ? [...v].map(Number).sort((a, b) => a - b) : v), z.array(z.number().int().nonnegative()))
        .optional(),
    /** True when this placement was PULLED from the printed-transfer shelf at build instead of nested
     *  (printed-transfer Phase B). It is EXCLUDED from the gang sheet (not on the film, not on the
     *  cut-list) but is still a pressed unit — pressedAt-tracked, surfaced on the press worklist, and it
     *  gates mark-printed — and its film was decremented from the ledger (reversed on unbuild). */
    pulledFromStock: z.boolean().optional(),
    /**
     * The printed-transfer SHELF band a PULLED film came off, when it differs from `sizeBand`: a youth
     * garment pressed with its design's byte-identical adult film (one-size fronts — see
     * `rest-api/src/services/shared-film.ts`). The pull decrement AND the unbuild film-return both key
     * the ledger on `shelfBand ?? sizeBand`, so a returned film lands on the pile it came from.
     *
     * `sizeBand` above stays the GARMENT band — it drives the label's YOUTH marker and the blank — which
     * is why this is a second field rather than a rewrite of that one. Absent on every nested item and
     * every pull whose pile matches the garment band. Optional to read: no worker writes item rows, and
     * a row written before this field reads as `sizeBand`, which is what it was pulled under.
     */
    shelfBand: SizeBandEnum.optional(),
    /** REISSUE items only: this reprinted garment is TRANSFER-ONLY — only its transfer was ruined, the
     *  blank is intact, so mark-printed draws NO replacement blank for its line. Per item (all placements
     *  of a transfer-only line carry it) so one reprint batch can mix transfer-only + normal garments.
     *  Absent on normal (blank-drawing) reissues and on all non-reissue batches. */
    reissueTransferOnly: z.boolean().optional(),
    /**
     * QUEUE-FLUSH reissue items only (P5): WHICH garments of the source line this reprint carries, as
     * their 1-based ranks on the SOURCE sheet, ascending and parallel to the reprint's own copy indexes
     * — `reissueRanks[copyIndex]` is the source rank of copy `copyIndex`.
     *
     * Needed because the reprint re-ranks from 1: reprinting garments #3 and #7 of a 10-piece line
     * produces one item with `quantity: 2` whose copies are 0 and 1, and nothing else on the row says
     * which two shirts those are. The undo path needs exactly that mapping — when a reprint sheet is
     * deleted it must DELETE the queue rows whose garment was already pressed and RETURN the rest, and
     * "already pressed" is a statement about a copy index while a queue row is identified by a source
     * rank. Without it the undo can only be all-or-nothing per line, which either re-queues a garment
     * that is already fixed (a wasted film and blank) or drops one that is not.
     *
     * Absent on direct reissues and every non-reissue item; readers must treat absence as "unknown"
     * and fall back to the conservative whole-line answer.
     */
    reissueRanks: z.array(z.number().int().positive()).optional(),
    /**
     * QUEUE-FLUSH reissue items only (P7): this placement's geometry was REBUILT FROM THE LIVE PRODUCT
     * MAPPING, not cloned off a surviving source sheet — the source sheet was deleted at close-out weeks
     * before the customer called about a lost parcel.
     *
     * Per ITEM rather than per batch because a merged sheet is the normal case and *"which garment do I
     * eyeball"* is a per-garment question — direct precedent is `reissueTransferOnly` above, which exists
     * for exactly that reason. The header's `reissueRebuilt` is the rollup of this field.
     *
     * Presence-means-true; absent on every clone-from-sheet reprint and every non-reissue item. Readers
     * must treat absence as "cloned from a sheet", which is the pre-P7 behaviour exactly.
     */
    rebuiltFromCatalog: z.literal(true).optional(),
    /** REISSUE items only: this garment was reprinted under an explicit OWED-GATE OVERRIDE — nobody owed
     *  it (refunded / cancelled) and a human chose to reprint anyway, spending a real film and a real
     *  blank. Stamped DURABLY on every item the override creates, because a log line is not an audit
     *  trail for a money decision: this is the record of who spent it and why. Absent on every normal
     *  reprint. */
    reissueNotOwed: z.boolean().optional(),
    reissueNotOwedReason: ReissueNotOwedReasonEnum.optional(),
    /** The signed-in operator who authorised the override (cognito email, else sub). */
    reissueNotOwedBy: z.string().optional(),
    createdAt: z.string(),
});
// ── REPRINT QUEUE ────────────────────────────────────────────────────────────────────────────────
// A shared, server-side queue of garments that need reprinting, collected across the day from three
// physically separate stations and flushed onto ONE gang sheet.
//
// Why server-side and not a browser cart: the shop runs three stations on three DEVICES (shipping,
// heat press, cutting bench). Three localStorage carts is three sheets, which is the waste the
// feature exists to remove.
//
// Why one queue and not N held DRAFT batches: `deleteBatch` removes ITEM rows BEFORE the META row in
// independently-durable chunks, and the ship gate derives its refs purely from items — so a
// half-deleted draft reads as "META alive, items gone", a confident zero, and PROMOTES the order.
/** The physical station that reported it. `stations` accumulates every station that has reported. */
export const ReprintStationEnum = z.enum(['PRESS', 'CUTTING', 'SHIPPING', 'OFFICE']);
/**
 * One placement of the queued garment, cloned EAGERLY from the source batch item at add time.
 *
 * Eager, not a reference, because the shop's daily close-out DELETES batches — a reference would die
 * overnight and take the reprint with it. These are exactly the fields `buildBatchItemRecord` needs, so
 * a flush can rebuild the item without re-reading the catalog (a re-map since the original build
 * therefore cannot corrupt the reprint).
 */
export const ReprintQueuePlacementSchema = z.object({
    location: z.string().min(1),
    designId: z.string(),
    designName: z.string().optional(),
    sizeBand: SizeBandEnum,
    widthIn: z.number().positive(),
    heightIn: z.number().positive(),
    resolvedBlankSku: z.string().optional(),
    variantId: z.string().nullable().optional(),
    sku: z.string().nullable().optional(),
});
/**
 * A queued garment. `PK = 'REPRINTQ#OUTSTANDING'` (one global partition),
 * `SK = 'ENTRY#<storeId>#<orderId>#<lineId>#<cohortKey>#<rank:3>'`.
 *
 * **Partition membership IS outstanding-ness.** There is no `status` attribute the ship gate must
 * remember to filter on, and no GSI anywhere in the gate's path — a GSI here would read as a false
 * zero during propagation, which promotes an order whose garment is sitting ruined on the bench. A row
 * leaves this partition only when its reprint sheet is marked printed, or on a reasoned dismiss.
 *
 * **Identity is the physical garment WITHIN ONE PRODUCTION RUN** — `(storeId, orderId, lineId,
 * cohortKey, owedRank)`. ⚠️ It was `(storeId, orderId, lineId, owedRank)` until PR-3b, and that sentence
 * is now FALSE: `owedRank` is allocated sheet-locally, so the same `(line, rank)` names DIFFERENT physical
 * garments across runs and the cohort is what tells them apart. Not the print
 * location: the cutter (ruined the back transfer) and the press (scorched the same shirt) would
 * otherwise create two rows for one garment and draw two blanks. Rank is mandatory; without it
 * garment #2 and garment #5 of a qty-7 line collapse into one.
 */
export const ReprintQueueEntryRecordSchema = z.object({
    PK: z.literal('REPRINTQ#OUTSTANDING'),
    SK: z.string(),
    entity: z.literal('reprintQueueEntry'),
    /** Server-minted uuid, stable across merges. The SCOPE KEY for every reverse — a delete or un-flush
     *  conditions on it, so an SK re-created by a later add (new entryId) survives an older operation's
     *  reverse instead of being silently destroyed by it. */
    entryId: z.string(),
    storeId: z.string(),
    orderId: z.string(),
    /** Shopify order name (#1001) when known — display only. */
    orderName: z.string().optional(),
    lineId: z.string(),
    /** 1-based ordinal of THIS garment within its line. Part of the identity, never derived at flush. */
    owedRank: z.number().int().positive(),
    /** BATCH = cloned from a source sheet's frozen items. CATALOG = rebuilt from the live product
     *  mapping because the source sheet is long gone (a lost package, weeks later). */
    origin: z.enum(['BATCH', 'CATALOG']),
    sourceBatchId: z.string().optional(),
    sourceBatchSeq: z.number().int().positive().optional(),
    /**
     * WHICH PRODUCTION RUN this garment belongs to — the segment that makes the identity honest.
     *
     * `owedRank` is documented as a line-level ordinal (`garment-identity.ts`) but ALLOCATED sheet-locally
     * from the batch item's own quantity (`scan.ts`, and the add route's `batchedQty` gate). So the same
     * `(line, rank)` names DIFFERENT physical garments across runs: a close-out delete calls
     * `resetLineOpen`, the line re-batches at its current `unfulfilledQty`, and its ranks restart at 1
     * while physically being copies 4 and 5. Phase 7 (an office re-send of a lost parcel) is the first
     * path where two runs are outstanding SIMULTANEOUSLY, and then the rows collide.
     *
     * The collision is not a 409 — `add` carries no condition at all, so it MERGES. And because
     * `if_not_exists` is per-attribute and a CATALOG row never WRITES `sourceBatchId`, a later BATCH
     * report at the same rank DONATES its live-sheet id to the CATALOG row, defeating the `NO_SOURCE`
     * guard and producing a line that flushes with zero blockers against the wrong sheet's frozen art.
     * Putting the run in the SK makes that structurally impossible.
     *
     * Format: `B<sourceBatchId>` for a BATCH-origin row. Deliberately NOT `sourceBatchSeq` — that is
     * explicitly optional and purely cosmetic (it renders as "off sheet #41"), and baking a display
     * number into an immutable identity is a trap. A future CATALOG origin gets `C.<resendToken>`, and
     * ⚠️ never a constant sentinel: `sourceBatchId ?? 'CATALOG'` would make two independent lost-parcel
     * re-sends of one line collide on ONE SK and merge — two owed shirts, one row, one film, one blank.
     *
     * OPTIONAL on the record, and that is load-bearing rather than laziness. Making it required with no
     * shim is a shop-wide production stop: during a rolling Api update one row written by pre-cohort code
     * would fail this schema, so `drain` → `listAll` → `findOutstandingReprints` all throw and the row
     * becomes invisible, undismissable AND unflushable — recoverable only by a hand-crafted DeleteItem.
     * Every WRITER supplies it (`reprintEntrySk` requires it); only READS tolerate its absence.
     */
    cohortKey: z.string().min(1).max(160).optional(),
    /** The frozen copy count of the line on its source sheet. Both the "can't reprint more than we
     *  printed" ceiling and the owed gate's denominator, so it is captured at add time and never
     *  recomputed from a live line that may since have been partially refunded. */
    batchedQty: z.number().int().positive(),
    /** The garment's FULL frozen placement set — every print it carries, never a filtered subset. It is
     *  `if_not_exists`-frozen at the storage layer, so a narrow first write would be permanent and a
     *  later reporter's film could never be added. Which of these actually go back on film is decided
     *  per read by `reprintPlacementsFor` from `transferOnly` + `ruinedLocations`. */
    placements: z.array(ReprintQueuePlacementSchema).min(1),
    reason: ReprintReasonEnum,
    /** The station that FIRST reported this garment. */
    station: ReprintStationEnum,
    /** Every station that has reported it — a DDB String Set, `ADD`ed to on each merge so two stations
     *  reporting the same garment is one row, not a lost report. Unmarshalled by the DocumentClient as a
     *  JS `Set`; normalized to a sorted array here so every reader and the JSON API see a stable list. */
    stations: z
        .preprocess((v) => (v instanceof Set ? [...v].map(String).sort() : v), z.array(ReprintStationEnum))
        .optional(),
    /** TRUE only when every reporter says the blank survived, so the flush draws no replacement blank.
     *  A ONE-WAY DOWNGRADE at the storage layer: `true` is written with `if_not_exists`, `false`
     *  unconditionally. Cutter-then-press must end at `false` — otherwise the scorched blank is never
     *  replaced and the order ships one garment short. */
    transferOnly: z.boolean().optional(),
    /** Every print location any station has reported RUINED on this garment — a DDB String Set, `ADD`ed
     *  to on each merge, so a second reporter WIDENS it and can never narrow it. NOT a filter on
     *  `placements`, which stays the garment's full frozen set: the reprint scope is derived on every
     *  read by `reprintPlacementsFor`, never frozen at rest.
     *
     *  Only ever narrows the sheet when `transferOnly` is also true — a fresh blank needs every print.
     *  Absent (a legacy row, or a station like the press kiosk that reports garments rather than films)
     *  means "unknown" ⇒ every placement reprints, which is the pre-feature behaviour exactly. */
    ruinedLocations: z
        .preprocess((v) => (v instanceof Set ? [...v].map(String).sort() : v), z.array(z.string().min(1)))
        .optional(),
    /** TRUE once ANY station has reported this garment WITHOUT naming which of its films were ruined —
     *  the press kiosk does exactly that, because a scan identifies a garment, not a film.
     *
     *  A ONE-WAY UPGRADE, written unconditionally, exactly like `transferOnly = false`. It is the DURABLE
     *  half of "unknown dominates": a merge collapses two reports onto ONE row, so without this marker the
     *  second reporter's "I named no film" vanishes and the row keeps the first reporter's narrow set —
     *  which under-prints, i.e. ships the garment still ruined. `reprintPlacementsFor` forces the FULL
     *  placement set whenever it is true, whatever `ruinedLocations` holds. */
    wholeGarmentReported: z.boolean().optional(),
    /** Which placement the reporter pointed at FIRST. AUDIT ONLY — never part of the key (identity is
     *  the physical garment, so the cutter and the press reporting one shirt stay one row) and never
     *  load-bearing on what gets printed. `ruinedLocations` is the field that decides that. */
    damagedLocation: z.string().optional(),
    note: z.string().max(500).optional(),
    /** Bumped on every merge. The flush conditions each consume on the `rev` the operator reviewed, so a
     *  station merging a report mid-flush cancels the whole transaction rather than printing a stale
     *  view of the garment. */
    rev: z.number().int().nonnegative(),
    /** Set when a flush consumed this entry into a reprint sheet. The row STAYS in the partition (still
     *  outstanding, still blocking) until that sheet is marked printed — which is what makes both the
     *  flush window and the nightly batch delete safe. */
    flushedIntoBatchId: z.string().optional(),
    flushedAt: z.string().optional(),
    /** The `rev` this row carried when the flush claimed it.
     *
     *  The retire at mark-printed deletes the row only while `rev` still EQUALS this — so a station that
     *  merged a fresh report after the sheet was built (rev moves, this does not) keeps its report: the
     *  delete is refused, the row survives, and the order stays blocked until someone flushes again.
     *  Without it the second defect report would be silently swallowed by the first reprint's close. */
    flushedAtRev: z.number().int().nonnegative().optional(),
    /** Stamped when a flushed sheet is deleted and the entry comes back to the queue, so the card can say
     *  "came back from sheet #N". A silent resurrection is as bad as a silent drop. */
    returnedFromBatchId: z.string().optional(),
    /**
     * WHY the row came back, because `returnedFromBatchId` alone cannot say — and the two causes need
     * OPPOSITE defaults on the flush selection, which is the last gate before a second film is spent.
     *
     * · `SHEET_GONE` — the sheet was deleted or unbuilt BEFORE this garment's replacement was made. The
     *   garment still needs film, so it stays pre-selected. Nothing changes for this case.
     * · `ALREADY_REPRINTED` — a replacement for this garment was ALREADY made (its sheet reached PRINTED,
     *   or the copy was pressed) and the row survived only because a station reported the same garment
     *   again after the sheet was built. That report may be a genuine second defect (the reprint itself
     *   was ruined) or a duplicate claim of the first one, and NOTHING in the data distinguishes them.
     *
     * Absent means UNKNOWN, and unknown must dominate toward the *unrecoverable* direction: a wrongly
     * pre-selected row spends a film, a cure cycle and a blank that cannot be recovered, while a wrongly
     * un-selected one waits for a human to tick it. So absent is treated as `ALREADY_REPRINTED` by
     * `returnedNeedsReview`, NOT as `SHEET_GONE`. (No such row exists today — the prod partition is
     * empty — but a legacy row must not be born pre-ticked.)
     */
    returnedReason: z.enum(['SHEET_GONE', 'ALREADY_REPRINTED']).optional(),
    /**
     * MONOTONE marker: a replacement for this garment has been committed to film at least once. Set
     * alongside `returnedReason: 'ALREADY_REPRINTED'` and **never cleared or downgraded**.
     *
     * `returnedReason` alone is not enough because it is last-write. A garment that came back
     * `ALREADY_REPRINTED`, was deliberately re-flushed, and then had THAT sheet deleted before printing
     * would be re-stamped `SHEET_GONE` — losing the memory that film was already spent on it once and
     * silently restoring the pre-ticked default. This field cannot go backwards, so the review
     * requirement survives every later round trip.
     */
    alreadyReprintedOnce: z.literal(true).optional(),
    /**
     * TRUE once a reprint sheet has been built for a DIFFERENT production run of this row's order line.
     *
     * The durable half of the cross-run duplicate gate, and it exists because the derived half evaporates.
     * The queue page holds a row back for a deliberate tick while its line has garments in flight from more
     * than one run — but that is read off live partition membership, and mark-printed RETIRES every row of
     * the sheet it closes. So: day one the operator correctly leaves this row unticked and prints the other
     * run; that sheet closes; the sibling rows vanish; and on day two this row is the line's only row, is no
     * longer held back, and prints pre-selected. One shirt, two films, two blanks, no warning at any point.
     *
     * Written AFTER the sheet's transaction commits (a marker written before a flush that then loses a race
     * would hold a row back for a print that never happened), unconditionally and never cleared — the same
     * monotone shape as `alreadyReprintedOnce`, for the same reason: it must not be walked back by a later
     * write that happens to know less. Optional to READ so a row written by pre-marker code still parses.
     *
     * ⚠️ It does NOT mean "this is a duplicate". Two runs of a line usually hold different shirts, which is
     * why this holds a row back for a human rather than refusing it. Nothing in the data can decide.
     */
    crossRunFlushedOnce: z.literal(true).optional(),
    /** Cognito email (else sub). Server-stamped — never a device clock or a client-supplied identity. */
    addedBy: z.string(),
    addedAt: z.string(),
    updatedAt: z.string(),
});
/** API view of a queued garment — the record minus the DDB keys. */
export const ReprintQueueEntrySchema = ReprintQueueEntryRecordSchema.omit({
    PK: true,
    SK: true,
    entity: true,
}).extend({
    /** The entry's sort key, echoed so the flush can name exactly the rows the operator reviewed. */
    sk: z.string(),
});
/**
 * `POST /batches/reprint-cart/entries` body.
 *
 * `sourceBatchId` + `owedRank` identify the garment; everything cloneable is read server-side from the
 * source batch's frozen items, so a client cannot inject geometry or a blank SKU.
 */
export const ReprintQueueAddBodySchema = z.object({
    storeId: z.string().min(1),
    orderId: z.string().min(1),
    lineId: z.string().min(1),
    owedRank: z.number().int().positive(),
    sourceBatchId: z.string().min(1),
    reason: ReprintReasonEnum,
    station: ReprintStationEnum,
    /** Blank survived — the flush should reprint the transfer only. Omitted means the blank is ruined,
     *  which is the SAFE default: it draws a replacement rather than assuming one is not needed. */
    transferOnly: z.boolean().optional(),
    /** Which of the garment's prints were RUINED, so only those go back on film — the cutting bench
     *  usually tears one film and leaves the rest of the garment fine.
     *
     *  Honoured ONLY alongside `transferOnly: true`; a garment drawing a fresh blank reprints every
     *  placement regardless of what is sent here, because a new shirt needs every print. The server
     *  intersects this against the source sheet's frozen placements and drops anything that does not
     *  match, so an unknown location widens the scope rather than emptying it. Free strings, matching
     *  `ReprintQueuePlacement.location`; capped well above any real garment. */
    ruinedLocations: z.array(z.string().min(1)).max(12).optional(),
    damagedLocation: z.string().optional(),
    note: z.string().max(500).optional(),
});
/**
 * One garment of the SAME order line already queued off a DIFFERENT production run — the advisory that
 * makes a second run's report visible at the station rather than at the end of the day.
 *
 * ⚠️ It deliberately does NOT claim the rows are the same shirt, and `owedRank` here is display only.
 * Rank is allocated per RUN (a re-batch restarts it at 1 while physically being copies 4 and 5), so no
 * comparison across runs identifies a garment — that is exactly why this is a WARNING and not a refusal.
 * Two runs of one line legitimately have their own ruined garments; the operator is the only one who can
 * tell "that is the shirt I already reported" from "that is a different shirt".
 */
export const ReprintQueueAlreadyOutstandingSchema = z.object({
    entryId: z.string(),
    /** Absent on a row written before the run went into the queue key. */
    cohortKey: z.string().optional(),
    /** Display only — see the schema note. Absent nowhere; every row has one. */
    owedRank: z.number().int().positive(),
    /** "off sheet #41". Absent when the source sheet's number was never captured. */
    sourceBatchSeq: z.number().int().positive().optional(),
    /** TRUE when this one is already on a reprint sheet — i.e. its film is committed, not merely queued. */
    onFilm: z.boolean(),
});
export const ReprintQueueAddResponseSchema = z.object({
    entry: ReprintQueueEntrySchema,
    /** True when this add merged into a row another station had already created. Never a 409 — the second
     *  reporter is reporting a genuinely different defect on the same garment. */
    merged: z.boolean(),
    /**
     * Other outstanding rows for this ORDER LINE from a different production run, newest information the
     * station can act on: *"this line already has a garment queued off sheet #41 — if that is the same
     * shirt, reporting it again builds a second sheet."*
     *
     * ⚠️ ADVISORY, and absent means "none found OR the lookup failed". The read is fail-open by design (a
     * 500 on a defect report loses the report, which is far worse than a missing warning), so this field
     * must never be the thing a gate depends on. The authoritative duplicate gate reads the whole queue on
     * the reprint-queue page, and the two cross-run refusals are server-side at flush.
     */
    alreadyOutstanding: z.array(ReprintQueueAlreadyOutstandingSchema).optional(),
});
export const ReprintQueueListResponseSchema = z.object({
    entries: z.array(ReprintQueueEntrySchema),
    /** Literal `true`, so a response can never be constructed from a read that silently returned nothing.
     *  An empty array plus `checked: true` is a real zero; a failed read must throw, not degrade to []. */
    checked: z.literal(true),
});
export const ReprintQueueRemoveBodySchema = z.object({
    /** Mandatory. Removing a garment from the queue un-blocks its order, so it is a decision that has to
     *  be attributable — it writes a production event. */
    reason: z.string().min(3).max(500),
});
/**
 * DDB idempotency marker for a reissue: `REISSUETOKEN#<clientToken>` / `MARKER` → the batch that token
 * minted. Written INSIDE the same `transactWrite` as the batch header + items, so claim and create are
 * atomic and cannot tear. Two consequences worth stating: the loser of a concurrent same-token race
 * replays (it never 500s, which is how a human would retry with a FRESH token and mint a second film),
 * and "marker present but batch absent" can only mean the batch was DELIBERATELY deleted — so a replay
 * refuses instead of resurrecting a reprint the operator just discarded.
 *
 * Carries NO `ttl`, deliberately: an expired marker would re-open the duplicate window, and TTL
 * deletion is best-effort (it can lag ~48h) — unpredictable in the unsafe direction. These rows are
 * also the audit trail of which token minted which reprint.
 */
export const BatchReissueTokenRecordSchema = z.object({
    PK: z.string(),
    SK: z.literal('MARKER'),
    entity: z.literal('batchReissueToken'),
    batchId: z.string(),
    sourceBatchId: z.string(),
    /** sha256 of the AGGREGATED selection. A replay carrying a different selection is a 409, never a
     *  200 — otherwise an operator who edits the selection after a failure gets the first batch back and
     *  the garment they added is silently never reprinted. Excludes the override + the token itself. */
    fingerprint: z.string(),
    createdAt: z.string(),
    createdBy: z.string(),
});
/** API view of a batch (drops DDB keys). */
export const BatchSummarySchema = z.object({
    batchId: z.string(),
    seq: z.number().int().positive().optional(),
    status: BatchStatusEnum,
    /** Which production container this is — the field both production pages filter on. Absent ⇒
     *  `'gang-sheet'`; see {@link BatchKindEnum}. */
    kind: BatchKindEnum.optional(),
    failureReason: z.string().optional(),
    storeIds: z.array(z.string()),
    /** Denormalized brand label of the batch's store (floor-artifact display; see BatchRecord). */
    brandLabel: z.string().optional(),
    lineCount: z.number().int().nonnegative(),
    pieceCount: z.number().int().nonnegative(),
    /** Placement locations produced by screen print (excluded from the sheet); absent on all-DTF batches. */
    screenPrintLocations: z.array(z.string()).optional(),
    /** The screen-print worklist (garments to screen-print off the sheet); absent on an all-DTF batch. */
    screenPrintWork: z.array(ScreenPrintWorkItemSchema).optional(),
    /** When the screen-print work was marked done; absent until then / when there is none. */
    screenPrintedAt: z.string().optional(),
    sheetCount: z.number().int().nonnegative().optional(),
    /** How many of the composited `sheets[]` carry a per-sheet `printedAt` — the floor's "2 of 5 run"
     *  progress, on the SUMMARY so the rail can rank a mid-job sheet above an untouched one and show its
     *  progress without fetching every detail (gang-sheets plan D3 / PR-5). Absent until the batch has
     *  composited sheets; `0` once it has and none is ticked. */
    printedSheetCount: z.number().int().nonnegative().optional(),
    densityPct: z.number().min(0).max(1).optional(),
    // Pull station (pulled-blanks v2): the record and the stamps, projected field by field in
    // `toBatchSummary` — plus the derived Σ qty so a list can say "Pulled 49" without walking the map.
    ...batchPulledFields,
    pulledUnits: z.number().int().nonnegative().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    /** When the batch was marked PRINTED (production done); absent until then. */
    printedAt: z.string().optional(),
    /** Source batchId when this is a per-transfer REPRINT (see BatchRecord.reissueOf); absent otherwise. */
    reissueOf: z.string().optional(),
    /** Source batch's "Sheet #N" for the reprint label; absent if the source predates the seq counter. */
    reissueOfSeq: z.number().int().positive().optional(),
    /** Every source sheet a CROSS-SOURCE reprint draws from, so the card can name all of them instead of
     *  just the primary. Absent on a single-source reissue. See BatchRecord.reissueSources. */
    reissueSources: BatchReissueSourceSchema.array().optional(),
    /** True when this reprint sheet came out of the shared reprint QUEUE rather than the direct reissue
     *  action — the card badges it, and the delete path treats a missing queue row on one as an anomaly. */
    fromReprintQueue: z.literal(true).optional(),
    /** True when this reprint sheet carries at least one garment rebuilt from the catalog rather than
     *  cloned off a surviving sheet — see BatchRecord.reissueRebuilt. On this schema because
     *  `toBatchSummary` is an explicit field-by-field projection: a field added to the RECORD and forgotten
     *  here never reaches the SPA and typecheck says nothing. */
    reissueRebuilt: z.literal(true).optional(),
});
/**
 * `POST /batches/:batchId/reissue` response. An EXTENSION of `BatchSummarySchema`, not a wrapper like
 * `{ batch, deduplicated }` — a wrapper would break a stale dashboard bundle reading
 * `res.data.data.batchId`, which is what drives the kiosk's reprint Undo panel. Additive optional
 * fields are simply ignored by an older bundle.
 */
export const BatchReissueResultSchema = BatchSummarySchema.extend({
    /** True when this response REPLAYED an existing reprint for the same idempotency token instead of
     *  creating one. The caller's UI need not distinguish them — that is the point of idempotency — but
     *  it makes the dedupe observable in logs and tests. */
    deduplicated: z.boolean().optional(),
    /** ADVISORY: reprints already outstanding for the same (store, order, line) at create time. Purely
     *  informational — it does NOT block, because a second money gate with its own override is
     *  disproportionate here. It is what makes the two-tab case (two tabs, two different tokens, two
     *  deliberate intents) VISIBLE rather than silent. */
    outstandingReprints: z.array(ReprintRefSchema).optional(),
});
// ── Reprint queue FLUSH (P5) ─────────────────────────────────────────────────────────────────────
// Declared down here, not beside the other reprint-queue schemas, because the result embeds
// `BatchSummarySchema` — a `const` referenced before its declaration is a TDZ crash at import time,
// not a type error, so the whole Lambda fails to start.
/**
 * One queue row the operator reviewed, as named back at flush time.
 *
 * BOTH fields are load-bearing conditions on the write, not hints:
 *  · `entryId` — the SK is REUSABLE by design (dismiss a garment, re-report it minutes later and it
 *    lands on the same SK with a fresh id). Conditioning on the id means a flush can never claim a
 *    DIFFERENT defect report than the one on screen.
 *  · `rev` — bumped by every station's report. If someone merges a fresh report between the operator
 *    reading the queue and confirming the cost, that report may have flipped `transferOnly` false,
 *    i.e. added a replacement blank to the bill. The whole transaction cancels rather than printing a
 *    sheet whose cost nobody agreed to.
 */
export const ReprintFlushEntryRefSchema = z.object({
    entryId: z.string().min(1),
    rev: z.number().int().nonnegative(),
});
/**
 * `POST /batches/reprint-cart/flush` body.
 *
 * There is deliberately NO `clientToken` here, unlike the reissue route. The queue rows ARE the
 * idempotency ledger: a consumed row carries `flushedIntoBatchId`, the transaction that sets it is
 * conditioned on its absence, and a retry therefore finds nothing left to flush and converges onto
 * the sheet the first attempt created. A token would additionally have to answer "which of the N
 * sheets did this token mint?" once the selection spills past the cap — a question with no good
 * answer, and one this design never has to ask.
 */
export const ReprintFlushBodySchema = z.object({
    entries: z.array(ReprintFlushEntryRefSchema).min(1).max(200),
    /** D3: friction on ADD kills the feature; friction here is free — it happens once a day and it
     *  spends film and blanks. Absent ⇒ 422 naming the exact cost, never a silent print. */
    confirmFlush: z.boolean().optional(),
});
/** Why a named entry was NOT consumed. Always reported — a silently dropped garment is a short ship. */
export const ReprintFlushSkipReasonEnum = z.enum([
    /** No row with that entryId is in the queue any more (dismissed, or retired by a sheet that printed). */
    'GONE',
    /** Already carried `flushedIntoBatchId` — it is on an earlier sheet. Its batch appears in `sheets`. */
    'ALREADY_FLUSHED',
    /**
     * A selection that spilled onto several sheets got some of them written and then lost the race for
     * a row on a later one — another station was flushing or re-reporting the same garment. The sheets
     * that DID commit are real and are returned; these garments were not printed.
     *
     * Reported as a partial success rather than an error on purpose: the earlier sheets are already
     * durable, so answering 409 would hide film that exists, and a human who cannot see it prints it
     * again.
     */
    'RACED',
]);
/** One sheet this flush produced (or converged onto), with the cost it actually carries. */
export const ReprintFlushSheetSchema = z.object({
    batch: BatchSummarySchema,
    /** Distinct physical garments on this sheet. */
    garments: z.number().int().nonnegative(),
    /** Transfers = batch item rows (a front+back garment is two). What the film carries. */
    transfers: z.number().int().nonnegative(),
    /** Replacement blanks this sheet will draw at its own mark-printed — transfer-only garments draw none. */
    replacementBlanks: z.number().int().nonnegative(),
    /** The queue entries consumed onto it, so the client can reconcile its cart exactly. */
    entryIds: z.array(z.string()),
});
export const ReprintFlushResultSchema = z.object({
    /** One per sheet. More than one when the selection spilled past the per-sheet cap, or spans stores
     *  (a sheet carries ONE store's brand on its caption and run sheet). Never a refusal — D2. */
    sheets: z.array(ReprintFlushSheetSchema),
    garments: z.number().int().nonnegative(),
    replacementBlanks: z.number().int().nonnegative(),
    /** True when this call created nothing because every named entry was already on a sheet — a retry
     *  converging, which is the whole reason the flush needs no idempotency token. */
    deduplicated: z.boolean(),
    skipped: z
        .array(z.object({
        entryId: z.string(),
        reason: ReprintFlushSkipReasonEnum,
        /** The sheet it is already on, when `ALREADY_FLUSHED`. */
        batchId: z.string().optional(),
    }))
        .optional(),
});
/** One READY batch with outstanding screen-print work, in the cross-batch worklist. `items` holds
 *  ALL of the batch's screen-print placements (each `pressedAt` reflects check-off state, so the UI
 *  can show pressed ones checked and allow un-pressing); a batch drops off the worklist only once
 *  every item is pressed (screenPrintedAt derived on). */
export const ScreenPrintWorklistBatchSchema = z.object({
    batchId: z.string(),
    seq: z.number().int().positive().optional(),
    status: BatchStatusEnum,
    /** Denormalized brand label for floor display; absent on batches built before it shipped. */
    brandLabel: z.string().optional(),
    storeIds: z.array(z.string()),
    createdAt: z.string(),
    /** Every screen-print placement on the batch; `pressedAt` present = already pressed. */
    items: z.array(ScreenPrintWorkItemSchema),
});
/** `GET /screen-print/worklist` data — every READY batch with ≥1 unpressed placement, oldest batch
 *  first. `batchCount` = batches; `garmentCount` = Σ quantity of the still-unpressed placements. */
export const ScreenPrintWorklistResponseSchema = z.object({
    batches: z.array(ScreenPrintWorklistBatchSchema),
    batchCount: z.number().int().nonnegative(),
    garmentCount: z.number().int().nonnegative(),
});
/** Decoration technique of a placement on the unified press worklist (B3). Screen-print placements
 *  live in the batch header's `screenPrintWork[]`; DTF placements are batch ITEM rows. The press
 *  station shows both, and the UI dispatches each check-off to the right EXISTING endpoint by this
 *  discriminator (screen-print → /screen-print/worklist/mark, dtf → /batches/:batchId/press). */
export const PressMethodEnum = z.enum(['screen-print', 'dtf']);
/** One placement on the unified cross-batch PRESS worklist (B3) — a screen-print item OR a DTF item,
 *  tagged with its `method`. Same display shape as a screen-print worklist item; a DTF row's
 *  orderName/title/variantTitle are joined from the order line at read time (the DTF item row doesn't
 *  store them). */
export const PressWorkItemSchema = ScreenPrintWorkItemSchema.extend({
    method: PressMethodEnum,
    /** DTF only: this placement is pressed from a printed transfer pulled off the shelf, not from this
     *  batch's sheet. Display hint so the press station labels it "from shelf stock". */
    fromStock: z.boolean().optional(),
    /** DTF only: per-copy press check-off — sorted set of 0-based copy indexes already pressed (see
     *  BatchItemRecord.pressedCopies). Carries the partial state to the press station so a qty-N DTF row
     *  can show N per-copy checkboxes. Absent on screen-print rows (coarse) and fully-legacy DTF rows. */
    pressedCopies: z.array(z.number().int().nonnegative()).optional(),
    /** REISSUE sheets only: this reprinted garment is TRANSFER-ONLY — it presses onto the shirt already
     *  out on the floor, and mark-printed consumes NO replacement blank for it. See
     *  `BatchItemRecord.reissueTransferOnly`.
     *
     *  Load-bearing on the press station's "Blanks to pull" strip and its printable pull sheet: both must
     *  skip these, or the floor pulls a shirt the ledger never draws down and real stock walks off
     *  untracked. Absent/false = a normal garment that does need its blank pulled. */
    reissueTransferOnly: z.boolean().optional(),
});
/** One READY batch with outstanding press work, in the unified worklist. `items` holds ALL of the
 *  batch's placements across BOTH techniques (already-pressed ones included, so the UI can show them
 *  checked and allow un-pressing); a batch drops off only once EVERY placement — screen-print AND DTF
 *  — is pressed. Mirrors ScreenPrintWorklistBatch, but each item carries the `method` discriminator. */
export const PressWorklistBatchSchema = z.object({
    batchId: z.string(),
    seq: z.number().int().positive().optional(),
    status: BatchStatusEnum,
    brandLabel: z.string().optional(),
    storeIds: z.array(z.string()),
    // Pull station (pulled-blanks v2): the batch chip on the press station reads these.
    ...batchPulledFields,
    pulledUnits: z.number().int().nonnegative().optional(),
    createdAt: z.string(),
    items: z.array(PressWorkItemSchema),
});
/** `GET /press/worklist` data — the unified cross-batch press station: every READY batch with ≥1
 *  unpressed placement (screen-print OR DTF), oldest batch first. Unlike the screen-print-only
 *  worklist, a batch whose screen print is fully pressed still appears while it has unpressed DTF.
 *  `batchCount` = batches; `garmentCount` = Σ quantity of the still-unpressed placements across both
 *  techniques (a garment with an unpressed front + back counts each). */
export const PressWorklistResponseSchema = z.object({
    batches: z.array(PressWorklistBatchSchema),
    batchCount: z.number().int().nonnegative(),
    garmentCount: z.number().int().nonnegative(),
});
/** `POST /screen-print/worklist/mark` body — toggle one placement's pressed state. The placement is
 *  keyed by (storeId, orderId, lineId, location) within the batch (one worklist entry per placement). */
export const ScreenPrintMarkRequestSchema = z.object({
    batchId: z.string().min(1).max(80),
    storeId: z.string().min(1),
    orderId: z.string().min(1),
    lineId: z.string().min(1),
    location: z.string().min(1),
    pressed: z.boolean(),
});
/** `POST /batches/:batchId/sheets/:sheetIndex/print` body — toggle one physical gang sheet's printed
 *  state (the sheet is keyed by its 0-based index in the batch's composited `sheets[]`). */
export const SheetPrintMarkRequestSchema = z.object({
    printed: z.boolean(),
});
/** API view of one batch item (one placement of a batched line). */
export const BatchItemSummarySchema = z.object({
    storeId: z.string(),
    orderId: z.string(),
    lineId: z.string(),
    variantId: z.string().nullable().optional(),
    sku: z.string().nullable().optional(),
    designId: z.string(),
    designName: z.string().optional(),
    location: z.string().min(1),
    sizeBand: SizeBandEnum,
    widthIn: z.number().positive(),
    heightIn: z.number().positive(),
    quantity: z.number().int().positive(),
    /** When this DTF placement's owed copies are all pressed — see BatchItemRecord.pressedAt. */
    pressedAt: z.string().optional(),
    /** Per-copy DTF press check-off: sorted set of 0-based copy indexes pressed — see
     *  BatchItemRecord.pressedCopies. DTF only; absent when none pressed individually / on SP items. */
    pressedCopies: z.array(z.number().int().nonnegative()).optional(),
    /** True when this placement was PULLED from the printed-transfer shelf (off the sheet, pressed from
     *  stock) — see BatchItemRecord.pulledFromStock. Drives the "from shelf stock" press hint. */
    pulledFromStock: z.boolean().optional(),
    /** REISSUE items only: this reprinted garment is transfer-only (no replacement blank drawn) — see
     *  BatchItemRecord.reissueTransferOnly. */
    reissueTransferOnly: z.boolean().optional(),
    /** True when this placement's geometry was rebuilt from the live product mapping rather than cloned
     *  off a surviving source sheet — see BatchItemRecord.rebuiltFromCatalog. Same projection hazard as
     *  `BatchSummary.reissueRebuilt`: `toBatchItemSummary` lists fields by hand. */
    rebuiltFromCatalog: z.literal(true).optional(),
});
/** `POST /batches/:batchId/press` body — toggle DTF press state for a placement, keyed by
 *  (storeId, orderId, lineId, location) within the batch. `pressed` sets/clears the state. When
 *  `copyIndex` is present, toggles just that ONE 0-based copy (per-copy check-off); when absent,
 *  toggles the WHOLE placement (all owed copies) — the bulk / pre-per-copy path. Completion is
 *  per-garment (B2): once every owed copy of every placement of the line is pressed, `settleLine`
 *  flips the line PRINTED and consumes its blank(s); un-pressing reverses. */
export const DtfPressMarkRequestSchema = z.object({
    storeId: z.string().min(1),
    orderId: z.string().min(1),
    lineId: z.string().min(1),
    location: z.string().min(1),
    pressed: z.boolean(),
    /** 0-based copy index (0..quantity-1) to toggle. Absent = toggle the whole placement (all owed
     *  copies) — preserves the bulk "mark all pressed" path and pre-per-copy callers. */
    copyIndex: z.number().int().nonnegative().optional(),
});
/** `GET /batches/:batchId` data — batch + its items + (when READY) the gang-sheet output. */
export const BatchDetailSchema = z.object({
    batch: BatchSummarySchema,
    items: z.array(BatchItemSummarySchema),
    gangSheet: GangSheetResultSchema.optional(),
});
/** `DELETE /batches/:batchId` data — the batch is removed and its lines returned to the queue. */
export const BatchDeleteResultSchema = z.object({
    batchId: z.string(),
    /** Distinct order lines flipped BATCHED → OPEN and re-queued (still-unfulfilled ones). */
    linesReopened: z.number().int().nonnegative(),
});
/**
 * One physical transfer on a composited gang sheet — a single printed design instance the
 * operator peels and presses. Produced by re-nesting the batch's items with the SAME deterministic
 * layout the compositor rendered, then joining each placed copy back to its order line + batch item.
 * `index` is a 1-based number in the canonical reassembly order (sheets in order, then top→bottom /
 * left→right within each sheet — the order a worker physically scans the film), so a future on-film
 * gutter number and this pick-list row stay in lockstep.
 */
export const BatchManifestTransferSchema = z.object({
    index: z.number().int().positive(),
    sheetIndex: z.number().int().nonnegative(),
    storeId: z.string(),
    orderId: z.string(),
    /** Human order number (e.g. "#1042") — from the order line; falls back to the orderId if the
     *  line can't be found (e.g. the order was purged after batching). */
    orderName: z.string(),
    lineId: z.string(),
    /** Product title + variant title (garment + colour/size), joined from the order line. */
    title: z.string(),
    variantTitle: z.string().optional(),
    /** Print location slug (front / back / left-chest / …). */
    location: z.string(),
    designId: z.string(),
    designName: z.string().optional(),
    sizeBand: SizeBandEnum,
    /** The S&S blank this garment prints on (pull-list key); absent on pre-catalog items. */
    resolvedBlankSku: z.string().optional(),
    /** True (unrotated) print size in inches — what the operator matches to the transfer. */
    widthIn: z.number().positive(),
    heightIn: z.number().positive(),
    /** Placement on the sheet, in px at the sheet DPI. `widthPx`/`heightPx` are the placed
     *  (post-rotation) footprint; `rot` true means the compositor rotated the source 90°. */
    xPx: z.number().int().nonnegative(),
    yPx: z.number().int().nonnegative(),
    widthPx: z.number().int().positive(),
    heightPx: z.number().int().positive(),
    rot: z.boolean(),
    /** Which copy of this line+location this transfer is (0-based) — distinguishes the N copies of a
     *  multi-quantity line. */
    copyIndex: z.number().int().nonnegative(),
    /** The source order line was CANCELLED after this transfer was batched — DO NOT print or press it
     *  (film + blank would be wasted). The manifest re-joins the live line, so this reflects the order's
     *  CURRENT state. Only cancellation warns: a line marked FULFILLED in Shopify while still BATCHED is
     *  the normal completed state here (no fulfillment write-back), so it is not flagged. */
    lineCancelled: z.boolean(),
    /** This SPECIFIC copy was refunded / removed from the order AFTER the batch was built, so its
     *  transfer is on the film but its garment is no longer owed — DO NOT press it (a pressed refund
     *  wastes a blank + labor). True iff the line is not cancelled and this copy's 0-based `copyIndex`
     *  is at/after the line's current (refund-netted) quantity — i.e. it is one of the trailing copies
     *  a refund removed. A full refund flags every copy; a 5→3 drop flags copies 3 and 4. Never set for
     *  a merely shipped/fulfilled line (its `currentQty` stays at the batched quantity). */
    lineQtyDropped: z.boolean(),
    /** This transfer is pressed from a printed transfer PULLED FROM STOCK (off the shelf), not printed
     *  on this batch's gang sheet (printed-transfer Phase B). It is therefore NOT on the film — it has
     *  no meaningful sheet position (`sheetIndex`/`xPx`/`yPx` are placeholders) and is EXCLUDED from the
     *  by-sheet cut list, the `transferCount`, and the per-sheet counts — but it IS a pressed unit, so it
     *  appears in the by-order run sheet (tagged "from stock") and its garment still needs a blank pulled.
     *  Absent/false = a normal on-sheet transfer. */
    fromStock: z.boolean().optional(),
    /** REISSUE sheets only: this reprinted garment is TRANSFER-ONLY — it presses onto the shirt that is
     *  already out on the floor, and mark-printed deliberately consumes NO replacement blank for it.
     *  See `BatchItemRecord.reissueTransferOnly`.
     *
     *  Load-bearing on the run sheet: "Blanks to pull" must skip these, or the floor pulls a shirt the
     *  ledger never draws down and real stock walks off untracked. Absent/false = a normal garment that
     *  does need its blank pulled. */
    reissueTransferOnly: z.boolean().optional(),
});
/** One sheet in the manifest — its px dims + how many transfers landed on it. */
export const BatchManifestSheetSchema = z.object({
    sheetIndex: z.number().int().nonnegative(),
    widthPx: z.number().int().positive(),
    heightPx: z.number().int().positive(),
    transferCount: z.number().int().nonnegative(),
});
/**
 * `GET /batches/:batchId/manifest` data — the read-time pick list. Re-nests the batch's persisted
 * items deterministically (no stored per-copy layout) and returns one row per physical transfer so
 * a floor operator can route each cut transfer back to its order. Works on any batch that has items;
 * the layout equals the printed film once the batch is READY. `oversizedCount` is normally 0 (build
 * + compositor block oversized designs) — a positive value flags a legacy/edge batch whose manifest
 * is incomplete.
 */
export const BatchManifestSchema = z.object({
    batchId: z.string(),
    seq: z.number().int().positive().optional(),
    status: BatchStatusEnum,
    /** Brand label of the batch's store — printed on the pick list so a colliding order # is
     *  unambiguous across brands. Absent on batches built before this shipped. */
    brandLabel: z.string().optional(),
    spec: GangSheetSpecSchema,
    sheetCount: z.number().int().nonnegative(),
    transferCount: z.number().int().nonnegative(),
    sheets: z.array(BatchManifestSheetSchema),
    transfers: z.array(BatchManifestTransferSchema),
    /** Placement locations produced by screen print (excluded from the sheet); absent on all-DTF batches. */
    screenPrintLocations: z.array(z.string()).optional(),
    /**
     * Set when this sheet is a REPRINT of another (`BatchRecord.reissueOf`) — carried onto the manifest so
     * a renderer that only ever sees the manifest can refuse work that is unsafe on a reprint.
     *
     * ⚠️ Its first consumer is the QR label print, and the reason is a mislabel: a reprint sheet re-ranks
     * its copies from 1, while the only `copyIndex` → SOURCE-rank map (`BatchItemRecord.reissueRanks`) is on
     * the ITEM and deliberately not here. So `garmentLabels` would encode `owedRank 1` for a garment that is
     * really source rank 3, and that tag scans as a DIFFERENT physical shirt — the press then checks off the
     * wrong garment and the real one ships unpressed. See
     * `docs/label-mislabel-mitigation-plan-v1.md` § 8.4 (L0).
     *
     * A truthiness flag on purpose: the value is only ever "is this sheet's rank space authoritative?".
     * Once `reissueRanks` reaches the manifest the labels can be built correctly and the refusal lifts.
     */
    reissueOf: z.string().optional(),
    // Pull station (pulled-blanks v2): the overlay that renders the pull list from this manifest reads the
    // record straight off it — no second fetch after a tick.
    ...batchPulledFields,
    pulledUnits: z.number().int().nonnegative().optional(),
    oversizedCount: z.number().int().nonnegative(),
    /** How many transfers belong to a line whose order was CANCELLED after batching (`lineCancelled`).
     *  Normally 0; a positive value means the operator must NOT print those rows — the film + blanks
     *  would be wasted on a cancelled order. */
    cancelledTransferCount: z.number().int().nonnegative(),
    /** How many transfers were refunded / removed from their order after batching (`lineQtyDropped`).
     *  Normally 0; a positive value means those copies are on the film but must NOT be pressed — the
     *  garment is no longer owed. Counted separately from `cancelledTransferCount` (a partial refund
     *  leaves the order live), and those copies are already excluded from "Blanks to pull". */
    qtyDroppedTransferCount: z.number().int().nonnegative(),
});
/**
 * `POST /batches/:batchId/mark-printed` body (optional; R1.1). Closing a sheet that still has
 * garments with NO press record bulk-settles them (flips to PRINTED + draws their blanks with no
 * who/when trail) — that is manager-gated and must be explicitly confirmed. A close over fully
 * checked-off garments needs no body at all. Absent/malformed body degrades to "not confirmed"
 * (fail-safe: the request is refused, never silently bulk-settled).
 */
export const BatchMarkPrintedRequestSchema = z.object({
    confirmBulkSettle: z.boolean().optional(),
    /**
     * Closing a REPRINT (reissue) sheet whose transfers have no press record draws a replacement blank
     * per garment with no who/when — the reissue-lane analogue of `confirmBulkSettle`, and a SEPARATE
     * consent on purpose. A reissue flips no lines, so `confirmBulkSettle` never applies to it; reusing
     * that flag would let a confirmation given for "bulk-settle these unrecorded garments" silently
     * authorize "draw N replacement blanks for a sheet nobody pressed". Absent ⇒ not confirmed.
     */
    confirmReprintClose: z.boolean().optional(),
});
/**
 * `POST /batches/:batchId/mark-printed` result: the now-PRINTED batch plus, for each order the batch
 * touched, that order's post-transition DTF-production rollup — so the operator sees which orders the
 * print just completed vs which are still waiting on another sheet.
 */
export const BatchMarkPrintedOrderSchema = z.object({
    storeId: z.string(),
    orderId: z.string(),
    orderName: z.string(),
    production: OrderProductionSchema,
});
export const BatchMarkPrintedResultSchema = z.object({
    batch: BatchSummarySchema,
    orders: z.array(BatchMarkPrintedOrderSchema),
});
/**
 * S2 stub response for `POST /batches` — retained only until the real create route
 * (increment 3) replaces it. Not surfaced in OpenAPI.
 * @deprecated Superseded by `BatchSummary` in S3.
 */
export const BatchCreateStubResponseSchema = z.object({
    eligibleCount: z.number().int().nonnegative(),
    note: z.string(),
});
// ── Rollo scan resolve (read-only) — GET /scan/resolve?code= ────────────────────
/**
 * Outcome of resolving a scanned garment QR against the LIVE order line + its batch. A flat,
 * status-discriminated result (not a union) so the OpenAPI generator + byte-sync stay clean and the
 * dashboard narrows on `status`. Fields populate progressively by how far resolution got.
 */
export const ScanResolveStatusEnum = z.enum([
    'OK', // resolved + still owed → show the design, ready to collate/press
    'UNRECOGNIZED', // not a v1 garment code (a foreign QR)
    'LINE_NOT_FOUND', // decoded, but the order line no longer exists
    'NOT_DTF', // the line is a non-DTF cap (no gang-sheet batch)
    'NOT_BATCHED', // no current READY/PRINTED batch (still OPEN, or DRAFT/COMPOSITING/FAILED) — transient
    'ALREADY_PRINTED', // the batch is PRINTED/SHIPPED (press closed) — design still shown
    'DO_NOT_PRESS', // line cancelled or this copy refunded (owed=false)
    'NOT_IN_BATCH', // rank beyond the batched range (surplus / stale label)
    'STOCK_BIN', // an `s1|<binId>` BIN-label QR (finished-garment shelf) — `stockBin` is populated
]);
export const ScanResolvePlacementSchema = z.object({
    location: z.string(),
    method: PressMethodEnum,
    designId: z.string(),
    designName: z.string().optional(),
    /** Presigned/CDN preview image (WebP), present only when the design's band file is READY. */
    previewUrl: z.string().optional(),
    /** The 0-based copy index this scanned garment presses for on THIS placement (= `owedRank - 1`,
     *  uniform across a garment's placements). It is exactly the `copyIndex` the station posts to
     *  `POST /batches/:batchId/press` to check this placement off. Present whenever placements are
     *  returned (the code always decodes an `owedRank`). */
    copyIndex: z.number().int().nonnegative(),
    /** Whether THIS placement's `copyIndex` copy is already pressed. DTF: derived from the batch item's
     *  `pressedAt` (all owed pressed) OR `pressedCopies` (this copy individually pressed). Screen-print:
     *  the coarse run-level `pressedAt` (SP is not pressed per-copy at the scan station). Drives the
     *  front✓/back✗ check-off + lets the station show an already-done garment without a re-press. */
    pressed: z.boolean(),
    /** DTF only: this transfer is pressed from a printed transfer PULLED OFF THE SHELF, not printed on
     *  this batch's gang sheet — so it is NOT on the film the operator just cut. Same flag the by-order
     *  run sheet tags "from stock" and the press worklist labels "from shelf stock"; the scan station
     *  shows it so the operator goes to the shelf instead of hunting the sheet. Absent/false = on-sheet.
     *  Never set on a screen-print placement (shelf stock is printed DTF film only). */
    fromStock: z.boolean().optional(),
    /** True (unrotated) print size in inches, FROZEN on the batch item at build — the same numbers the
     *  manifest and run sheet show for this transfer. Press-mode scales the design on the platen frame
     *  from these so the guide lines land where the transfer's edges will; the preview image alone
     *  cannot say (it is a downscaled WebP that keeps only the aspect). Absent on a screen-print
     *  placement (`ScreenPrintWorkItemSchema` carries no geometry) and from an API older than this
     *  field — a client must then fall back to an aspect-only box and draw NO guides. */
    widthIn: z.number().positive().optional(),
    heightIn: z.number().positive().optional(),
    /**
     * The design band's alignment box — the part of the file the press guides should line up with
     * (docs/design-alignment-plan-v1.md § 6). Absent means the whole file, which is the identity case:
     * a client that ignores this field, or a design nobody has aligned, places exactly as before.
     */
    alignment: AlignmentGeometrySchema.optional(),
});
export const ScanResolveResultSchema = z.object({
    status: ScanResolveStatusEnum,
    /** The raw scanned string, echoed back. */
    code: z.string(),
    // Decoded garment identity (absent only for UNRECOGNIZED).
    storeId: z.string().optional(),
    orderId: z.string().optional(),
    lineId: z.string().optional(),
    owedRank: z.number().int().positive().optional(),
    // Order-line context (present once the line is located).
    orderName: z.string().optional(),
    title: z.string().optional(),
    variantTitle: z.string().optional(),
    // Batch context (present once the batch is located).
    batchId: z.string().optional(),
    batchSeq: z.number().int().positive().optional(),
    batchStatus: z.string().optional(),
    reissueOf: z.boolean().optional(),
    /** Owed garments on this line — the "N" in "copy R of N". */
    copyOf: z.number().int().nonnegative().optional(),
    // ── Garment identity, for reprinting a burnt hem tag (docs/label-printer-formats-plan-v1.md § 6.3).
    // Both come from the BATCH ITEM, never from the live order line, and that is load-bearing rather
    // than incidental: `line.resolvedBlankSku` / `line.sizeBand` exist, but `buildLineRecord` re-derives
    // them from the synced catalog on every webhook and every ≤15-min reconcile, so they answer "today's
    // blank" (see ResendPreviewGarmentSchema's note: "the blank the customer actually received was never
    // recorded on the line"). The PRINTED label reads the frozen item value through the manifest
    // (batch-manifest.ts → BatchManifestTransferSchema → batch-lib → garmentLabels), so a reprint keyed
    // off the line would name a different blank than the original as soon as a re-map lands.
    /** The blank the batch was BUILT against (`BLANK#<ssSku>`), frozen on the batch item. Absent for an
     *  unmapped line — the reprint then falls back to the Shopify title exactly as the original did. */
    resolvedBlankSku: z.string().optional(),
    /** Print size band of the garment, off the same batch item the manifest carries — so a reprinted tag
     *  shows the YOUTH marker exactly when the original did. Absent only before the batch is resolved;
     *  a client that cannot read it must default to `adult`, matching `garmentLabels`. */
    sizeBand: SizeBandEnum.optional(),
    /** The garment's apparel kind (tee / hoodie) from the catalog product META row, reached through the
     *  line's `productId`. Display-only: Press-mode keys hoodie placement rules on it (a hoodie front sits
     *  lower, under the hood, and shorter, above the pocket). Absent when the line carries no product id,
     *  the product row is gone, the product never classified, or the catalog read failed — the kiosk then
     *  applies the tee rule, never refuses the scan. */
    apparelKind: ApparelKindEnum.optional(),
    /** Front/back (+ any) placements with design + art, present once the batch is resolved. */
    placements: z.array(ScanResolvePlacementSchema).optional(),
    /**
     * `STOCK_BIN` only: what the scanned finished-garment shelf bin holds (bin-labels plan § 3.5).
     * Identity comes from the stock ledger's own rows — the same values the printed label reads — so
     * the station shows exactly what the label claims, plus live on-hand and the count anchors.
     */
    stockBin: z
        .object({
        binId: z.string(),
        stockKey: z.string(),
        onHand: z.number().int(),
        /** Brand + style + color, ` · `-joined; falls back to the bare ssSku. */
        blankLabel: z.string(),
        ssSku: z.string(),
        sizeName: z.string().optional(),
        apparelKind: ApparelKindEnum.optional(),
        placements: z.array(z.object({
            location: z.string(),
            designId: z.string(),
            designName: z.string().optional(),
            /** Preview art (CDN/presigned WebP) when the design's band file is READY. */
            previewUrl: z.string().optional(),
        })),
        lastCountedAt: z.string().optional(),
        lastCountedQty: z.number().int().optional(),
    })
        .optional(),
});
// ── Print-job queue (label-printer-formats plan § 7.2) ──────────────────────────────────────────
/**
 * A one-label print job, queued by a station that CANNOT reach a printer and drained by one that can.
 *
 * WHY a queue at all: every printer is USB/LAN-attached to the one Windows bench (plan D2), and the
 * `/scan` kiosk is an iPad. iPads cannot print to a Rollo or a DYMO at all — neither speaks AirPrint —
 * so a `window.print()` at the kiosk opens a sheet with no thermal printer in it. The kiosk therefore
 * queues; a `/print-agent` tab on the bench polls, renders with the exact `@page` geometry, and prints.
 *
 * ⚠️ WHY THE JOB CARRIES SOURCE FACTS AND NOT A RENDERED LABEL. The tempting alternative is to store
 * the finished label the kiosk already built. That would put a second copy of the dashboard's
 * `GarmentLabel` shape in this file, and duplicated schemas in here DRIFT (the OpenAPI generator has
 * five such copies already). Instead the job stores what `/scan/resolve` returned, and the agent runs
 * the SAME `labelForGarment` the batch sheet runs — one derivation, one model: frozen source facts plus
 * a live blank-name / style-code join at render. The agent must therefore gate on those queries having
 * RESOLVED, exactly as the kiosk does (`docs/label-mislabel-mitigation-plan-v1.md` § 8.3).
 */
export const PrintJobPlacementSchema = z.object({
    location: z.string(),
    method: PressMethodEnum,
    designId: z.string().optional(),
    designName: z.string().optional(),
});
/** The garment a hem tag identifies — the narrowed `/scan/resolve` result the label is built from. */
export const PrintJobGarmentSchema = z.object({
    /**
     * The QR payload, VERBATIM as scanned.
     *
     * ⚠️ Never recomputed from (storeId, orderId, lineId, rank) anywhere in this flow. A reprint sheet
     * re-ranks its copies from 1, so a recomputed payload can name a DIFFERENT physical garment — the
     * press then checks off the wrong shirt and the real one ships unpressed (mislabel plan § 8.4).
     * Passing the scanned string through end to end makes that whole hazard unreachable.
     */
    code: z.string().min(1),
    storeId: z.string().min(1),
    orderId: z.string().min(1),
    lineId: z.string().min(1),
    orderName: z.string(),
    /** 1-based owed rank — DISPLAY only ("copy R of N"); `code` is the identity. */
    rank: z.number().int().positive(),
    copyOf: z.number().int().nonnegative(),
    placements: z.array(PrintJobPlacementSchema),
    /** `BLANK#<ssSku>` frozen on the batch item, NOT the live order line's. Absent for an unmapped line. */
    resolvedBlankSku: z.string().optional(),
    title: z.string().optional(),
    variantTitle: z.string().optional(),
    /** Drives the YOUTH marker. Absent would print an adult-looking tag on a youth garment. */
    sizeBand: SizeBandEnum,
});
/**
 * PENDING → PRINTED, and there is deliberately no third state.
 *
 * ⚠️ No CLAIMED/IN_FLIGHT step, on purpose. A claim-then-confirm protocol leaves a job wedged whenever
 * the agent dies between the two, and a wedged hem tag is invisible to the operator waiting at the
 * press. The transition is instead a single atomic PENDING → PRINTED, guarded by a condition on the
 * job still being pending, so of two agents polling the same queue exactly one wins. If the paper
 * jams, the operator taps the kiosk again — a duplicate TAG for one garment (two agents both printing)
 * is a mislabel, whereas a lost job is a re-tap.
 */
export const PrintJobStatusEnum = z.enum(['PENDING', 'PRINTED']);
export const CreatePrintJobBodySchema = z.object({
    /** Which geometry to print at; the agent claims only jobs matching the format it serves. */
    formatId: z.string().min(1).max(64),
    garment: PrintJobGarmentSchema,
});
export const PrintJobSchema = z.object({
    jobId: z.string(),
    formatId: z.string(),
    status: PrintJobStatusEnum,
    garment: PrintJobGarmentSchema,
    /** Who queued it — the Cognito subject, so the bench can say who asked. */
    queuedBy: z.string().optional(),
    createdAt: z.string(),
    printedAt: z.string().optional(),
});
/** `POST /print-jobs/next` — the claimed job, or `null` when the queue is empty for that format. */
export const ClaimPrintJobResponseSchema = z.object({
    job: PrintJobSchema.nullable(),
});
export const PrintJobListSchema = z.object({
    jobs: z.array(PrintJobSchema),
});
/** The persisted row. Pending jobs carry GSI1 keys; claiming REMOVES them, so the poll's index stays
 *  sparse and a printed job costs the query nothing. */
export const PrintJobRecordSchema = z.object({
    PK: z.string(),
    SK: z.literal('CONFIG'),
    /** `PRINTJOBS#PENDING` while pending, then removed — a sparse GSI, so the agent's 3-second poll
     *  reads only the queue and never the history. */
    GSI1PK: z.string().optional(),
    /** `<createdAt>#<jobId>`, so a forward query drains oldest-first. */
    GSI1SK: z.string().optional(),
    entity: z.literal('printJob'),
    jobId: z.string(),
    formatId: z.string(),
    status: PrintJobStatusEnum,
    garment: PrintJobGarmentSchema,
    queuedBy: z.string().optional(),
    createdAt: z.string(),
    printedAt: z.string().optional(),
});
// ── Change feed (realtime updates, `docs/realtime-updates-plan-v1.md` Phase 4 reader) ──
/**
 * The invalidation vocabulary.
 *
 * ⚠️ **Deliberately duplicated from `CHANGE_DOMAINS` in `@fsp/utils/change-cursor.ts`, which is the
 * source of truth.** `@fsp/contracts` cannot import `@fsp/utils` (that would drag the AWS SDK into the
 * most foundational package, and the arrow points the wrong way), and `@fsp/utils` cannot import
 * contracts (it would pull in pricing-engine + gang-sheet-engine). So the list is written twice and
 * pinned by a parity test in `rest-api`, which depends on both — the same technique already used for
 * `owedConsumeQty` / `lineOwed`. Adding a domain in one place and not the other fails the build.
 */
export const ChangeDomainEnum = z.enum([
    'orders',
    'batches',
    'designs',
    'inventory',
    'transfers',
    'rolls',
    'catalog',
    'purchaseOrders',
    'stores',
    'pricingConfig',
    'config',
]);
/**
 * The single cursor row written by the publisher: PK `CHANGES#FEED` / SK `CURSOR`.
 *
 * Loose on purpose. The revision counters are named `rev_<domain>` and created lazily by `ADD`, so
 * enumerating them here would mean editing this schema every time a domain is added — a second place to
 * forget. The reader narrows each one, and the RESPONSE schema below is what actually guarantees
 * completeness of the output.
 */
export const ChangeCursorRecordSchema = z.looseObject({
    PK: z.literal('CHANGES#FEED'),
    SK: z.literal('CURSOR'),
    /** Epoch ms of the most recent bump. Absent until the first write after the publisher was enabled. */
    asOf: z.number().int().nonnegative().optional(),
});
/**
 * `GET /changes` — the correctness floor for realtime updates.
 *
 * **Content-free by contract:** domain names and integers only, never business data. That is what keeps
 * this endpoint immune to the authorization concerns a data-carrying feed would have — every actual read
 * still goes back through the REST routes and their group gates.
 *
 * `domains` is optional, and that is a safety feature rather than convenience: it is ABSENT whenever
 * `feedEnabled` is false, so TypeScript forces a caller to narrow before reading a revision. A client
 * that skipped the check would otherwise treat a frozen cursor as "nothing changed" and silently stop
 * refreshing forever — the exact failure this endpoint exists to prevent.
 *
 * When present it is COMPLETE: `z.record` over an enum key requires every member, so a client can rely
 * on every domain being there rather than defending against holes.
 */
export const ChangeFeedResponseSchema = z.object({
    /**
     * Whether the cursor can be trusted. False when the reader OR the publisher is switched off; a client
     * seeing false must fall back to its own refetching and surface "live updates off" to staff.
     *
     * ⚠️ Reflects the API Lambda's own view of `CHANGE_CURSOR_ENABLED`. It cannot see the 8 async workers
     * (a separate stack), so the flag must be set on both or worker-side writes stop publishing while this
     * still reports true.
     */
    feedEnabled: z.boolean(),
    /** Epoch ms of the last bump; 0 when nothing has been published yet. Diagnostic only. */
    asOf: z.number().int().nonnegative(),
    /**
     * Monotonic per-domain revisions. Present ⇔ `feedEnabled`; complete when present.
     *
     * Note for client authors: `openapi-typescript` widens this to `{[key: string]: number}`, losing the
     * enum key. That is harmless here — `noUncheckedIndexedAccess` is on repo-wide, so `domains.orders` is
     * still `number | undefined`. For an exhaustive domain→query-key map, import `ChangeDomainEnum` from
     * this package directly (the dashboard already depends on it) rather than relying on the generated type.
     */
    domains: z.record(ChangeDomainEnum, z.number().int().nonnegative()).optional(),
    /**
     * Where to open the push socket (Phase 7c). **Its PRESENCE is the enable signal** — absent means "do not
     * connect", and there is deliberately no `enabled: boolean`, so `{enabled: true}` with no endpoint is not
     * a representable state.
     *
     * Three things fall out of the server owning this rather than the client baking it in at build time:
     *
     * 1. **7c ships inert with no second flag.** While `CHANGE_PUBLISH_ENABLED` is false nothing is ever
     *    published, so this is omitted and no client connects — no connection-minutes billed for a socket
     *    that could not deliver anything. Flipping the publisher on is what turns the socket on.
     * 2. **The existing kill-switch governs the socket too.** This lives inside the `feedEnabled` branch, so
     *    switching the reader off stops clients connecting on next load as well as stopping invalidation.
     * 3. ⚠️ **`channel` is sent, not shared as a constant, because publisher and subscriber MUST agree
     *    byte-for-byte.** Amplify passes the channel string to AppSync verbatim (`channel: query`, no
     *    normalization), so a leading-slash or naming disagreement would subscribe to a channel nothing
     *    publishes to — and the failure mode is silence, not an error. Emitting the publisher's own constant
     *    makes disagreement unrepresentable, and visible in the response for diagnosis.
     */
    push: z
        .object({
        /** Amplify `API.Events.endpoint` — the AppSync Events HTTP endpoint, `https://<dns>/event`. */
        endpoint: z.string(),
        /** Amplify `API.Events.region`. */
        region: z.string(),
        /** `<namespace>/<channel>` exactly as the publisher addresses it. */
        channel: z.string(),
    })
        .optional(),
});
// Rollo garment identity — durable-identity QR codec + canonical garment↔placement pairing.
// Pure, IO-free; the single source of truth shared by the label encoder and the scan→settle wiring.
// See ./garment-identity.ts for the charter note (why this pure module lives in contracts).
export * from './garment-identity.js';
// Reprint scope — which placements of a queued garment go back on film, and whether a replacement
// blank is drawn. Pure, IO-free; the single source of truth shared by the flush's cost quote, the
// flush's build, and the cutting-bench picker. See ./reprint-scope.ts for the invariant.
export * from './reprint-scope.js';
export * from './stock-bin-identity.js';
// ─────────────────────────────────────────────────────────────────────────────
// Shipping station (P5 S2) — `/shipments`: pack verification, rate quote, gated
// label buy, void, and the Shopify fulfillment write-back marker.
//
// Storage layout (all co-located on the order partition, like production events —
// their own SK types, so the ingest workers' full-row header/line puts never touch them):
//   ORDER#<storeId>#<orderId> / SHIPMENT#<shipmentId>          — one row per quoted/bought label
//   ORDER#<storeId>#<orderId> / SHIPVERIFY#<lineId>#RANK#<n>   — one row per scanned garment QR
//   ORDER#<storeId>#<orderId> / SHIPVERIFY#<lineId>#EYE        — logged eye-check count (tagless units)
//   ORDER#<storeId>#<orderId> / SHIPFULFILL                    — the once-only fulfillment write-back marker
// ─────────────────────────────────────────────────────────────────────────────
export const ShipmentProviderEnum = z.enum(['easypost']);
/** Linear lifecycle. VOIDED = refund REQUESTED (USPS refunds settle asynchronously — see refundStatus). */
export const ShipmentStatusEnum = z.enum(['QUOTED', 'BOUGHT', 'VOIDED']);
/**
 * The buy attempt's outbound-idempotency marker — the S&S live-order pattern verbatim
 * (`purchase-order-store.ts`): stamped `in_flight` BEFORE the provider POST, flipped to `placed` with
 * the confirmed label, REMOVED on a definite provider rejection, pinned `unknown` on an ambiguous
 * outcome. Unlike S&S, `unknown` here is machine-reconcilable: the provider shipment id is pinned at
 * quote time, so a later GET of that id answers "was it bought?" definitively.
 */
export const ShipmentBuyAttemptStatusEnum = z.enum(['in_flight', 'placed', 'unknown']);
/** Provider refund lifecycle (EasyPost: submitted → refunded | rejected; not_applicable = carrier quirk). */
export const ShipmentRefundStatusEnum = z.enum([
    'submitted',
    'refunded',
    'rejected',
    'not_applicable',
]);
/**
 * A postal address as the label provider consumes it. Used for both ship-to (fetched live from
 * Shopify at pack time — the snapshot on the shipment record is the ONLY persisted copy, per the
 * deliberate PII non-goal on `OrderRecord`) and ship-from (the shop's own `/shipto-config` address).
 */
export const ShipAddressSchema = z.object({
    name: z.string().min(1).max(120),
    company: z.string().max(120).optional(),
    street1: z.string().min(1).max(200),
    street2: z.string().max(200).optional(),
    city: z.string().min(1).max(100),
    state: z.string().min(1).max(50),
    zip: z.string().min(1).max(20),
    /** ISO-2. Defaults US — the measured cohort is 99.95% domestic; international is an S5 concern. */
    country: z.string().length(2).default('US'),
    phone: z.string().max(30).optional(),
    email: z.string().max(200).optional(),
});
/** Outbound parcel. Weight in OUNCES (the provider unit); dims in inches. 70 lb = the USPS ceiling. */
export const ShipParcelSchema = z.object({
    weightOz: z.number().positive().max(1120),
    lengthIn: z.number().positive().max(120),
    widthIn: z.number().positive().max(120),
    heightIn: z.number().positive().max(120),
});
/** One rated service, snapshotted from the provider at quote time. `rate` is USD. */
export const ShipmentRateSchema = z.object({
    rateId: z.string(),
    carrier: z.string(),
    service: z.string(),
    rate: z.number().nonnegative(),
    currency: z.string(),
    deliveryDays: z.number().int().nullable().optional(),
});
/** Per-line verification tally snapshotted onto the shipment at buy time (audit — live state is rows). */
export const ShipVerificationLineSummarySchema = z.object({
    lineId: z.string(),
    owed: z.number().int().nonnegative(),
    scanned: z.number().int().nonnegative(),
    eyeChecked: z.number().int().nonnegative(),
});
export const ShipVerificationSummarySchema = z.object({
    lines: z.array(ShipVerificationLineSummarySchema),
    /** Present iff a manager bought past the gate. The reason is required at the route. */
    override: z.object({ by: z.string(), reason: z.string(), at: z.string() }).optional(),
});
/**
 * One outbound shipment: `ORDER#<storeId>#<orderId>` / `SHIPMENT#<shipmentId>`. Created QUOTED (the
 * provider shipment + its rates already exist upstream — rating is free; only buy spends). GSI1
 * (`SHIPMENTS` / `<createdAt>#<shipmentId>`) lists shop-wide shipments newest-first (reconciliation
 * sweep + reporting). `providerMode` makes a test-key label permanently distinguishable from postage.
 */
export const ShipmentRecordSchema = z.object({
    PK: z.string(),
    SK: z.string(),
    GSI1PK: z.literal('SHIPMENTS'),
    GSI1SK: z.string(),
    entity: z.literal('shipment'),
    shipmentId: z.string(),
    storeId: z.string(),
    orderId: z.string(),
    orderName: z.string(),
    provider: ShipmentProviderEnum,
    providerShipmentId: z.string(),
    providerMode: z.enum(['test', 'production']),
    status: ShipmentStatusEnum,
    rates: z.array(ShipmentRateSchema),
    parcel: ShipParcelSchema,
    shipTo: ShipAddressSchema,
    shipFrom: ShipAddressSchema,
    createdAt: z.string(),
    createdBy: z.string().optional(),
    buyAttemptStatus: ShipmentBuyAttemptStatusEnum.optional(),
    buyAttemptedAt: z.string().optional(),
    buyAttemptedBy: z.string().optional(),
    boughtAt: z.string().optional(),
    boughtBy: z.string().optional(),
    chosenRateId: z.string().optional(),
    carrier: z.string().optional(),
    service: z.string().optional(),
    /** What the buy actually charged the wallet (USD), from the purchased rate. */
    cost: z.number().nonnegative().optional(),
    trackingCode: z.string().optional(),
    labelUrl: z.string().optional(),
    labelFileType: z.string().optional(),
    publicTrackingUrl: z.string().optional(),
    verification: ShipVerificationSummarySchema.optional(),
    voidedAt: z.string().optional(),
    voidedBy: z.string().optional(),
    refundStatus: ShipmentRefundStatusEnum.optional(),
});
/** API view: the shipment record minus DDB keys. */
export const ShipmentViewSchema = ShipmentRecordSchema.omit({
    PK: true,
    SK: true,
    GSI1PK: true,
    GSI1SK: true,
    entity: true,
});
/**
 * One scanned garment QR verified into the box: `SHIPVERIFY#<lineId>#RANK#<rank>`. The conditional
 * put on this row is what makes a re-scan of the same garment a DUPLICATE instead of a second count.
 */
export const ShipVerifyRankRecordSchema = z.object({
    PK: z.string(),
    SK: z.string(),
    entity: z.literal('shipVerifyRank'),
    storeId: z.string(),
    orderId: z.string(),
    lineId: z.string(),
    rank: z.number().int().positive(),
    at: z.string(),
    by: z.string().optional(),
});
/**
 * The logged eye-check count for one line: `SHIPVERIFY#<lineId>#EYE`. Tagless units (reissued
 * reprints print no tag; Ship-only stock never had QRs) are verified by a human count instead of a
 * scan — a distinct, attributable path, capped at the line's owed qty server-side.
 */
export const ShipVerifyEyeRecordSchema = z.object({
    PK: z.string(),
    SK: z.string(),
    entity: z.literal('shipVerifyEye'),
    storeId: z.string(),
    orderId: z.string(),
    lineId: z.string(),
    count: z.number().int().nonnegative(),
    reason: z.string().max(300).optional(),
    at: z.string(),
    by: z.string().optional(),
});
/**
 * The once-only Shopify fulfillment write-back marker: `ORDER#<storeId>#<orderId>` / `SHIPFULFILL`.
 * Claimed with `attribute_not_exists` BEFORE the fulfillmentCreate mutation with `fulfillmentIds: []`
 * (= PENDING), then completed with the real id(s) after Shopify confirms. A definite Shopify refusal
 * releases the pending marker (retry allowed); an ambiguous failure keeps it, and the next call lets
 * Shopify's fulfillment-order state decide (CLOSED = it happened). So at most one mutation attempt
 * is ever in flight from our side, beneath Shopify's own CLOSED-order refusal. The write-back is
 * deliberately one whole-order event in v1 — multi-parcel orders attach every tracking code to it.
 */
export const ShipFulfillmentRecordSchema = z.object({
    PK: z.string(),
    SK: z.literal('SHIPFULFILL'),
    entity: z.literal('shipFulfillment'),
    storeId: z.string(),
    orderId: z.string(),
    /** Shopify fulfillment ids (one per fulfillment order Shopify split the order into). */
    fulfillmentIds: z.array(z.string()),
    trackingCodes: z.array(z.string()),
    at: z.string(),
    by: z.string().optional(),
});
/** API view of the fulfillment marker. */
export const ShipFulfillmentViewSchema = ShipFulfillmentRecordSchema.omit({
    PK: true,
    SK: true,
    entity: true,
});
/** Per-line verification progress, live (owed = shippable qty: refund-netted, 0 when cancelled). */
export const ShipVerifyProgressLineSchema = z.object({
    lineId: z.string(),
    owed: z.number().int().nonnegative(),
    scanned: z.number().int().nonnegative(),
    eyeChecked: z.number().int().nonnegative(),
    /** min(owed, scanned-in-range + eyeChecked) — the number the gate compares to owed. */
    verified: z.number().int().nonnegative(),
    complete: z.boolean(),
});
/** A manifest line: verification progress + what the packer sees (title/size/photo, ship-only badge). */
export const ShipManifestLineSchema = ShipVerifyProgressLineSchema.extend({
    title: z.string(),
    variantTitle: z.string().optional(),
    sizeName: z.string().optional(),
    imageUrl: z.string().optional(),
    productionClass: ProductionClassEnum.optional(),
    eyeReason: z.string().optional(),
    // ── Shelf provenance (bin-labels plan § 3.7) — present only on a ship-from-stock line. Derived
    // from the FROZEN stockPickKey the pick stamped, never from the live line's mapping: a catalog
    // re-map between pick and pack must keep naming the shirts actually drawn (same rule as the undo
    // credit). These garments carry no scannable tag for THIS order, so the eye-check is their path;
    // the chip tells the packer what to eye-check against.
    /** Shirts drawn from the finished-garment shelf for this line. */
    stockPickQty: z.number().int().positive().optional(),
    /** The drawn bin's label id (sha256 of the frozen stockPickKey, first 12 hex). */
    stockPickBinId: z.string().optional(),
});
/**
 * `GET /shipments/:storeId/:orderId/manifest` — everything the pack screen needs: the expected box
 * contents with live verification progress, the reprint hold, prior shipments (labels bought/voided),
 * and the fulfillment write-back state. `reprintOutstanding` comes from the same fail-closed
 * `findOutstandingReprints` union every ship-gate surface uses — a lookup failure is a 500 here,
 * never a silent 0.
 */
export const ShipmentManifestResponseSchema = z.object({
    storeId: z.string(),
    orderId: z.string(),
    orderName: z.string(),
    orderFulfillmentState: FulfillmentStateEnum,
    lines: z.array(ShipManifestLineSchema),
    allVerified: z.boolean(),
    reprintOutstanding: z.number().int().nonnegative(),
    shipments: z.array(ShipmentViewSchema),
    fulfillment: ShipFulfillmentViewSchema.optional(),
    /** False until the shop's own address exists in `/shipto-config` — quoting refuses without it. */
    shipFromConfigured: z.boolean(),
});
/**
 * `POST /shipments/:storeId/:orderId/verify` body — exactly one of:
 * - `code`: a scanned garment QR (the same `v1|store|order|line|rank` payload the labels print);
 * - `eyeCheck`: the logged human count for a tagless line (count REPLACES the line's eye tally —
 *   absolute, not additive — so a correction is just re-entering the right number).
 */
export const ShipVerifyRequestSchema = z.union([
    z.object({ code: z.string().min(1).max(256) }),
    z.object({
        eyeCheck: z.object({
            lineId: z.string().min(1),
            count: z.number().int().nonnegative().max(999),
            reason: z.string().max(300).optional(),
        }),
    }),
]);
/**
 * Scan outcomes, mirroring `/scan/resolve`'s posture: every non-counting scan is an explicit state,
 * never an error. WRONG_ORDER is the loud one — a garment from a different order headed into this box.
 * NOT_OWED = the rank is beyond the line's shippable qty (a refunded unit — do not ship it).
 */
export const ShipVerifyStatusEnum = z.enum([
    'COUNTED',
    'DUPLICATE',
    'NOT_OWED',
    'WRONG_ORDER',
    'LINE_NOT_FOUND',
    'UNRECOGNIZED',
    'EYE_COUNTED',
]);
export const ShipVerifyResponseSchema = z.object({
    status: ShipVerifyStatusEnum,
    lineId: z.string().optional(),
    rank: z.number().int().positive().optional(),
    /** On WRONG_ORDER: where the scanned garment actually belongs, for the loud banner. */
    scannedStoreId: z.string().optional(),
    scannedOrderId: z.string().optional(),
    lines: z.array(ShipVerifyProgressLineSchema),
    allVerified: z.boolean(),
});
/** `POST /shipments/:storeId/:orderId/verify/reset` — clears the order's verification rows. */
export const ShipVerifyResetResponseSchema = z.object({
    cleared: z.number().int().nonnegative(),
});
/**
 * `POST /shipments/:storeId/:orderId/quote` body. Rating is free — no gate beyond the order existing,
 * being shippable (not CANCELLED, not already FULFILLED), and the ship-from being configured. When
 * `shipTo` is omitted the route fetches it live from Shopify (the normal path); supplying it is the
 * manual door (address correction, or a store whose token lacks the protected-data grant).
 */
export const QuoteShipmentRequestSchema = z.object({
    parcel: ShipParcelSchema,
    shipTo: ShipAddressSchema.optional(),
});
export const QuoteShipmentResponseSchema = z.object({
    shipment: ShipmentViewSchema,
});
/**
 * `POST /shipments/:storeId/:orderId/:shipmentId/buy` body. `rateId` must be one of the rates
 * snapshotted on the QUOTED shipment. `additionalParcel` is the explicit acknowledgement required to
 * buy a second live label for an order that already has one (multi-box orders). `override` is the
 * manager door past an incomplete verification or an outstanding reprint — reason required, logged
 * as SHIP_GATE_OVERRIDDEN, snapshotted on the record.
 */
export const BuyLabelRequestSchema = z.object({
    rateId: z.string().min(1),
    additionalParcel: z.boolean().optional(),
    override: z.object({ reason: z.string().min(3).max(300) }).optional(),
});
export const BuyLabelResponseSchema = z.object({
    shipment: ShipmentViewSchema,
    /** True when this call found the label already bought (idempotent replay / reconciled unknown). */
    alreadyBought: z.boolean(),
});
export const VoidLabelResponseSchema = z.object({
    shipment: ShipmentViewSchema,
});
/** `POST /shipments/:storeId/:orderId/fulfill` body — the Shopify write-back (separate, retryable). */
export const FulfillShipmentRequestSchema = z.object({
    notifyCustomer: z.boolean().default(true),
});
export const FulfillShipmentResponseSchema = z.object({
    fulfillment: ShipFulfillmentViewSchema,
    /** True when the marker already existed — the write-back had already happened; nothing was sent. */
    alreadyCreated: z.boolean(),
});
//# sourceMappingURL=index.js.map